/**
 * The world map.
 *
 * A DOM-and-SVG overlay rather than a canvas layer, for the same reason the
 * menu is: a strategic screen is mostly type, numbers and buttons, and the
 * canvas is for the field. Twenty-four provinces is a small enough graph that
 * re-rendering the whole thing on change is simpler and faster than keeping a
 * scene graph in sync — and a signature check means it only re-renders when the
 * board has actually moved. That mattered less when the realm was turn-based
 * and moved on a click; it matters a great deal now that it moves on its own,
 * because `update` is called every frame of a screen that is never idle.
 *
 * The day is in the signature, so the clock line redraws once every forty
 * simulated seconds rather than sixty times a second. It is a *date*, not a
 * countdown, for exactly that reason.
 *
 * This screen reads the simulation and writes nothing. Every action goes out as
 * a `Command`, exactly as the settlement HUD's build buttons do.
 */

import { keepScroll } from './dom.ts';
import { holdingSplit, yieldCuts } from './income.ts';
import {
  controlIn,
  downstreamOf,
  isTerminal,
  nodeIncome,
  nodeLeader,
  nodeName,
  realmTradeValue,
  tradeIncomeOf,
  tradeNode,
  tradeNodeOf,
  upstreamOf,
} from '../sim/campaign/trade.ts';
import {
  canPost,
  freeMerchants,
  merchantAt,
  merchantCap,
  merchantsOf,
  postReason,
  travelMonths,
} from '../sim/campaign/merchants.ts';
import { tradeOptions } from '../sim/campaign/tradeai.ts';
import { MISSION_NAMES, MerchantMission, type Merchant } from '../sim/campaign/tradetypes.ts';
import { bookFor, bookView, marketPrice } from './market.ts';
import { findOrder, ordersOf, postRefusal } from '../sim/campaign/market.ts';
import {
  BASE_REACH_HOPS,
  HULLS_PER_REACH,
  MARKET_KEYS,
  MARKET_PRICES,
  MAX_LIVE_ORDERS,
  MAX_ORDER_UNITS,
  MAX_PRICE_TENTHS,
  MIN_PRICE_TENTHS,
  ORDER_TTL_MONTHS,
  OrderKind,
  type MarketOrder,
} from '../sim/campaign/markettypes.ts';
import { RESOURCE_KEYS, RESOURCE_LABELS, UF, type ResourceKey } from '../sim/types.ts';
import { unitType } from '../data/units.ts';
import {
  canInterceptFleet,
  cargoArmies,
  cargoPoints,
  embarkRefusal,
  findFleet,
  fleetCapacity,
  fleetPoints,
  fleetRefusal,
  fleetSummary,
  fleetsAt,
  fleetsOf,
  isSailing,
  landingRefusal,
  laneAdjacency,
  maxAffordableShips,
  portBill,
  portIncome,
  portRefusal,
  sailMonths,
  sailRefusal,
  seaNeighbours,
  voyageProgress,
} from '../sim/campaign/fleets.ts';
import { FleetAction } from '../sim/campaign/commands.ts';
import {
  fortifyBlock,
  fortifyCost,
  tierGarrison,
  tierName,
  tierOf,
  tierYield,
  yieldScale,
} from '../sim/campaign/holdings.ts';
import {
  holdingRank,
  rankName,
  shelterCapacity,
  shelteredIn,
  shelteredPoints,
  shelterRefusal,
  standingGarrison,
} from '../sim/campaign/garrisons.ts';
import { Cmd, cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import {
  TurnPhase,
  TURN_PHASE_NAMES,
  ArmyStance,
  BANDIT_NATION,
  BOUNTY_GOLD,
  BOUNTY_TURNS,
  CAMP_LOOT_GOLD,
  CAMP_LOOT_IRON,
  GARRISON_BASE,
  GARRISON_PER_FORT,
  MAX_ARMY_POINTS,
  UPKEEP_GOLD_PER_POINT,
  MAX_VETERANCY,
  MIN_ARMY_POINTS,
  SIEGE_TURNS_PER_FORT,
  VETERANCY_PER_WIN,
} from '../sim/campaign/types.ts';
import type { Army, BanditCamp, CampaignState, Fleet, MarchWarning } from '../sim/campaign/types.ts';
import {
  MAX_FLEET_SHIPS,
  MAX_PORT,
  MIN_FLEET_SHIPS,
  SHIP_UPKEEP_GOLD,
} from '../sim/campaign/types.ts';
import {
  armiesIn,
  armiesOf,
  armiesStandingIn,
  armyModels,
  armyPoints,
  armySummary,
  canOrderMarch,
  findArmy,
  marchPath,
  orderRefusal,
  marchTurns,
  maxAffordablePoints,
  rawPoints,
  raisePrice,
} from '../sim/campaign/armies.ts';
import {
  atWar,
  defenceMultiplier,
  garrisonCap,
  mayAttack,
  nationName,
  provinceName,
} from '../sim/campaign/state.ts';
import { projectedIncome } from '../sim/campaign/turn.ts';
// The levy reader. Nothing in it is new machinery — see `sim/campaign/levies.ts`.
import { LEVY_DRAG_WORDS, LevyDrag, levyReport } from '../sim/campaign/levies.ts';
import {
  DAYS_PER_MONTH,
  MONTH_SECONDS,
  dateFromDay,
  daysToNextMonth,
  formatDateLong,
  formatDateShort,
  secondsToNextMonth,
} from '../sim/systems/calendar.ts';
import {
  canWalkBetween,
  linkBetween,
  neighbours,
  roadComponent,
  roadComponentCount,
} from '../sim/campaign/graph.ts';
import { canIntercept, marchesInProgress, type MarchLane } from '../sim/campaign/marches.ts';
import { WarnKind } from '../sim/campaign/types.ts';
import { campAt, campLoot, campYieldScale, hasCamp } from '../sim/campaign/bandits.ts';
import { visibleProvinces, warningsFor } from '../sim/campaign/warnings.ts';
import {
  DiploAction,
  diplomaticView,
  offerText,
  pendingOffers,
  warView,
  type CrownView,
  type WarView,
} from '../sim/campaign/diploacts.ts';
import {
  CB_NAMES,
  CasusBelli,
  OfferKind,
  SUBJECT_NAMES,
  SubjectKind,
  TERM_NAMES,
  type DiploOffer,
} from '../sim/campaign/diplotypes.ts';
import {
  LINK_NAMES,
  LinkKind,
  PROVINCES,
  PROVINCE_TERRAIN,
  REGIONS,
  SEA_CELLS,
  provinceDef,
} from '../data/provinces.ts';
import type { ProvinceTerrain } from '../data/provinces.ts';
import { nationAccent, nationCapital, nationDef } from '../data/nations.ts';
// `factions.ts` is no longer read here at all: its free-text `race` string was
// this file's only use of it, and the four labels that wanted it now ask the
// nation's own raceId instead. See `peopleOf`.
import { KIN_NAMES, race } from '../data/races.ts';
import { C, mix, toHex } from '../data/palette.ts';
import { reliefLayer, seaLayer, textureLayer } from './relief.ts';
import { biomeLayer, coastRuns, groundLayer } from './shore.ts';
import { riverPaths } from './rivers.ts';
import { NARROW_PX, TouchInput, isNarrow } from './touch.ts';
import {
  LEAGUES_PER_UNIT,
  MAP_VIEWBOX,
  VIEW,
  borderPaths,
  cellPath,
  coastPath,
  echoPaths,
  isCoastal,
  labelRank,
  waveMarks,
  provinceCentre,
  provinceLabel,
  provinceMarks,
  provinceSite,
  roadPath,
  roadPoints,
  alongRoad,
  alongSeaLane,
  portSlot,
  seaLanePath,
  seaLanePoints,
  warningSlot,
  openGround,
  warp,
} from '../data/cartography.ts';
import type { Pt, TerrainMark } from '../data/cartography.ts';

export interface WorldMapCallbacks {
  /** Everything this screen does, it does by issuing one of these. */
  onCommand(c: Command): void;
  /** Go and stand in the capital — the other half of the game. */
  onEnterCity(): void;
  /**
   * Go and stand in a holding of yours, by province index.
   *
   * Distinct from `onEnterCity` on purpose rather than for want of a default
   * argument: the capital's door is the Tab key and the sheet bar's City cell
   * and answers "take me home", and this one answers "take me *there*". Merging
   * them would mean the sheet bar's cell had to decide which town home is,
   * which is a question only `main.ts` can answer.
   */
  onEnterHold(province: number): void;
  /** The Ledger — the same screen the chrome bar's button and L open. */
  onOpenLedger(): void;
  /**
   * The Levies — where the crown's men come from, and what spending them costs.
   *
   * A screen of its own rather than a tab in this file's detail column, for the
   * reason `manage.ts` is one: it is about the whole realm rather than about
   * the province under the cursor, and the column is three hundred pixels wide.
   */
  onOpenLevies(): void;
  /** Back to the title screen. */
  onQuit(): void;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string);
}

function hex(n: number): string {
  return `#${(n >>> 0).toString(16).padStart(6, '0')}`;
}

const NEUTRAL = 0x4a463f;

/**
 * What a province is to *you*.
 *
 * The map had one visual channel for ownership — the holder's banner colour —
 * and twenty-four crowns to spend it on. That is a chart of *identity*, and
 * identity is the question nobody asks first. The question everybody asks first
 * is "which of this is mine, and which of it is going to kill me", and answering
 * it meant reading twenty-four names off a list and matching twenty-four muddy
 * tints on the board. It could not be done in a second and it could not be done
 * at all by anybody who cannot separate a brown from a slightly different brown.
 *
 * So relationship is the primary channel now and identity is secondary. There
 * are seven of these and never more, they map onto colours that already mean
 * these things everywhere else in the game — gold is you, oxblood is a wound,
 * plague-green is the one friendly accent in the palette — and each of them also
 * carries a *hatch*, so the board still reads with the colour taken away. Which
 * crown specifically is still on the board: it is the frontier band, the banner
 * and the lettering, which is where a chart has always put it.
 */
type Rel = 'mine' | 'sworn' | 'ally' | 'truce' | 'war' | 'other' | 'none';

/**
 * The tint each relation lays over the ground, and how much of it.
 *
 * "Only a banner is allowed to be saturated" is the palette's rule and it still
 * holds — these are mixes into a terrain ink that starts near black, so the
 * strongest of them is a dark olive-gold, not a flag. What they are allowed to
 * be is *separable*, which the twenty-four national accents at 20% were not.
 *
 * Yours is the heaviest deliberately. Everything else on this screen is graded
 * against it: the eye should find your own ground before it finds anything, and
 * then find the red.
 */
const REL_INK: Record<Rel, { ink: number; t: number }> = {
  mine: { ink: C.goldPale, t: 0.42 },
  sworn: { ink: C.gold, t: 0.3 },
  ally: { ink: C.plague, t: 0.46 },
  truce: { ink: C.will, t: 0.3 },
  war: { ink: C.oxbloodLight, t: 0.52 },
  other: { ink: C.parchment, t: 0.07 },
  none: { ink: C.parchment, t: 0.03 },
};

/**
 * Why those weights, and how to change them without breaking the chart.
 *
 * The first set of these was chosen by eye and it did not work. Mixed down into
 * the ash they came out as seven browns: an enemy sat 23 units of RGB distance
 * from a crown you had never met, and an ally sat 11 from a vassal — which is to
 * say the map had been given a second channel and had spent it on nothing. The
 * eye cannot do better than about 25 units on ground this dark, and no amount of
 * looking at the code says so. It has to be measured.
 *
 * What the current set measures, as fills over the standard paper:
 *
 *     mine   #8d8162  lum 129    the brightest thing on the board, by 27
 *     sworn  #726443  lum 101
 *     ally   #636b3b  lum 102
 *     truce  #5c656c  lum 100
 *     war    #763d3a  lum  73    the darkest, and the only red
 *     other  #565046  lum  81
 *     none   #514c42  lum  76
 *
 * Every pair is 30 apart or more except two, and both are deliberate: `other`
 * and `none` are 8 apart because they are the two answers that should be quiet
 * and identical-looking, and `sworn` and `ally` are 18 apart because they are
 * the two answers that are nearly the same answer — and they carry opposite
 * hatches, which is the channel that separates them.
 *
 * So: if you retune these, check the pairwise distances rather than the swatch.
 * `mine` must stay the lightest by a clear margin, `war` the darkest and the
 * only red, and nothing but `other`/`none` may sit inside 25 of anything else.
 */

/** How each relation is named in the key and in the province panel. */
const REL_WORD: Record<Rel, string> = {
  mine: 'yours',
  sworn: 'sworn to you',
  ally: 'allied',
  truce: 'under truce',
  war: 'at war with you',
  other: 'another crown',
  none: 'no crown holds it',
};

/**
 * Which relations get a hatch, and what it looks like.
 *
 * A second channel on top of the tint, for the same reason a chart hatches a
 * disputed border rather than colouring it: colour alone is one channel and this
 * board asks a lot of one channel. Only the four that matter are hatched — your
 * own ground, your sworn, your allies and your enemies — because hatching
 * everything is hatching nothing, and "another crown" and "nobody" are precisely
 * the two answers that should be quiet.
 *
 * The spacing is in map units, so it belongs to the *ground* rather than to the
 * type: it grows with the terrain hatching when you zoom in, and does not get
 * the `--wm-k` treatment that the lettering and the counters get. That is the
 * same rule `wm-terr` follows, and breaking it here would make the pattern
 * denser the further in you went until it turned into a solid fill.
 */
const REL_HATCH: Partial<Record<Rel, { d: string; w: number; op: number }>> = {
  // Yours: a close double-diagonal, the densest thing on the board.
  mine: { d: 'M-1 1 l2 -2 M0 4 l4 -4 M3 5 l2 -2', w: 0.34, op: 0.5 },
  // Sworn to you: the same lean, half as often — a lighter version of yours,
  // which is what a vassal is.
  sworn: { d: 'M0 4 l4 -4', w: 0.3, op: 0.42 },
  // Allied: the other diagonal, so an ally never reads as a weak version of you.
  ally: { d: 'M0 0 l4 4', w: 0.3, op: 0.44 },
  // At war: a cross. Two channels of "stop" — the only cross-hatch on the map.
  war: { d: 'M0 0 l4 4 M0 4 l4 -4', w: 0.32, op: 0.5 },
};

/**
 * The ground colour under each terrain, before the holder's tint goes on.
 *
 * **`Partial` on purpose, and the `??` at every call site is the point.** The
 * realm's terrain vocabulary is not this file's to decide — a sibling generating
 * deserts or tundra appends to `ProvinceTerrain`, and a total `Record` would
 * turn that into a compile error in the *map* rather than an unlettered patch of
 * ash. A missing entry draws as plain ground, which is a thing a player can look
 * at while somebody writes the glyph.
 */
const TERRAIN_INK: Partial<Record<ProvinceTerrain, number>> = {
  /**
   * **These are the chart's own colours, not `C.terr*`.**
   *
   * They used to be, and that was the fault behind "it all looks the exact
   * same". `C.terrAsh`, `C.terrForest`, `C.terrStone` and the rest live inside
   * about a fifth of the value range *on purpose* — `palette.ts`'s rule is that
   * ground must sit far below the units in value, because three hundred men
   * fighting over ash that competes with them is soup. That rule is about the
   * settlement, where the ground is a backdrop for soldiers drawn at size.
   *
   * On the strategic chart there are no soldiers and the ground **is** the
   * picture. Six greys eight units apart, then washed a quarter of the way to
   * parchment and tinted again toward the holder's colour, arrive as one
   * grey-brown from edge to edge — a forest and a desert within a couple of
   * units of each other. The same diagnosis, and the same answer, as the
   * minimap's ground table in `ui/minimap.ts`.
   *
   * So: keep every hue relationship the old table argued for — jungle is the
   * wet end of forest, desert is the one warm ground on a cold chart, ice is
   * stone pulled toward the water — and spread the **values** until a player
   * can name a province's country without reading its label. Marsh sits below
   * plain, hill above it, mountain highest and palest, because that is the
   * order they read in on every physical map anyone has looked at.
   */
  plain: 0x6d6b3e,
  hill: 0x7d6a42,
  forest: 0x33552c,
  marsh: 0x3f5340,
  coast: 0x4e6f74,
  mountain: 0x77716c,
  ruin: 0x6a5a4e,
  jungle: 0x1f5a24,
  desert: 0xa88b4a,
  ice: 0x9fb0bc,
};

/**
 * Why these carry hue and not only value.
 *
 * A first pass spread the ten grounds across the *value* range and left them
 * all warm grey, which fixed the flattest of the problem and not the visible
 * half of it: mountain, hill, plain and ruin are half the board between them,
 * and four warm greys eight units apart still read as one country. What tells
 * a highland from a heath on any map anyone has looked at is **hue** — heath is
 * olive, upland is tan, rock is neutral, dead ground is brown — so each of
 * these now sits somewhere different on the wheel as well as on the ladder.
 *
 * This is a deliberate exception to `palette.ts`'s "only units and banners may
 * be saturated", and the exception is scoped to this one view: there are no
 * units on the strategic chart to be drowned out, and the ground is the entire
 * subject. Every other screen still obeys the rule.
 */

/**
 * The ground's colour, for the per-cell wash under the politics.
 *
 * The same expression `territoryLayer` used to fold into every province fill —
 * the terrain's ink with a breath of parchment in it — lifted out so that the
 * bake in `shore.ts` can paint it per raster cell instead of per polygon. The
 * chart's colour language stays here; `shore.ts` is handed a function and never
 * learns what any of the colours are.
 */
function quiltInk(kind: ProvinceTerrain): number {
  return mix(TERRAIN_INK[kind] ?? C.terrAsh, C.parchment, 0.1);
}

/**
 * A point a fraction of the way along a sea lane.
 *
 * `seaLanePoints` gives the lane's own polyline — the same one the lane is
 * drawn from — so a ship placed with this is on the line rather than on a
 * straight guess between two ports, which on a lane that bends round a headland
 * would put it on the beach.
 */
function laneAt(a: number, b: number, t: number): Pt | null {
  const pts = seaLanePoints(a, b);
  if (pts.length === 0) return null;
  if (pts.length === 1) return pts[0];
  const f = Math.max(0, Math.min(0.999, t)) * (pts.length - 1);
  const i = Math.floor(f);
  const r = f - i;
  const p0 = pts[i];
  const p1 = pts[Math.min(pts.length - 1, i + 1)];
  return { x: p0.x + (p1.x - p0.x) * r, y: p0.y + (p1.y - p0.y) * r };
}

/**
 * A coordinate, at two decimals.
 *
 * *(Tried at one decimal on 2026-08-20 to shrink the repaint, and reverted:
 * `npm run chartperf` measured the markup at 5,963 kB before and after, because
 * the layers that actually carry the bytes — the borders, the terrain, the sea
 * lanes — are built in `data/cartography.ts` and format their own numbers. This
 * function is not in that path. An unmeasured saving is not a saving.)*
 */
function n(v: number): string {
  return v.toFixed(2);
}

// ── The terrain vocabulary ──────────────────────────────────────────────────
//
// **Everything here is a filled silhouette, and that is not a style choice.**
//
// A stroke on this chart is held to a constant weight on the glass — see the
// note on `harbourMark`, which learned it the hard way — while the geometry it
// traces grows with the map. So a glyph drawn as four strokes is a wire when you
// are close in and four pixels of nothing when you are out, and *that* is what
// the first cut of this file drew: hairline chevrons for mountains, hairline
// twigs for trees. Held against a hand-drawn map they read as scratches on a
// coloured polygon rather than as ground somebody had drawn.
//
// A filled shape scales with the map and therefore survives both ends. The
// hairlines that remain are *casings* on a fill, exactly as the town mark and
// the anchor use them: they sharpen an edge that is already there instead of
// being the only thing there.
//
// The second rule is that a mark alone is a texture and a mark **chained** is
// cartography. Mountains and hills are not drawn per-mark; the marks of a
// province are bucketed into bands along the local ridge bearing and each band
// is drawn as *one* continuous ridge — peaks upright, saddles between them,
// a common base. That is what makes a range read as a range instead of as
// scattered bumps, and it is why `RIDGE_BEARING` exists below.

/**
 * How tall a peak stands above its mark, as a multiple of the mark's radius.
 * Ridges want air above them and the scatter already leaves it: `SPACING`
 * in `cartography.ts` gives mountains the widest gap of any terrain.
 */
const PEAK_H = 2.7;
const HILL_H = 1.4;

/** Terrains that are drawn as a chained ridge rather than as separate marks. */
function isRelief(kind: string): boolean {
  return kind === 'mountain' || kind === 'hill';
}

/**
 * Countries the chart draws with ground and no scattered marks at all.
 *
 * The list has grown as the ground got better at speaking for itself, and the
 * rule behind it is worth stating because it decides where it stops.
 *
 * **A symbol earns its place when the ground cannot say the thing.** Shaded
 * relief says "steep" and a hillshade says which way a slope faces, so peaks
 * went first. A combed ridged fabric says "sand sea" — so the dune glyphs go,
 * and with them the little upright three-pronged tuft on open country, which
 * read as a cactus at every zoom anybody looked at it. Ice cracks and shore
 * ripples are likewise surface, and the fabric now draws both.
 *
 * What stays is what is *not* surface. A wood is land cover rather than
 * landform: no amount of shading says "these trees are dead", which is the one
 * fact the Deadwood exists to communicate. A fen's standing water is a fact
 * about the ground rather than its texture. And a ruin is a built thing, which
 * a terrain fabric has no vocabulary for at all.
 */
function noSymbols(kind: string): boolean {
  return (
    isRelief(kind) ||
    kind === 'desert' ||
    kind === 'ice' ||
    kind === 'coast' ||
    kind === 'plain'
  );
}

/**
 * A run of marks that will be drawn as one ridge, and the order they run in.
 *
 * Marks are laid down on a jittered grid over the whole cell, so a wide
 * mountain province holds several rows of them. Chaining all of them into one
 * polyline would zig-zag across the county; chaining each *row* gives parallel
 * ridges, which is what a real range looks like from above and what every
 * hand-drawn map draws.
 *
 * The rows are found by projecting onto the ridge bearing: `s` runs along the
 * range and `t` across it, and a band is a greedy run of marks whose `t` values
 * are within one grid step of each other.
 */
function ridgeBands(marks: TerrainMark[], theta: number): TerrainMark[][] {
  const ux = Math.cos(theta);
  const uy = Math.sin(theta);
  const proj = marks.map((m) => ({ m, s: m.x * ux + m.y * uy, t: -m.x * uy + m.y * ux }));
  proj.sort((a, b) => a.t - b.t);
  const bands: TerrainMark[][] = [];
  let cur: typeof proj = [];
  let anchor = 0;
  /**
   * How wide a row is, and it has to be measured rather than fixed.
   *
   * The scatter's own grid step is in `SPACING`, but a band runs along the ridge
   * bearing rather than along the grid, so at forty-five degrees a fixed width
   * cuts every row in half and the range comes apart into teeth. Scaling with
   * the marks' own radius handles the other half of it: the far level of detail
   * draws half the marks at half again the size, so its rows are twice as far
   * apart and want a band twice as wide.
   */
  const BAND = (marks.reduce((a, m) => a + m.r, 0) / marks.length) * 5.4;
  for (const p of proj) {
    if (cur.length === 0 || p.t - anchor <= BAND) {
      if (cur.length === 0) anchor = p.t;
      cur.push(p);
    } else {
      bands.push(cur.sort((a, b) => a.s - b.s).map((q) => q.m));
      cur = [p];
      anchor = p.t;
    }
  }
  if (cur.length > 0) bands.push(cur.sort((a, b) => a.s - b.s).map((q) => q.m));
  return bands;
}

/**
 * One band of marks, drawn as a single range.
 *
 * Two shapes come back: the body, which is the dark silhouette of the whole
 * chain, and the sunlit western faces, one pale wedge a summit. The east flanks
 * are left to the body's own ink, because a third value at this size is grain.
 *
 * **Both are built off one list of vertices, and they have to be.** The first
 * cut computed the lit faces independently — apex down to the base, full width —
 * and every interior summit's wedge stuck out past the silhouette, because an
 * interior summit's west slope does not run to the base at all: it runs to the
 * *saddle*, which is half way up. The picture came back with a pale zigzag
 * hanging off the side of every range. A shape derived from the same corners as
 * the outline cannot escape the outline, which is the general form of the fix.
 *
 * Peaks stay upright however the chain runs. A range that leans is a range whose
 * *chain* leans — the peaks themselves always point at the top of the sheet,
 * which is the convention every chart has used since there were charts.
 */
function ridgeChain(band: TerrainMark[], hill: boolean, detail = false): string {
  const wide = hill ? 2.5 : 2.35;
  /**
   * A band is split again before it is drawn, where two marks are further apart
   * than their own shoulders can bridge.
   *
   * The scatter leaves holes — a lake, a town clearing, the shape of the cell —
   * and joining across one produces a long flat plinth with a summit at each
   * end, which read in the picture as a leaning ribbon rather than as mountains.
   * A range is allowed to *stop* and start again ten units later; that is what
   * ranges do.
   */
  const runs: TerrainMark[][] = [];
  let run: TerrainMark[] = [];
  let dir = 0;
  for (const m of band) {
    const prev = run[run.length - 1];
    /**
     * Two reasons to end a run, and the second one is a correctness bug rather
     * than a matter of taste.
     *
     * A chain is drawn as one polygon: a skyline out along the summits and a
     * baseline back underneath them. That is only a simple polygon while the
     * summits march steadily in **one** horizontal direction — the moment the
     * chain doubles back, or runs straight down the sheet, the baseline crosses
     * the skyline and the fill turns into an hourglass. A north–south range is a
     * perfectly ordinary thing for a world to have, and the picture had one:
     * a bow-tie of light and dark sitting where a mountain should be.
     *
     * So a run is x-monotone by construction. A range that turns a corner is two
     * runs that share a summit, which is what it looks like anyway.
     */
    if (prev) {
      const dx = m.x - prev.x;
      const far = Math.hypot(dx, m.y - prev.y) > (prev.r + m.r) * wide * 1.6;
      const turned = dx === 0 || (dir !== 0 && Math.sign(dx) !== dir);
      if (far || turned) {
        runs.push(run);
        run = [];
        dir = 0;
      } else dir = Math.sign(dx);
    }
    run.push(m);
  }
  if (run.length > 0) runs.push(run);
  // Each run is x-monotone by the test above, so sorting by x either leaves it
  // alone or turns it round — and the polygon below wants it left to right.
  return runs
    .map((r) => ridgeRun([...r].sort((a, b) => a.x - b.x), hill, detail))
    .join('');
}

function ridgeRun(band: TerrainMark[], hill: boolean, detail: boolean): string {
  if (band.length === 0) return '';
  const H = hill ? HILL_H : PEAK_H;
  const cls = hill ? 'hill' : 'ridge';
  // Wide enough that two summits in a chain meet at the shoulder. A peak twice
  // as tall as it is broad is a tooth; one about as tall as it is broad is a
  // mountain, and it is the shoulders touching that turn a row into a range.
  const wide = hill ? 2.5 : 2.35;

  // A skyline of summits all one height is a saw blade. The variant is already
  // in the mark, and spending it here costs nothing and is the difference
  // between a range and a serration.
  const tall = [0.86, 1.0, 1.18, 0.94];
  const apex: Pt[] = band.map((m) => ({ x: m.x, y: m.y - m.r * H * (tall[m.v % 4] ?? 1) }));
  const foot: number[] = band.map((m) => m.y + m.r * 0.5);
  const halfW: number[] = band.map((m) => m.r * wide);
  /**
   * The west corner of each summit: the shoulder of the range for the first,
   * and the notch it shares with its neighbour for everybody after.
   *
   * **The notch is cut deep, close to the ground, and that number is the one
   * that decides whether this reads as mountains.** Set high — the first cut put
   * it at two fifths of the summit height — the slope from one apex to the next
   * is nearly straight and the chain comes out as a folded ribbon leaning
   * across the county. Cut down to a sixth, each summit has its own silhouette
   * and the chain is a skyline: peaks that clearly belong to one range and are
   * clearly separate peaks.
   */
  const west: Pt[] = band.map((m, k) => {
    if (k === 0) return { x: m.x - halfW[0], y: foot[0] };
    const p = band[k - 1];
    return {
      x: (p.x + m.x) / 2,
      y: (foot[k - 1] + foot[k]) / 2 - Math.min(p.r, m.r) * H * 0.16,
    };
  });
  const tail: Pt = {
    x: band[band.length - 1].x + halfW[band.length - 1],
    y: foot[band.length - 1],
  };

  let d = `M${n(west[0].x)} ${n(west[0].y)}`;
  for (let k = 0; k < band.length; k++) {
    if (k > 0) d += `L${n(west[k].x)} ${n(west[k].y)}`;
    d += hill
      ? `Q${n(apex[k].x - halfW[k] * 0.45)} ${n(apex[k].y)} ${n(apex[k].x)} ${n(apex[k].y)}`
      : `L${n(apex[k].x)} ${n(apex[k].y)}`;
  }
  d += hill
    ? `Q${n(tail.x - halfW[band.length - 1] * 0.45)} ${n(tail.y)} ${n(tail.x)} ${n(tail.y)}`
    : `L${n(tail.x)} ${n(tail.y)}`;
  for (let k = band.length - 1; k >= 0; k--) d += `L${n(band[k].x)} ${n(foot[k])}`;

  // The sunlit face: apex, west corner, and the foot directly under the apex.
  // Every corner of it is a corner of the body, so it is inside the body by
  // construction however the chain bends.
  const lit = band
    .map(
      (m, k) =>
        `M${n(apex[k].x)} ${n(apex[k].y)}L${n(west[k].x)} ${n(west[k].y)}L${n(m.x)} ${n(foot[k])}Z`,
    )
    .join('');

  /**
   * Hachures down the shaded flank, and **only in the near markup**.
   *
   * At the fit view a summit is eight pixels and three more lines inside it are
   * grain; four notches in the same summit is sixty pixels of flat dark fill
   * with a flat pale triangle stuck to one side, which is a paper cut-out. This
   * is the one piece of the vocabulary that is genuinely worth drawing at one
   * end of the zoom and worth *not* drawing at the other, and since the two
   * levels of detail are already separate markup it costs the fit view nothing.
   *
   * Every tick runs from a point on the east edge toward the foot under the
   * apex. That triangle is convex and both of its ends are corners of the body,
   * so a hachure cannot leave the mountain.
   */
  let hach = '';
  if (detail && !hill) {
    for (let k = 0; k < band.length; k++) {
      const east = k === band.length - 1 ? tail : west[k + 1];
      const f = { x: band[k].x, y: foot[k] };
      for (const t of [0.34, 0.56, 0.78]) {
        const px = apex[k].x + (east.x - apex[k].x) * t;
        const py = apex[k].y + (east.y - apex[k].y) * t;
        hach += `M${n(px)} ${n(py)}L${n(px + (f.x - px) * 0.5)} ${n(py + (f.y - py) * 0.5)}`;
      }
    }
  }
  /**
   * ── The three things that make a drawn peak read as a solid ──────────────
   *
   * What was here before was a pale triangle with a dark triangle stuck to it:
   * two flat fills, hard edges, sitting *on* the ground rather than in it. That
   * is a paper cut-out, and it is exactly the failure every guide to drawing
   * relief by hand warns about — "avoid smooth or straight lines", "place the
   * deepest shadow at the peak and fade it outward as cast shadow".
   *
   * So, in the order they are drawn:
   *
   * **A cast shadow on the ground**, offset down and right — away from the
   * north-west light everything else in this game uses. This is the single
   * biggest cue and the one that was entirely missing: a shape with a shadow
   * under it is standing on something, and a shape without one is printed on it.
   * It is the *silhouette* offset, not a blur, because the chart is vector and a
   * filter here would cost a rasterised layer per province.
   *
   * **Gradient faces instead of flat fills.** A real slope is brightest where it
   * turns toward the light at the ridge and falls off toward its own foot. Two
   * flat tones cannot say that at any size; two ramps say it at every size, and
   * cost one `<defs>` entry shared by every mountain on the board.
   *
   * **Snow on the summits.** The upper fifth of each apex, clipped to the body,
   * on the taller half of the peaks — the variant already in the mark decides
   * which, so a range has a snowline rather than a uniform frosting.
   */
  const shadow = SHADOW_OFF;
  const cast = band
    .map((m, k) => {
      const a = apex[k];
      const w = west[k];
      const e = k === band.length - 1 ? tail : west[k + 1];
      /**
       * A **uniform** translation of the silhouette, and that matters.
       *
       * The first cut offset each corner by a different amount — apex by one,
       * the east notch by 1.6, the foot by 1.9 — reasoning that a shadow
       * stretches. It does not stretch like that on a plan view, and the result
       * was a sheared quadrilateral that read as a second grey mountain
       * standing behind the first rather than as its shadow. A cast shadow is
       * the shape, moved; the only liberty taken is that it is foreshortened
       * vertically, because the ground recedes.
       */
      const dx = shadow;
      const dy = shadow * 0.55;
      return (
        `M${n(a.x + dx)} ${n(a.y + dy)}` +
        `L${n(e.x + dx)} ${n(e.y + dy)}` +
        `L${n(m.x + dx)} ${n(foot[k] + dy)}` +
        `L${n(w.x + dx)} ${n(w.y + dy)}Z`
      );
    })
    .join('');

  /**
   * The snow cap: the apex triangle down to a fifth of the way to the foot.
   *
   * Its corners are the apex and two points on the two edges the apex already
   * owns, so like the lit face it is inside the body by construction however
   * the chain bends — no clip path, and nothing can leak onto the county.
   */
  let snow = '';
  if (!hill) {
    for (let k = 0; k < band.length; k++) {
      if ((band[k].v & 1) === 0) continue;
      const a = apex[k];
      const w = west[k];
      const e = k === band.length - 1 ? tail : west[k + 1];
      const t = 0.26;
      snow +=
        `M${n(a.x)} ${n(a.y)}` +
        `L${n(a.x + (e.x - a.x) * t)} ${n(a.y + (e.y - a.y) * t)}` +
        `L${n(a.x + (w.x - a.x) * t)} ${n(a.y + (w.y - a.y) * t)}Z`;
    }
  }

  return (
    (hill ? '' : `<path class="ridgecast" d="${cast}" />`) +
    `<path class="${cls}" d="${d}Z" fill="url(#wm-${hill ? 'hill' : 'ridge'}-dark)" />` +
    `<path class="${hill ? 'hilllit' : 'ridgelit'}" d="${lit}" fill="url(#wm-${hill ? 'hill' : 'ridge'}-lit)" />` +
    (snow ? `<path class="ridgesnow" d="${snow}" />` : '') +
    (hach ? `<path class="ridgehach" d="${hach}" />` : '')
  );
}

/**
 * How far a peak's shadow is thrown, in map units.
 *
 * Small — a third of a summit's own half-width. Relief shadow on a chart is a
 * *seating* cue, not a lighting effect: enough that the eye reads the mountain
 * as standing on the county, little enough that a range does not turn into a
 * smear of grey down its eastern side.
 */
const SHADOW_OFF = 0.5;

/**
 * One terrain mark, for everything that is not relief.
 *
 * Deliberately crude — at the fit view a mark is about eight pixels across, and
 * anything more detailed than a silhouette turns into grain. The `v` variant
 * comes out of the same hash that placed the mark, so a wood is a wood and not
 * one stamp repeated forty times.
 *
 * The `default` branch is load-bearing: a terrain this file has never heard of
 * gets the plain's tussocks rather than a blank county. See `TERRAIN_INK`.
 */
function terrainGlyph(kind: ProvinceTerrain, x: number, y: number, r: number, v: number): string {
  const s = r * 1.5;
  switch (kind) {
    case 'forest': {
      /**
       * Deadwood. Three trees in four keep a crown; the fourth is a bare fork.
       *
       * The crown sits **close on a stubby trunk** and not on a stalk, which is
       * the whole difference between a wood and a row of lollipops — the first
       * cut drew a circle a full glyph-height above the ground and the picture
       * came back looking like mushrooms. A map tree is nearly all canopy.
       *
       * The bare fork is drawn small and last: it is the *accent* that says
       * which wood this is, and when it was the same size as the live trees it
       * was the loudest thing in the county.
       */
      if (v % 4 === 3) {
        return `<path class="tree bare" d="M${n(x - s * 0.13)} ${n(y + s * 0.75)}
            L${n(x - s * 0.09)} ${n(y - s * 0.35)}L${n(x - s * 0.62)} ${n(y - s * 1.0)}
            L${n(x - s * 0.05)} ${n(y - s * 0.72)}L${n(x - s * 0.04)} ${n(y - s * 1.35)}
            L${n(x + s * 0.05)} ${n(y - s * 1.35)}L${n(x + s * 0.07)} ${n(y - s * 0.75)}
            L${n(x + s * 0.58)} ${n(y - s * 1.05)}L${n(x + s * 0.11)} ${n(y - s * 0.4)}
            L${n(x + s * 0.15)} ${n(y + s * 0.75)}Z" />`;
      }
      /**
       * The crown is **lobed, not round**, and that is the difference between a
       * tree and a lollipop.
       *
       * An ellipse on a stick is what the previous cut drew and the picture came
       * back reading as a field of mushrooms — a circle is the one silhouette
       * nothing in nature has. Seven points at alternating radii, joined by
       * quadratics through their midpoints, give a smooth outline that bulges
       * where the points are: an oak from six inches away and a mass of foliage
       * from six feet, which are the two distances this glyph is looked at.
       */
      // The crown sits low and wide. A tree drawn as a small head on a long
      // stalk is a lollipop however good the outline is, and the ratio that
      // fixes it is blunt: crown at least three times the visible trunk.
      const w = s * (v === 0 ? 1.2 : v === 1 ? 1.0 : 0.88);
      const ch = s * (v === 1 ? 0.9 : 0.72);
      const LOBES = 7;
      const ring: Pt[] = [];
      for (let k = 0; k < LOBES; k++) {
        const a = -Math.PI / 2 + (k * 2 * Math.PI) / LOBES;
        // The variant shifts which lobes are the long ones, so two neighbouring
        // trees of the same size are still not the same tree.
        const rr = w * ((k + v) % 2 === 0 ? 1 : 0.76);
        ring.push({ x: x + Math.cos(a) * rr, y: y - ch + Math.sin(a) * rr * 0.9 });
      }
      const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      const m0 = mid(ring[LOBES - 1], ring[0]);
      let crown = `M${n(m0.x)} ${n(m0.y)}`;
      for (let k = 0; k < LOBES; k++) {
        const m = mid(ring[k], ring[(k + 1) % LOBES]);
        crown += `Q${n(ring[k].x)} ${n(ring[k].y)} ${n(m.x)} ${n(m.y)}`;
      }
      return `<path class="tree" d="M${n(x - s * 0.2)} ${n(y + s * 0.95)}
          L${n(x - s * 0.15)} ${n(y - ch)}L${n(x + s * 0.15)} ${n(y - ch)}
          L${n(x + s * 0.2)} ${n(y + s * 0.95)}Z
          ${crown}Z" />`;
    }
    case 'marsh':
      /**
       * Standing water with reed in it, and the water has to win.
       *
       * The first cut drew a tall three-spike clump and it came back reading as
       * a fir tree — which is the worst possible failure for this glyph, because
       * marsh and forest are the two terrains a player most needs to tell apart
       * and they were saying the same thing. The clump is now squat and wider
       * than it is tall, and it sits *on* three flat courses of the pen. Reed is
       * the accent; the water is the mark.
       */
      return `<path class="marshwater" d="M${n(x - s * 1.5)} ${n(y + s * 0.2)}h${n(s * 3.0)}
          M${n(x - s * 1.05)} ${n(y + s * 0.95)}h${n(s * 2.1)}
          M${n(x - s * 0.75)} ${n(y - s * 0.55)}h${n(s * 1.5)}" />
        <path class="tussock" d="M${n(x - s * 0.86)} ${n(y + s * 0.2)}
          L${n(x - s * 0.56)} ${n(y - s * 0.52)}L${n(x - s * 0.22)} ${n(y + s * 0.2)}
          L${n(x + s * 0.02)} ${n(y - s * 0.72)}L${n(x + s * 0.26)} ${n(y + s * 0.2)}
          L${n(x + s * 0.58)} ${n(y - s * 0.45)}L${n(x + s * 0.86)} ${n(y + s * 0.2)}Z" />`;
    case 'coast':
      // Shingle and the swell coming onto it. Two ripples and three stones.
      return `<path class="ripple" d="M${n(x - s * 1.5)} ${n(y)}q${n(s * 0.75)} ${n(-s * 0.7)} ${n(s * 1.5)} 0
          q${n(s * 0.75)} ${n(s * 0.7)} ${n(s * 1.5)} 0" />
        <path class="shingle" d="M${n(x - s * 0.9)} ${n(y + s * 0.85)}a${n(s * 0.26)} ${n(s * 0.2)} 0 1 0 ${n(s * 0.52)} 0
          a${n(s * 0.26)} ${n(s * 0.2)} 0 1 0 ${n(-s * 0.52)} 0Z
          M${n(x + s * 0.35)} ${n(y + s * 1.05)}a${n(s * 0.22)} ${n(s * 0.17)} 0 1 0 ${n(s * 0.44)} 0
          a${n(s * 0.22)} ${n(s * 0.17)} 0 1 0 ${n(-s * 0.44)} 0Z" />`;
    case 'jungle': {
      /**
       * A tree-fern: a burst of arced blades off a stub of trunk.
       *
       * The one job of this glyph is to not be the forest twenty units away,
       * and the oak's failure mode is the guide: the oak is all round crown, so
       * the jungle is all *points* — five blades drawn as filled crescents,
       * each one a quadratic out and a quadratic back with a sharp tip, on a
       * trunk kept even stubbier than the oak's. Read together with the densest
       * scatter on the chart (`SPACING` gives jungle 3.2 against the wood's
       * 3.8), a county of these is unmistakably the wet green hell the region
       * text promises. One glyph in four is an understorey dome with the blades
       * poking through — a canopy is layers, and forty identical ferns are a
       * wallpaper.
       */
      const blades = 5;
      let d = '';
      for (let k = 0; k < blades; k++) {
        // -156° to -24°, so the spray opens upward and none of it stabs the ground.
        const a = (-156 + (k * 132) / (blades - 1) + ((v * 7 + k * 11) % 5) - 2) * (Math.PI / 180);
        const len = s * (1.15 + (((v + k) % 3) - 1) * 0.14);
        const tx = x + Math.cos(a) * len;
        const ty = y - s * 0.25 + Math.sin(a) * len * 0.92;
        // Control points off the chord, so each blade bows like a frond and
        // comes back to the stem with width — a filled crescent, not a line.
        const mx = (x + tx) / 2 - Math.sin(a) * s * 0.34;
        const my = (y - s * 0.25 + ty) / 2 + Math.cos(a) * s * 0.34;
        const nx = (x + tx) / 2 + Math.sin(a) * s * 0.1;
        const ny = (y - s * 0.25 + ty) / 2 - Math.cos(a) * s * 0.1;
        d += `M${n(x)} ${n(y - s * 0.25)}Q${n(mx)} ${n(my)} ${n(tx)} ${n(ty)}Q${n(nx)} ${n(ny)} ${n(x)} ${n(y - s * 0.25)}`;
      }
      const dome =
        v % 4 === 1
          ? `<path class="canopy" d="M${n(x - s * 1.05)} ${n(y + s * 0.6)}
              Q${n(x - s * 0.6)} ${n(y - s * 0.25)} ${n(x)} ${n(y - s * 0.05)}
              Q${n(x + s * 0.62)} ${n(y - s * 0.28)} ${n(x + s * 1.05)} ${n(y + s * 0.6)}Z" />`
          : '';
      return `${dome}<path class="frond" d="M${n(x - s * 0.1)} ${n(y + s * 0.7)}
          L${n(x - s * 0.07)} ${n(y - s * 0.25)}L${n(x + s * 0.07)} ${n(y - s * 0.25)}
          L${n(x + s * 0.1)} ${n(y + s * 0.7)}Z ${d}" />`;
    }
    case 'desert': {
      /**
       * Dunes: two low crescents, and nothing else for six and a half units.
       *
       * A desert on a chart is mostly the *absence* of marks — `SPACING` gives
       * it the widest gap on the board — so what marks there are must be low
       * and horizontal where every other glyph is upright: a long windward back
       * drawn as one shallow quadratic and a slip face dropping off the crest.
       * The second, smaller dune breaks the silhouette so a county of them
       * reads as a sand sea and not a row of breves. One in four is a scatter
       * of three stones — the bones of the ground showing through, which is
       * what the flavour text keeps saying about this terrain.
       */
      if (v % 4 === 3) {
        return `<path class="shingle" d="M${n(x - s * 0.9)} ${n(y + s * 0.3)}a${n(s * 0.28)} ${n(s * 0.2)} 0 1 0 ${n(s * 0.56)} 0a${n(s * 0.28)} ${n(s * 0.2)} 0 1 0 ${n(-s * 0.56)} 0Z
            M${n(x + s * 0.2)} ${n(y + s * 0.5)}a${n(s * 0.24)} ${n(s * 0.17)} 0 1 0 ${n(s * 0.48)} 0a${n(s * 0.24)} ${n(s * 0.17)} 0 1 0 ${n(-s * 0.48)} 0Z
            M${n(x - s * 0.1)} ${n(y - s * 0.35)}a${n(s * 0.2)} ${n(s * 0.15)} 0 1 0 ${n(s * 0.4)} 0a${n(s * 0.2)} ${n(s * 0.15)} 0 1 0 ${n(-s * 0.4)} 0Z" />`;
      }
      const flip = v % 2 === 1 ? -1 : 1;
      const cx1 = x + flip * s * 0.35;
      const cx2 = x - flip * s * 0.75;
      return `<path class="dune" d="M${n(x - flip * s * 1.5)} ${n(y + s * 0.45)}
          Q${n(cx1 - flip * s * 0.5)} ${n(y - s * 0.75)} ${n(cx1)} ${n(y - s * 0.6)}
          Q${n(cx1 + flip * s * 0.42)} ${n(y - s * 0.28)} ${n(cx1 + flip * s * 1.0)} ${n(y + s * 0.45)}Z
          M${n(cx2 - flip * s * 0.8)} ${n(y + s * 0.45)}
          Q${n(cx2 - flip * s * 0.1)} ${n(y - s * 0.15)} ${n(cx2 + flip * s * 0.2)} ${n(y - s * 0.08)}
          Q${n(cx2 + flip * s * 0.4)} ${n(y + s * 0.12)} ${n(cx2 + flip * s * 0.62)} ${n(y + s * 0.45)}Z" />
        <path class="dunecrest" d="M${n(x - flip * s * 1.5)} ${n(y + s * 0.45)}
          Q${n(cx1 - flip * s * 0.5)} ${n(y - s * 0.75)} ${n(cx1)} ${n(y - s * 0.6)}" />`;
    }
    case 'ice': {
      /**
       * Seracs: two leaning shards of pressure ice over a drift line.
       *
       * The Hoarfast's ground is already the coldest ink on the chart, so the
       * glyph's job is texture rather than colour: hard verticals with sharp
       * tips — the one silhouette the living terrains never make — standing on
       * one long horizontal, which is the wind. Filled pale with a near-black
       * casing, the same two-value rule every glyph on this chart obeys, so it
       * survives both the unheld near-black ground and the pale gold of a
       * player's own county. One in four is the drift alone: an empty white
       * mile is most of what eternal winter is.
       */
      if (v % 4 === 2) {
        return `<path class="icedrift" d="M${n(x - s * 1.3)} ${n(y + s * 0.3)}h${n(s * 2.6)}
            M${n(x - s * 0.7)} ${n(y + s * 0.75)}h${n(s * 1.5)}" />`;
      }
      const lean = v % 2 === 0 ? 1 : -1;
      return `<path class="serac" d="M${n(x - lean * s * 0.95)} ${n(y + s * 0.6)}
          L${n(x - lean * s * 0.45)} ${n(y - s * 1.1)}L${n(x - lean * s * 0.05)} ${n(y + s * 0.6)}Z
          M${n(x + lean * s * 0.25)} ${n(y + s * 0.6)}
          L${n(x + lean * s * 0.6)} ${n(y - s * 0.55)}L${n(x + lean * s * 0.95)} ${n(y + s * 0.6)}Z" />
        <path class="icedrift" d="M${n(x - s * 1.35)} ${n(y + s * 0.6)}h${n(s * 2.7)}" />`;
    }
    case 'ruin':
      // A broken course of wall, filled: the gaps in the skyline are the whole
      // glyph, and a hairline outline of them was invisible at every zoom.
      return `<path class="ruin" d="M${n(x - s * 1.5)} ${n(y + s * 0.85)}L${n(x - s * 1.5)} ${n(y - s * 0.5)}
          L${n(x - s * 0.72)} ${n(y - s * 0.62)}L${n(x - s * 0.72)} ${n(y + s * 0.12)}
          L${n(x - s * 0.2)} ${n(y + s * 0.12)}L${n(x - s * 0.2)} ${n(y - s * 1.15)}
          L${n(x + s * 0.42)} ${n(y - s * 1.25)}L${n(x + s * 0.42)} ${n(y + s * 0.12)}
          L${n(x + s * 0.86)} ${n(y + s * 0.12)}L${n(x + s * 0.86)} ${n(y - s * 0.45)}
          L${n(x + s * 1.5)} ${n(y - s * 0.5)}L${n(x + s * 1.5)} ${n(y + s * 0.85)}Z" />`;
    default:
      // Ash plain: a low filled tuft of grass. Present, but barely — this is the
      // ground everything else is measured against and it must never shout.
      return `<path class="tuft" d="M${n(x - s * 0.85)} ${n(y + s * 0.2)}
          L${n(x - s * 0.5)} ${n(y - s * 0.72)}L${n(x - s * 0.2)} ${n(y + s * 0.2)}
          L${n(x + s * 0.02)} ${n(y - s * 0.92)}L${n(x + s * 0.24)} ${n(y + s * 0.2)}
          L${n(x + s * 0.56)} ${n(y - s * 0.66)}L${n(x + s * 0.86)} ${n(y + s * 0.2)}Z" />`;
  }
}

/**
 * Which way the range runs through each province — the whole of what makes a
 * chain of peaks a *range* rather than a row of teeth.
 *
 * **This is an approximation and it is the wrong place for it.** The realm
 * genuinely knows: `realm.ts` declares its ranges as polylines and drops the
 * passes at their saddles, and if that were exposed this function would be four
 * lines of "sample the nearest segment". It is not exposed, so the bearing is
 * recovered from the only thing that is — *which provinces came out mountainous*
 * — by taking the principal axis of the vectors to a province's relief
 * neighbours. Doubled angles, because a range has an axis and not a direction:
 * a neighbour to the north-east and one to the south-west are the same range.
 *
 * A massif with relief on every side cancels to nothing; there the fallback is
 * the principal axis of the province's own marks, which is the shape of the
 * ground it was given rather than a guess.
 *
 * Lazy, because `borderPaths()` builds the whole traced complex and this file
 * must not do that at import time.
 */
let ridgeBearingCache: number[] | null = null;

function ridgeBearing(i: number): number {
  if (!ridgeBearingCache) {
    const adj: number[][] = PROVINCES.map(() => []);
    for (const s of borderPaths()) {
      if (s.b < 0) continue;
      adj[s.a]?.push(s.b);
      adj[s.b]?.push(s.a);
    }
    ridgeBearingCache = PROVINCES.map((_, k) => {
      const kind = provinceDef(k).terrain;
      if (!isRelief(kind)) return 0;
      const here = provinceCentre(k);
      let cx = 0;
      let sy = 0;
      for (const j of adj[k] ?? []) {
        const other = provinceDef(j).terrain;
        if (!isRelief(other)) continue;
        // A mountain neighbour speaks louder than a foothill about where the
        // spine of the range is.
        const w = other === 'mountain' ? 2 : 1;
        const q = provinceCentre(j);
        const a = Math.atan2(q.y - here.y, q.x - here.x);
        cx += Math.cos(2 * a) * w;
        sy += Math.sin(2 * a) * w;
      }
      if (cx * cx + sy * sy > 0.35) return Math.atan2(sy, cx) / 2;
      // No relief next door: fall back to the shape of this province's own
      // hatching, which is the shape of the ground.
      const marks = provinceMarks(k);
      if (marks.length < 2) return 0;
      let mx = 0;
      let my = 0;
      for (const m of marks) {
        mx += m.x;
        my += m.y;
      }
      mx /= marks.length;
      my /= marks.length;
      let sxx = 0;
      let sxy = 0;
      let syy = 0;
      for (const m of marks) {
        sxx += (m.x - mx) ** 2;
        sxy += (m.x - mx) * (m.y - my);
        syy += (m.y - my) ** 2;
      }
      return 0.5 * Math.atan2(2 * sxy, sxx - syy);
    });
  }
  return ridgeBearingCache[i] ?? 0;
}

/**
 * Every mark a province's terrain wants, at one of two densities.
 *
 * **The level-of-detail rule, and it is the job.** The reference is a static
 * illustration at enormous resolution and it can afford to draw every tree; this
 * is one interactive chart of a hundred and fifty provinces, and drawing every
 * tree at the fit view produces noise rather than a wood. So the terrain is
 * drawn *twice*, into two groups, and the chart's `wm-lod*` class shows exactly
 * one of them:
 *
 * - **far** (the fit view) — every other mark, drawn half again as large. Fewer
 *   glyphs, each big enough to have a silhouette at eight pixels. This is the
 *   view where the question is "is that a mountain range or a forest", and the
 *   answer has to survive being two hundred provinces wide.
 * - **near** (everything closer) — every mark at its own size, which at that
 *   zoom is a drawing rather than a texture.
 *
 * Neither set is scaled by `--wm-k`. Terrain belongs to the *ground*: it grows
 * with the map exactly as the ground does, which is the same rule `wm-relhatch`
 * follows and the opposite of the one the lettering follows. Getting that
 * backwards makes the hatching denser the further in you go until it is a solid
 * fill.
 */
function terrainOf(i: number, far: boolean): string {
  const kind = provinceDef(i).terrain;
  /**
   * **Relief draws no symbols at all any more.**
   *
   * Mountains and hills used to be drawn here as chains of little peaks — a
   * lit face, a dark face, a snow cap, a cast shadow — and however carefully
   * each one was built, a range made of discrete stamps reads as a row of
   * teeth. The owner's word for them was the right one.
   *
   * What replaced them is not another symbol. It is the *ground*: `ui/relief.ts`
   * bakes a shaded relief with dendritic erosion detail, and a range now reads
   * the way a range reads on a real shaded-relief map — as a dissected mass
   * with spurs and valleys, continuous with the country around it. A symbol on
   * top of that would be a second, worse statement of the same fact.
   *
   * Everything that is not relief keeps its marks, because a wood, a fen and a
   * dune field are *land cover* rather than *landform* — no amount of shading
   * says "these trees are dead", and a hillshade cannot draw a ruin.
   */
  if (noSymbols(kind)) return '';
  const all = provinceMarks(i);
  const relief = false as boolean;
  /**
   * Relief thins differently from everything else, and this is the one place
   * the level-of-detail rule is not uniform.
   *
   * A wood can lose half its trees at the fit view and still be a wood — the
   * remaining ones, drawn larger, read as a mass. **A range cannot lose half its
   * peaks**, because what makes a range a range is that it is *continuous*: take
   * every other summit out and the chain comes apart into a scatter of dark
   * teeth, which is precisely the fault this whole pass was opened to fix. So
   * relief keeps every mark and grows more gently; everything else halves and
   * grows harder.
   */
  const marks = far
    ? relief
      ? all.map((m) => ({ ...m, r: m.r * 1.35 }))
      : // `|| all.length < 3` keeps a one-mark province from vanishing at the
        // fit view, which reads as a hole in the map rather than as a small one.
        all.filter((_, k) => k % 2 === 0 || all.length < 3).map((m) => ({ ...m, r: m.r * 1.9 }))
    : all;
  if (marks.length === 0) return '';
  if (relief) {
    const hill = kind === 'hill';
    return ridgeBands(marks, ridgeBearing(i))
      .map((b) => ridgeChain(b, hill, !far))
      .join('');
  }
  return marks.map((m) => terrainGlyph(kind, m.x, m.y, m.r, m.v)).join('');
}

/**
 * The settlement mark, and it *is* the fortification: what a player needs from
 * a glance at a province is how hard it is to take, so the walls are the icon
 * rather than a number stuck next to one.
 */
/**
 * The baked settlement still for the province sheet, where the drop-in
 * building art exists.
 *
 * This was first tried *on the chart*, in place of the ink keeps, and the
 * result is worth recording: a town counter is twelve pixels tall on the
 * glass at every zoom, and at twelve pixels a full-colour render is a smudge
 * where the ink keep is a word. Silhouette-first is not a style preference,
 * it is what that size demands — so the chart keeps its ink, and the still
 * goes where there is room to read it: the sheet, beside the walls line it
 * illustrates.
 *
 * The four fortification levels borrow the four buildings whose silhouettes
 * already say the same ascending thing in the city view — an open farmstead,
 * a hearth-hall, a walled barracks, a tower — so the sheet and the ground
 * agree about what a holding looks like. Probed once, the artdrop way: a
 * missing file simply shows nothing.
 */
const GROUND_STILLS = ['farm', 'hearth', 'barracks', 'arrow_tower'] as const;
const groundStillReady: boolean[] = [];
let groundStillsProbed = false;

function groundStill(fort: number): string {
  if (!groundStillsProbed) {
    groundStillsProbed = true;
    GROUND_STILLS.forEach((id, k) => {
      const img = new Image();
      img.onload = () => {
        groundStillReady[k] = true;
      };
      img.src = `art/buildings/ashen/${id}.png`;
    });
  }
  const k = Math.max(0, Math.min(GROUND_STILLS.length - 1, fort));
  if (!groundStillReady[k]) return '';
  return `<img class="wm-groundpic" src="art/buildings/ashen/${GROUND_STILLS[k]}.png" alt="" />`;
}

/**
 * How much bigger every settlement mark is than it was drawn.
 *
 * > *"the cities needs to be bigger on the campaign map"*
 *
 * The marks were authored for the twenty-four-province board and never grew
 * with it: a walled seat is about five units across on a chart six hundred and
 * fifty provinces wide, which at the fit view is a smudge and at play zoom is
 * still smaller than the name printed under it. A place is more important than
 * its label.
 *
 * **4.2x, and this is the third time of asking.** 1.75 read as "slightly
 * bigger"; 2.4 was reported back as still way too small. The pattern across
 * this whole screen has been that my first number is roughly half what the
 * board needs, so this one is deliberately past what looks right in isolation:
 * a mark on a chart of six hundred and fifty provinces is competing with a
 * name set at 22px and with the ground under both.
 *
 * Applied as a transform about the mark's own point rather than by editing
 * forty coordinates, so the *shape* is untouched and the tiers still differ
 * from each other by the things that distinguish them — a gate, a second tower
 * — rather than by a scale factor somebody has to keep in step.
 */
const MARK_SCALE = 7.4;

/**
 * How far the settlement mark sits below the province's point, and how far the
 * name sits above it.
 *
 * > *"it should also be below the name otherwise it gets lost"*
 *
 * Both are needed, not one: dropping the mark alone leaves it in the same place
 * relative to a name that was already sometimes underneath it. Together they
 * make one shape — name on top, mark below, always the same way round on every
 * holding — which is what makes a board of six hundred and fifty of them
 * scannable rather than a field of things to decode individually.
 */
const MARK_DROP = 5.6;
const LABEL_LIFT = 2.2;

/**
 * **A place is as big on the chart as it is in the realm.**
 *
 * > *"copy eu5 for the size of the city on the map, it should change the bigger
 * > the city is an age"*
 *
 * That is the device those games use and it is the one thing a settlement mark
 * can say without a single word: a village is a dot, a great city is a mark you
 * can pick out across the board. This chart drew every holding at one size and
 * said the difference in *shape* — a gate at fort 3, a second tower — which is
 * a distinction you have to already know how to read, and which says nothing at
 * all about how big the place is, only how well defended.
 *
 * Two inputs, because the owner named two. **Rung** (`tier`, 1..10) is the
 * settlement's own development and does most of the work. **Age** is the
 * board's, not the province's — `CampaignState.age` moves for everybody at once
 * — so it is a gentler term: it lifts the whole chart a little as the world
 * grows, which is what an age *is* here, rather than pretending one town aged
 * and its neighbour did not.
 *
 * The span is deliberately wide. A range of ±15% is a range nobody sees; this
 * runs from 0.62 of the base mark at a rung-1 hamlet in the first age to 1.42
 * at a rung-10 city in the fifth, which is better than a factor of two between
 * the ends and legible at a glance.
 */
function markScale(tier: number, age: number): number {
  const rung = Math.max(1, Math.min(10, tier)) - 1; // 0..9
  const era = Math.max(1, Math.min(5, age)) - 1; // 0..4
  return MARK_SCALE * (0.62 + rung * 0.078 + era * 0.025);
}

/** Grow a mark about its own anchor, leaving its geometry alone. */
function grown(s: Pt, body: string, scale = MARK_SCALE): string {
  return `<g transform="translate(${n(s.x)} ${n(s.y)}) scale(${scale.toFixed(3)}) translate(${n(
    -s.x,
  )} ${n(-s.y)})">${body}</g>`;
}

/**
 * The outbuildings a place has grown, beyond the one mark that names it.
 *
 * > *"it should add more buildings when it grows"*
 *
 * Scale alone says "this is a bigger place" the way a bigger font does — it is
 * read as emphasis rather than as *more*. What says a town grew is that there
 * is more of it: another roof, then another, clustered round the first the way
 * a real one spreads. So a rung-1 hamlet is the single mark it always was, and
 * every few rungs adds a hut beside it.
 *
 * They are drawn *behind* the main mark and deliberately smaller, so the
 * silhouette that tells you what the place **is** — walls, gate, towers — stays
 * the thing you read first and the cluster is texture around it.
 */
function outbuildings(s: Pt, tier: number): string {
  const count = Math.max(0, Math.min(4, Math.floor((Math.max(1, tier) - 1) / 2)));
  if (count === 0) return '';
  // Fixed offsets, not random: a mark that moves between repaints is a mark
  // that shimmers, and this layer is rebuilt whenever the realm ticks.
  const spots: Array<[number, number, number]> = [
    [-3.5, 1.15, 0.62],
    [3.4, 1.35, 0.55],
    [-2.2, -1.55, 0.48],
    [3.0, -1.2, 0.44],
  ];
  let out = '';
  for (let k = 0; k < count; k++) {
    const [dx, dy, r] = spots[k];
    const x = s.x + dx;
    const y = s.y + dy;
    out += `<path class="wm-mark hut" d="M${n(x - r)} ${n(y + r * 0.9)}L${n(x - r)} ${n(y - r * 0.2)}
      L${n(x)} ${n(y - r * 1.15)}L${n(x + r)} ${n(y - r * 0.2)}L${n(x + r)} ${n(y + r * 0.9)}Z" />`;
  }
  return out;
}

function townMark(s: Pt, fort: number, tier = 4, age = 1): string {
  return grown(s, outbuildings(s, tier) + townMarkBody(s, fort), markScale(tier, age));
}

function townMarkBody(s: Pt, fort: number): string {
  const x = s.x;
  const y = s.y;
  if (fort <= 0) {
    return `<path class="wm-mark" d="M${n(x - 2.5)} ${n(y + 1.3)}L${n(x - 2.5)} ${n(y - 0.2)}
        L${n(x - 1.5)} ${n(y - 1.3)}L${n(x - 0.5)} ${n(y - 0.2)}L${n(x - 0.5)} ${n(y + 1.3)}Z
        M${n(x + 0.3)} ${n(y + 1.3)}L${n(x + 0.3)} ${n(y + 0.1)}L${n(x + 1.2)} ${n(y - 0.8)}
        L${n(x + 2.1)} ${n(y + 0.1)}L${n(x + 2.1)} ${n(y + 1.3)}Z" />`;
  }
  if (fort === 1) {
    return `<path class="wm-mark" d="M${n(x - 1.4)} ${n(y + 1.5)}L${n(x - 1.4)} ${n(y - 1.6)}
        L${n(x - 0.75)} ${n(y - 1.6)}L${n(x - 0.75)} ${n(y - 2.2)}L${n(x - 0.15)} ${n(y - 2.2)}
        L${n(x - 0.15)} ${n(y - 1.6)}L${n(x + 0.45)} ${n(y - 1.6)}L${n(x + 0.45)} ${n(y - 2.2)}
        L${n(x + 1.05)} ${n(y - 2.2)}L${n(x + 1.05)} ${n(y - 1.6)}L${n(x + 1.4)} ${n(y - 1.6)}
        L${n(x + 1.4)} ${n(y + 1.5)}Z" />`;
  }
  // Two or three: a curtain wall, and at three a second tower and a gate.
  const wide = fort >= 3 ? 3.5 : 2.7;
  const gate =
    fort >= 3
      ? `<path class="wm-gate" d="M${n(x - 0.6)} ${n(y + 1.5)}L${n(x - 0.6)} ${n(y + 0.1)}
          q0.6 -0.8 1.2 0L${n(x + 0.6)} ${n(y + 1.5)}Z" />`
      : '';
  const tower = (tx: number): string =>
    `M${n(tx - 0.85)} ${n(y + 1.5)}L${n(tx - 0.85)} ${n(y - 2.1)}L${n(tx - 0.3)} ${n(y - 2.1)}
     L${n(tx - 0.3)} ${n(y - 2.7)}L${n(tx + 0.3)} ${n(y - 2.7)}L${n(tx + 0.3)} ${n(y - 2.1)}
     L${n(tx + 0.85)} ${n(y - 2.1)}L${n(tx + 0.85)} ${n(y + 1.5)}Z`;
  return `<path class="wm-mark" d="M${n(x - wide)} ${n(y + 1.5)}L${n(x - wide)} ${n(y - 0.7)}
      L${n(x - wide + 0.55)} ${n(y - 0.7)}L${n(x - wide + 0.55)} ${n(y - 1.3)}
      L${n(x + wide - 0.55)} ${n(y - 1.3)}L${n(x + wide - 0.55)} ${n(y - 0.7)}
      L${n(x + wide)} ${n(y - 0.7)}L${n(x + wide)} ${n(y + 1.5)}Z
      ${tower(x - wide + 0.9)}${fort >= 3 ? tower(x + wide - 0.9) : ''}" />${gate}`;
}

/**
 * A town mark standing *on* the ground rather than printed on it.
 *
 * Every silhouette on this chart was a flat black shape — no shadow under it, no
 * light on it — on a board where the hillside behind it is lit from the north
 * west, the curtain wall in `curtainart.ts` has its left face lit, and
 * `drawBuilding` lights the left of every roof in the settlement. The one
 * surface disagreeing with the rest of the game was the chart's own furniture.
 *
 * Rather than redraw four glyphs face by face — which is a lot of geometry for
 * the same answer, and a lot of geometry the next glyph would have to repeat —
 * the shape is stamped three times: a soft copy dropped to the south-east, which
 * is the shadow it throws; a pale copy lifted to the north-west, which shows
 * only as a rim on the lit side because the body covers the rest of it; and then
 * the body itself. The offsets are in the town group's own glass units, so a
 * keep is lit the same at every zoom.
 *
 * It costs three paths where there was one. That is about eleven kilobytes of
 * markup on a full board — measured against the twenty-odd this screen already
 * spends on the terrain layer, and paid once per redraw rather than per frame.
 */
function litMark(mark: string): string {
  return (
    `<g class="wm-markcast" transform="translate(0.62 0.5)">${mark}</g>` +
    `<g class="wm-marklit" transform="translate(-0.34 -0.3)">${mark}</g>` +
    mark
  );
}

/** The banner. The one saturated thing standing on the land. */
function banner(s: Pt, accent: string): string {
  const x = s.x - 3.4;
  const y = s.y + 1.4;
  return `<path class="wm-pole" d="M${n(x)} ${n(y)}L${n(x)} ${n(y - 5.2)}" />
    <path class="wm-flag" fill="${accent}" d="M${n(x)} ${n(y - 5.2)}L${n(x - 3.1)} ${n(y - 4.5)}
      L${n(x)} ${n(y - 3.5)}Z" />`;
}


/**
 * A host standing on the ground, as a banner on a pole.
 *
 * The two shield pips this replaced said "somebody is here" and nothing else:
 * not whose, not how many, and not clickably. On a board of a hundred and fifty
 * provinces the first question a player asks of the map is *where are my men*,
 * and the answer has to be the same saturated mark the town's own banner is —
 * one channel, used once, meaning "a crown is standing here".
 *
 * Drawn in a counter, so it is a constant size on the glass at every zoom.
 */
/**
 * What a host mostly *is*, by the models in it.
 *
 * > *"above troop name there should be the icon of what type of troop they are"*
 *
 * A host is a list of regiments and the list is in the tooltip, which is a
 * place you have to go and ask. The arm is the one fact about a host you want
 * before you have decided to ask anything — whether the thing marching at you
 * is foot, horse, shot or engines changes what you do about it — so it belongs
 * on the mark and at the top of the row.
 *
 * Decided by **models, not regiments**: four regiments of one archer apiece is
 * a company of archers on paper and a handful of men in the field, and the
 * question this answers is what the host is made of.
 */
export type Arm = 'foot' | 'horse' | 'shot' | 'engine' | 'caster';

function armOf(a: Army): Arm {
  const tally: Record<Arm, number> = { foot: 0, horse: 0, shot: 0, engine: 0, caster: 0 };
  for (const r of a.regiments) {
    const t = unitType(r.typeIdx);
    const f = t.flags ?? 0;
    const arm: Arm =
      (f & UF.Siege) !== 0 ? 'engine'
      : (f & UF.Caster) !== 0 ? 'caster'
      : (f & UF.Cavalry) !== 0 ? 'horse'
      : (f & UF.Ranged) !== 0 ? 'shot'
      : 'foot';
    tally[arm] += r.models;
  }
  let best: Arm = 'foot';
  for (const k of Object.keys(tally) as Arm[]) if (tally[k] > tally[best]) best = k;
  return best;
}

/** The word for an arm, for the row that carries the glyph. */
const ARM_WORD: Record<Arm, string> = {
  foot: 'Foot',
  horse: 'Horse',
  shot: 'Shot',
  engine: 'Engines',
  caster: 'Adepts',
};

/**
 * The arm as a mark, drawn in a 12x12 box.
 *
 * Drawn rather than lettered, for the reason the chart draws everything else:
 * a glyph reads at a size no typeface does, and these are used at eleven pixels
 * in a list and at counter scale on the board. Silhouettes only — a spear, a
 * horse's head, a drawn bow, a wheel, a rune — because anything with interior
 * detail turns to mud at that size.
 */
function armGlyph(arm: Arm, cls = ''): string {
  const g = (d: string): string => `<path class="wm-armmark ${cls}" d="${d}" />`;
  switch (arm) {
    case 'horse':
      // A horse's head and neck, facing right.
      return g('M2.2 10.4 L3.4 5.6 Q4 3.2 6.4 2.4 L8.6 1.6 L9.8 3.2 L8.2 4.2 Q6.6 5 6.2 6.6 L5.2 10.4 Z');
    case 'shot':
      // A drawn bow: the stave, the string, the arrow.
      return g('M4.2 1.6 Q8.6 6 4.2 10.4 L3.4 9.8 Q7 6 3.4 2.2 Z M4 6 L10 6 L8.4 4.9 M10 6 L8.4 7.1');
    case 'engine':
      // A wheel and an arm: a machine, not a man.
      return g('M3.6 9.8 m-2.2 0 a2.2 2.2 0 1 0 4.4 0 a2.2 2.2 0 1 0 -4.4 0 M3.6 7.8 L9.4 2.2 L10.6 3.4 L5 9');
    case 'caster':
      // A four-pointed star: the game's own mark for a power.
      return g('M6 1.4 L7.2 4.8 L10.6 6 L7.2 7.2 L6 10.6 L4.8 7.2 L1.4 6 L4.8 4.8 Z');
    default:
      // A spear behind a shield: the default arm, and the commonest mark.
      return g('M8.8 1.4 L9.8 2.4 L4.6 7.6 L3.6 6.6 Z M3.2 7 L5.2 9 L3.9 10.4 L1.9 8.4 Z' +
               ' M7.4 6 Q9.8 7 9.8 9.4 Q9.8 10.6 8.6 10.9 Q7.4 10.6 7.4 9.4 Z');
  }
}

/** How much bigger a host mark is than it was drawn. See `hostFlag`. */
const HOST_MARK = 1.85;

function hostFlag(accent: string, count: number, men: number, arm: Arm = 'foot'): string {
  /**
   * **A soldier, not a pennant — and the strength written under him.**
   *
   * > *"the army flag should be a solider instead, and it should be clear the
   * > amount f troops in that army, look at eu4 and eu 5 how the army moves and
   * > is displayed on the map"*
   *
   * The reference is exact about both halves and they are one idea. In EU4 and
   * EU5 a stack on the map is a *figure* — a man, unmistakably a man, at a
   * glance and at any zoom — with a **number under him** that is the first
   * thing you compare when two stacks are near each other. Neither half works
   * without the other: a figure with no number is a picture of an army rather
   * than an account of one, and a number with no figure is a label on the
   * ground.
   *
   * What was here was a pennant on a pole, which said *a crown is standing
   * here* and nothing else. The count it carried was the number of **hosts**
   * stacked in the province — one, two, four — which is a fact about the map's
   * own bookkeeping and not the question anybody asks. Nobody looks at a
   * border and wonders how many separate orders the enemy has outstanding; they
   * wonder how many men are coming.
   *
   * So: the men are the number, always shown, and the stack count survives as a
   * small "×2" beside it for the case where one mark stands for several hosts.
   *
   * The figure is a silhouette and stays one. At the fit view this mark is about
   * eight units tall on a board six hundred and fifty provinces wide, and every
   * attempt at a face, a shield device or a weapon in the *other* hand turns to
   * a smudge two notches out. A head, shoulders, and a spear held upright is
   * the most that survives — which is, not coincidentally, what the games this
   * is drawn from use.
   */
  const strength = men >= 10000 ? `${(men / 1000).toFixed(0)}k` : men >= 1000 ? `${(men / 1000).toFixed(1)}k` : String(men);
  /**
   * **Scaled up bodily, because a counter does not grow when you zoom.**
   *
   * > *"the icon for troops and troops moving needs to be bigger as well its un
   * > readable, when you zoom in"*
   *
   * Everything with `data-cx` is held constant on the glass — that is the whole
   * design of `applyView`, and it is right: a stack is a piece of information
   * and information should not change size because the camera moved. But it
   * means the mark never gets any bigger *ever*, so when the board is wound in
   * and the ground beneath it doubles, the host stays the same handful of
   * pixels and reads as smaller than it did. The number under it is 3.5 units
   * of type on a 1600-pixel screen.
   *
   * So the constant is simply a bigger constant. `HOST_MARK` scales the figure
   * and its plate together, and the hit disc in `hostLayer` is raised with it —
   * the target must not be smaller than the thing it is a target for.
   */
  return `<g transform="scale(${HOST_MARK})"><path class="wm-pole" d="M4.0 5.6 L4.0 -10.4" />
    <path class="wm-flag" fill="${accent}" d="M4.0 -12.4 L5.5 -10.1 L2.5 -10.1 Z" />
    <circle class="wm-flag" fill="${accent}" cx="-0.6" cy="-6.4" r="2.15" />
    <path class="wm-flag" fill="${accent}"
      d="M-3.7 -4.0 Q-0.6 -5.1 2.5 -4.0 L1.9 5.6 L-3.1 5.6 Z" />
    <g transform="translate(-11.6 -11.4) scale(0.62)">${armGlyph(arm, 'host')}</g>
    <text class="wm-hostmen" y="9.9">${strength}${
      count > 1 ? `<tspan class="wm-hoststack"> ×${count}</tspan>` : ''
    }</text></g>`;
}

/**
 * The stack, under the banner: one pip a host, the held ones filled.
 *
 * **This is the answer to "a province holding two of your hosts can only offer
 * the first from the map".** Three gestures were on the table and this is why
 * it is this one:
 *
 *   • *Cycling on repeated clicks* — which the board already does and which
 *     stays — is one click for the second host and four for the fourth, and at
 *     no point does it say how many there are or which one you have. For two
 *     hosts it is perfect. For the border province with four in it, which is
 *     the case this item names, it is a slot machine.
 *   • *Fanning the banners out* was the first cut and it is wrong at the only
 *     zoom that matters: four banners at the fit view is four overlapping
 *     marks, and the counter rule means they do not shrink, so a busy front
 *     turns into a wall of pennants with the *map* behind it. `hostLayer`'s own
 *     note already made this argument once — one legible mark with a count beats
 *     four illegible ones.
 *   • *A hover list* is a menu, and a menu is a thing you have to keep the
 *     cursor inside on a board that pans under it.
 *
 * A pip row is none of those: it is drawn **inside the banner's own counter**,
 * so it is glued to the mark and constant on the glass at every zoom; it says
 * how many there are at a glance without being read; a click on one takes that
 * host directly, in one gesture, whichever of the four it is; and it shows
 * *which* are held, which is the thing the group selection made necessary and
 * no amount of cycling could ever say.
 *
 * Only ever drawn for your own hosts, and only when there is more than one:
 * a rival's stack is a number, not a thing to pick from.
 */
function hostPips(hosts: Army[], held: number[]): string {
  if (hosts.length < 2) return '';
  // Beyond this the row is wider than the banner and stops reading as one mark.
  const shown = hosts.slice(0, 6);
  /**
   * Sized against the glass, not against the banner.
   *
   * A counter is held to a constant size at every zoom — which is what makes
   * the board legible, and which also means **zooming in does not make a pip
   * bigger**. Measured in a shot at three notches of zoom: the banner is a
   * 6 x 13 pixel mark on a 1600-wide screen, and the first cut of this row put
   * four 2.4-pixel dots under it with a 4-pixel hit disc. Proportionate to the
   * mark, unusable as a control, and no amount of zoom would have fixed it.
   * These numbers are chosen so the dot is legible and the hit disc is a
   * thumb-sized fraction of the banner's own, at every zoom there is.
   */
  const gap = 4.0;
  const y = 12.6;
  return shown
    .map((a, i) => {
      const x = (i - (shown.length - 1) / 2) * gap;
      const on = held.includes(a.id);
      return (
        `<g class="wm-hostpip${on ? ' on' : ''}" data-hostpick="${a.id}">` +
        `<circle class="wm-piphit" cx="${x.toFixed(2)}" cy="${y}" r="2.6" />` +
        `<circle class="wm-pipdot" cx="${x.toFixed(2)}" cy="${y}" r="1.6" />` +
        `</g>`
      );
    })
    .join('');
}

/**
 * A host at sea, in the same language as a host on land — hull, mast, banner.
 *
 * A loaded convoy flies the full flag; an empty escort flies a pennant half the
 * size. That distinction is the one a player has to be able to make from across
 * the room, because a fleet with men aboard is an invasion and a fleet without
 * one is a patrol, and they are drawn on the same water.
 */
function fleetGlyph(accent: string, carrying: boolean): string {
  /**
   * The plate is not decoration and it was not in the first cut.
   *
   * A hull is drawn on open water, which is the darkest ground on the chart and
   * the only part of it with no terrain marks to give a mark an edge. Without
   * something behind it the whole glyph came to two hairlines the colour of the
   * sea, and a picture at full zoom showed a fleet of nine as a scratch. Every
   * other counter on this map stands on hatching or on a province fill; this one
   * has to bring its own.
   */
  return `<circle class="wm-fleetplate" r="4.2" />
    <path class="wm-hull" d="M-3.5 0.9 L3.5 0.9 L2.4 2.7 L-2.4 2.7 Z" />
    <path class="wm-mast" d="M0 0.9 L0 -4.1" />
    ${
      carrying
        ? `<path class="wm-flag" fill="${accent}" d="M0 -4.1 L3.6 -3.3 L0 -2.0 Z" />`
        : `<path class="wm-flag" fill="${accent}" d="M0 -4.1 L2.2 -3.7 L0 -3.0 Z" />`
    }`;
}

/**
 * A harbour, drawn off the town toward the water rather than on it.
 *
 * `portSlot` picks the point: the four slots round a town mark were spoken for
 * before the sea existed, so a port does not take one. The pips under the anchor
 * are the rungs of the works — a wharf, a harbour, a naval yard — because the
 * level is what decides both what it earns and what it can lay down.
 */
function harbourMark(p: Pt, level: number): string {
  const x = p.x;
  const y = p.y;
  const pips: string[] = [];
  for (let k = 0; k < level; k++) {
    pips.push(
      `<circle class="wm-portpip" cx="${n(x - (level - 1) * 0.55 + k * 1.1)}" cy="${n(y + 2.9)}" r="0.38" />`,
    );
  }
  /**
   * Filled, not stroked, and that is the whole difference between a mark you can
   * see and one you cannot.
   *
   * The first cut drew this as four strokes. A stroke on this chart is held to a
   * constant weight on the glass — two pixels — while its geometry grows with
   * the map, so zoomed in the anchor became a four-unit wire nobody could pick
   * out of the water, and zoomed out it became four pixels of nothing. The town
   * mark beside it is a filled silhouette with a hairline casing and reads at
   * every zoom, which is the rule this now follows.
   */
  return `<g class="wm-port">
      <path class="wm-anchor" d="M${n(x - 0.3)} ${n(y - 1.5)}L${n(x + 0.3)} ${n(y - 1.5)}
        L${n(x + 0.3)} ${n(y + 1.5)}L${n(x - 0.3)} ${n(y + 1.5)}Z
        M${n(x - 1.5)} ${n(y - 0.85)}L${n(x + 1.5)} ${n(y - 0.85)}
        L${n(x + 1.5)} ${n(y - 0.35)}L${n(x - 1.5)} ${n(y - 0.35)}Z
        M${n(x - 2.0)} ${n(y + 0.45)}q${n(0.25)} ${n(1.95)} ${n(2.0)} ${n(2.2)}
        q${n(1.75)} ${n(-0.25)} ${n(2.0)} ${n(-2.2)}l${n(-0.85)} ${n(0.2)}
        q${n(-0.3)} ${n(1.3)} ${n(-1.15)} ${n(1.45)}
        q${n(-0.85)} ${n(-0.15)} ${n(-1.15)} ${n(-1.45)}Z" />
      <circle class="wm-anchor-eye" cx="${n(x)}" cy="${n(y - 2.05)}" r="0.62" />
      ${pips.join('')}
    </g>`;
}

/**
 * The mark that says what kind of crossing this is, set on the road where it
 * crosses. Two bank ticks for a ford; a chevron over the line for a pass.
 */
function crossingMark(a: number, b: number, kind: 'ford' | 'pass'): string {
  const pts = roadPoints(a, b);
  const i = Math.floor(pts.length / 2);
  const p = pts[i];
  const q = pts[Math.min(pts.length - 1, i + 1)];
  const ang = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
  const d =
    kind === 'ford'
      ? 'M-0.9 -1.5 L-0.9 1.5 M0.9 -1.5 L0.9 1.5'
      : 'M-1.8 1.5 L0 -1.1 L1.8 1.5';
  return `<path class="wm-cross" d="${d}"
    transform="translate(${n(p.x)} ${n(p.y)}) rotate(${ang.toFixed(1)})" />`;
}

/**
 * A count of realm time, in the unit the realm actually keeps.
 *
 * Every "turn" the strategic layer counts is a month of the Long Ledger — a
 * march of three, a siege of two — and with the button gone there is no reason
 * to keep saying "turn" at the player. The number itself is unchanged; only the
 * noun is, because the noun was the only part of it that was ever a lie.
 */
function months(n: number): string {
  return `${n} month${n === 1 ? '' : 's'}`;
}

/**
 * A book price, in the words the player reads.
 *
 * The market is fixed-point — every price on it is an integer number of tenths
 * of a gold, so that a hashed and saved order cannot round differently from
 * itself — and the player is never shown that. One decimal, the same shape the
 * chronicle's own market lines use.
 */
function goldPrice(tenths: number): string {
  return `${(tenths / 10).toFixed(1)}g`;
}

/**
 * A stretch of simulated seconds, in the words a player uses about an evening.
 *
 * Months are the unit the realm resolves in; minutes are the unit the player is
 * actually sitting in, and a month is `MONTH_SECONDS` of them. Quoting both is
 * the only honest answer to "how long have I got" now that the End Turn button
 * is gone — and it is quoted at ×1, because the speed control is the player's
 * and the map does not know what they will leave it on.
 */
function span(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `~${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `~${m} min`;
  return `~${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * What kind of thing this road is — "the old road", "the ford", "the pass".
 *
 * `roadName` in `marches.ts` builds the whole sentence with both province names
 * in it, which is right for the chronicle and too long for a panel line that has
 * already said where the host is going. This is the noun on its own.
 */
function roadWord(c: CampaignState, a: number, b: number): string {
  const link = linkBetween(c, a, b);
  return `the ${LINK_NAMES[link ? link.kind : LinkKind.Wild] ?? 'road'}`;
}

/** A bar, drawn in text-free HTML, for progress and supply. */
function meter(frac: number, cls = ''): string {
  const f = Math.max(0, Math.min(1, frac));
  return `<div class="wm-meter ${cls}"><i style="width:${(f * 100).toFixed(1)}%"></i></div>`;
}

// ── The view ───────────────────────────────────────────────────────────────
//
// A hundred and twenty-seven provinces do not fit on a screen at a size anybody
// can read, and pretending otherwise is how a big map becomes an unusable one.
// So the chart pans and zooms, and two things follow from that which are easy to
// get wrong and ruin the whole screen if you do.
//
// **Type does not scale.** A name drawn in map units is illegible when zoomed
// out and absurd when zoomed in. Everything lettered is scaled by `--wm-k`, the
// reciprocal of the zoom, so it is the same size on the glass at every zoom —
// which is what every chart and every mapping application has always done. The
// same goes for line weights: a frontier four pixels wide zoomed out is a
// frontier sixteen pixels wide zoomed in, and sixteen pixels of black is not a
// border, it is a wall.
//
// **Not every name can be shown.** Constant-size type means that zooming out
// does not make room, it only removes it, so names have to *leave*. Each holding
// carries a rank (`labelRank`, in `cartography.ts`) and the view shows names down
// to a rank that depends on how far in it is: the seats of the crowns always, the
// walled and wealthy from halfway in, everything at the bottom. That is the
// difference between a chart and porridge and it is worth stating out loud.

interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Narrowest the view may get.
 *
 * **This is the one number that says how deep the chart goes**, and it was 88 —
 * five provinces across on a board of a hundred and ninety, five notches of the
 * zoom control from the fit view. The board is doubling to about three hundred
 * and eighty and each province is therefore about half the area and seven tenths
 * the width, so 88 would have been seven provinces across: the same picture with
 * more in it, which is not what "zoom in more" means.
 *
 * 40 is a little under twelve notches of magnification off the fit view (`VIEW.w`
 * is 510) and seven presses of the button. On the new board that is a little over
 * three provinces on the screen — closer than the old chart could ever get, and
 * close enough that the terrain is a drawing rather than a texture.
 *
 * **Going deeper is not one number, and that is the whole lesson of this pass.**
 * Everything on this chart obeys one of two rules — it is either held to a
 * constant size on the glass and multiplied by `--wm-k`, or it belongs to the
 * ground and grows with the map — and anything that quietly obeys *both*, or the
 * wrong one, only shows it at the end of the range nobody had been to. What was
 * wrong here was the settlement furniture: see `townLayer`.
 */
const MIN_VIEW_W = 40;

/**
 * The view width at which the relation hatch stops growing with the ground and
 * freezes on the glass — the old zoom floor, so every picture the hatch was
 * ever tuned against is unchanged and only the new, deeper notches differ.
 * See the note in `applyView`.
 */
const HATCH_FREEZE_W = 88;

/**
 * The furniture's own coordinate system, and **the number that made every piece
 * of it too big**.
 *
 * > *"all the legends and map ui elements are awfully big"*
 *
 * It was 174 units — "exactly the frame the twenty-four-province map had", so
 * that every size in the compass, the key and the cartouche stayed the number
 * it always was. That was the right instinct when the board had twenty-four
 * provinces on it. The board now has **six hundred and fifty**, and a frame
 * sized for the old one means the apparatus grew, relative to the map, by the
 * whole of that difference: a key plate wide enough to sit under a quarter of
 * the realm.
 *
 * Widening the frame is the one lever that moves all of it at once and keeps
 * every proportion inside it — the compass stays a compass, the scale bar stays
 * the same fraction of the key beside it. **The neatline is unaffected**, which
 * is what makes this safe: it spans `H = VIEW.h / s`, so the border still meets
 * the edges of the view and only what is drawn *inside* it shrinks.
 *
 * 174 → 300 is around 40% off every piece of furniture on the sheet. Measured by
 * eye against shots at the fit view: at 250 the key plate still covered a
 * quarter of the map width, which is what the complaint was about; at 340 its
 * rows had stopped being readable at all. A key nobody can read is not a
 * smaller key, it is a decoration.
 */
const FURN_W = 300;

/** The box the holdings actually occupy, for anything that must stay on land. */
const LAND = PROVINCES.reduce(
  (a, p) => ({
    x0: Math.min(a.x0, p.x),
    x1: Math.max(a.x1, p.x),
    y0: Math.min(a.y0, p.y),
    y1: Math.max(a.y1, p.y),
  }),
  { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity },
);

function lodTier(w: number): number {
  if (w > 235) return 0;
  if (w > 150) return 1;
  if (w > 100) return 2;
  return 3;
}

/**
 * What the right-hand column is currently about.
 *
 * The map had exactly one selection — a province — and everything else drawn on
 * it was scenery. A march, a warning and a camp are each a thing with a story,
 * and each of them needs the column, so the column has a subject rather than a
 * province. `province` is the resting state and everything falls back to it.
 */
type FocusKind = 'province' | 'march' | 'camp' | 'warn' | 'realm' | 'fleet' | 'market' | 'muster';

/**
 * Which question the chart is answering.
 *
 * Two of these are new and both exist because the board grew a fact the old two
 * could not state. **Reach** is the road graph: on a realm whose roads are in
 * seven pieces and where a hundred and eleven provinces of a hundred and fifty
 * cannot be walked to from the heartland, "whose is it" is the second question
 * and "can I get there" is the first. **Trade** is the market graph: every
 * province pays a third of its gold into a node, and until the map could show
 * which node, that third went somewhere the player had no name for.
 */
/**
 * Every colouring the board can take.
 *
 * The array is the authority and the type is derived from it, so the switch that
 * accepts a tab click and the list that draws the tabs cannot disagree — which
 * they did the moment a fifth mode was added.
 */
/**
 * The steepest a region's name may be set, in radians.
 *
 * Lettering that follows the ground is the point of `regionLettering`, and
 * lettering that follows it *all the way* is unreadable — a name written down
 * the page is a name nobody reads. Twenty-four degrees is about as far as a
 * chart's small caps will lean and still scan at a glance.
 */
const MAX_LABEL_TILT = 0.42;

const MAP_VIEWS = ['relations', 'crowns', 'trade', 'reach', 'terrain'] as const;
type MapView = (typeof MAP_VIEWS)[number];

/**
 * The countries of the board, in the order a key should list them: driest and
 * highest at one end, wettest and lowest at the other.
 *
 * A list rather than `Object.keys(TERRAIN_INK)`, because the order is an
 * editorial decision about how the key reads and object order is an accident of
 * how somebody typed the table.
 */
const TERRAIN_ORDER: ProvinceTerrain[] = [
  'mountain', 'hill', 'plain', 'desert', 'forest', 'jungle', 'marsh', 'coast', 'ice', 'ruin',
];

/** What each country is called on the key. */
const TERRAIN_WORD: Record<string, string> = {
  mountain: 'mountain', hill: 'hills', plain: 'open country', desert: 'desert',
  forest: 'forest', jungle: 'jungle', marsh: 'fen', coast: 'shore', ice: 'ice', ruin: 'ruin',
};

/**
 * The tints a market takes on the chart.
 *
 * Nineteen nodes and twelve inks, so two distant markets can share one — which
 * is why the node boundary is drawn as a frontier in this view and every seat
 * carries its name. The tint groups the ground; the line and the label say which
 * group it is. Colour alone was never going to carry nineteen categories and
 * pretending otherwise is how a chart becomes a fruit salad.
 */
const NODE_INKS = [
  C.gold,
  C.plague,
  C.will,
  C.oxbloodLight,
  C.goldPale,
  C.terrRiver,
  C.emberLight,
  C.terrForest,
  C.terrMarsh,
  C.terrStone,
  C.terrRuin,
  C.parchment,
];

export class WorldMap {
  private el: HTMLElement;
  private visible = false;
  private selectedProvince = -1;
  private selectedArmy = -1;
  /**
   * The province under the cursor while one of your hosts is selected, or −1.
   *
   * Deliberately *not* part of `signature`. The chart is rebuilt from markup
   * whenever the signature moves, and a rebuild on every mouseover of a
   * hundred-and-twenty-seven-province board is a rebuild four times a second
   * while the cursor is moving. The route plot is written straight into its own
   * `<g>` instead — see `drawPlot`.
   */
  private plotProv = -1;
  /** The live route layer, found once per rebuild. */
  private plotEl: SVGGElement | null = null;
  /**
   * The finger recogniser over the chart.
   *
   * Held rather than dropped on the floor because the stage is replaced on
   * every repaint and the old recogniser's listeners go with it — keeping the
   * reference is what makes that a *replacement* rather than an accumulation of
   * dead handlers on detached nodes.
   */
  private touch: TouchInput | null = null;
  /** The phone layout is on, and which sheet is up. Mirrors the settlement HUD. */
  private narrow = false;
  /** Held; see the note where it is created. */
  private narrowQuery: MediaQueryList | null = null;
  private sheet = '';
  /**
   * The panels are put away and the board has the window.
   *
   * View state and not world state, so it lives here and is never saved: coming
   * back to a realm should show you the realm, not the last thing you did to
   * the furniture. It survives a repaint because it is a class on the root and
   * `paint` does not touch the root's class list.
   */
  private bare = false;
  /**
   * Is the key on the sheet? Off by default, which is the change.
   *
   * View state, never saved: a key is asked for, read, and dismissed, and a
   * player coming back to the realm wants the board rather than the last state
   * of the furniture.
   */
  private showKey = false;
  /**
   * What the board last said back to the player, and when it stops saying it.
   *
   * Wall clock rather than realm time, because it is a reply to a gesture and
   * not an event in the world: it must fade at the same rate whether the
   * campaign is paused or running at speed.
   */
  private said = '';
  private saidUntil = 0;
  private saidEl: HTMLElement | null = null;
  /** The host the selection line was last said for, so it is said once. */
  private saidArmy = -1;
  /** What the detail column is about. See `FocusKind`. */
  private focus: FocusKind = 'province';
  /** Army id, for `march` focus. */
  private focusArmy = -1;
  /** Province, for `camp` and `warn` focus — both are marks on a province. */
  private focusProv = -1;
  /**
   * Which question the chart is answering: what is mine, or who is who.
   *
   * Relations by default, because it is the question a player has on opening the
   * screen and the one the old map could not answer at all. See `territoryLayer`.
   */
  private mapView: MapView = 'relations';
  /** Fleet id, for `fleet` focus. */
  private focusFleet = -1;
  /** Trade node, for `market` focus. */
  private focusNode = -1;
  /**
   * The order the player is composing on the Markets tab.
   *
   * UI state and nothing else: it is a *draft*, it never touches the campaign,
   * and it is deliberately not a set of `<input>` elements. This screen is
   * rebuilt with `innerHTML` whenever the realm moves, which on a live campaign
   * is often, and a text field inside that markup would lose its value and the
   * caret mid-typing. Buttons carry the whole form instead — the same
   * `data-act` vocabulary every other control on this screen speaks — and the
   * draft lives out here where a repaint cannot reach it.
   *
   * `price` is in **tenths of a gold**, like everything else on the book. The
   * player is shown gold with one decimal and never sees the fixed point.
   */
  private draft: { kind: number; key: ResourceKey; amount: number; price: number } = {
    kind: OrderKind.Sell,
    key: MARKET_KEYS[0],
    amount: 10,
    price: MARKET_PRICES[MARKET_KEYS[0]] ?? 10,
  };
  /** The crown whose row is open in the realm tab, or -1. */
  private openCrown = -1;
  /** The war whose terms are open in the realm tab, or -1. */
  private openWar = -1;
  /**
   * The five places the clock is written. Looked up once per render rather than
   * per frame: one of them sits below the whole chart in document order, and a
   * `querySelector` for it walks every mark on the board.
   */
  private clockEls: {
    date: Element | null;
    council: Element | null;
    long: Element | null;
    wait: Element | null;
    drawn: Element | null;
  } = { date: null, council: null, long: null, wait: null, drawn: null };
  /** The hover card. Created once and re-parented after every redraw. */
  private tipEl: HTMLElement | null = null;
  /**
   * Every counter on the chart that must stay a constant size on the glass.
   *
   * Cached at wire time and re-transformed only when the zoom actually changes:
   * a pan is four attribute writes and must not become a tree walk.
   */
  private counters: SVGGElement[] = [];
  /** The relation hatch patterns, re-scaled past the freeze point. See `applyView`. */
  private hatchPats: SVGPatternElement[] = [];
  private appliedK = -1;
  /** Cheap change detector, so we are not rebuilding the DOM sixty times a second. */
  private signature = '';
  /**
   * The same detector with the selection left out of it, which is what decides
   * whether a frame costs a rebuild or `paintSelection`. Cleared alongside
   * `signature` by `forceRepaint`, and only there — the two must never drift,
   * because a stale `boardSig` is a board that quietly stops redrawing.
   */
  private boardSig = '';
  /** The sea, the coast and the hatching never change. Built once, kept as text. */
  private bedSvg = '';
  /**
   * The two levels of terrain detail, built once each and held as text — and
   * **only one of them is ever in the document.**
   *
   * The obvious way to do this is to emit both and let a CSS rule hide one, and
   * that is how it was first written: no work at all when the zoom crosses a
   * tier. It was measured and it costs too much somewhere else. The terrain is
   * the largest layer on the chart and two copies of it took this screen's
   * markup from 971 KB to 1,275 KB — and `paint` builds the whole screen by
   * assigning `innerHTML`, so that is a third more for the browser to parse
   * *every time the realm moves*, which on a live campaign is often.
   *
   * A tier crossing happens when the player works the zoom control, a handful of
   * times in a session, and it is one `innerHTML` write on one group under the
   * cursor's own gesture. Paying there rather than on every repaint is the right
   * side of that trade by two orders of magnitude in frequency.
   */
  private terrFar = '';
  private terrNear = '';
  /** Which of the two the document currently holds. −1 until something is in it. */
  private appliedTerrain = -1;
  /**
   * The lane graph, which is geometry over content in the same way the coast is:
   * two hundred and fifty-two polylines that are the same on every frame of
   * every month. Keyed on the lane count so a board that grows one re-draws.
   */
  private laneSvg = '';
  private laneCount = -1;
  /** The realm's date, off the world rather than off the campaign. */
  private day = 0;
  private dateLine = '';
  private shortDate = '';
  private daysToCouncil = 0;
  /** Simulated seconds until the council sits. What a month is worth in time. */
  private secsToCouncil = 0;
  /** What part of the realm is on screen. Survives every re-render. */
  private view: ViewBox = { x: VIEW.x, y: VIEW.y, w: VIEW.w, h: VIEW.h };
  private chart: SVGSVGElement | null = null;
  /**
   * The hosts a march order will go to, when there is more than one of them.
   *
   * Empty is the ordinary case and means "whatever `selectedArmy` is". When it
   * is not empty its **first entry is `selectedArmy`**, so every panel, every
   * tooltip and every existing order button on this screen keeps working
   * unchanged and reads the lead host — a group is an addition to the selection
   * rather than a replacement for it.
   *
   * A group of one is not a group: `update` folds it back to a plain selection,
   * so nothing downstream has to handle both spellings of the same state.
   */
  private groupArmies: number[] = [];
  /**
   * The marquee, in **map units**, while one is being dragged; null otherwise.
   *
   * Map units and not pixels, so the box stays over the same ground if the view
   * moves under it, and so the hit test against `provinceSite` is a comparison
   * rather than a projection.
   */
  private band: { x0: number; y0: number; x1: number; y1: number } | null = null;
  /** The live marquee layer, found once per rebuild — the same trick `plotEl` plays. */
  private bandEl: SVGGElement | null = null;
  /**
   * The levy marks on the chart, on or off.
   *
   * UI state and nothing else: it changes what is drawn and never what is true,
   * so it is not campaign state, is not hashed and is not saved. It *is* in the
   * signature, because turning it on has to repaint the board.
   */
  private levyMarks = false;
  /** Drag state. `moved` is what tells a pan from a click on a province. */
  private panning = false;
  private panFrom = { x: 0, y: 0 };
  private panView: ViewBox = { x: 0, y: 0, w: 0, h: 0 };
  private moved = 0;

  constructor(private cb: WorldMapCallbacks) {
    this.el = document.createElement('div');
    this.el.id = 'worldmap';
    this.el.className = 'gone';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
    this.injectStyle();
    this.makeTip();
    /**
     * Watch the breakpoint, rather than only sampling it when the board is
     * repainted.
     *
     * `applyNarrow` runs on every wire, which covers a page that loads narrow —
     * and covers nothing at all for a window that is *resized*, because a resize
     * changes no campaign state and so triggers no repaint. A desktop window
     * dragged narrow and back stayed in the phone layout with its key hidden and
     * its columns still sheets. The same listener the settlement HUD uses.
     */
    /**
     * The query is held in a field, and that is not tidiness.
     *
     * A `MediaQueryList` nobody references is eligible for collection, and a
     * collected one stops delivering `change` — so a listener attached to a
     * throwaway `matchMedia(...)` works until the first GC and then silently
     * does not. Measured here: the first resize fired and the second did not.
     */
    this.narrowQuery = window.matchMedia(`(max-width: ${NARROW_PX}px)`);
    this.narrowQuery.addEventListener('change', () => {
      this.applyNarrow();
      if (this.visible && this.state) {
        this.forceRepaint();
        this.render(this.state);
      }
    });
  }

  /**
   * The hover card.
   *
   * One element, created once, outside the markup that gets replaced on every
   * render — and driven by *delegated* listeners on the screen's root rather than
   * by handlers attached to each mark, because the chart is rebuilt wholesale and
   * a few hundred per-element listeners would have to be rebuilt with it.
   *
   * The content is plain text carried on `data-tip`, deliberately: it is written
   * straight from campaign state, and text that can never be markup cannot ever
   * be an injection. Newlines survive through `white-space: pre-line`.
   */
  private makeTip(): void {
    const tip = document.createElement('div');
    tip.className = 'wm-tip gone';
    this.el.appendChild(tip);
    this.tipEl = tip;

    const place = (ev: MouseEvent): void => {
      const box = this.el.getBoundingClientRect();
      const w = tip.offsetWidth;
      const h = tip.offsetHeight;
      let x = ev.clientX - box.left + 16;
      let y = ev.clientY - box.top + 18;
      if (x + w > box.width - 8) x = ev.clientX - box.left - w - 14;
      if (y + h > box.height - 8) y = ev.clientY - box.top - h - 14;
      tip.style.left = `${Math.max(6, x)}px`;
      tip.style.top = `${Math.max(6, y)}px`;
    };

    this.el.addEventListener('mouseover', (ev) => {
      const host = (ev.target as Element | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!host) return;
      tip.textContent = host.dataset.tip ?? '';
      tip.classList.remove('gone');
      place(ev as MouseEvent);
    });
    this.el.addEventListener('mousemove', (ev) => {
      if (tip.classList.contains('gone')) return;
      const host = (ev.target as Element | null)?.closest?.('[data-tip]');
      if (!host) {
        tip.classList.add('gone');
        return;
      }
      place(ev as MouseEvent);
    });
    this.el.addEventListener('mouseleave', () => tip.classList.add('gone'));
  }

  /** A hover card, as an attribute. Lines are joined with real newlines. */
  private static tip(lines: Array<string | false | undefined>): string {
    const text = lines.filter((l): l is string => Boolean(l)).join('\n');
    return text ? ` data-tip="${esc(text)}"` : '';
  }

  get isOpen(): boolean {
    return this.visible;
  }

  show(w: World): void {
    this.visible = true;
    this.el.classList.remove('gone');
    const c = w.campaign;
    if (c && this.selectedProvince < 0) {
      this.selectedProvince = c.provinces.findIndex((p) => p.owner === c.playerNation);
    }
    /**
     * Open on home, every time, and select it.
     *
     * The screen used to open on whatever the view last was — the fit view of
     * a 734-unit sheet on the first open — which on a board of three hundred
     * and eighty provinces means the player's four are somewhere in a
     * thumbnail-sized realm and the first minute of every visit is spent
     * finding yourself. The reference genre answers this the same way: the
     * map opens looking at your own capital, close enough that its name is
     * readable and its neighbours are a decision, and the fit view is one
     * button away rather than the starting point.
     */
    if (c) {
      const home = provinceSite(c.capital);
      const HOME_VIEW_W = 150;
      this.view = this.clampView({
        x: home.x - HOME_VIEW_W / 2,
        y: home.y - (HOME_VIEW_W * (VIEW.h / VIEW.w)) / 2,
        w: HOME_VIEW_W,
        h: HOME_VIEW_W * (VIEW.h / VIEW.w),
      });
    }
    this.forceRepaint();
    this.update(w);
  }

  hide(): void {
    this.visible = false;
    this.el.classList.add('gone');
  }

  /** Called every frame; does nothing at all unless the board actually moved. */
  update(w: World): void {
    if (!this.visible) return;
    const c = w.campaign;
    if (!c) return;
    /**
     * Sample the breakpoint here as well as on the media query's own event.
     *
     * The event alone was not reliable: a viewport change and the `change` it
     * fires do not settle in a fixed order, and a resize measured immediately
     * after one caught the old layout about half the time. `applyNarrow`
     * early-returns when nothing moved, so asking every frame costs one boolean
     * comparison and removes the ordering question entirely. The listener stays
     * because it is what repaints the board when only the *window* changed.
     */
    this.applyNarrow();
    // The realm's own date, read once per frame and rendered once per day.
    this.day = w.calendarDay;
    this.dateLine = formatDateLong(w);
    this.shortDate = formatDateShort(w);
    this.daysToCouncil = daysToNextMonth(w);
    this.secsToCouncil = secondsToNextMonth(w);
    // Nothing may stay focused on something that has stopped existing. A host
    // that arrived, a camp that was cleared, a warning that was answered — all
    // three fall back to the ground they happened on rather than to a blank
    // panel, because the ground is still the thing the player was looking at.
    if (this.focus === 'march') {
      const m = findArmy(c, this.focusArmy);
      if (!m || m.moveTo < 0) this.focus = 'province';
    }
    if (this.focus === 'camp' && !hasCamp(c, this.focusProv)) this.focus = 'province';
    // A fleet that was sunk, paid off or run down must not keep the column. It
    // falls back to the water it was last on, which is where the player was
    // looking — the same rule a vanished host follows.
    if (this.focus === 'fleet' && !findFleet(c, this.focusFleet)) this.focus = 'province';
    // A market focus with no node chosen is the whole graph, which is a real
    // state and the one the Markets tab opens in. Only a node that has stopped
    // existing sends the column home.
    if (this.focus === 'market' && this.focusNode >= 0 && !tradeNode(c, this.focusNode)) {
      this.focusNode = -1;
    }
    if (this.focus === 'market' && (c.trade?.nodes.length ?? 0) === 0) this.focus = 'province';
    if (this.focus === 'warn' && warningsFor(c, this.focusProv).length === 0) {
      this.focus = 'province';
    }
    // Keep the host selection honest: a host that has been destroyed, folded
    // into another or stood down must not stay selected, and a province with
    // exactly one of your hosts in it selects that host — which is what makes
    // "muster here" be followed immediately by a list of places to march to,
    // rather than by having to click the province again.
    /**
     * The group, kept honest, and folded back to a plain selection when it has
     * stopped being several things.
     *
     * A host in a group can be destroyed, folded into another, or set marching
     * by an intercept between the drag that took it and the next frame. Pruning
     * here rather than at the point of use is what lets `orderedHosts`,
     * `hostLayer` and the plot all read the field without each carrying its own
     * copy of the same three tests.
     */
    if (this.groupArmies.length > 0) {
      const live = this.groupArmies.filter((id) => {
        const a = findArmy(c, id);
        return a !== undefined && a.owner === c.playerNation;
      });
      if (live.length !== this.groupArmies.length) {
        this.groupArmies = live.length > 1 ? live : [];
      }
      if (this.groupArmies.length > 0 && !this.groupArmies.includes(this.selectedArmy)) {
        this.selectedArmy = this.groupArmies[0];
      }
    }
    const sel = this.selectedArmy >= 0 ? findArmy(c, this.selectedArmy) : undefined;
    if (this.selectedArmy >= 0 && !sel) this.selectedArmy = -1;
    // The panel follows the host you are watching. Without this, marching your
    // army out of the province you had selected left the two pointing at
    // different places, and the orders listed were for a host the panel said
    // was not there.
    // …but only while the column is about the ground. Watching a march is
    // watching where it is *going*, and the follow would drag the selection back
    // to where it set out from on the very next frame.
    if (sel && this.focus === 'province' && sel.at !== this.selectedProvince) {
      this.selectedProvince = sel.at;
    }
    if (this.selectedArmy < 0 && this.selectedProvince >= 0) {
      const mine = armiesIn(c, this.selectedProvince).filter((a) => a.owner === c.playerNation);
      if (mine.length === 1) this.selectedArmy = mine[0].id;
    }

    this.paintClock(c);

    /**
     * Two questions, not one: *has the board moved*, and *has the player's hold
     * on it moved*. They used to be the same string, and the second one cost
     * what the first one costs.
     *
     * `npm run chartperf` measured that price: a repaint is ~100 ms of DOM and
     * SVG layout, and `this.selectedProvince` was a term in it — so **clicking a
     * province rebuilt six megabytes of markup**. That is the lag on this
     * screen. It is not the realm ticking and it is not the hosts moving; it is
     * the player touching the board, which is the one thing that must not feel
     * slow.
     *
     * So a change that is only a selection takes `paintSelection`, which
     * rewrites the two columns, the counter overlay and about a dozen class
     * attributes and leaves the ground, the coast, the roads, the frontiers and
     * six hundred and fifty towns exactly where they are.
     */
    const sig = this.signatureOf(c);
    if (`${sig.board}|${sig.sel}` === this.signature) return;
    this.draw(c, sig);
  }

  /**
   * Redraw now, at whatever the change is actually worth.
   *
   * Every handler on this screen used to end `this.forceRepaint(); this.render(c)`
   * — an unconditional rebuild of the whole board, from a click whose only
   * effect was usually to move the selection. Going through here instead means
   * the same two lines ask the signature first and take `paintSelection` when
   * nothing but the player's hold on the board has moved.
   *
   * It also stops the **double** render those pairs were quietly causing:
   * clearing the signature and rendering left the detector empty, so the very
   * next frame found a mismatch and rebuilt the screen a second time. The
   * signature is written here from the state actually drawn, so the next frame
   * has nothing to do.
   */
  private draw(c: CampaignState, sig = this.signatureOf(c)): void {
    this.signature = `${sig.board}|${sig.sel}`;
    if (sig.board === this.boardSig && this.canPaintSelection()) {
      this.paintSelection(c);
      return;
    }
    this.boardSig = sig.board;
    this.render(c);
  }

  /**
   * Whether the cheap path is honest right now.
   *
   * Two cases where it is not. **Nothing has been painted yet** — there is no
   * tree to patch, and `.wm-chart` is how we know. And **the reach view**, whose
   * every province fill is measured from `reachBase`, which is the selected
   * host: on that view a selection change really does repaint the whole
   * board, and pretending otherwise would leave the map answering the previous
   * host's question.
   */
  /**
   * "Rebuild the whole screen on the next frame" — the escape hatch every
   * handler on this file reaches for after changing something the signature
   * does not carry.
   *
   * It was `this.signature = ''` at twenty-seven call sites, which stopped being
   * enough the moment the detector grew a second half: clearing only the full
   * signature would leave `boardSig` matching, and the frame would take the
   * cheap selection path and miss whatever the handler had just done. Both, or
   * neither.
   */
  private forceRepaint(): void {
    this.signature = '';
    this.boardSig = '';
  }

  private canPaintSelection(): boolean {
    return this.chart !== null && this.mapView !== 'reach';
  }

  /**
   * The player picked something up. Repaint what that changed, and nothing else.
   *
   * The list is exhaustive on purpose — anything drawn from `selectedProvince`,
   * `selectedArmy` or `groupArmies` has to be in here or it goes stale, and the
   * signature can no longer be relied on to catch it. In draw order:
   *
   * 1. the ground: `.wm-cell` and its band, by class,
   * 2. the ring on the province outline, by moving one `href`,
   * 3. the town mark, by class,
   * 4. the sea lanes out of the selected coast — their own small group,
   * 5. the counter overlay, which carries `sel` on host banners and the pips
   *    that say which of a stack is held,
   * 6. the two columns, which are 27 kB against the chart's 6,500,
   * 7. the route plot, which is drawn from the held hosts.
   *
   * `wire` runs at the end for the same reason `paint` calls it: the columns and
   * the overlay are new markup and their handlers went with the old.
   */
  private paintSelection(c: CampaignState): void {
    this.state = c;
    const p = this.selectedProvince;
    /**
     * Three elements off, three on — never a walk.
     *
     * The first cut of this toggled `.sel` on every cell, every band and every
     * town, testing each against the selection: two thousand `classList` calls
     * against a nineteen-thousand-element tree, which is a style invalidation
     * broad enough to give most of the saving straight back. Asking the document
     * which elements *currently* carry the class costs one selector match and
     * leaves nothing to drift — there is no remembered index that can disagree
     * with what is on screen.
     */
    for (const el of this.el.querySelectorAll('.wm-cell.sel, .wm-band.sel, .wm-town.sel')) {
      el.classList.remove('sel');
    }
    if (p >= 0) {
      for (const q of [`.wm-cell[data-prov="${p}"]`, `[data-bandprov="${p}"]`, `[data-town="${p}"]`]) {
        this.el.querySelector(q)?.classList.add('sel');
      }
    }

    const ring = this.el.querySelector('.wm-selring');
    if (ring) {
      ring.classList.toggle('off', p < 0);
      if (p >= 0) ring.setAttribute('href', `#wmp${p}`);
    }

    const lit = this.el.querySelector('.wm-lanelit');
    if (lit) lit.innerHTML = this.litLanes(c);
    const overlay = this.el.querySelector('.wm-overlay');
    if (overlay) {
      overlay.innerHTML = this.marchLayer(c);
      /**
       * The new counters, scaled — and *only* the new ones.
       *
       * `wire` normally clears `appliedK` so `applyView` rewrites the transform
       * on every mark on the board, because after a full rebuild none of them
       * carries one. Here thirteen hundred of them still do and only the
       * overlay's are new, so the cache stays and this pays for the handful that
       * changed. Forcing the full rewrite here measured as most of what was left
       * of the click.
       */
      const s = (this.view.w / VIEW.w).toFixed(4);
      for (const g of overlay.querySelectorAll<SVGGElement>('[data-cx]')) {
        g.setAttribute('transform', `translate(${g.dataset.cx ?? '0'} ${g.dataset.cy ?? '0'}) scale(${s})`);
      }
    }

    keepScroll(this.el, ['.wm-left', '.wm-right', '.wm-chron'], () => {
      const left = this.el.querySelector('.wm-left');
      const right = this.el.querySelector('.wm-right');
      if (left) left.innerHTML = this.realmPanel(c);
      if (right) right.innerHTML = this.detailPanel(c);
    });
    this.wire(c, false);
    this.drawPlot(c);
  }

  /**
   * The clock, written straight onto the elements that show it.
   *
   * The date used to be *in* the change signature, which meant the entire
   * screen — a quarter of a megabyte of chart markup, every counter, every
   * panel — was thrown away and rebuilt once every simulated day, about every
   * second and a third at normal pace, purely to change four words. That was
   * survivable when nothing on the screen could be touched. It is not survivable
   * now: a rebuild under the cursor drops the hover card, and a click that
   * straddles one lands on an element that no longer exists.
   *
   * So the four things that follow the clock are patched in place — the same
   * trick `applyView` uses for the scale bar — and the signature is back to
   * meaning "the board has moved".
   */
  private paintClock(c: CampaignState): void {
    const put = (el: Element | null, text: string): void => {
      if (el && el.textContent !== text) el.textContent = text;
    };
    const t = this.clockEls;
    put(t.date, this.shortDate);
    put(t.council, this.councilWord(c));
    put(t.long, this.dateLine);
    put(t.wait, this.waitWord(c));
    put(t.drawn, `drawn ${this.shortDate}, year ${Math.floor((c.turn - 1) / 8) + 1}`);
    this.paintSaid();
  }

  private councilWord(c: CampaignState): string {
    if (c.phase !== TurnPhase.Orders) return `${TURN_PHASE_NAMES[c.phase] ?? '—'}…`;
    return `in ${this.daysToCouncil} day${this.daysToCouncil === 1 ? '' : 's'}`;
  }

  private waitWord(c: CampaignState): string {
    if (c.pending) return 'The field is waiting on you.';
    if (c.phase !== TurnPhase.Orders) return 'the council is sitting';
    return `the council sits in ${this.daysToCouncil} day${this.daysToCouncil === 1 ? '' : 's'}`;
  }

  /**
   * Cheap change detector.
   *
   * Everything the screen *draws* has to be in here or the panel showing it goes
   * stale — that is why camps, warnings, offers and war scores were added when
   * they became things you can click rather than scenery. It is walked every
   * frame, so every term is a number or a short digest and nothing here calls
   * anything that allocates a view.
   */
  private signatureOf(c: CampaignState): { board: string; sel: string } {
    /**
     * Unrest, development and the old claim are drawn, so they are in here.
     *
     * They were not, and the province panel was quietly stale in three places
     * for it — the unrest row, the "Claimed by" row, and now the takings, which
     * are unrest and development multiplied together. It never showed because
     * all three only move when `doIncome` runs or when ground changes hands, and
     * both of those also move `c.turn` or `p.owner`, which *are* here. That is
     * luck, not correctness: the panel is right because of a term it does not
     * depend on. Quantised to a fiftieth so a decaying unrest figure repaints
     * when the number on screen would actually read differently.
     */
    const owners = c.provinces
      .map(
        (p) =>
          `${p.owner}.${Math.round(p.garrison)}.${p.fort}.${p.siegeBy}` +
          `.${Math.round(p.unrest * 50)}.${Math.round(p.development * 50)}.${p.claimedBy}` +
          // The harbour: drawn on the chart, priced in the province panel, and
          // built by a command that lands mid-month rather than at a council.
          `.${p.port ?? 0}`,
      )
      .join('');
    const armies = c.armies
      .map(
        (a) =>
          `${a.id}:${a.at}:${a.moveTo}:${a.moveT}:${a.stance}:${armyModels(a)}` +
          `:${a.victories}:${Math.round(a.supply * 20)}:${a.intercepting}` +
          // Inside the walls or out. It changes the counter on the board, the
          // orders on offer, and whether the host is drawn at all.
          `:${a.sheltered ? 1 : 0}`,
      )
      .join('');
    const camps = c.camps.map((k) => `${k.at}.${Math.round(k.strength)}.${k.bounty}`).join('');
    /**
     * The hulls, and the factors.
     *
     * Both move on a *command* rather than at a council — a fleet ordered to
     * sail casts off the moment the order lands, a merchant posted to a market
     * leaves the same instant — so neither is covered by `c.turn` the way
     * control shares and node income are. Without these two terms the fleet
     * counter stayed where it was until something else on the board happened to
     * move, which on a quiet month is a very long time to watch a ship not
     * leave harbour.
     */
    const fleets = (c.fleets ?? [])
      .map(
        (f) =>
          `${f.id}:${f.at}:${f.moveTo}:${f.moveT}:${f.ships}:${f.cargo.length}:${f.intercepting}`,
      )
      .join('');
    const factors = (c.trade?.merchants ?? [])
      .map((m) => `${m.id}:${m.node}:${m.mission}:${m.toward}:${m.travel}`)
      .join('');
    /**
     * The order book, and only your own side of it.
     *
     * Every other crown's posting, cancelling, expiry and every match happens at
     * a council, which moves `c.turn` — already the first term here. Yours land
     * on a command, mid-month, exactly like a fleet sailing. So the cheap digest
     * is `nextOrderId` (which a post always moves) plus your own live orders
     * (which a cancel and a partial fill move), six of them at the outside,
     * rather than a walk over a book that may carry two hundred and fifty lines
     * on every frame.
     */
    const book =
      `${c.market.nextOrderId}` +
      c.market.orders
        .filter((o) => o.nation === c.playerNation)
        .map((o) => `:${o.id}.${o.amount}.${o.price}`)
        .join('');
    const warns = c.warnings.map((w) => `${w.kind}.${w.armyId}.${w.to}.${w.turns}.${w.strength}`).join('');
    const wars = c.diplo.wars.map((w) => `${w.id}.${Math.round(w.score)}`).join('');
    const offers = c.diplo.offers.map((o) => `${o.id}.${o.kind}`).join('');
    /**
     * Treaties, truces and who is sworn to whom.
     *
     * These were not in the signature because nothing on the chart depended on
     * them — the realm tab did, and went quietly stale, which is a bug that was
     * easy to miss because a war usually lands in the same month. The relations
     * view makes an alliance a *colour on the board*, so a treaty signed while
     * you are looking at the map has to repaint it.
     */
    const pacts =
      c.diplo.treaties.map((t) => `${t.kind}.${t.a}.${t.b}`).join('') +
      '|' +
      c.diplo.truces.map((t) => `${t.a}.${t.b}.${t.until}`).join('') +
      '|' +
      c.nations.map((nt) => `${nt.liege}.${nt.subject}`).join('');
    const me = c.nations[c.playerNation];
    const board = [
      c.turn,
      c.phase,
      owners,
      armies,
      fleets,
      factors,
      book,
      `${this.draft.kind}.${this.draft.key}.${this.draft.amount}.${this.draft.price}`,
      camps,
      warns,
      wars,
      offers,
      pacts,
      Math.round(me.treasury.gold),
      Math.round(me.manpower),
      // The markets row, and the "To market" row on every province panel. One
      // pass over sixteen nodes rather than a digest of every crown's control in
      // all of them — control drifts continuously, so hashing it would repaint
      // this screen every frame it moved a thousandth. The payout is what is on
      // screen, and it only moves at a council.
      Math.round(tradeIncomeOf(c, c.playerNation)),
      c.pending ? `p${c.pending.province}` : '-',
      // The levy marks, the view and the focus. All three change what is
      // *drawn* — the sheaves on your own ground, every province's fill, the lit
      // counter under a fleet or a warning — and none of them is cheap to patch,
      // so they stay on the board's side and cost a rebuild.
      this.levyMarks ? 'levy' : '',
      this.mapView,
      `${this.focus}.${this.focusArmy}.${this.focusProv}.${this.openCrown}.${this.openWar}` +
        `.${this.focusFleet}.${this.focusNode}`,
      c.log.length,
      c.over ? 'over' : '',
    ].join('|');
    /**
     * The three terms that are **only** a selection, held apart from the rest.
     *
     * Everything above says the board moved and is worth a rebuild. These three
     * say the player touched it, which is a different and far cheaper thing —
     * see `paintSelection`. The group is here with the other two because it is
     * the same fact said about more than one host: it lights banners, it puts
     * pips under a stack, and it decides which hosts an order goes to.
     */
    const sel = `${this.selectedProvince}|${this.selectedArmy}|${this.groupArmies.join('.')}`;
    return { board, sel };
  }

  // ── Realm time, in the words the player thinks in ──────────────────────────

  /**
   * When something that is `turns` months away actually lands.
   *
   * The strategic layer counts in months and the player is watching a clock, so
   * both are printed. The arithmetic is done here rather than in the simulation
   * because it is a *presentation* of two numbers the simulation already keeps —
   * `moveT` and the calendar — and inventing a campaign-side "seconds until"
   * would be a second clock that could drift from the first.
   */
  private arrivalLine(turns: number): string {
    if (turns <= 0) return 'here now';
    const secs = this.secsToCouncil + (turns - 1) * MONTH_SECONDS;
    const day = this.day + this.daysToCouncil + (turns - 1) * DAYS_PER_MONTH;
    const d = dateFromDay(day);
    return `${months(turns)} · ${d.month} ${d.day}, Year ${d.year} · ${span(secs)}`;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  /**
   * Rebuild the screen — and put the three scrolling columns back where the
   * player had them.
   *
   * This screen redraws whenever the board moves, which on a realm with
   * twenty-four crowns and hosts on the roads is every few seconds. Each redraw
   * replaced `.wm-left`, `.wm-right` and `.wm-chron` wholesale, and a scrolling
   * box whose contents are replaced is clamped back to the top. The realm column
   * runs to about thirteen hundred pixels and the detail column runs longer than
   * that whenever a host is selected, so both of them are lists that must be
   * scrolled to be read — and both of them jumped to the top the moment anything
   * on the map moved. The player was not doing anything wrong.
   */
  /**
   * The campaign this screen is currently drawing.
   *
   * Held because a growing number of things that are *not* a render need it —
   * the route plot, the march order behind a long press, the escape key — and
   * every one of them fires from a listener rather than from the paint. Written
   * on every render and nowhere else, so it cannot be staler than the frame it
   * is read in.
   */
  private state: CampaignState | null = null;

  private render(c: CampaignState): void {
    this.state = c;
    keepScroll(this.el, ['.wm-left', '.wm-right', '.wm-chron'], () => this.paint(c));
  }

  /**
   * The bar that is always up on a phone, because it is the way back to
   * everything that is not.
   *
   * `#worldmap` runs from under the strip to the bottom of the glass — it
   * covers the settlement's own `#tabbar` completely, so this screen needs its
   * own. (It used to be `inset: 0; z-index: 80` and cover the strip as well,
   * which is what the shared-furniture pass ended; see `injectStyle`.)
   *
   * **City is a cell and not a sheet.** It is the door home, and it currently
   * lives as a seal in `.wm-foot`, which measures at y = 2,543 inside a
   * scrolling column: the way out of the realm is two and a half thousand
   * pixels down a list. `quit` stays beside it in the Realm sheet — leaving the
   * game is not a thing to put one tap from going home.
   */
  private sheetBar(c: CampaignState): string {
    // The same source `detailPanel` badges with, rather than a second count
    // that could disagree with it.
    const calls = pendingOffers(c).length;
    const cell = (id: string, word: string, badge = 0): string =>
      `<span class="wm-sheettab${this.sheet === id ? ' on' : ''}" data-act="sheet" data-sheet="${id}">` +
      `${esc(word)}${badge > 0 ? `<i class="wm-badge urgent">${badge}</i>` : ''}</span>`;
    return (
      `<div class="wm-sheetbar">` +
      cell('left', 'Realm') +
      // The badge rides Ground because that is the sheet the calls to arms are
      // in, and `detailPanel`'s own note says an unanswered call costs exactly
      // what refusing costs — so it may never be somewhere you have to go
      // looking for it, and behind a shut sheet is exactly that.
      cell('right', 'Ground', calls) +
      cell('chron', 'Chronicle') +
      /**
       * The Ledger, because the settlement's own tab bar has one and this bar
       * did not — and the two bars are the same furniture at the same edge of
       * the same phone. On a desktop the door is the named button on the chrome
       * bar, which is up on this screen now; on a phone that button is at the
       * far right of a strip that scrolls, which is a door you have to know is
       * there. Five cells here, five cells in the town: the same count and the
       * same widths.
       */
      `<span class="wm-sheettab" data-act="ledger">Ledger</span>` +
      `<span class="wm-sheettab" data-act="city">City</span>` +
      `</div>`
    );
  }

/**
   * The little map of the whole realm, welded into the bottom-right corner.
   *
   * > *"keep the mini map at the bottom right but more it all the way to the
   * > bottom right"*
   *
   * It used to live inside the furniture SVG, positioned against a box `H` tall
   * where `H = VIEW.h / s` — a height that assumes the map cell has the
   * viewBox's own aspect ratio. It does not, because the SVG letterboxes, so on
   * a wide cell the minimap was pinned to the bottom of a phantom rectangle
   * floating somewhere up the board. That is why it would not go to the corner
   * however small the margin was made: the margin was never the problem.
   *
   * As its own absolutely-positioned element it is measured against the cell it
   * is in, so `bottom: 0; right: 0` means what it says at every window shape and
   * every zoom. It has its own viewBox and is therefore still drawn in realm
   * coordinates inside.
   */
  private miniMap(): string {
    const w = VIEW.w;
    const h = VIEW.h;
    return `<svg class="wm-minimap" viewBox="${VIEW.x.toFixed(1)} ${VIEW.y.toFixed(1)} ${w.toFixed(
      1,
    )} ${h.toFixed(1)}" preserveAspectRatio="xMidYMid meet">
      <path class="wm-minicoast" d="${coastPath()}" />
      <rect class="wm-minibox" x="${VIEW.x}" y="${VIEW.y}" width="${VIEW.w}" height="${VIEW.h}" />
    </svg>
    <div class="wm-scaletag wm-scalebar"><span class="wm-scale-t">&nbsp;</span></div>`;
  }

  private paint(c: CampaignState): void {

    this.el.innerHTML = `
      <div class="wm-grid">
        <div class="wm-left">${this.realmPanel(c)}</div>
        <div class="wm-map">${this.mapSvg(c)}${this.miniMap()}${this.furniture()}</div>
        <div class="wm-right">${this.detailPanel(c)}</div>
        <div class="wm-log">${this.logPanel(c)}</div>
      </div>
      ${this.sheetBar(c)}`;
    // The hover card is not part of the markup and must outlive it. Setting
    // `innerHTML` had been quietly deleting it on the first redraw — which on a
    // screen that redraws whenever the realm moves meant the tooltips worked for
    // about a second and then never again.
    if (this.tipEl) this.el.appendChild(this.tipEl);
    this.wire(c);
  }

  /**
   * The muster: everything under arms, everything afloat, and what feeds both.
   *
   * The board already knew all of this — hosts in the province panels, hulls in
   * the harbour rows, upkeep buried in the treasury arithmetic — but only one
   * place at a time. This is the quartermaster's answer: every host and every
   * hull you pay for on one sheet, the whole bill a month, and the levy stream
   * that decides how fast losses come back. Rows reuse `hostRow` and the fleet
   * row, so clicking one walks the map to it exactly as it does anywhere else.
   */
  private musterPanel(c: CampaignState): string {
    const me = c.nations[c.playerNation];
    const hosts = c.armies.filter((a) => a.owner === c.playerNation);
    const fleets = c.fleets.filter((f) => f.owner === c.playerNation);
    const points = hosts.reduce((n, a) => n + rawPoints(a), 0);
    const models = hosts.reduce((n, a) => n + armyModels(a), 0);
    const hulls = fleets.reduce((n, f) => n + f.ships, 0);
    const hostBill = points * UPKEEP_GOLD_PER_POINT;
    const fleetBill = hulls * SHIP_UPKEEP_GOLD;
    const income = projectedIncome(c, c.playerNation);

    const fleetRows = fleets
      .map(
        (f) =>
          `<div class="wm-army mine" data-act="focusfleet" data-fleet2="${f.id}"${this.fleetTip(c, f)}>
            <b>${esc(f.name)}</b>
            <span>${esc(fleetSummary(c, f))}</span>
            <span class="wm-dim">at ${esc(provinceName(f.at))} · lift ${fleetCapacity(f)} pts</span>
          </div>`,
      )
      .join('');

    return `
      <div class="wm-title small">The Muster</div>
      <div class="wm-creed">Every sword and every hull the treasury answers for.</div>
      <div class="wm-stat"><span>Hosts</span><b>${hosts.length} · ${Math.round(points)} pts · ${models} men</b></div>
      <div class="wm-stat"><span>Hulls</span><b>${fleets.length} fleet${fleets.length === 1 ? '' : 's'} · ${hulls} hulls</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `Hosts ${hostBill.toFixed(1)}g, hulls ${fleetBill.toFixed(1)}g, every month, paid or felt.`,
        'An unpaid month costs supply instead, and the men notice within the turn.',
      ])}><span>The bill</span><b class="${hostBill + fleetBill > income.gold ? 'bad' : ''}">${(hostBill + fleetBill).toFixed(1)}g a month</b></div>
      <div class="wm-stat"${WorldMap.tip([
        'What the countryside sends each month, scaled by tier, development, unrest — and the Hosts estate.',
        'This is the rate your losses come back at. Ground is the only way to raise it.',
      ])}><span>Levies</span><b>${me.manpower} banked · +${income.levy.toFixed(0)} a month</b></div>
      <div class="wm-stat"><span>Treasury</span><b>${Math.round(me.treasury.gold)}g · +${income.gold.toFixed(0)} a month</b></div>
      <div class="wm-btnrow">
        <span class="wm-btn" data-act="levies"${WorldMap.tip([
          'Every province you hold, what it owes the muster, and what is holding it down.',
          'And what a host costs you in months of countryside, before you raise one.',
        ])}>The Levies</span>
        <span class="wm-btn${this.levyMarks ? ' on' : ''}" data-act="levymarks"${WorldMap.tip([
          'Draw the levy of every province of yours on the chart.',
        ])}>${this.levyMarks ? 'Marks on the chart ✓' : 'Marks on the chart'}</span>
      </div>
      <div class="wm-sub">Hosts</div>
      ${hosts.map((a) => this.hostRow(c, a)).join('') || '<div class="wm-hint">Nobody under arms.</div>'}
      <div class="wm-sub">Fleets</div>
      ${fleetRows || '<div class="wm-hint">Nothing afloat.</div>'}`;
  }

  private realmPanel(c: CampaignState): string {
    const me = c.nations[c.playerNation];
    const def = nationDef(me.def);
    const held = c.provinces.filter((p) => p.owner === c.playerNation).length;
    const hosts = c.armies.filter((a) => a.owner === c.playerNation);
    const standing = hosts.reduce((n, a) => n + rawPoints(a), 0);

    const rivals = c.nations
      .map((n, i) => ({ i, n, held: c.provinces.filter((p) => p.owner === i).length }))
      .filter((r) => r.n.alive)
      .sort((a, b) => b.held - a.held || a.i - b.i)
      .map((r) => {
        const war = r.i !== c.playerNation && c.nations[c.playerNation].atWar[r.i] === 1;
        return `<div class="wm-rival${r.i === c.playerNation ? ' me' : ''}${war ? ' war' : ''}"
             ${r.i === c.playerNation ? '' : `data-act="crown" data-nation="${r.i}"`}
             ${WorldMap.tip([
               `${nationName(c, r.i)} — ${this.peopleOf(nationDef(r.n.def))}`,
               `${r.held} province${r.held === 1 ? '' : 's'}`,
               war ? 'You are at war with them.' : '',
               r.i === c.playerNation ? 'You.' : 'Click for what stands between you.',
             ])}>
             <i style="background:${hex(nationAccent(r.i))}"></i>
             <span>${esc(nationName(c, r.i))}<em>${esc(
               this.peopleOf(nationDef(r.n.def)),
             )}</em></span><b>${r.held}</b></div>`;
      })
      .join('');

    return `
      <div class="wm-title">${esc(def.name)}</div>
      <div class="wm-race">${esc(this.peopleOf(def))}</div>
      <div class="wm-creed">${esc(def.creed)}</div>
      <!--
        The date, the council, the purse and the levies were nine rows here.
        They are on the resource strip now - the same chips, the same drawn
        icons, the same corner of the glass as the town's own stores - and a
        number that is in two places at once on one screen is the confusion this
        pass exists to remove. What is left in this column is what the strip
        cannot say: how much ground you hold, how much of it is under arms, and
        who else is out there.

        The two sentences worth keeping went with their numbers: the timber note
        is on the timber chip's card and the banked-versus-income note is on the
        levies chip's, both in hud.ts.
      -->
      <div class="wm-stat"><span>Provinces</span><b>${held}</b></div>
      ${this.marketRow(c)}
      <div class="wm-stat"><span>Hosts</span><b>${hosts.length} · ${Math.round(standing)} pts</b></div>
      <div class="wm-sub">The powers</div>
      ${rivals}
      <div class="wm-foot">
        <span class="wm-seal" data-act="city">Enter ${esc(provinceDef(c.capital).name)} <i>Tab</i></span>
        <span class="wm-ghost" data-act="quit">Leave the realm</span>
      </div>`;
  }

  /**
   * The chart.
   *
   * Drawn in layers, bottom to top: sea, land, territory tint, terrain
   * hatching, borders, roads, towns, then anything the campaign wants to put on
   * top, then the cartographer's furniture. The order is not decoration — it is
   * what makes the thing legible. A road under a territory tint disappears; a
   * name under a neighbour's fill is unreadable; hatching over a border makes
   * the border stop existing.
   *
   * Only the layers that depend on `CampaignState` are rebuilt. The sea, the
   * coast and every terrain mark are geometry over content, so they are built
   * once and cached as strings.
   */
  private mapSvg(c: CampaignState): string {
    if (!this.bedSvg) this.bedSvg = this.chartBed();
    // Whichever detail the current zoom wants. `applyView` swaps it after this
    // if the zoom moves across a tier; recording it here is what stops the swap
    // firing once for nothing on every repaint.
    const far = lodTier(this.view.w) === 0;
    this.appliedTerrain = far ? 0 : 1;
    return `<div class="wm-stage">
      <svg class="wm-chart" viewBox="${MAP_VIEWBOX}" preserveAspectRatio="xMidYMid meet">
        ${this.bedSvg}
        <!-- The shelf sits on the sea and under everything else: it is the one
             layer that is *about* the water, and the lanes and the coastline
             have to read over it. -->
        ${seaLayer()}
        ${this.seaLaneLayer(c)}
        ${this.trafficLayer(c)}
        <!--
          The ground's own colour, per raster cell.

          Under the politics, because the politics is a tint *on* the ground and
          the ground is not a property of who holds it. This is what used to be
          folded into every province's flat fill, and folding it in there is
          what made two neighbouring provinces of different country meet at a
          hard polygon edge — see the note over biomeImage in shore.ts.
        -->
        ${biomeLayer(quiltInk)}
        ${this.territoryLayer(c)}
        <!--
          The ground itself, where it is not the biome's flat colour: the strand,
          the sea cliffs and their surf, and the snow lying on the tops.

          **Over the province fills and under the relief**, which is the whole
          reason it is a layer of its own. A beach is a different *colour* of
          ground, not a shade of the ground beside it, so neither the hillshade
          (which may only lighten and darken) nor the fabric (which may only
          darken) could say it. Putting it under the relief is what keeps the
          light on it: the strand is still lit from the north-west, the snowfield
          still has the crags showing through, the cliff top still shades.
        -->
        ${groundLayer()}
        <!--
          The relief, over the province colours and under everything else.

          Over, because it is shading *them* — a hillshade beneath a flat fill
          is a hillshade nobody can see. Under the terrain glyphs, the lettering
          and the roads, because those are marks on a chart rather than features
          of the ground, and a road with a hillside painted over it reads as a
          road in a fog.
        -->
        ${reliefLayer()}
        ${textureLayer()}
        <!-- The paper, over the ground and under every mark made on it: a chart
             is drawn *on* its surface, so the roads, the names and the banners
             must not be sanded by it. -->
        <rect class="wm-paper" x="${VIEW.x}" y="${VIEW.y}" width="${VIEW.w}" height="${VIEW.h}"
          fill="url(#wm-paper)" />
        <!--
          The shoreline again, over the ground this time.

          The coast in the bed is drawn *under* the province fills, so all that
          ever showed of it was the seaward half of one stroke — and with a
          strand, a hillshade and a fabric laid on top of the land, four notches
          into the zoom the edge of the world had become a soft grey fade. The
          shoreline is the answer to the only question this game asks, so it gets
          a pen that nothing paints over.

          And the half of it that is not a beach gets its own: coastRuns cuts the
          coast where the ground's steepness at the water changes, so an
          ironbound headland takes a heavier line with hachures combed inland —
          the cliff symbol every survey chart has used for two hundred years —
          and a low shore keeps the plain pen with the strand behind it.
        -->
        ${this.shorePen()}
        <g class="wm-terrain">${this.terrainDetail(far)}</g>
        ${this.regionLettering()}
        ${this.realmLettering(c)}
        <!-- Water goes under the frontiers and the roads: a border follows a
             river often enough that the river must not be drawn over it, and a
             bridge is a road crossing water rather than water crossing a road. -->
        ${riverPaths()}
        ${this.borderLayer(c)}
        ${this.roadLayer(c)}
        ${this.mapView === 'trade' ? this.flowLayer(c) : ''}
        ${this.townLayer(c)}
        <g class="wm-overlay">${this.marchLayer(c)}</g>
        ${this.levyLayer(c)}
        <g class="wm-plot"></g>
        <!-- The marquee. Above the plot because it is the gesture in progress,
             and its own group for the reason the plot has one: it is written
             on every frame of a drag and must never cost a rebuild. -->
        <g class="wm-marquee"></g>
        ${this.seaNames()}
      </svg>
      <div class="wm-say"></div>
      ${
        this.levyMarks
          ? `<div class="wm-levykey" data-act="levymarks"
              ${WorldMap.tip([
                'What every province of yours sends the muster each month.',
                'Click to put the marks away.',
              ])}>⚑ Levies — put away</div>`
          : ''
      }
      <div class="wm-zoom">
        <span data-act="zoomin" title="Closer">+</span>
        <span data-act="zoomout" title="Further">−</span>
        <span data-act="zoomfit" title="The whole realm">◻</span>
        <span data-act="home" title="Back to your seat (Home)">⌂</span>
        <span data-act="bare" title="Put the panels away">⛶</span>
        <span data-act="key" title="The key">?</span>
      </div>
      ${this.viewSwitch(c)}
    </div>`;
  }

  /**
   * The two views, and the key to whichever is on.
   *
   * It sits on the chart rather than in a panel because it is about the chart,
   * and because a key that is not next to the thing it explains is a key nobody
   * reads. The swatches are the real fills and the real hatches — built from the
   * same `REL_INK` and `REL_HATCH` the board is built from, so the key cannot
   * drift from the map, which is the usual way a legend goes wrong.
   *
   * The counts are the point of it as much as the colours are. "Yours · 6" is a
   * sentence about the game; a gold square is only a sentence about the map.
   */
  /**
   * What people a crown is, for the chart's labels.
   *
   * Read off the **nation's own `raceId`**, not off its faction's `race` string.
   *
   * `factions.ts` carries a free-text `race` for flavour, and until the eastern
   * continent arrived every faction happened to have one that matched. It does
   * not any more: the new peoples were seated on factions whose string still
   * said something else, so saurian kingdoms announced themselves as
   * "PLAGUEBEARERS" and giants as "ORCS & OGRES" — across the powers list, the
   * hover, the crown header and the lettering written over their own ground.
   *
   * `NationDef.raceId` is the authority and `KIN_NAMES` is what a people is
   * called. One helper rather than four call sites, because four copies of a
   * lookup is how the four of them came to disagree in the first place.
   */
  private peopleOf(def: { raceId: string }): string {
    return KIN_NAMES[race(def.raceId).kin] ?? race(def.raceId).name;
  }

  private viewSwitch(c: CampaignState): string {
    const rel = this.crownRelations(c);
    const tally: Record<string, number> = {};
    let camps = 0;
    for (let i = 0; i < c.provinces.length; i++) {
      const r = this.relationOf(rel, c.provinces[i].owner);
      tally[r] = (tally[r] ?? 0) + 1;
      if (hasCamp(c, i)) camps++;
    }

    /**
     * How many separate islands your own realm is spread across.
     *
     * The key's job is to say things about *the game* rather than about the
     * map — "6 yours" over a gold square — and on a board whose roads are in
     * seven pieces this is the most important of them. A realm on one island is
     * a realm you can defend by marching; a realm on three is three wars, and
     * the player needs to know which they have before they raise a host that
     * cannot reach the fighting.
     *
     * Only said when it is more than one. "Your realm is on 1 island" is a line
     * of the key explaining that nothing is wrong, and the key is short on
     * purpose.
     */
    const myIslands = new Set<number>();
    for (let i = 0; i < c.provinces.length; i++) {
      if (c.provinces[i].owner !== c.playerNation) continue;
      const k = roadComponent(c, i);
      if (k >= 0) myIslands.add(k);
    }

    /**
     * The reach key: the road graph, counted, from wherever the question is
     * being asked. The middle row is the point of the whole view — ground you
     * hold and cannot walk to — and it is the only red line in it.
     */
    const base = this.reachBase(c);
    let canWalk = 0;
    let mineCut = 0;
    let mineWalk = 0;
    for (let i = 0; i < c.provinces.length; i++) {
      const walk = canWalkBetween(c, base, i);
      if (walk) canWalk++;
      if (c.provinces[i].owner !== c.playerNation) continue;
      if (walk) mineWalk++;
      else mineCut++;
    }

    const order: Rel[] = ['mine', 'sworn', 'ally', 'truce', 'war', 'other', 'none'];
    const rows = order
      // A relation nobody on the board actually is would be a line of the key
      // explaining a colour that is not there. Yours is always listed, even at
      // nought, because a realm with nothing left in it is worth saying out loud.
      .filter((r) => (tally[r] ?? 0) > 0 || r === 'mine')
      .map((r) => {
        const h = REL_HATCH[r];
        const fill = toHex(mix(mix(C.terrAsh, C.parchment, 0.26), REL_INK[r].ink, REL_INK[r].t));
        return (
          `<span class="wm-keyrow${r === 'mine' ? ' own' : ''}">` +
          `<svg class="wm-keysw" viewBox="0 0 12 12"><rect width="12" height="12" fill="${fill}" />` +
          (h
            ? `<rect width="12" height="12" fill="url(#wmk-${r})" />`
            : '') +
          `<rect width="12" height="12" fill="none" stroke="rgba(0,0,0,0.55)" stroke-width="1" /></svg>` +
          `<b>${tally[r] ?? 0}</b> ${esc(REL_WORD[r])}</span>`
        );
      })
      .join('');

    // The key's own copies of the hatches, at the swatch's scale rather than the
    // map's: a twelve-pixel square filled with a pattern sized for a province
    // shows one line of it, or none.
    const keyDefs = (Object.keys(REL_HATCH) as Rel[])
      .map((r) => {
        const h = REL_HATCH[r];
        if (!h) return '';
        return `<pattern id="wmk-${r}" patternUnits="userSpaceOnUse" width="4" height="4">
            <path d="${h.d}" stroke="${toHex(mix(REL_INK[r].ink, C.parchment, 0.35))}"
              stroke-width="0.9" fill="none" opacity="0.85" /></pattern>`;
      })
      .join('');

    /** One key row with a solid swatch of a given ink over the standard paper. */
    const inkRow = (
      ink: number,
      t: number,
      count: number | string,
      words: string,
      cls = '',
      tip: string[] = [],
    ): string =>
      `<span class="wm-keyrow ${cls}"${tip.length ? WorldMap.tip(tip) : ''}>` +
      `<svg class="wm-keysw" viewBox="0 0 12 12">` +
      `<rect width="12" height="12" fill="${toHex(mix(mix(C.terrAsh, C.parchment, 0.26), ink, t))}" />` +
      `<rect width="12" height="12" fill="none" stroke="rgba(0,0,0,0.55)" stroke-width="1" /></svg>` +
      `<b>${count}</b> ${esc(words)}</span>`;

    const tabs: Array<[MapView, string, string[]]> = [
      [
        'terrain',
        'Land',
        [
          'The country itself, with no crown’s colour over it.',
          'Mountain and fen decide what a march costs and where a line can be held; this is the view that says which is which.',
        ],
      ],
      [
        'relations',
        'Relations',
        [
          'Colour the ground by what it is to you.',
          'Yours, your sworn, your allies, your enemies — and everyone else kept quiet.',
          'Which crown holds it is still the band inside the frontier, and the banner on the town.',
        ],
      ],
      [
        'crowns',
        'Crowns',
        [
          'A colour a crown, the way a political map does it.',
          'Better once you know the board; no use at all for finding your own ground.',
        ],
      ],
      [
        'trade',
        'Markets',
        [
          'Colour the ground by the market its trade runs through, and draw what actually went down each road last month.',
          'The heavy lines are market boundaries, not frontiers.',
          'The arrows are measured, not illustrative: their width is the gold that moved.',
        ],
      ],
      [
        'reach',
        'Reach',
        [
          'Whether your men can walk there — from the selected host if you have one, and from the seat of the realm if you do not.',
          'The roads on this board are in pieces. Ground you hold and cannot march to is the one thing this view is red about.',
        ],
      ],
    ];

    const nodes = c.trade?.nodes ?? [];
    let myMarkets = 0;
    let myLed = 0;
    for (let k = 0; k < nodes.length; k++) {
      if (controlIn(c, k, c.playerNation) < 0.02) continue;
      myMarkets++;
      if (nodeLeader(c, k).nation === c.playerNation) myLed++;
    }
    const fleets = fleetsOf(c, c.playerNation);
    const atSea = fleets.filter((f) => isSailing(f)).length;

    let key: string;
    if (this.mapView === 'relations') {
      key = `<div class="wm-keylist">${rows}${
        camps > 0
          ? `<span class="wm-keyrow"><svg class="wm-keysw" viewBox="0 0 12 12">` +
            `<path d="M1.5 10 L6 2.4 L10.5 10 Z" fill="none" stroke="${toHex(C.emberLight)}" stroke-width="1.3" />` +
            `</svg><b>${camps}</b> brigand${camps === 1 ? '' : 's'} camped</span>`
          : ''
      }${
        fleets.length > 0
          ? `<span class="wm-keyrow"${WorldMap.tip([
              `${fleets.length} fleet${fleets.length === 1 ? '' : 's'} of yours are on the board${atSea > 0 ? `, ${atSea} of them at sea` : ''}.`,
              'A hull flies its crown’s banner, and a laden convoy flies the full flag.',
            ])}><svg class="wm-keysw" viewBox="0 0 12 12">` +
            `<path d="M1.5 8.6 H10.5 L9 11 H3 Z" fill="none" stroke="${toHex(C.parchment)}" stroke-width="1.1" />` +
            `<path d="M6 8.6 V1.4 L10 2.6 L6 4 Z" fill="${toHex(C.gold)}" stroke="none" />` +
            `</svg><b>${fleets.length}</b> fleet${fleets.length === 1 ? '' : 's'}${atSea > 0 ? `, ${atSea} at sea` : ''}</span>`
          : ''
      }${
        myIslands.size > 1
          ? `<span class="wm-keyrow cut"${WorldMap.tip([
              `Your ground lies on ${myIslands.size} of the realm's ${roadComponentCount(c)} road networks.`,
              'No host can march between them. Anything you send from one to another goes by sea.',
              'The Reach view colours exactly which is which.',
            ])}><svg class="wm-keysw" viewBox="0 0 12 12">` +
            `<path d="M6 0 V4.4 M6 7.6 V12" stroke="${toHex(C.oxbloodLight)}" stroke-width="1.6" fill="none" />` +
            `</svg><b>${myIslands.size}</b> islands, and no road between</span>`
          : ''
      }</div>`;
    } else if (this.mapView === 'trade') {
      key = nodes.length
        ? `<div class="wm-keylist">
            <span class="wm-keyrow own"><b>${nodes.length}</b> markets on the board</span>
            ${inkRow(C.gold, 0.3, myMarkets, `you take a share of`, myMarkets > 0 ? 'own' : '', [
              'A market you hold any real share of pays you a cut of everything taken in it.',
              'Ground is not enough: control is value, forts, hosts, harbours and factors together.',
            ])}
            <span class="wm-keyrow${myLed > 0 ? ' own' : ''}"><b>${myLed}</b> you lead</span>
            <span class="wm-keyrow"${WorldMap.tip([
              'An arrow is a trade road, and its width is the gold that actually went down it last month.',
              'The market you have open is drawn at full strength; the rest are kept quiet, or the chart disappears under them.',
            ])}><svg class="wm-keysw" viewBox="0 0 12 12">
              <path d="M1 6 H7" stroke="${toHex(C.goldPale)}" stroke-width="2.2" fill="none" />
              <path d="M11 6 L7 3.6 L7 8.4 Z" fill="${toHex(C.goldPale)}" /></svg
              >gold down a road, last month</span>
            <span class="wm-keyrow dimline">Click a market for its roads, who is draining it, and where a factor would earn most.</span>
          </div>`
        : `<div class="wm-keylist"><span class="wm-keyrow dimline">This realm has no markets.</span></div>`;
    } else if (this.mapView === 'reach') {
      key = `<div class="wm-keylist">
          ${inkRow(C.goldPale, 0.52, 1, `${provinceName(base)} — from here`, 'own')}
          ${inkRow(C.goldPale, 0.36, mineWalk, 'yours, and walkable', 'own')}
          ${inkRow(C.oxbloodLight, 0.5, mineCut, 'yours, and no road to it', 'cut', [
            'Your own ground, with no road from where the question is being asked.',
            'A host raised here can never march to it. It has to be carried, which means a harbour, hulls, and a month or more of sea.',
          ])}
          ${inkRow(C.parchment, 0.2, canWalk, 'reachable on foot, all told', '', [
            `The road network ${provinceName(base)} stands on, of ${roadComponentCount(c)} on the board.`,
          ])}
          ${inkRow(C.terrRiver, 0.2, c.provinces.length - canWalk, 'reachable only by sea', '', [
            'No road of any kind runs from here to any of these. Taking one means a harbour, hulls, and a month or more of sea.',
          ])}
          <span class="wm-keyrow dimline">Select a host to ask the question from where it is standing.</span>
        </div>`;
    } else if (this.mapView === 'terrain') {
      /**
       * A count beside every country, which is what makes this a key about the
       * *game* rather than about the drawing — "31 forest" is a sentence about
       * where a war would be fought, and a green square is not.
       */
      const count: Record<string, number> = {};
      for (let i = 0; i < c.provinces.length; i++) {
        const t = provinceDef(i).terrain;
        count[t] = (count[t] ?? 0) + 1;
      }
      key = `<div class="wm-keylist">${TERRAIN_ORDER.filter((t) => (count[t] ?? 0) > 0)
        .map((t) => {
          const fill = toHex(mix(TERRAIN_INK[t] ?? C.terrAsh, C.parchment, 0.1));
          return (
            `<span class="wm-keyrow">` +
            `<svg class="wm-keysw" viewBox="0 0 12 12"><rect width="12" height="12" fill="${fill}" /></svg>` +
            `<b>${count[t]}</b> ${esc(TERRAIN_WORD[t] ?? t)}</span>`
          );
        })
        .join('')}</div>`;
    } else {
      key =
        `<div class="wm-keylist"><span class="wm-keyrow dimline">A colour a crown. ` +
        `Switch to Relations to find your own ground.</span></div>`;
    }

    return `<div class="wm-view">
      <svg width="0" height="0" style="position:absolute"><defs>${keyDefs}</defs></svg>
      <div class="wm-viewtabs">
        ${tabs
          .map(
            ([id, word, tip]) =>
              `<span class="${this.mapView === id ? 'on' : ''}" data-act="view" data-view="${id}"
                ${WorldMap.tip(tip)}>${word}</span>`,
          )
          .join('')}
      </div>
      ${key}
    </div>`;
  }

  // ── Pan and zoom ─────────────────────────────────────────────────────────

  /**
   * Push the view back onto the realm.
   *
   * A little overscan is allowed — a quarter of the view past the edge — because
   * clamping hard to the frame makes the map feel like it is fighting the mouse
   * when you are looking at a coastal province. What is not allowed is losing
   * the realm off the side of the screen entirely.
   */
  private clampView(v: ViewBox): ViewBox {
    const w = Math.max(MIN_VIEW_W, Math.min(VIEW.w, v.w));
    const h = w * (VIEW.h / VIEW.w);
    const slackX = w * 0.25;
    const slackY = h * 0.25;
    return {
      w,
      h,
      x: Math.max(VIEW.x - slackX, Math.min(VIEW.x + VIEW.w - w + slackX, v.x)),
      y: Math.max(VIEW.y - slackY, Math.min(VIEW.y + VIEW.h - h + slackY, v.y)),
    };
  }

  /**
   * Everything that depends on how far in the view is zoomed, applied without
   * touching the DOM tree.
   *
   * This is the reason panning is smooth at this board size: re-rendering the
   * chart is a quarter of a megabyte of markup to parse and would be hopeless at
   * sixty frames a second, but moving the view is four attribute writes.
   */
  private applyView(): void {
    const v = this.view;
    const k = v.w / VIEW.w;
    if (this.chart) {
      this.chart.setAttribute('viewBox', `${v.x.toFixed(2)} ${v.y.toFixed(2)} ${v.w.toFixed(2)} ${v.h.toFixed(2)}`);
      // The reciprocal of the zoom: everything lettered, and every line weight,
      // is multiplied by it and therefore stays put on the glass.
      this.chart.style.setProperty('--wm-k', k.toFixed(4));
      this.chart.setAttribute('class', `wm-chart wm-lod${lodTier(v.w)}`);
      /**
       * The terrain's level of detail, swapped when — and only when — the zoom
       * crosses the one boundary that matters. Everything else on this screen
       * changes level of detail through a CSS rule and costs nothing; this layer
       * is too big to keep two copies of in the document, and the argument is at
       * `terrFar`.
       */
      const wantFar = lodTier(v.w) === 0 ? 0 : 1;
      if (wantFar !== this.appliedTerrain) {
        this.appliedTerrain = wantFar;
        const g = this.chart.querySelector('.wm-terrain');
        if (g) g.innerHTML = this.terrainDetail(wantFar === 0);
      }
    }
    // The campaign's counters are marks with numbers in them, so they follow the
    // type rule rather than the terrain rule: constant on the glass, at every
    // zoom. Only rewritten when the zoom actually moved — a pan must stay four
    // attribute writes, not a walk over every mark on the board.
    if (k !== this.appliedK) {
      this.appliedK = k;
      const s = k.toFixed(4);
      for (const g of this.counters) {
        g.setAttribute(
          'transform',
          `translate(${g.dataset.cx ?? '0'} ${g.dataset.cy ?? '0'}) scale(${s})`,
        );
      }
      /**
       * The relation hatch is the ground's until the ground outgrows the pen.
       *
       * The hatch belongs to the terrain rule — it grows with the map, which is
       * what keeps it a property of the province rather than of the screen, and
       * shrinking it with `--wm-k` would thicken it into a solid fill at the fit
       * view. That argument was airtight while the zoom stopped at `VIEW.w / 88`;
       * at the new floor of 40 the same four-unit weave comes out as seventy-pixel
       * candy stripes, which no chart has ever hatched anything with. So the
       * pattern rides the ground down to the old maximum and then *freezes on the
       * glass*: past `HATCH_FREEZE_W` it is scaled back by exactly the overshoot,
       * so the deepest view keeps the weave the fifth notch had. Four attribute
       * writes, in the block that already only runs when the zoom moves.
       */
      const hs = Math.min(1, v.w / HATCH_FREEZE_W);
      for (const p of this.hatchPats) {
        p.setAttribute('patternTransform', `scale(${hs.toFixed(4)})`);
      }
      /**
       * The route preview's own circles, which are sized off `k`.
       *
       * Inside this block and not outside it: `applyView` runs on every frame
       * of a pan, and `drawPlot` walks a breadth-first search per held host. It
       * may only be paid when the zoom has actually moved, which is a handful
       * of times a session.
       */
      if (this.plotProv >= 0 && this.state) this.drawPlot(this.state);
    }
    /**
     * Queried off the root, not off `.wm-frame`.
     *
     * The minimap and the scale readout used to live inside the furniture SVG
     * and moved out of it when the minimap was welded to the corner of the map
     * cell. `this.frame.querySelector` went on looking for them in the SVG,
     * found nothing, and failed silently — so the camera box on the minimap
     * would have stopped following the camera and the scale would have stopped
     * counting, with no error anywhere. A querySelector that returns null is
     * the quietest bug this file can have.
     */
    if (this.el) {
      const scale = this.el.querySelector('.wm-scalebar');
      const bar = this.el.querySelector('.wm-scale-t');
      if (bar) {
        // The bar is a fixed 24 furniture-units long. What that is worth in
        // leagues depends on the zoom, so the number is written every time.
        const units = (24 / FURN_W) * v.w;
        bar.textContent = `${Math.round(units * LEAGUES_PER_UNIT)} leagues`;
      }
      if (scale) scale.setAttribute('data-w', v.w.toFixed(0));
      const box = this.el.querySelector('.wm-minibox');
      if (box) {
        box.setAttribute('x', v.x.toFixed(1));
        box.setAttribute('y', v.y.toFixed(1));
        box.setAttribute('width', v.w.toFixed(1));
        box.setAttribute('height', v.h.toFixed(1));
      }
    }
  }

  private zoomBy(factor: number, ax = 0.5, ay = 0.5): void {
    const v = this.view;
    // Keep the point under the cursor where it is: that is the whole difference
    // between a zoom that feels like a map and one that feels like a slideshow.
    const fx = v.x + v.w * ax;
    const fy = v.y + v.h * ay;
    const next = this.clampView({ x: 0, y: 0, w: v.w * factor, h: v.h * factor });
    this.view = this.clampView({
      x: fx - next.w * ax,
      y: fy - next.h * ay,
      w: next.w,
      h: next.h,
    });
    this.applyView();
  }

  /** Sea, shallows, coastline: everything under the politics. */
  private chartBed(): string {
    /**
     * Every territory's outline, written once and referenced everywhere after.
     *
     * It is drawn three times per province — the fill, the band of the holder's
     * colour inside its edge, and the clip that keeps that band inside — and at
     * a hundred and twenty-seven provinces of forty-odd points each, spelling
     * the path out three times is about half a megabyte of markup for the
     * browser to parse on every redraw. A `<use>` is eleven characters.
     */
    const defs = PROVINCES.map(
      (_, i) =>
        `<path id="wmp${i}" d="${cellPath(i)}" />` +
        `<clipPath id="wmc${i}"><use href="#wmp${i}" /></clipPath>`,
    ).join('');
    const echoes = echoPaths()
      .map(
        (d, k) =>
          `<path d="${d}" fill="none" stroke="rgba(146,178,192,${(0.2 - k * 0.04).toFixed(3)})"
             stroke-width="${(0.4 - k * 0.06).toFixed(2)}" />`,
      )
      .reverse()
      .join('');
    // A wave is two strokes of a pen and nothing else. Any more and the water
    // starts competing with the land for attention, which it must never do.
    const waves = waveMarks()
      .map(
        (p) =>
          `M${(p.x - 1.5).toFixed(1)} ${p.y.toFixed(1)}q0.75 -0.55 1.5 0q0.75 0.55 1.5 0`,
      )
      .join('');
    return `<defs>
        <radialGradient id="wm-sea" cx="50%" cy="46%" r="68%">
          <stop offset="0" stop-color="#18242c" /><stop offset="1" stop-color="#0a1017" />
        </radialGradient>
        <!--
          The paper the chart is drawn on. A CC0 fibre scan, mirrored into a
          seamless tile — see public/art/map/ATTRIBUTION.md for the source, the
          licence and what was done to it.

          88 map units to a tile: small enough that the weave is a texture
          rather than a pattern of blobs, large enough that it does not alias
          into a moire at the fit view. It is the one downloaded asset on this
          screen and it is doing the one job procedural noise does badly —
          real fibre is unevenly *clumped* at several scales at once, and value
          noise is not.
        -->
        <!--
          Slope ramps, shared by every mountain on the board.
          A real face is brightest where it turns toward the light at the ridge
          and falls away toward its own foot; two flat tones cannot say that at
          any size and two ramps say it at every size.
        -->
        <linearGradient id="wm-ridge-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#2a251c" stop-opacity="0.88" />
          <stop offset="1" stop-color="#100e0b" stop-opacity="0.62" />
        </linearGradient>
        <linearGradient id="wm-ridge-lit" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stop-color="#fbf4dd" stop-opacity="0.72" />
          <stop offset="1" stop-color="#c9bd9c" stop-opacity="0.3" />
        </linearGradient>
        <linearGradient id="wm-hill-dark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#2b271e" stop-opacity="0.5" />
          <stop offset="1" stop-color="#14120e" stop-opacity="0.3" />
        </linearGradient>
        <linearGradient id="wm-hill-lit" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stop-color="#efe7cd" stop-opacity="0.42" />
          <stop offset="1" stop-color="#c6bb9c" stop-opacity="0.18" />
        </linearGradient>
        <pattern id="wm-paper" patternUnits="userSpaceOnUse"
          x="${VIEW.x}" y="${VIEW.y}" width="88" height="88">
          <image href="art/map/paper-grain.jpg" width="88" height="88" preserveAspectRatio="none" />
        </pattern>
        ${this.hatchDefs()}
        <!-- The land, as one shape, so a raster laid over it can be cut to the
             drawn coastline instead of to its own pixel edge. Used by the biome
             wash; see biomeLayer. -->
        <clipPath id="wm-land"><path d="${coastPath()}" /></clipPath>
        ${defs}
      </defs>
      <rect x="${VIEW.x}" y="${VIEW.y}" width="${VIEW.w}" height="${VIEW.h}" fill="url(#wm-sea)" />
      <path class="wm-wave" d="${waves}" />
      ${echoes}
      <path d="${echoPaths()[1]}" fill="rgba(40,58,66,0.42)" stroke="none" />
      <path d="${echoPaths()[0]}" fill="rgba(52,72,80,0.45)" stroke="none" />
      <path class="wm-coast" d="${coastPath()}" fill="${toHex(mix(C.terrAsh, C.parchment, 0.19))}" />
      <path class="wm-coast-edge" d="${coastPath()}" />`;
  }

  /**
   * What every crown is to you, indexed by nation, worked out once per render.
   *
   * `diplomaticView` is the authority on all four of these questions and it is
   * already called on this screen for the wars panel; asking it again here is one
   * pass over twenty-four crowns, not a second opinion. Doing the arithmetic
   * per-province instead would be a hundred and twenty-seven lookups of the same
   * two dozen answers, and — worse — a place for the map to disagree with the
   * panel about who your allies are.
   */
  private crownRelations(c: CampaignState): Rel[] {
    const rel: Rel[] = c.nations.map(() => 'other');
    rel[c.playerNation] = 'mine';
    for (const v of diplomaticView(c, c.playerNation)) {
      // Ordered worst-first: a crown that is at war with you is at war with you
      // whatever else is also true, and a truce is only interesting when there is
      // no live war under it.
      rel[v.nation] = v.atWar
        ? 'war'
        : v.theirSubjection > 0
          ? 'sworn'
          : v.allied
            ? 'ally'
            : v.truceTurns > 0
              ? 'truce'
              : 'other';
    }
    return rel;
  }

  /** What a given province is to you. Ground nobody holds is nobody's. */
  private relationOf(rel: Rel[], owner: number): Rel {
    return owner < 0 ? 'none' : (rel[owner] ?? 'other');
  }

  /**
   * Who holds what — and, first, what that means for you.
   *
   * Two views, and the toggle beside the zoom buttons switches them. **Relations**
   * is the default and is the answer to "what is mine": the ground takes the
   * colour and the hatch of its relationship to you, and the holder's own banner
   * colour is demoted to the band inside the frontier, where a chart has always
   * put identity. **Crowns** is the old view, every holder its own hue, kept
   * because once you know the board it is the better one for reading who is
   * eating whom.
   *
   * The band stays in the national accent in both views on purpose. It is the
   * thing that says *which* enemy, and dropping it would trade one unanswerable
   * question for another.
   */
  /**
   * Where "can my men walk to it" is measured from.
   *
   * The selected host, when you have one selected and it is standing on ground
   * rather than on a road or a deck — because with a host in hand the question
   * is about *that host* — and otherwise the seat of the realm, which is where a
   * newly raised one would start from. Both are the same query on the same road
   * graph; only the origin moves.
   */
  private reachBase(c: CampaignState): number {
    const a = this.selectedArmy >= 0 ? findArmy(c, this.selectedArmy) : undefined;
    if (a && a.owner === c.playerNation && a.moveTo < 0 && a.aboard < 0) return a.at;
    return c.capital;
  }

  /** The fill a province takes in the trade view: its market's ink. */
  private nodeInk(c: CampaignState, i: number): { ink: number; t: number } {
    const node = c.trade?.nodes.length ? tradeNodeOf(c, i) : -1;
    if (node < 0) return { ink: C.parchment, t: 0.03 };
    return { ink: NODE_INKS[node % NODE_INKS.length], t: 0.3 };
  }

  /**
   * The fill a province takes in the reach view.
   *
   * Four answers, and the third is the one the view exists for: **ground you
   * hold that no host of yours can walk to.** On this board that is the common
   * case, not the exception — the roads are in seven pieces and a hundred and
   * eleven provinces of a hundred and fifty are cut off from the heartland — and
   * a map that colours it exactly like the rest of your realm is hiding the most
   * important fact about it. So it is the only red on this view, on the same
   * argument `war` is the only red on the relations view.
   */
  private reachInk(c: CampaignState, i: number, base: number): { ink: number; t: number } {
    const walk = canWalkBetween(c, base, i);
    const mine = c.provinces[i].owner === c.playerNation;
    if (i === base) return { ink: C.goldPale, t: 0.52 };
    // Ground you could walk to is bone; ground you would have to sail to takes
    // the water's own colour, which is the plainest way to say *why* it is out
    // of reach. Two greys eight units apart would have been a view with one
    // channel spent on nothing — the same fault `REL_INK`'s note records.
    if (walk) return mine ? { ink: C.goldPale, t: 0.36 } : { ink: C.parchment, t: 0.2 };
    return mine ? { ink: C.oxbloodLight, t: 0.5 } : { ink: C.terrRiver, t: 0.2 };
  }

  private territoryLayer(c: CampaignState): string {
    const rel = this.crownRelations(c);
    const byRelation = this.mapView === 'relations';
    const base = this.mapView === 'reach' ? this.reachBase(c) : -1;
    return c.provinces
      .map((p, i) => {
        const def = provinceDef(i);
        const ground = TERRAIN_INK[def.terrain] ?? C.terrAsh;
        const owned = p.owner >= 0;
        const accent = owned ? nationAccent(p.owner) : NEUTRAL;
        const r = this.relationOf(rel, p.owner);
        // The palette rule: the ground is ash and bone, and only a banner is
        // allowed to be saturated. So the territory takes a *breath* of colour
        // and the frontier band takes the rest of it. A map filled with flat
        // national colours is a board game, not a chart — and that holds however
        // the colour is chosen.
        /**
         * A *breath* of parchment over the biome, not a quarter of one.
         *
         * This was 0.26, which is enough to pull ten spread-out grounds back
         * into the same putty — it was doing to the biomes exactly what it was
         * put there to do to the national colours. The chart still needs the
         * paper feel, and 0.10 gives it without eating the country underneath.
         *
         * That mix now happens per raster cell in `quiltInk`, and the province
         * carries only the tint over it — see below.
         */
        const ink = byRelation
          ? REL_INK[r]
          : this.mapView === 'trade'
            ? this.nodeInk(c, i)
            : this.mapView === 'reach'
              ? this.reachInk(c, i, base)
              : this.mapView === 'terrain'
                ? // Nothing over the ground at all. That is the whole mode: on
                  // every other view a crown's tint sits on top of the country
                  // and the country loses, which is why ten distinct biomes read
                  // as one grey-brown board however they are coloured.
                  { ink: ground, t: 0 }
                : { ink: accent, t: owned ? 0.2 : 0.04 };
        /**
         * **The politics, and nothing else.**
         *
         * This was `mix(paper, ink.ink, ink.t)` — the relation's colour mixed
         * into the province's own ground and painted as one opaque fill. It is
         * now the relation's colour painted *at that same fraction* over the
         * per-cell biome wash underneath, which is the identical arithmetic:
         * `a·t + b·(1−t)` compositing straight alpha is `mix(b, a, t)`. Every
         * number in `REL_INK`, `nodeInk` and `reachInk` still means exactly what
         * its own note says it means, and the seven fills the `REL_INK` table
         * lists by hex still come out at those hexes over ground of the same
         * colour. What changed is that the ground under them is no longer flat
         * across a whole province.
         *
         * The terrain view falls out of it: `t` is 0 there, so the province
         * paints nothing at all and the wash is the view.
         */
        const sel = i === this.selectedProvince;
        const hatch = byRelation ? REL_HATCH[r] : undefined;
        // `data-rel` is carried in both views, whether or not the fill is using
        // it. It is what the province is to you, on the element that is the
        // province — so the screenshot harness and anything else driving this
        // screen can ask for "a province I hold" without knowing which crown I
        // am or matching a colour.
        //
        // `data-walk` is the same idea for the question this board made
        // important: 1 when your men can walk here from the seat, 0 when the
        // only way is by sea. It is on the cell rather than worked out by the
        // harness because the harness must not re-derive a rule — and because
        // "show me a province I own and cannot reach" is a picture somebody will
        // want to take.
        // `--wm-t` carries the fill's own weight to the stylesheet, because the
        // hover highlight has to be able to *add* to it: a province at 0.07 and
        // a province at 0.52 both have to brighten visibly under the mouse, and
        // `filter: brightness()` on a fill this thin does nothing you can see.
        return `<use class="wm-cell${sel ? ' sel' : ''}" data-prov="${i}" data-rel="${r}"
            data-walk="${canWalkBetween(c, c.capital, i) ? 1 : 0}" href="#wmp${i}"
            style="--wm-t:${ink.t.toFixed(3)}"
            fill="${toHex(ink.ink)}" fill-opacity="${ink.t.toFixed(3)}" />
          ${hatch ? `<use class="wm-relhatch" href="#wmp${i}" fill="url(#wmh-${r})" />` : ''}
          <use class="wm-band${sel ? ' sel' : ''}${byRelation && r === 'mine' ? ' own' : ''}"
            data-bandprov="${i}" data-owned="${owned ? 1 : 0}"
            href="#wmp${i}" fill="none" stroke="${toHex(accent)}"
            opacity="${owned ? 0.5 : 0.16}"
            clip-path="url(#wmc${i})" />`;
      })
      .join('');
  }

  /**
   * The hatch patterns, one per relation that has one.
   *
   * `patternUnits="userSpaceOnUse"` because these are a property of the ground:
   * four map units is about a fifth of a province at any zoom, so the hatch stays
   * the same size *relative to the province it is describing* rather than
   * relative to the screen. That is the terrain rule, not the type rule, and it
   * is the reason this is not multiplied by `--wm-k`.
   */
  private hatchDefs(): string {
    return (Object.keys(REL_HATCH) as Rel[])
      .map((r) => {
        const h = REL_HATCH[r];
        if (!h) return '';
        const c = REL_INK[r].ink;
        return `<pattern id="wmh-${r}" patternUnits="userSpaceOnUse" width="4" height="4">
            <path d="${h.d}" stroke="${toHex(mix(c, C.parchment, 0.25))}"
              stroke-width="${h.w}" fill="none" opacity="${h.op}" />
          </pattern>`;
      })
      .join('');
  }

  /**
   * The hatching. Every province already carries a terrain; this is that terrain
   * *drawn* rather than named — chained ridges for mountains, rolling scallops
   * for hills, canopies and bare forks for the Deadwood, reed clumps standing in
   * water for marsh, broken courses of wall for ruin.
   *
   * One of two versions, chosen by how far the view is zoomed out. See
   * `terrainOf` for the rule and why it is the job.
   *
   * Built once per version and then held as text: this is the largest single
   * block of markup on the screen and it depends on nothing that moves.
   */
  private terrainDetail(far: boolean): string {
    const held = far ? this.terrFar : this.terrNear;
    if (held) return held;
    const built = this.terrainLayer(far);
    if (far) this.terrFar = built;
    else this.terrNear = built;
    return built;
  }

  private terrainLayer(far: boolean): string {
    const out: string[] = [];
    for (let i = 0; i < PROVINCES.length; i++) {
      const kind = provinceDef(i).terrain;
      const g = terrainOf(i, far);
      if (g) out.push(`<g class="wm-terr t-${kind}">${g}</g>`);
    }
    /**
     * Squeezed, and it is worth a line of explanation.
     *
     * The glyph functions are written across several lines with the path data
     * indented to be readable — and every one of those newlines and leading
     * spaces goes into the document, because `paint` builds this screen by
     * assigning `innerHTML`. At a hundred and fifty provinces drawn at two
     * levels of detail that is tens of kilobytes of indentation for the browser
     * to parse every time the realm moves. Runs of whitespace are always
     * equivalent to one space inside SVG path data and inside a tag, so this is
     * free, and it keeps the source readable rather than making the author
     * choose between the two.
     */
    return out.join('').replace(/\s+/g, ' ');
  }

  /**
   * The regions, lettered across the whole realm — the largest type on the map
   * and the only type at the fit view.
   *
   * This is the reference's own device: `THE JADE EMPIRE (Lizardfolk Dominion)`,
   * a proper name in spaced small caps with what the place *is* underneath it in
   * brackets. `realm.ts` already writes both — `RegionDef.name`, `.subtitle` and
   * an `x`/`y` that its own comment calls "where to write its name" — so this is
   * a seam somebody built for this and nothing here re-derives it.
   *
   * **The ladder of lettering, which is the whole level-of-detail story for
   * type.** Constant-size type means zooming out removes room rather than making
   * it, so each tier of the view gets exactly one scale of name:
   *
   *   fit       region names only. Seven or eight of them across the world.
   *   one in    region names fade out, crowns' names come up, seats are named.
   *   two in    crowns' names, and the walled and the wealthy are named.
   *   right in  no captions for things off screen: towns, and every town.
   *
   * A region name at the fit view is the only thing on that view that answers
   * "what part of the world am I looking at", and before this the answer was
   * nothing at all: the crowns' names are hidden there on purpose because
   * twenty-four of them at that size is porridge.
   */
  /**
   * …and it is set on a curve fitted to the region's own ground.
   *
   * A chart of this kind is half lettering, and the half of *that* which says
   * "drawn" rather than "printed" is that a country's name runs along the
   * country. Straight names centred on a point are what an atlas index looks
   * like; a name that bends with the coast is what a chart looks like, and it is
   * also more legible, because it stops the eye from having to guess which of
   * three neighbouring shapes a horizontal caption belongs to.
   *
   * The curve is derived rather than authored, from the region's own provinces:
   *
   *   1. **The principal axis** of the province centres — the direction the
   *      region actually runs in. A region that runs north-east gets a name that
   *      runs north-east.
   *   2. **A least-squares parabola** in that frame, so a crescent of provinces
   *      round a gulf gets a name that follows the gulf.
   *   3. **Bent back toward level.** A name at the region's true angle is
   *      unreadable when the region runs steeply, so the axis is pulled halfway
   *      to horizontal and the parabola's bend is capped. This is lettering, not
   *      a plot.
   *
   * The path is made **generously longer than the type could possibly be** and
   * the text is hung on it centred, because a `textPath` whose path runs out
   * simply stops drawing the rest of the name — a failure that would show up on
   * exactly the longest and most important names. Overlong path, on the other
   * hand, costs nothing: it is never stroked.
   */
  private regionLettering(): string {
    const out: string[] = [];
    for (const rg of REGIONS ?? []) {
      // A region of one or two holdings is a name lying on top of two towns.
      if (rg.provinces.length < 3) continue;
      const pts = rg.provinces.map((i) => provinceCentre(i));
      let cx = 0;
      let cy = 0;
      for (const q of pts) {
        cx += q.x;
        cy += q.y;
      }
      cx /= pts.length;
      cy /= pts.length;

      // The principal axis, as the eigenvector of the 2×2 covariance with the
      // larger eigenvalue — three sums and a closed form, not an iteration.
      let sxx = 0;
      let syy = 0;
      let sxy = 0;
      for (const q of pts) {
        sxx += (q.x - cx) * (q.x - cx);
        syy += (q.y - cy) * (q.y - cy);
        sxy += (q.x - cx) * (q.y - cy);
      }
      const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
      let ux = Math.cos(theta);
      let uy = Math.sin(theta);
      if (ux < 0) {
        ux = -ux;
        uy = -uy;
      }
      /**
       * Bent back toward level, **by angle and not by component**.
       *
       * The first cut of this scaled `uy` by a half, which does nothing at all
       * to an axis that is already near-vertical: `(0.1, 0.995)` becomes
       * `(0.196, 0.976)`, still seventy-eight degrees. Measured in the browser,
       * THE HEARTHLANDS came out twenty-one pixels wide and eighty-seven tall —
       * a region name written down the page. Clamping the *angle* is the thing
       * that was meant, and it is the thing that behaves the same however steep
       * the ground is.
       */
      const ang = Math.max(-MAX_LABEL_TILT, Math.min(MAX_LABEL_TILT, Math.atan2(uy, ux) * 0.6));
      ux = Math.cos(ang);
      uy = Math.sin(ang);
      // Perpendicular, pointing down the sheet, so "below" means below.
      const vx = -uy;
      const vy = ux;

      const ts = pts.map((q) => (q.x - cx) * ux + (q.y - cy) * uy);
      const ss = pts.map((q) => (q.x - cx) * vx + (q.y - cy) * vy);
      const span = Math.max(...ts) - Math.min(...ts);

      /**
       * s = a + c·t², by normal equations on two moments — **and there is
       * deliberately no linear term.**
       *
       * A `b·t` would be a tilt, and the tilt has just been decided above and
       * clamped for legibility; letting the fit put one back is how the first
       * cut of this ended up with vertical names again after the axis had been
       * levelled. So the fit is allowed to say where the curve sits and how it
       * bends, and nothing about which way it runs.
       *
       * Guarded rather than trusted: a region whose provinces are nearly
       * collinear gives a singular matrix, and the honest answer there is a
       * straight line, which is what the zero fallback is.
       */
      let a = 0;
      let cq = 0;
      {
        const n0 = pts.length;
        let t2 = 0;
        let t4 = 0;
        let s0 = 0;
        let s2 = 0;
        for (let i = 0; i < n0; i++) {
          const t = ts[i];
          t2 += t * t;
          t4 += t * t * t * t;
          s0 += ss[i];
          s2 += ss[i] * t * t;
        }
        const det = n0 * t4 - t2 * t2;
        if (Math.abs(det) > 1e-6 && n0 >= 4) {
          a = (s0 * t4 - t2 * s2) / det;
          cq = (n0 * s2 - t2 * s0) / det;
        } else if (n0 > 0) {
          a = s0 / n0;
        }
      }
      // Cap the bend at a twelfth of the run, measured at the ends: a parabola
      // fitted to a dozen scattered points can leave the sheet entirely, and
      // measured in the browser an eighth still put a fourteen-letter name over
      // forty pixels of rise on eighty of run, which reads as a wobble rather
      // than as a curve.
      const half = Math.max(span, rg.name.length * 13) / 2;
      const bend = Math.abs(cq) * half * half;
      const cap = half * 0.085;
      if (bend > cap) cq *= cap / bend;

      const line = (off: number): string => {
        let d = '';
        const steps = 12;
        for (let i = 0; i <= steps; i++) {
          const t = -half + (2 * half * i) / steps;
          const s = a + cq * t * t + off;
          d +=
            `${i === 0 ? 'M' : 'L'}${(cx + ux * t + vx * s).toFixed(1)} ` +
            `${(cy + uy * t + vy * s).toFixed(1)}`;
        }
        return d;
      };
      const id = `wmrg${rg.provinces[0]}`;
      out.push(
        `<path id="${id}" d="${line(0)}" fill="none" />` +
          (rg.subtitle ? `<path id="${id}s" d="${line(6.4)}" fill="none" />` : '') +
          `<text class="wm-regionname"><textPath href="#${id}" startOffset="50%"
             >${esc(rg.name.toUpperCase())}</textPath></text>` +
          (rg.subtitle
            ? `<text class="wm-regionsub"><textPath href="#${id}s" startOffset="50%"
                 >(${esc(rg.subtitle)})</textPath></text>`
            : ''),
      );
    }
    return `<g class="wm-lettering wm-regions">${out.join('')}</g>`;
  }

  /** The powers, lettered across their own ground the way a chart letters a country. */
  private realmLettering(c: CampaignState): string {
    const out: string[] = [];
    for (let n = 0; n < c.nations.length; n++) {
      if (!c.nations[n].alive) continue;
      const held = c.provinces.map((p, i) => ({ p, i })).filter((r) => r.p.owner === n);
      // Two provinces is not a country you letter across; it is a name lying on
      // top of two towns. Three, and there is room for it.
      if (held.length < 3) continue;
      let x = 0;
      let y = 0;
      for (const r of held) {
        const q = provinceCentre(r.i);
        x += q.x;
        y += q.y;
      }
      // Keep the lettering well inside the land: a nation name half in the sea
      // reads as a label for the sea. The bounds are *measured* — they were
      // written as 24..76 by 10..86, which were the right numbers when province
      // coordinates ran 0..100 and which quietly herded every nation's name into
      // the far west of a realm that now runs to 264.
      //
      // Clamped by half the *name's own width*, not by a fixed margin: "The
      // Covenant of Cairnfall" lettered across a six-province realm is a hundred
      // map units long, and centring it on the territory puts a third of it in
      // the water. The width is figured at the widest zoom the lettering is
      // shown at, which is the worst case.
      const halfW = nationName(c, n).length * 4.6 * 0.34;
      const lx = Math.max(
        Math.min(LAND.x0 + halfW, (LAND.x0 + LAND.x1) / 2),
        Math.min(Math.max(LAND.x1 - halfW, (LAND.x0 + LAND.x1) / 2), x / held.length),
      );
      const ly = Math.max(LAND.y0 + 4, Math.min(LAND.y1 - 4, y / held.length));
      /**
       * The people, under the name, in brackets.
       *
       * Straight from the reference map the owner supplied, which letters every
       * region as `THE JADE EMPIRE (Lizardfolk Dominion)` — the proper name and
       * then what they actually are. It earns its place here because this realm
       * has twenty-four crowns across nine peoples, and "The Compact of
       * Sedgegate" tells a player nothing while "(Ratmen)" tells them how the
       * war will go. Until now that was only in the left column, which means it
       * was only available for the crown you had already gone looking for.
       *
       * Held to four provinces rather than the name's three: a subtitle is a
       * second line of type lying across the same ground, and on a realm that
       * small it is one line too many. Drawn at the same whisper the name is,
       * because this is the layer the eye is meant to skip until it wants it.
       */
      const people = this.peopleOf(nationDef(c.nations[n].def));
      out.push(
        `<text class="wm-realmname" x="${lx.toFixed(1)}" y="${ly.toFixed(1)}"
           fill="${toHex(mix(nationAccent(n), C.parchment, 0.55))}">${esc(nationName(c, n).toUpperCase())}</text>` +
          (held.length >= 4
            ? `<text class="wm-realmpeople" x="${lx.toFixed(1)}" y="${(ly + 5.2).toFixed(1)}"
                 fill="${toHex(mix(nationAccent(n), C.parchment, 0.62))}">(${esc(people)})</text>`
            : ''),
      );
    }
    return `<g class="wm-lettering">${out.join('')}</g>`;
  }

  /**
   * The shoreline, drawn over the ground rather than under it.
   *
   * Cached as text on the same argument the coastline itself is: it is geometry
   * over content, and nothing in the campaign can move a coast.
   */
  private shorePen(): string {
    if (!this.shoreSvg) {
      this.shoreSvg =
        `<g class="wm-shorepen"><path class="wm-coast-case" d="${coastPath()}" />` +
        `<path class="wm-coast-top" d="${coastPath()}" />` +
        coastRuns()
          .filter((r) => r.cliff)
          .map(
            (r) =>
              `<path class="wm-cliff" d="${r.d}" /><path class="wm-cliff-tick" d="${r.ticks}" />`,
          )
          .join('') +
        `</g>`;
    }
    return this.shoreSvg;
  }

  private shoreSvg = '';

  /**
   * Borders. A line between two provinces of the same crown is a shire line and
   * is drawn as one — faint, dotted. A line between two crowns is a frontier and
   * gets the ink. That distinction is most of what makes a political map read.
   */
  private borderLayer(c: CampaignState): string {
    /**
     * In the trade view the line that matters is not the crown's, it is the
     * market's: two provinces of one realm that pay into different nodes are on
     * opposite sides of the only boundary this view is about. Drawing the
     * political frontier here instead would put a heavy line where the view says
     * nothing changes and no line where everything does.
     */
    const trade = this.mapView === 'trade' && (c.trade?.nodes.length ?? 0) > 0;
    const side = (p: number): number =>
      trade ? tradeNodeOf(c, p) : (c.provinces[p]?.owner ?? -1);
    /**
     * A frontier is drawn with **two pens, in two passes**, and the passes have
     * to be separate.
     *
     * The geometry is traced off a raster and smoothed, which is what makes a
     * border on this chart read softer than a drawn one — and the geometry is
     * not this file's to change. What is this file's is how many strokes a line
     * gets. A pale wide casing under a narrow near-black line is the oldest
     * trick in engraving and it does exactly what is needed here: the casing
     * lifts the border off whatever ground it happens to cross, and the dark
     * line on top of it is thin enough to look *drawn* rather than puffy.
     *
     * Two passes because a casing is wider than the line it carries. Emitted
     * interleaved, one frontier's casing would paint over the neighbouring
     * frontier's line at every junction, and the map is nothing but junctions.
     */
    const cases: string[] = [];
    const lines: string[] = [];
    const shires: string[] = [];
    for (const s of borderPaths()) {
      if (side(s.a) === side(s.b)) {
        shires.push(`<path class="wm-shire" d="${s.d}" />`);
        continue;
      }
      cases.push(`<path class="wm-frontier-case" d="${s.d}" />`);
      lines.push(`<path class="wm-frontier" d="${s.d}" />`);
    }
    /**
     * The selection ring is **always emitted** and hidden with a class, never
     * emitted conditionally. `paintSelection` moves it by rewriting one `href`
     * and toggling `.off`; a ring that only exists while something is selected
     * would give that path nothing to move, and selecting from nothing would be
     * back to rebuilding six megabytes of chart. Same rule as `.wm-selmark`.
     */
    const p = this.selectedProvince;
    const sel = `<use class="wm-selring${p >= 0 ? '' : ' off'}" href="#wmp${Math.max(p, 0)}" fill="none" />`;
    return `<g class="wm-borders">${shires.join('')}${cases.join('')}${lines.join('')}${sel}</g>`;
  }

  /**
   * The roads, and the things that are not roads. An old road is a solid tan
   * line with a dark casing; open country is a dotted track; a ford is drawn in
   * water with two bank ticks; a pass is a dashed line with a chevron where it
   * crosses the ridge. A player should be able to tell at a glance that walking
   * into Deepgate is not the same as walking into Lastfield.
   */
  private roadLayer(c: CampaignState): string {
    const out: string[] = [];
    for (const l of c.links) {
      const d = roadPath(l.a, l.b);
      if (l.kind === LinkKind.Road) {
        out.push(`<path class="wm-road-case" d="${d}" /><path class="wm-road" d="${d}" />`);
      } else if (l.kind === LinkKind.River) {
        out.push(`<path class="wm-ford" d="${d}" />${crossingMark(l.a, l.b, 'ford')}`);
      } else if (l.kind === LinkKind.Pass) {
        out.push(`<path class="wm-pass" d="${d}" />${crossingMark(l.a, l.b, 'pass')}`);
      } else {
        out.push(`<path class="wm-track" d="${d}" />`);
      }
    }
    return `<g class="wm-roads">${out.join('')}</g>`;
  }

  /**
   * The towns. The mark is the fortification: roofs for an open holding, a
   * tower for walls, a curtain wall for a proper keep, twin towers and a gate
   * for a fastness. The banner on the pole is the only saturated thing on the
   * land, which is exactly the rule the palette asks for.
   *
   * Slots around a town, so nothing collides as the campaign layer grows:
   * flag to the upper left, name below (or above, where the cell is short),
   * host pips low left and low right, and the upper right left deliberately
   * empty for a warning marker.
   *
   * **A town is a counter, not ground, and getting that wrong is what stopped
   * this chart zooming in.** Every shape in here was drawn in map units — a
   * keep seven units wide, a banner five units tall, the name five units below
   * the site — while the *casing* on the keep and the *type* of the name were
   * already multiplied by `--wm-k`. So one glyph obeyed both rules at once: at
   * the old maximum the walled seats were sixty-pixel black blobs with an
   * invisible hairline round them, sitting on provinces eighteen units wide.
   * Two notches deeper a fastness would have been wider than its own county,
   * and on the new board — provinces about seven tenths the width — it would
   * have been wider than its county at the *old* maximum.
   *
   * So the whole cluster is one group at the site, and `applyView` folds the
   * zoom's reciprocal into its transform exactly as it does for a host counter.
   * Everything inside is then in **glass units**: the keep, the banner, the star,
   * the selection ring, the anchor *and the offsets between them* — including
   * the name's, which is what keeps a name under its own town at every zoom
   * rather than drifting five units further off the further in you go.
   *
   * Two consequences worth knowing before you add something here:
   *
   * - **Do not multiply anything inside this group by `--wm-k`.** The group has
   *   already done it, and a second helping shrinks the mark to nothing. That is
   *   why `.wm-mark` and `.wm-label` have plain widths and sizes below where
   *   every other rule on this screen has a `calc()`.
   * - **Say nothing inside this template.** An SVG comment written in here is
   *   emitted six hundred and fifty times: the six lines that used to explain
   *   `.wm-selmark` cost 277 kB of markup, which `npm run chartperf` read as the
   *   town layer growing from 1,261 kB to 1,538 kB. Notes about the town go
   *   here, above the loop, where they are written once.
   * - `.wm-selmark` is drawn for **every** town and hidden by CSS until its
   *   group wears `sel`. It used to be emitted only for the selected one, which
   *   made a selection change a markup change — and a markup change to this
   *   layer is the most expensive thing on the screen. `paintSelection` toggles
   *   the class instead; `data-town` is how it finds the group.
   * - `portSlot` and `provinceLabel` are asked for absolute points and turned
   *   into offsets here. **They are still derived at draw time** — nothing about
   *   a province's coordinates is cached — which matters while the board is
   *   being reshaped underneath this file.
   */
  private townLayer(c: CampaignState): string {
    const out: string[] = [];
    const O: Pt = { x: 0, y: 0 };
    for (let i = 0; i < c.provinces.length; i++) {
      const p = c.provinces[i];
      const def = provinceDef(i);
      const s = provinceSite(i);
      const sel = i === this.selectedProvince;
      const accent = p.owner >= 0 ? toHex(nationAccent(p.owner)) : toHex(NEUTRAL);
      // A capital only gets its ring while its own crown still holds it.
      const capital = c.nations.some((nt, k) => nt.alive && nationCapital(nt.def) === i && p.owner === k);
      const label = provinceLabel(i);
      const port = (p.port ?? 0) > 0 ? portSlot(i) : null;

      /**
       * Home. One province on the board is *yours* in a way no tint can say,
       * and the owner's first session said the map never says it: a laurel of
       * two gold rings under the whole cluster, on the player's capital alone.
       * Glass-scaled with the rest of the group, so it is the same quiet badge
       * at every zoom — and drawn first, so everything else sits on top of it.
       */
      const home = i === c.capital && p.owner === c.playerNation;

      out.push(`<g class="wm-town${sel ? ' sel' : ''}" data-town="${i}"
        data-cx="${s.x.toFixed(2)}" data-cy="${s.y.toFixed(2)}"
        transform="translate(${s.x.toFixed(2)} ${s.y.toFixed(2)})">
        ${home ? `<circle class="wm-homeglow" r="11.6" /><circle class="wm-homering" r="8.6" />
             <circle class="wm-home" r="6.4" /><circle class="wm-home in" r="5.4" />` : ''}
        <circle class="wm-selmark" r="5.2" />
        ${p.siegeBy >= 0 ? `<circle class="wm-siege" r="5.4" />` : ''}
        <g transform="translate(0 ${MARK_DROP})">${litMark(townMark(O, p.fort, p.tier, c.age))}${
          p.owner >= 0 ? banner(O, accent) : ''
        }</g>
        ${port ? harbourMark({ x: port.x - s.x, y: port.y - s.y }, p.port) : ''}
        <!--
          The name sits above the mark, always.

          The label used to take whichever side the layout solver preferred, so
          half the board had its name *under* its mark and the two ran into each
          other — which is what "otherwise it gets lost" describes. The solver
          still chooses the horizontal slot, which is the half of it that
          resolves collisions; only the side is forced.

          A capital wears its star here rather than on the mark. On the mark it
          was one more shape in a cluster of shapes; beside the name it is the
          thing it is meant to be, which is a label on the place you live.
        -->
        <text class="wm-label r${labelRank(i)}${capital ? ' cap' : ''}"
          transform="translate(${(label.x - s.x).toFixed(2)} ${(-Math.abs(label.y - s.y) - LABEL_LIFT).toFixed(2)})"
          >${capital ? '★ ' : ''}${esc(def.name)}</text>
      </g>`);
    }
    return `<g class="wm-towns">${out.join('')}</g>`;
  }

  /** Hosts on the march: an arrow drawn along the road they are actually taking. */
  /**
   * Hosts on the roads, brigands on the ground, and what is coming for you.
   *
   * All three were already in the simulation and none of them were on the map:
   * marches became lanes, warnings became state, camps became provinces nobody
   * could see. A warning the player cannot see is not a warning, and the
   * fairness rule the whole strategic layer rests on says it has to be one.
   */
  private marchLayer(c: CampaignState): string {
    return [
      this.laneLayer(c),
      this.hostLayer(c),
      this.fleetLayer(c),
      this.campLayer(c),
      this.mapView === 'trade' ? this.marketLayer(c) : '',
      this.warningLayer(c),
    ].join('');
  }

  /**
   * The sea lanes: the other half of the road network, and the only way to three
   * quarters of this board.
   *
   * Drawn under the politics and above the water, faint — two hundred and
   * fifty-two of them, and a lane graph drawn at the weight of a road would be a
   * net thrown over the map. What they are for is answering "could a ship get
   * from here to there", so the ones running out of the selected province are
   * drawn again on top at full strength, which turns the whole layer into a
   * reachability query you make by clicking a coast.
   *
   * The base is cached as text on the same argument the coastline is: it is
   * geometry over content. `c.lanes` is grown once from the realm and is state
   * only because a blockade might one day close one, so the cache is keyed on
   * the count rather than assumed constant.
   */
/**
   * Merchant and fishing craft, working the lanes.
   *
   * > *"also add ships and merchants for flavour movnig"*
   *
   * The sea on this chart is a place things are *drawn* — coasts, ice, the sea
   * floor, lanes between ports — and nothing has ever been on it. A realm whose
   * whole eastern half is water reads as a realm with a wall down the middle.
   *
   * **It moves with the council, not with the frame**, and that is the whole
   * design rather than a shortcut. The chart repaints when `signatureOf`
   * changes, which is already about a hundred milliseconds of work (see
   * `npm run chartperf`); traffic that drifted continuously would force that
   * repaint every frame and make the map slower to look prettier. Deriving the
   * position from `c.turn` means the ships advance when the realm does, on a
   * board that was going to be rebuilt for that anyway — free, and in step with
   * the clock the player is actually watching.
   *
   * Deterministic from the lane and the turn, never from a random stream: two
   * clients in a room draw the same sea, and nothing here consumes a draw that
   * the simulation would notice.
   */
  private trafficLayer(c: CampaignState): string {
    const lanes = c.lanes ?? [];
    if (lanes.length === 0) return '';
    const out: string[] = [];
    for (let i = 0; i < lanes.length; i++) {
      const l = lanes[i];
      /**
       * A lane carries traffic only if somebody is trading along it.
       *
       * A port at both ends, and at least one of them held: an empty coast has
       * nothing to send anywhere, and drawing hulls on a lane between two
       * unowned wilds would be scenery pretending to be information.
       */
      const a = c.provinces[l.a];
      const b = c.provinces[l.b];
      if (!a || !b) continue;
      /**
       * Somebody lives at one end of it.
       *
       * The first cut asked for a **port**, which is the tidier rule and made
       * the sea empty at the only moment anybody looks at it fresh: no crown
       * has built a harbour on turn one, so a new campaign opened onto a chart
       * with nothing on the water at all. Coastal holdings send boats out
       * whether or not they have built anything to tie them up to; a port makes
       * a lane *busier*, below, which is the part it should be saying.
       */
      if (a.owner < 0 && b.owner < 0) continue;

      /**
       * **Most lanes carry nothing, and that is the point.**
       *
       * Drawing one craft per eligible lane put **520 hulls** on the board —
       * counted, not guessed — which is not flavour, it is a second fleet, and
       * it is a thousand more paths on a screen whose repaint `npm run chartperf`
       * already measures at about a hundred milliseconds.
       *
       * A port always has something coming or going, because that is what a
       * port is. Everywhere else is a sparse scatter: one lane in five, chosen
       * by a hash of the lane so it is the *same* lanes each turn — traffic that
       * teleported between lanes as the council sat would read as flicker
       * rather than as shipping.
       */
      const ports = (a.port ?? 0) + (b.port ?? 0);
      const busy = ports > 1 ? 2 : ports > 0 ? 1 : i % 5 === 0 ? 1 : 0;
      if (busy === 0) continue;
      for (let k = 0; k < busy; k++) {
        const seed = (i * 2654435761 + k * 40503) >>> 0;
        // A whole turn's travel per council, offset so they are not in convoy.
        const phase = ((seed % 1000) / 1000 + c.turn * 0.16 + k * 0.37) % 1;
        // Out and back rather than round and round: a merchant returns home.
        const t = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        const pt = laneAt(l.a, l.b, t);
        if (!pt) continue;
        const back = laneAt(l.a, l.b, Math.max(0, Math.min(1, t + (phase < 0.5 ? -0.02 : 0.02))));
        const ang = back ? (Math.atan2(pt.y - back.y, pt.x - back.x) * 180) / Math.PI : 0;
        out.push(
          `<g class="wm-traffic" transform="translate(${n(pt.x)} ${n(pt.y)}) rotate(${ang.toFixed(
            1,
          )})"><path class="wm-hull" d="M-1.6 0.35 L1.6 0.35 L1.05 1.05 L-1.05 1.05 Z" />` +
            `<path class="wm-sail" d="M0 -1.5 L0.95 0.3 L-0.95 0.3 Z" /></g>`,
        );
      }
    }
    return `<g class="wm-trafficlayer">${out.join('')}</g>`;
  }

  private seaLaneLayer(c: CampaignState): string {
    const lanes = c.lanes ?? [];
    if (this.laneCount !== lanes.length) {
      this.laneCount = lanes.length;
      this.laneSvg = lanes
        .map((l) => `<path class="wm-lane" d="${seaLanePath(l.a, l.b)}" />`)
        .join('');
    }
    return `<g class="wm-lanes">${this.laneSvg}<g class="wm-lanelit">${this.litLanes(c)}</g></g>`;
  }

  /**
   * The lanes running out of the selected coast, in their own group so
   * `paintSelection` can rewrite them without touching the 210 kB of lane graph
   * underneath. Two hundred and fifty-two lanes are geometry; these six are the
   * answer to a click.
   */
  private litLanes(c: CampaignState): string {
    const p = this.selectedProvince;
    if (p < 0) return '';
    return (c.lanes ?? [])
      .filter((l) => l.a === p || l.b === p)
      .map((l) => `<path class="wm-lane lit" d="${seaLanePath(l.a, l.b)}" />`)
      .join('');
  }

  /**
   * Every host standing still, as its crown's banner.
   *
   * Hosts on a road are `laneLayer`'s and hosts at sea are `fleetLayer`'s; this
   * is the third case and it was the one the map barely drew — two unlabelled
   * shield pips beside the town, which said "somebody is here" and could not be
   * clicked. Grouped by crown rather than one mark per host, because four hosts
   * of one crown in one province is one fact, and four marks on top of each
   * other is four illegible ones.
   *
   * The mark carries `data-prov`, so clicking a banner does exactly what clicking
   * the ground under it does: selects the province and, if one of the hosts is
   * yours, that host. That is one handler rather than two and it cannot drift
   * from the ground click.
   */
  private hostLayer(c: CampaignState): string {
    // Province → owner → the hosts standing there. Insertion order is army
    // order, which is stable, so the fan does not shuffle between frames.
    const byProv = new Map<number, Map<number, Army[]>>();
    for (const a of c.armies) {
      if (a.moveTo >= 0 || a.aboard >= 0) continue;
      let crowns = byProv.get(a.at);
      if (!crowns) byProv.set(a.at, (crowns = new Map()));
      const list = crowns.get(a.owner);
      if (list) list.push(a);
      else crowns.set(a.owner, [a]);
    }

    const out: string[] = [];
    for (const [prov, crowns] of byProv) {
      const s = provinceSite(prov);
      const groups = [...crowns.entries()];
      groups.sort((x, y) => (x[0] === c.playerNation ? -1 : y[0] === c.playerNation ? 1 : x[0] - y[0]));
      groups.forEach(([owner, hosts], k) => {
        const mine = owner === c.playerNation;
        const hostile = !mine && atWar(c, c.playerNation, owner);
        const points = hosts.reduce((t, a) => t + armyPoints(a), 0);
        const held = mine ? this.orderedHosts(c).map((a) => a.id) : [];
        const sel = hosts.some((a) => held.includes(a.id));
        const at = {
          x: s.x + (k - (groups.length - 1) / 2) * 5.0,
          y: s.y + 4.4,
        };
        out.push(
          this.counter(
            at,
            `wm-hostflag${mine ? ' mine' : hostile ? ' hostile' : ''}${sel ? ' sel' : ''}`,
            `data-prov="${prov}"`,
            6.6 * HOST_MARK,
            hostFlag(
              toHex(nationAccent(owner)),
              hosts.length,
              hosts.reduce((t, a) => t + armyModels(a), 0),
              armOf(hosts[0]),
            ) +
              // Your own stacks only. A rival's is a number, not a thing to
              // pick from — and a pip carrying a host id you may not order is a
              // control that answers a click by doing nothing.
              (mine ? hostPips(hosts, held) : ''),
            WorldMap.tip([
              `${nationName(c, owner)}${mine ? ' — yours' : hostile ? ' — at war with you' : ''}`,
              `${hosts.length} host${hosts.length === 1 ? '' : 's'} standing at ${provinceName(prov)}, ${Math.round(points)} pts`,
              ...hosts
                .slice(0, 3)
                .map((a) => `${a.name} — ${Math.round(armyPoints(a))} pts, ${armyModels(a)} models`),
              hosts.length > 3 ? `…and ${hosts.length - 3} more` : undefined,
              mine
                ? hosts.length > 1
                  ? 'Click for orders, again for the next host, or click a pip to pick one. Shift-click takes them all.'
                  : 'Click for orders. Drag from the banner to box-select several.'
                : 'Click to look at them.',
            ]),
          ),
        );
      });
    }
    return out.join('');
  }

  /**
   * The navy.
   *
   * Ports, fleets and lanes have been fully simulated since the sea landed —
   * armies embark, sail, land on any coast and can be run down at sea — and none
   * of it has ever been on the chart. A player could be invaded across the water
   * with no way to see a hull, which is the one thing the strategic layer's
   * fairness rule says may not happen.
   *
   * A voyage is drawn the way a march is: the course as a dashed line, the fleet
   * as a counter on it at the point it has actually reached, and the number in
   * the counter is **months of sea left**. That last is not decoration. A fleet
   * under way cannot be given an order, cannot fight, and cannot put a man
   * ashore — it is doing nothing at all until it arrives — so the months are the
   * only fact about it that matters and they go on the mark itself.
   */
  private fleetLayer(c: CampaignState): string {
    const fleets = c.fleets ?? [];
    if (fleets.length === 0) return '';
    // Several hulls may lie off one harbour. Fan them, in fleet-id order, so a
    // squadron does not draw as one ship.
    const berthed = new Map<number, number>();
    const out: string[] = [];
    for (const f of fleets) {
      const mine = f.owner === c.playerNation;
      const hostile = !mine && atWar(c, c.playerNation, f.owner);
      const col = toHex(nationAccent(f.owner));
      const sel = this.focus === 'fleet' && this.focusFleet === f.id;
      const carrying = f.cargo.length > 0;
      const cls =
        `wm-fleet${mine ? ' mine' : hostile ? ' hostile' : ''}` +
        `${carrying ? ' laden' : ''}${sel ? ' sel' : ''}`;
      const sailing = isSailing(f);
      let at: Pt;
      let numeral: string;
      if (sailing) {
        at = alongSeaLane(f.moveFrom, f.moveTo, voyageProgress(f));
        // Months of sea left, and only here. A fleet in harbour has no countdown
        // and a numeral under every hull in every port is a chart of numbers
        // nobody asked for — the hull count is on the mark's card, where a
        // figure you do not have to act on this month belongs.
        numeral = f.moveT > 0 ? String(f.moveT) : '!';
        out.push(`<path class="wm-course${sel ? ' sel' : ''}" d="${seaLanePath(f.moveFrom, f.moveTo)}"
          stroke="${col}" />`);
      } else {
        const k = berthed.get(f.at) ?? 0;
        berthed.set(f.at, k + 1);
        const slot = portSlot(f.at);
        at = { x: slot.x + k * 4.4, y: slot.y - 3.4 };
        numeral = '';
      }
      out.push(
        this.counter(
          at,
          cls,
          `data-fleet="${f.id}"`,
          3.4,
          `${fleetGlyph(col, carrying)}
           <text class="wm-countertext fleet" y="6.6">${numeral}</text>`,
          this.fleetTip(c, f),
        ),
      );
    }
    return out.join('');
  }

  /**
   * What a fleet is, what it is carrying, where it is bound and how much sea is
   * left — the four things the owner asked to be able to read off the map.
   */
  private fleetTip(c: CampaignState, f: Fleet): string {
    const mine = f.owner === c.playerNation;
    const sailing = isSailing(f);
    const hosts = cargoArmies(c, f);
    return WorldMap.tip([
      f.name,
      `${nationName(c, f.owner)}${mine ? ' — yours' : atWar(c, c.playerNation, f.owner) ? ' — at war with you' : ''}`,
      fleetSummary(c, f),
      ...hosts.slice(0, 3).map((a) => `Aboard: ${a.name}, ${Math.round(rawPoints(a))} pts`),
      hosts.length > 3 ? `…and ${hosts.length - 3} more aboard` : undefined,
      sailing
        ? `${provinceName(f.moveFrom)} → ${provinceName(f.moveTo)}`
        : `Lying off ${provinceName(f.at)}`,
      sailing ? `Makes landfall ${this.arrivalLine(f.moveT)}` : undefined,
      sailing ? `${Math.round(voyageProgress(f) * 100)}% of the way across` : undefined,
      // The whole point of drawing a voyage rather than a destination.
      sailing
        ? 'A fleet under way does nothing: it cannot fight on land, be given a new course, or put a man ashore until it arrives.'
        : hosts.length > 0
          ? 'The hosts aboard are doing nothing until they are put ashore.'
          : undefined,
      f.intercepting >= 0 ? 'Out hunting another fleet.' : undefined,
      'Click for orders.',
    ]);
  }

  /**
   * The markets, on the ground they trade through.
   *
   * A node has no coordinates of its own — it sits on a seat province, and that
   * is where the fair is — so the mark goes on the seat and the tint on the
   * territory says which provinces pay into it. The numeral is your own control
   * of the market as a percentage, because that is the only number in the whole
   * trade layer that decides whether any of its gold reaches you.
   */
  private marketLayer(c: CampaignState): string {
    const nodes = c.trade?.nodes ?? [];
    if (nodes.length === 0) return '';
    const me = c.playerNation;
    return nodes
      .map((node) => {
        const st = c.trade.states[node.id];
        const lead = nodeLeader(c, node.id);
        const share = controlIn(c, node.id, me);
        const sel = this.focus === 'market' && this.focusNode === node.id;
        const col = toHex(lead.nation >= 0 ? nationAccent(lead.nation) : NEUTRAL);
        const at = provinceSite(node.seat);
        /**
         * *Terminal* is `isTerminal`, not `depth === 0`, and the difference is
         * this board.
         *
         * Depth is hops to salt water, so on a realm built round one great inland
         * sea every coastal node is depth 0 — and value still runs between them,
         * because over water it runs from the lesser market to the greater rather
         * than downhill. Calling all of those terminals would put the ring on a
         * third of the map and label a busy harbour as the place the money stops.
         */
        const terminal = isTerminal(c, node.id);
        return this.counter(
          { x: at.x, y: at.y - 6.6 },
          `wm-market${lead.nation === me ? ' mine' : ''}${sel ? ' sel' : ''}${
            terminal ? ' terminal' : ''
          }`,
          `data-market="${node.id}"`,
          3.6,
          // Scales: a beam and two pans. A terminal market gets a ring, because
          // nothing flows out of one and it is therefore where the money stops.
          `${terminal ? `<circle class="wm-termring" r="3.5" />` : ''}
           <path class="wm-scalesbeam" d="M-2.6 -1.4 H2.6 M0 -1.4 V1.0" />
           <path class="wm-scalespan" fill="${col}" d="M-2.6 -1.4 L-1.4 0.6 L-3.8 0.6 Z
             M2.6 -1.4 L3.8 0.6 L1.4 0.6 Z" />
           <text class="wm-countertext market" y="4.6">${Math.round(share * 100)}%</text>`,
          WorldMap.tip([
            `${nodeName(c, node.id)} — ${terminal ? 'a terminal market: nothing flows out of it, so everything that reaches it is taken here' : node.depth === 0 ? 'on salt water' : `${node.depth} step${node.depth === 1 ? '' : 's'} from salt water`}`,
            `${node.provinces.length} province${node.provinces.length === 1 ? '' : 's'} trade through it`,
            `${(st?.value ?? 0).toFixed(1)}g passes through a month; ${Math.round((st?.retained ?? 0) * 100)}% of it is taken here`,
            lead.nation >= 0
              ? `${nationName(c, lead.nation)} holds ${Math.round(lead.share * 100)}% of it`
              : 'Nobody has a hold on it worth the name',
            `You hold ${Math.round(share * 100)}% and took ${nodeIncome(c, node.id, me).toFixed(1)}g here last month`,
            'Click for the market, its roads, and where to send a factor.',
          ]),
        );
      })
      .join('');
  }

  /**
   * Where the money actually went, as arrows between markets.
   *
   * The width is `outflow` — the gold that went down that road last month, which
   * the simulation records because the flow pass has to. So this is measurement,
   * not illustration: a fat arrow is a fat trade route and a hair-thin one is a
   * road nobody uses, and neither is a guess about what the graph *ought* to be
   * doing. Drawn under the towns so a market's own mark stays on top of the roads
   * feeding it.
   */
  private flowLayer(c: CampaignState): string {
    const nodes = c.trade?.nodes ?? [];
    if (nodes.length === 0) return '';
    let widest = 0;
    for (const st of c.trade.states) {
      for (const v of st.outflow) widest = Math.max(widest, v);
    }
    if (widest <= 0) return '';
    const out: string[] = [];
    for (const node of nodes) {
      const st = c.trade.states[node.id];
      const from = provinceSite(node.seat);
      /**
       * The market whose roads are being read is drawn at full strength and the
       * rest are kept to a whisper.
       *
       * The first cut drew all thirty-eight arrows at full weight and the chart
       * disappeared under them: seats are far apart, so a straight line between
       * two of them crosses six provinces, and thirty-eight of those is a cat's
       * cradle laid over the ground the arrows are supposed to describe. Width
       * still carries the gold — that part is measurement — but *which* arrows
       * are shouting is a selection, the same rule the sea lanes follow.
       */
      const lit = this.focus === 'market' && this.focusNode === node.id;
      node.outgoing.forEach((to, i) => {
        const gold = st?.outflow[i] ?? 0;
        if (gold <= 0.01) return;
        const seat = tradeNode(c, to)?.seat ?? -1;
        if (seat < 0) return;
        const q = provinceSite(seat);
        const w = (lit ? 0.45 : 0.22) + (lit ? 1.5 : 0.75) * Math.sqrt(gold / widest);
        // Both ends are pulled in: an arrow that starts and stops on the seat
        // itself is drawn under the market mark at both ends.
        const a = { x: from.x + (q.x - from.x) * 0.12, y: from.y + (q.y - from.y) * 0.12 };
        const b = { x: from.x + (q.x - from.x) * 0.86, y: from.y + (q.y - from.y) * 0.86 };
        const ang = (Math.atan2(q.y - from.y, q.x - from.x) * 180) / Math.PI;
        out.push(`<path class="wm-flow${lit ? ' lit' : ''}" d="M${n(a.x)} ${n(a.y)}L${n(b.x)} ${n(b.y)}"
            stroke-width="${n(w)}" />
          <path class="wm-flowhead${lit ? ' lit' : ''}" d="M0 0 L-2.2 -1.2 L-2.2 1.2 Z"
            transform="translate(${n(b.x)} ${n(b.y)}) rotate(${ang.toFixed(1)}) scale(${n(0.5 + w * 0.35)})" />`);
      });
    }
    return `<g class="wm-flows">${out.join('')}</g>`;
  }

  /**
   * A host between two provinces, drawn where it actually is.
   *
   * Read from `marchesInProgress` rather than from `army.moveTo`, which fixes a
   * real fault as a side effect: a lane is always along a link, so a route can
   * no longer be drawn as a road-shaped curve between two provinces that share
   * no road.
   */
  private laneLayer(c: CampaignState): string {
    return marchesInProgress(c)
      .map((m) => {
        const col = toHex(nationAccent(m.owner));
        const pts = roadPoints(m.from, m.to);
        const fwd = m.from < m.to ? pts : [...pts].reverse();
        const d = fwd.map((q, k) => `${k === 0 ? 'M' : 'L'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`).join('');
        const at = alongRoad(m.from, m.to, m.progress);
        // Sized on strength, floored so the smallest host is still findable.
        // Scaled by `HOST_MARK` for the reason written at `hostFlag`: a counter
        // is constant on the glass, so the only way for it to be bigger is to be
        // a bigger constant. A host on the road is the same object as a host
        // standing still and must not be a different size.
        const r = (2.1 + Math.min(1.9, m.points / 900)) * HOST_MARK;
        const turns = m.turnsLeft > 0 ? String(m.turnsLeft) : '!';
        const mine = m.owner === c.playerNation;
        const sel = this.focus === 'march' && this.focusArmy === m.armyId;
        const hostile = !mine && atWar(c, c.playerNation, m.owner);
        return `<path class="wm-march${sel ? ' sel' : ''}" d="${d}" stroke="${col}" />
          ${this.counter(
            at,
            `wm-marcher${mine ? ' mine' : hostile ? ' hostile' : ''}${sel ? ' sel' : ''}`,
            `data-march="${m.armyId}"`,
            r * 1.5,
            `<path d="M${-r} ${-r} L${r} ${-r} L${r} ${(r * 0.35).toFixed(2)} L0 ${(r * 1.35).toFixed(2)} L${-r} ${(r * 0.35).toFixed(2)} Z"
                  fill="${col}" stroke="#12100e" stroke-width="0.4" />
            <text class="wm-countertext" y="${(r * 0.4).toFixed(2)}">${turns}</text>`,
            this.marchTip(c, m),
          )}`;
      })
      .join('');
  }

  /**
   * One interactive mark on the chart.
   *
   * Two rules are baked in here rather than left to each caller, because both
   * were got wrong the first time and neither is visible in a still picture.
   *
   * **A counter is a constant size on the glass.** It carries its position in
   * `data-cx`/`data-cy` and its transform is rewritten by `applyView` with the
   * zoom's reciprocal folded in, so a host counter is the same object at every
   * zoom instead of a smear at one and a billboard at the other. Doing it in
   * script rather than in CSS is deliberate: `transform-box` on an SVG group
   * whose parent already translates is a coin flip across browsers, and this is
   * a scale that has to be exact.
   *
   * **The hit target is a circle, and it is bigger than the mark.** Every layer
   * above the territory fill is `pointer-events: none` so that clicking a
   * mountain still selects the ground under it; a counter re-enables pointers for
   * *itself only*, over a disc rather than over a five-sided path, so a camp
   * triangle is not something you have to hunt for with the cursor.
   */
  private counter(
    at: Pt,
    cls: string,
    data: string,
    hit: number,
    body: string,
    tip: string,
  ): string {
    return `<g class="wm-counter ${cls}" ${data}${tip}
        data-cx="${at.x.toFixed(2)}" data-cy="${at.y.toFixed(2)}"
        transform="translate(${at.x.toFixed(2)} ${at.y.toFixed(2)})">
        <circle class="wm-hit" r="${hit.toFixed(2)}" />
        ${body}
      </g>`;
  }

  private marchTip(c: CampaignState, m: MarchLane): string {
    const a = findArmy(c, m.armyId);
    return WorldMap.tip([
      a ? a.name : 'A host',
      `${nationName(c, m.owner)}${m.owner === c.playerNation ? ' — yours' : ''}`,
      `${m.points} pts, ${a ? armyModels(a) : 0} models`,
      `${provinceName(m.from)} → ${provinceName(m.to)}`,
      `Arrives ${this.arrivalLine(m.turnsLeft)}`,
      `${Math.round(m.progress * 100)}% of the way`,
      'Click for orders.',
    ]);
  }

  /**
   * The levy, on the ground it is raised from.
   *
   * The item's third part is that a player "cannot see where their manpower
   * comes from", and a list on a screen only half answers that — the other half
   * is *in place*, because where the men come from is a fact about the shape of
   * your realm. A crown whose whole levy is two provinces on a contested border
   * is in a different game from one whose levy is spread over nine safe ones,
   * and no table says that at a glance.
   *
   * So: one mark per province you hold, a sheaf of spears whose height is the
   * share of the realm's levy that province sends, the number beside it, and
   * the mark drawn in oxblood with the reason written on it when something is
   * holding it down — a siege, a rising, brigands on the roads. Off by default,
   * because the chart's primary job is the relations view and this is a
   * question a player asks a few times a session.
   *
   * Nothing here is state. `levyReport` is a pure read of the same
   * `holdingIncome` the month uses, and the toggle is a field on a UI class.
   */
  private levyLayer(c: CampaignState): string {
    if (!this.levyMarks) return '';
    const rep = levyReport(c, c.playerNation);
    if (rep.sources.length === 0) return '';
    const most = Math.max(...rep.sources.map((q) => q.levy), 0.001);
    return rep.sources
      .map((src) => {
        const g = openGround(src.province);
        /**
         * Height by share of the biggest contributor, floored so a province
         * that sends almost nothing is still a mark and not a dot.
         *
         * The numbers are in the counter's local units, where **one unit is
         * about three quarters of a pixel on the glass at every zoom** — that
         * is what the counter rule means, and it is the whole reason the first
         * cut of this mark was invisible in a screenshot: a sheaf eight units
         * tall drawn in 0.42-unit strokes is a six-by-nine-pixel scribble of
         * third-of-a-pixel hairlines. Sized against the host banner, which is
         * the loudest thing on this chart and about sixteen units tall.
         */
        const h = 3.0 + 7.2 * Math.min(1, src.levy / most);
        const word = LEVY_DRAG_WORDS[src.drag] ?? '';
        const blades = Math.max(2, Math.min(5, Math.round(1 + (src.levy / most) * 4)));
        const spears = Array.from({ length: blades }, (_, i) => {
          const x = (i - (blades - 1) / 2) * 2.2;
          const lean = (i - (blades - 1) / 2) * 0.8;
          const tip = 2.6 - h;
          // A shaft and a head. The head is what makes it read as a spear
          // rather than as a tally mark at nine pixels.
          return (
            `<path d="M${x.toFixed(2)} 2.6 L${(x + lean).toFixed(2)} ${tip.toFixed(2)}" />` +
            `<path class="wm-levyhead" d="M${(x + lean).toFixed(2)} ${tip.toFixed(2)} ` +
            `L${(x + lean - 0.9).toFixed(2)} ${(tip + 1.8).toFixed(2)} ` +
            `L${(x + lean + 0.9).toFixed(2)} ${(tip + 1.8).toFixed(2)} Z" />`
          );
        }).join('');
        return this.counter(
          { x: g.x, y: g.y - 1.5 },
          `wm-levy${src.drag === LevyDrag.None ? '' : ' held'}`,
          `data-prov="${src.province}"`,
          4.6,
          `<circle class="wm-levyplate" r="4.6" />${spears}
           <text class="wm-countertext levy" y="7.4">${src.levy.toFixed(0)}</text>`,
          WorldMap.tip([
            `${provinceName(src.province)} — ${src.levy.toFixed(1)} men a month`,
            `${rep.gross > 0 ? Math.round((src.levy / rep.gross) * 100) : 0}% of everything your ground sends`,
            `${src.tierWord}, tier ${src.tier}`,
            word ? `Held down: ${word}. It could send ${src.settled.toFixed(1)}.` : 'In good order.',
            'The Levies screen has the whole account.',
          ]),
        );
      })
      .join('');
  }

  /** Brigands sit on open ground, deliberately clear of the town mark. */
  private campLayer(c: CampaignState): string {
    return (c.camps ?? [])
      .map((camp) => {
        const g = openGround(camp.at);
        const sel = this.focus === 'camp' && this.focusProv === camp.at;
        const mine = c.provinces[camp.at].owner === c.playerNation;
        return this.counter(
          g,
          `wm-camp${camp.bounty > 0 ? ' paid' : ''}${sel ? ' sel' : ''}`,
          `data-camp="${camp.at}"`,
          3.0,
          `<path d="M-2.6 1.8 L0 -2.4 L2.6 1.8 Z" />
           <path class="wm-campin" d="M-1.4 1.8 L0 -0.5 L1.4 1.8 Z" />`,
          WorldMap.tip([
            `Brigands on ${provinceName(camp.at)}`,
            `${Math.round(camp.strength)} strong, and growing`,
            `The province pays ${Math.round(campYieldScale(c, camp.at) * 100)}% of what it should`,
            camp.bounty > 0 ? `A bounty is out — ${months(camp.bounty)} of it left` : undefined,
            mine ? 'Your ground. Click to post a bounty or send a host.' : 'Click for the reckoning.',
          ]),
        );
      })
      .join('');
  }

  /**
   * What is coming, marked on the province it is coming to.
   *
   * `c.warnings` is already only what the player can see — this is the scouting
   * report, not omniscience. The number is turns until arrival and it is exact,
   * because the simulation does not lie to the player about something it has
   * already decided. A bandit warning is dropped where the camp itself is
   * already drawn, so the same fact is not stated twice on one province.
   */
  private warningLayer(c: CampaignState): string {
    const shown = new Set<number>();
    return (c.warnings ?? [])
      .filter((wn) => {
        if (wn.kind === WarnKind.Bandits && hasCamp(c, wn.to)) return false;
        if (shown.has(wn.to)) return false;
        shown.add(wn.to);
        return true;
      })
      .map((wn) => {
        const p = warningSlot(wn.to);
        const urgent = wn.kind === WarnKind.March && wn.turns <= 1;
        const sel = this.focus === 'warn' && this.focusProv === wn.to;
        // One mark per province, and the panel behind it lists everything
        // `warningsFor` knows about that ground — two hosts converging on one
        // holding is one mark and two lines, never two marks on top of each other.
        const all = warningsFor(c, wn.to);
        const more = all.length > 1 ? ` +${all.length - 1}` : '';
        const glyph =
          wn.kind === WarnKind.Massing
            ? `<circle r="1.6" />`
            : `<path d="M0 -2.7 L2.4 1.7 L-2.4 1.7 Z" />`;
        const label =
          wn.kind === WarnKind.Massing
            ? more
              ? `<text class="wm-countertext warn" y="4.4">${more.trim()}</text>`
              : ''
            : `<text class="wm-countertext warn" y="5">${wn.turns}${more}</text>`;
        return this.counter(
          p,
          `wm-warn${urgent ? ' urgent' : ''}${sel ? ' sel' : ''}`,
          `data-warn="${wn.to}"`,
          2.8,
          `${glyph}${label}`,
          WorldMap.tip([
            ...all.slice(0, 3).map((k) => this.warnLine(c, k)),
            all.length > 3 ? `…and ${all.length - 3} more` : undefined,
            'Click for what your riders actually said.',
          ]),
        );
      })
      .join('');
  }

  /** One warning, said the way the riders said it. Used by the tip and the panel. */
  private warnLine(c: CampaignState, w: MarchWarning): string {
    if (w.kind === WarnKind.March) {
      return `${nationName(c, w.nation)}: ${w.strength} pts on ${roadWord(c, w.from, w.to)} to ${provinceName(w.to)}, ${this.arrivalLine(w.turns)}`;
    }
    if (w.kind === WarnKind.Massing) {
      return `${nationName(c, w.nation)}: ${w.strength} pts standing at ${provinceName(w.from)}, one march from ${provinceName(w.to)}`;
    }
    /**
     * A landing. `armyId` is a **fleet** id on this kind, and until the map drew
     * hulls this line fell through to the brigand sentence — so a convoy of nine
     * hundred points bound for your coast was reported to the player as
     * "Brigands on Saltmere". The one warning that cannot be marched away from
     * was the one worded as the one that never moves.
     */
    if (w.kind === WarnKind.Landing) {
      const f = findFleet(c, w.armyId);
      return (
        `${nationName(c, w.nation)}: ${w.strength} pts at sea` +
        `${f ? ` aboard ${f.name}` : ''}, bound for ${provinceName(w.to)}, ${this.arrivalLine(w.turns)}`
      );
    }
    return `Brigands on ${provinceName(w.from)} — ${w.strength} strong`;
  }

  /**
   * The names on the water. These belong to the chart rather than to the frame:
   * a sea is a place, and a place's name pans and zooms with it.
   */
  private seaNames(): string {
    /**
     * The Midsea, named on the water it actually is.
     *
     * `MAPREF.md` is emphatic that the central inland sea is *the* organising
     * feature of the world and that the waters are named as carefully as the
     * land — and this chart had two names on it, both stuck to the outer edge
     * of the sheet at fixed coordinates, so the biggest single feature on the
     * board was the one unlabelled thing on it.
     *
     * The position is derived rather than typed: `SEA_CELLS` marks which water
     * is `interior`, and the mean of those cells is the middle of the enclosed
     * sea by construction. If a sibling reshapes the world, the name moves with
     * it. **The name itself is the only thing invented here**, and it is the
     * codebase's own word for that water — `midseaPush` in `realm.ts` is what
     * carves it. Naming the other seven the reference asks for wants a table in
     * `realm.ts`; see the note in the report.
     */
    const interior = SEA_CELLS.filter((s) => s.interior);
    let mid = '';
    if (interior.length > 12) {
      const mx = interior.reduce((a, s) => a + s.x, 0) / interior.length;
      const my = interior.reduce((a, s) => a + s.y, 0) / interior.length;
      const p = warp({ x: mx, y: my });
      mid = `<text class="wm-seaname great" x="${p.x.toFixed(1)}" y="${p.y.toFixed(1)}"
        >The Midsea</text>`;
    }
    return `<g class="wm-lettering wm-seanames">
      ${mid}
      <text class="wm-seaname" transform="translate(${(VIEW.x + VIEW.w - 16).toFixed(0)} ${(
        VIEW.y +
        VIEW.h * 0.5
      ).toFixed(0)}) rotate(90)">The Drowned Sea</text>
      <text class="wm-seaname small" transform="translate(${(VIEW.x + 15).toFixed(0)} ${(
        VIEW.y +
        VIEW.h * 0.32
      ).toFixed(0)}) rotate(-90)">The Cold Water</text>
    </g>`;
  }

  /**
   * The cartography: a neatline, a title, a compass rose, a legend, a scale bar
   * and a small map of the whole realm with the view drawn on it. None of it is
   * information the panels do not already carry — it is what stops the picture
   * being a diagram.
   *
   * All of it lives in a second, *fixed* SVG above the chart, because a compass
   * that scrolls off the top of the screen when you pan north is not a compass.
   * It draws in its own 174-unit coordinate system — exactly the frame the
   * twenty-four-province map had — so every size in here is the number it always
   * was, and the group is scaled onto the view box at the end.
   */
  private furniture(): string {
    /**
     * The realm entire, an inch across, with the window drawn on it.
     *
     * The one thing a zoomable map owes the player is an answer to "where am I".
     * The coastline is already computed; drawn at a twentieth of the size it is
     * a thumbnail of the realm, and the rectangle is the view.
     */
    /**
     * Hard into the corner.
     *
     * It sat six units in from both edges, which is a cartographer's margin and
     * reads as a panel floating over the board. Flush to the corner it reads as
     * chrome — the same reason a minimap in any of the games this is aimed at is
     * welded to the edge of the screen. 1.6 is the plate's own border, so the
     * *plate* lands on the corner rather than the map inside it.
     */

    /**
     * The key, and the terrain half of it is new.
     *
     * The reference's legend keys everything it draws, and this one keyed only
     * the lines: a player looking at a chained ridge or a reed clump had no
     * way to be *told* what it was, only to guess. The glyphs in the swatches
     * are the real ones, built by the same functions the ground is built by, so
     * the key cannot drift from the map — the same argument the relations key
     * already makes for its fills and its hatches.
     *
     * `wm-terr` and its terrain class are wanted on the row, because the ink for
     * every glyph is chosen by that pair of selectors and nothing else.
     */
    const glyphRow = (kind: ProvinceTerrain, marks: Array<[number, number, number, number]>): string =>
      `<g class="wm-terr t-${kind}" transform="translate(3.6 0.4)">${
        isRelief(kind)
          ? ridgeChain(
              marks.map(([mx, my, r, v]) => ({ x: mx, y: my, r, v })),
              kind === 'hill',
            )
          : marks.map(([mx, my, r, v]) => terrainGlyph(kind, mx, my, r, v)).join('')
      }</g>`;

    const legendRows: Array<[string, string]> = [
      [`<path class="wm-frontier-case" d="M0 0 h7" /><path class="wm-frontier" d="M0 0 h7" />`, 'frontier'],
      [`<path class="wm-shire" d="M0 0 h7" />`, 'shire line'],
      [`<path class="wm-road-case" d="M0 0 h7" /><path class="wm-road" d="M0 0 h7" />`, 'old road'],
      [`<path class="wm-track" d="M0 0 h7" />`, 'open country'],
      [`<path class="wm-ford" d="M0 0 h7" /><path class="wm-cross" d="M3.5 -1.1 v2.2" />`, 'ford'],
      [`<path class="wm-pass" d="M0 0 h7" /><path class="wm-cross" d="M2.2 1 L3.5 -1 L4.8 1" />`, 'pass'],
      [`<g transform="translate(3.5 0.2)">${townMark({ x: 0, y: 0 }, 2)}</g>`, 'walled seat'],
      [`<g transform="translate(3.5 0.2)">${banner({ x: 3.4, y: -1.4 }, 'var(--gold)')}</g>`, 'banner'],
      /**
       * **A key row may only exist for a mark the map actually makes.**
       *
       * Mountains and hills had rows here, drawn by calling `ridgeChain`
       * directly — so they went on being rendered in the key for some time
       * after the chart itself stopped drawing peaks at all. A legend that
       * shows a symbol the board does not use is worse than no legend: it sends
       * the player looking for something that is not there.
       *
       * High ground is now said by shading and by colour, and the Land view's
       * own key lists every country by tint with a count beside it, which is
       * the better answer to "what is that ground" anyway.
       */
      [glyphRow('forest', [[-2.4, 0.7, 0.6, 0], [0.1, 0.8, 0.66, 3], [2.5, 0.7, 0.58, 1]]), 'the Deadwood'],
      [glyphRow('marsh', [[0, 0.2, 0.72, 0]]), 'fen'],
      [glyphRow('ruin', [[0, 0.6, 0.66, 0]]), 'old ruin'],
    ];
    /**
     * Two columns, because a key is furniture and furniture must not be the
     * biggest thing on the chart.
     *
     * Thirteen rows in one column is fifty-six units of a hundred-and-ten-unit
     * sheet — the first cut of this ran from the coast to the bottom of the
     * frame and sat on the whole western seaboard. Folded in half it is half as
     * tall and a third wider, which is the trade a legend should always take:
     * the map has far more width to spare than height.
     *
     * Both the plate and the fold are measured off the row count, so adding a
     * row is one line in the table above and nothing else.
     */
    const COL = 7;
    const legendW = 22.5;
    const legend = legendRows
      .map((r, k) => {
        const col = Math.floor(k / COL);
        const row = k % COL;
        return `<g transform="translate(${(col * legendW).toFixed(1)} ${(row * 4.1).toFixed(1)})">${r[0]}
           <text class="wm-legend-t" x="9.4" y="0.8">${r[1]}</text></g>`;
      })
      .join('');
    const legendH = Math.min(COL, legendRows.length) * 4.1 + 3.6;
    const legendCols = Math.ceil(legendRows.length / COL);

    const keyW = legendCols * legendW + 2;
    /**
     * The key, anchored to the map cell rather than to the sheet.
     *
     * It was the last piece of furniture positioned in the chart's own
     * coordinates, which is why it came up in the middle of the left-hand side
     * instead of in a corner: the sheet is the *realm*, and the realm is not the
     * window. Its rows are still SVG, and still built by the same functions the
     * board's own marks are built by — that is the whole reason a key is worth
     * having — so they move as a group into an SVG of their own with a viewBox
     * cut to the rows' own bounds.
     */
    return `<div class="wm-keybox${this.showKey ? '' : ' away'}">
      <div class="wm-keyhead">The Key</div>
      <svg class="wm-keysvg" viewBox="-3 -3 ${keyW.toFixed(1)} ${(legendH + 2).toFixed(1)}"
        preserveAspectRatio="xMinYMin meet">${legend}</svg>
    </div>`;
  }


  // ── The detail column ──────────────────────────────────────────────────────
  //
  // One column, two tabs, and five things it can be about. The ground is the
  // resting state; a march, a warning and a camp each take it over when one is
  // clicked, and the realm tab is the one thing on this screen that is not about
  // a place at all.

  private detailPanel(c: CampaignState): string {
    const offers = pendingOffers(c);
    const wars = warView(c, c.playerNation);
    const realm = this.focus === 'realm';
    const market = this.focus === 'market';
    const muster = this.focus === 'muster';
    const hasTrade = (c.trade?.nodes.length ?? 0) > 0;
    // An unanswered call to arms costs exactly what refusing costs, so it may
    // never be somewhere the player has to go looking for it.
    const badge =
      offers.length > 0
        ? `<i class="wm-badge urgent">${offers.length}</i>`
        : wars.length > 0
          ? `<i class="wm-badge">${wars.length}</i>`
          : '';
    const body = realm
      ? this.warsPanel(c, wars, offers)
      : market
        ? this.tradePanel(c)
        : muster
          ? this.musterPanel(c)
          : this.focus === 'march'
            ? this.marchPanel(c)
            : this.focus === 'fleet'
              ? this.fleetPanel(c)
              : this.focus === 'camp'
                ? this.campPanel(c)
                : this.focus === 'warn'
                  ? this.warnPanel(c)
                  : this.provincePanel(c);
    /**
     * The markets get a tab of their own rather than a row in the realm column.
     *
     * Trade has been simulated, hashed, saved and played by the AI council since
     * the day it landed, and a player could see exactly one number of it: a
     * "Markets" figure in the left column. The graph, the control, the factors
     * and the three orders that drive them had no screen at all, so the one
     * lever this layer gives a player was a lever they could not reach.
     */
    return `<div class="wm-tabs">
        <span class="wm-tab${realm || market || muster ? '' : ' on'}" data-act="tab" data-tab="ground"
          ${WorldMap.tip(['The ground, and whatever is standing on it.'])}>Ground</span>
        <span class="wm-tab${muster ? ' on' : ''}" data-act="tab" data-tab="muster"
          ${WorldMap.tip([
            'Every host and every hull you pay for, what the lot costs a month,',
            'and how fast the countryside replaces the men.',
          ])}>Muster</span>
        ${
          hasTrade
            ? `<span class="wm-tab${market ? ' on' : ''}" data-act="tab" data-tab="market"
          ${WorldMap.tip([
            'The trade graph: which market each province pays into, who controls it, and where your factors are.',
            'A third of every province’s gold goes to a market and comes back only through control of it.',
          ])}>Markets</span>`
            : ''
        }
        <span class="wm-tab${realm ? ' on' : ''}" data-act="tab" data-tab="realm"
          ${WorldMap.tip([
            'Every war you are in, the terms you could ask for today,',
            'and everything sitting on your table.',
            offers.length > 0 ? `${offers.length} waiting on an answer.` : '',
          ])}>Wars &amp; envoys${badge}</span>
      </div>${body}`;
  }

  /**
   * What the markets paid you, and how much of the board's trade you hold.
   *
   * One row, and it is here because without it the trade layer is invisible from
   * the strategic screen entirely. Every province panel now says a third of its
   * gold went "to market"; if the realm column never says any of it came back,
   * the player has been shown a leak and never shown the other end of the pipe.
   *
   * Last month's takings rather than a forecast, because that is what
   * `tradeIncomeOf` is — the gold the nodes actually paid out at the last
   * council. It is stated as a *count of markets* beside the figure rather than
   * as a percentage, because "you take a share of 4 markets" is a fact about the
   * board a player can go and change, and "you hold 11% of the realm's trade" is
   * a statistic about a spreadsheet.
   *
   * Deliberately not a panel. The node graph, the merchants and the three orders
   * that drive them have no UI at all and this row is not pretending to be one —
   * it is the honest minimum that keeps the income story from having a hole in
   * the middle of it.
   */
  private marketRow(c: CampaignState): string {
    const nodes = c.trade?.nodes ?? [];
    if (nodes.length === 0) return '';
    const me = c.playerNation;
    const paid = tradeIncomeOf(c, me);
    // A market you have any real hold on at all. The threshold keeps a rounding
    // error's worth of control in some far-off node off the count.
    let inHand = 0;
    let led = 0;
    for (let k = 0; k < nodes.length; k++) {
      if (controlIn(c, k, me) < 0.02) continue;
      inHand++;
      if (nodeLeader(c, k).nation === me) led++;
    }
    return `<div class="wm-stat"${WorldMap.tip([
      'What the markets paid you at the last council.',
      'Every province puts a share of its gold into the market it trades through, and each market pays out to whoever controls it — which is not always the crown that grew the goods.',
      inHand === 0
        ? 'You have no hold on any market. Every coin your ground sends to trade is being collected by somebody else.'
        : `You take a share of ${inHand} market${inHand === 1 ? '' : 's'}${led > 0 ? `, and hold the whip hand in ${led}` : ', and lead in none'}.`,
      'A province panel names the market its ground trades through.',
    ])}><span>Markets</span><b${led > 0 ? ' class="own"' : ''}>${Math.round(paid)}g <i class="${
      paid > 0 ? 'up' : 'down'
    }">${inHand} held</i></b></div>`;
  }

  /** The ground, and whatever is standing on it. */
  private provincePanel(c: CampaignState): string {
    const p = this.selectedProvince;
    if (p < 0) return `<div class="wm-hint">Choose a province.</div>`;
    const def = provinceDef(p);
    const state = c.provinces[p];
    const terrain = PROVINCE_TERRAIN[def.terrain];
    const mine = state.owner === c.playerNation;
    const here = armiesIn(c, p);
    const army = this.selectedArmy >= 0 ? findArmy(c, this.selectedArmy) : undefined;
    const camp = campAt(c, p);
    const warns = warningsFor(c, p);
    const seen = visibleProvinces(c, c.playerNation)[p];

    // Hosts in the open field here, and hosts behind the gates — two lists,
    // because they are two situations and the panel that showed them as one was
    // the panel a player would have planned a defence off.
    const hostList = here.filter((a) => !a.sheltered).map((a) => this.hostRow(c, a)).join('');
    const inside = shelteredIn(c, p);
    const standing = standingGarrison(c, p);

    /**
     * What it pays, and what it is paying *today*.
     *
     * `holdingIncome` is the reader `doIncome` itself pays out of, so the figure
     * on screen cannot disagree with the figure in the treasury. That matters
     * more than it sounds: this panel has now been wrong twice for the same
     * reason. First it multiplied `def.wealth` by a hand copy of the sim's scale;
     * then it called `provinceYield`, which was the right reader until the
     * holdings ladder landed and stopped being one — `provinceYield` has no tier
     * term, so every province above a hamlet was quietly under-reported. Read the
     * thing that pays, not a thing that agrees with it.
     *
     * **The split is a subtraction, not a multiplication.** `TRADE_SHARE` does
     * not appear here. The market's cut is `provinceTradeValue`, the sim's own
     * number, and what is left over is what the treasury gets — which is true by
     * construction and stays true if the share is ever retuned or made to vary
     * by node. Writing `gross * (1 - TRADE_SHARE)` would have been a sixth copy.
     *
     * The card price has moved into the tooltip, where a number nobody receives
     * belongs. Rounded, because a levy of 4.7 men is not a thing, and
     * `Math.round` rather than floor so a province delivering 0.6 gold does not
     * read as delivering nothing at all.
     */
    const owned = state.owner >= 0;
    const split = holdingSplit(c, p);
    const toMarket = split.market;
    const scale = owned ? yieldScale(c, p) : 0;
    const short = owned && scale < 0.995 * tierYield(tierOf(c, p));
    const goodsLine = split.goods
      .filter((g) => g.amount >= 0.5)
      .map((g) => ` · ${Math.round(g.amount)} ${esc(g.key)}`)
      .join('');
    const pays = !owned
      ? `nothing — nobody collects it`
      : `${Math.round(split.tax)}g · ${Math.round(split.levy)} levies${goodsLine}`;

    /**
     * The other third, and where it went.
     *
     * A third of every province's wealth stops at its market instead of its
     * treasury, and comes back only through *control* of that market — which may
     * be a rival's. Without this row the player sees a village paying two-thirds
     * of what it used to and no reason for it, which reads as a bug and not as a
     * mechanic. It is a **destination, not a deduction**: kept out of the "why it
     * is short" footnote and given its own row, because unrest is a problem to
     * solve and trade is a place the money has gone.
     *
     * Naming who holds the whip hand there is the point of it. "3g to Emberhold,
     * and The Iron Bench takes most of it" is the sentence that turns a number
     * into a reason to do something.
     */
    const node = state.owner >= 0 ? tradeNodeOf(c, p) : -1;
    const lead = node >= 0 ? nodeLeader(c, node) : { nation: -1, share: 0 };
    const myShare = node >= 0 ? controlIn(c, node, c.playerNation) : 0;
    const tradeRow =
      node >= 0 && toMarket > 0.005
        ? `<div class="wm-stat wm-click" data-act="market" data-node="${node}"${WorldMap.tip([
            `Another ${toMarket.toFixed(1)} gold a month leaves this province for the market at ${nodeName(c, node)}.`,
            'It is not lost. It is paid out to whoever controls that market, in proportion to their hold on it.',
            lead.nation >= 0
              ? `${nationName(c, lead.nation)} holds ${Math.round(lead.share * 100)}% of it.`
              : 'Nobody has a hold on it worth the name.',
            mine ? `You hold ${Math.round(myShare * 100)}%.` : '',
            'Click for the market itself, and what to do about it.',
          ])}><span>To market</span><b${lead.nation === c.playerNation ? ' class="own"' : ''}>${Math.round(
            toMarket,
          )}g → ${esc(nodeName(c, node))}</b></div>`
        : '';

    /**
     * That you took it, and who from.
     *
     * `claimedBy` is the crown that held this ground before whoever holds it
     * now, so on a province of yours it is exactly the answer to "did I win
     * this, and off whom" — the game already stores it, to hand that crown a
     * reconquest pretext, and it has never been shown as the good news it also
     * is. Ground you started with has no previous holder and gets the other
     * sentence, which is worth saying because "yours since the first council" is
     * how a player tells their heartland from their conquests.
     */
    const ownBanner = mine
      ? `<div class="wm-own">Yours<em>${
          state.claimedBy >= 0 && state.claimedBy !== c.playerNation
            ? `Taken from ${esc(nationName(c, state.claimedBy))}. They have not forgotten.`
            : 'Yours since the first council.'
        }</em></div>`
      : '';

    /**
     * Whether your men can walk here — which on this board is the first
     * question, not the last.
     *
     * The road graph is in seven pieces and a hundred and eleven of a hundred and
     * fifty provinces cannot be reached from the heartland on foot. On the old
     * single-landmass board "can I get there" was almost always yes and the map
     * only had to answer "whose is it". Now a player can be looking at ground
     * they *own* with no land route to it at all, and until this row existed
     * nothing anywhere said so — they would order a march, watch it refused, and
     * have no way to find out why.
     *
     * Two answers, because they are different advice. With a host selected this
     * is `marchRefusal` for that host: the simulation's own sentence, from the
     * same clause list `canMarch` enforces, so the reason shown and the rule
     * applied cannot drift. With nothing selected it is the standing fact about
     * the ground — is it on the same road network as your seat — which is what
     * you want to know while you are still deciding where to go.
     */
    const seatWalk = canWalkBetween(c, c.capital, p);
    const roadRow =
      p === c.capital
        ? ''
        : `<div class="wm-stat"${WorldMap.tip(
            army && army.owner === c.playerNation
              ? [
                  `Whether ${army.name} can be ordered here today.`,
                  orderRefusal(c, army, p) || 'It can march here.',
                ]
              : [
                  seatWalk
                    ? `A road runs here from ${provinceDef(c.capital).name}, so a host raised at home can walk to it.`
                    : `No road connects this ground to ${provinceDef(c.capital).name}. Whatever takes it has to be carried by sea.`,
                  `The realm's roads are in ${roadComponentCount(c)} separate pieces; this is not a gap you can march around.`,
                ],
          )}><span>By road</span><b class="${
            army && army.owner === c.playerNation
              ? orderRefusal(c, army, p)
                ? 'bad'
                : 'own'
              : seatWalk
                ? ''
                : 'bad'
          }">${esc(
            army && army.owner === c.playerNation
              ? orderRefusal(c, army, p) || 'this host may march here'
              : seatWalk
                ? 'walkable from home'
                : 'by sea only',
          )}</b></div>`;

    const orders = !c.pending && !c.over;
    const actions: string[] = [];

    /**
     * The door into the holding — the one thing this panel could not do.
     *
     * *"i took over a town how do i go into it to build"*. Every other button
     * below is an order the crown gives from a distance; this one is the player
     * going there. It is first because it is the only one that changes which
     * game you are playing, and it is offered on **every** holding of yours
     * rather than only on ones you have already entered, because founding and
     * revisiting are one act from the player's side — see `enterHold` in
     * `main.ts`.
     *
     * Not gated on `orders`. A pending battle stops you *ordering* things; it
     * does not stop you standing in your own town, and a player told to fight a
     * siege should be able to go and look at their walls first.
     */
    if (mine && !c.over) {
      const settled = state.settled > 0;
      const sacked = state.wreck > 0.005;
      const besieged = state.siegeBy >= 0;
      actions.push(
        `<span class="wm-btn${besieged ? ' off' : ''}" data-act="hold" data-prov2="${p}"${WorldMap.tip([
          settled
            ? `Go and stand in ${provinceName(p)}. It has been running while you were away.`
            : `Go and stand in ${provinceName(p)}. There is a ${tierName(tierOf(c, p)).toLowerCase()} here, and this is the first time you will have seen it.`,
          sacked
            ? `It was taken by storm: about ${Math.round(state.wreck * 100)}% of it is rubble, and it will be rubble when you walk in.`
            : '',
          settled ? '' : 'A settlement world is generated for this holding the first time you enter it, and kept for the rest of the campaign.',
          besieged ? 'There is an army camped outside the walls.' : '',
        ])}>${settled ? 'Enter the town' : 'Enter the holding'}${
          sacked ? ' <i>wrecked</i>' : settled ? '' : ' <i>new</i>'
        }</span>`,
      );
    }
    if (orders && mine && state.siegeBy < 0) {
      const most = maxAffordablePoints(c, c.playerNation);
      if (most >= MIN_ARMY_POINTS) {
        const bill = raisePrice(c, c.playerNation, most);
        actions.push(
          `<span class="wm-btn" data-act="raise"${WorldMap.tip([
            `The largest host ${provinceName(p)} can pay for today.`,
            `${most} points, ${Math.round(bill.gold)} gold${bill.iron > 0 ? ` and ${Math.round(bill.iron)} iron` : ''}.`,
            'Iron you do not have is bought in, dearly.',
          ])}>Raise a host
             <i>${most} pts · ${Math.round(bill.gold)}g${bill.iron > 0 ? ` · ${Math.round(bill.iron)} iron` : ''}</i></span>`,
        );
      } else {
        actions.push(`<span class="wm-btn off">Raise a host <i>not enough coin or levies</i></span>`);
      }
      /**
       * The walls, priced by the simulation rather than by this file.
       *
       * Every number here used to be written out by hand — a ceiling of 3, a
       * flat `90g · 70 stone`, and an affordability test against the national
       * treasury — and all three became wrong in the same week. The ceiling is
       * `maxFortAt` now, because a tier-10 citadel may go to 4. The price grows
       * per level and the stone is hauled out of the *supplying city's yard*,
       * not the treasury, so a check against `me.treasury.stone` was asking
       * about a purse the mason never opens. And the haulage multiplier means
       * the same wall costs different amounts in different provinces, which no
       * constant in a UI file can express.
       *
       * `fortifyBlock` returns the empty string when the order is legal and
       * otherwise a finished sentence — "Emberhold and everything behind it are
       * 22 iron short." — so the button is greyed on its emptiness and the
       * sentence is printed verbatim. Writing a rival explanation here is how
       * the UI ends up disagreeing with the rule it is describing.
       */
      const wall = fortifyCost(c, p);
      if (wall) {
        const why = fortifyBlock(c, p, c.playerNation);
        const price =
          `${wall.gold}g` +
          wall.goods.map((g) => ` · ${g.amount} ${g.key}${g.insteadOf ? `*` : ''}`).join('');
        actions.push(
          `<span class="wm-btn${why ? ' off' : ''}" data-act="fortify"${WorldMap.tip([
            `Walls ${state.fort} → ${wall.to}.`,
            `The garrison cap rises ${garrisonCap(c, p)} → ${garrisonCap(c, p) + GARRISON_PER_FORT},`,
            'and everything standing behind them fights harder.',
            wall.route.city >= 0
              ? `The stone comes out of ${provinceName(wall.route.city)}'s yard, ${wall.route.dist} away — haulage ×${wall.haulMul.toFixed(2)}.`
              : '',
            // A substitution is the reason a price looks strange, so it is said
            // rather than starred and left.
            ...wall.goods
              .filter((g) => g.insteadOf)
              .map((g) => `${g.amount} ${g.key} in place of ${g.insteadOf}, which costs more.`),
            why,
          ])}>Raise the walls
             <i>${esc(price)}</i></span>`,
        );
      }
    }

    /**
     * The harbour, and the hulls lying in it.
     *
     * A coastal province is a door on this board — a hundred and eleven of a
     * hundred and fifty provinces cannot be walked to at all — and the whole
     * apparatus for using one has been in the simulation, unreachable, since the
     * sea landed. The prices come from `portBill`/`fleetPrice`, the refusals from
     * `portRefusal`/`fleetRefusal`, and the earnings from `portIncome`, so
     * nothing on this block is a number this file worked out for itself.
     */
    const coastal = isCoastal(p);
    const portLevel = state.port ?? 0;
    const harbourActs: string[] = [];
    if (orders && mine && coastal) {
      const why = portRefusal(c, c.playerNation, p);
      if (portLevel < MAX_PORT) {
        const bill = portBill(c, c.playerNation, portLevel);
        harbourActs.push(
          `<span class="wm-btn${why ? ' off' : ''}" data-act="buildport"${WorldMap.tip([
            `Harbour works ${portLevel} → ${portLevel + 1}.`,
            'A harbour earns on traffic rather than on ground: what it is worth is how many settled coasts lie one lane away, and whether they have harbours of their own.',
            'It also weighs on who controls the market this coast trades through — a wharf wins a market as well as earning at one.',
            why,
          ])}>${portLevel > 0 ? 'Widen the harbour' : 'Build a harbour'}
             <i>${Math.round(bill.gold)}g · ${bill.timber} timber · ${bill.stone} stone</i></span>`,
        );
      }
      if (portLevel > 0) {
        const most = maxAffordableShips(c, c.playerNation);
        const want = Math.max(MIN_FLEET_SHIPS, most);
        const noFleet = fleetRefusal(c, c.playerNation, p, want);
        harbourActs.push(
          `<span class="wm-btn${noFleet ? ' off' : ''}" data-act="raisefleet" data-prov2="${p}"
             data-ships="${want}" data-fleet2="-1"${WorldMap.tip([
            `The largest squadron ${provinceName(p)} can lay down today: ${want} hulls.`,
            'A hull is 60 points of fight and 260 of lift. Nine of them carry the largest host in the game.',
            `Upkeep is ${SHIP_UPKEEP_GOLD.toFixed(1)}g a hull a month.`,
            'Timber you have not got is bought in, dearly.',
            noFleet,
          ])}>Lay down a fleet <i>${want} hulls</i></span>`,
        );
      }
    }
    const harbourRow = coastal
      ? `<div class="wm-stat"${WorldMap.tip([
          portLevel > 0
            ? `Harbour works at rung ${portLevel} of ${MAX_PORT}.`
            : 'It stands on the water, and has no harbour on it.',
          portLevel > 0
            ? `It pays ${portIncome(c, p, laneAdjacency(c)).toFixed(1)}g a month off the traffic — every settled coast one lane away is a partner, and a partner with a harbour of its own counts double.`
            : 'A harbour here would be a door: hulls to build, hosts to lift, and a say in the market this coast trades through.',
          `${seaNeighbours(c, p).length} coast${seaNeighbours(c, p).length === 1 ? '' : 's'} lie one lane away.`,
        ])}><span>Harbour</span><b class="${portLevel > 0 ? 'own' : ''}">${
          portLevel > 0
            ? `${portLevel} · ${portIncome(c, p, laneAdjacency(c)).toFixed(1)}g a month`
            : 'none'
        }</b></div>`
      : '';

    const hullsHere = fleetsAt(c, p)
      .map(
        (f) =>
          `<div class="wm-army${f.owner === c.playerNation ? ' mine' : ''}"
             data-act="focusfleet" data-fleet2="${f.id}"${this.fleetTip(c, f)}>
            <b>${esc(f.name)}</b>
            <span>${esc(nationName(c, f.owner))} · ${esc(fleetSummary(c, f))}</span>
            <span class="wm-dim">lying off ${esc(provinceName(p))}</span>
          </div>`,
      )
      .join('');

    return `
      <div class="wm-title small">${esc(def.name)}<i class="wm-look" data-act="look" title="Show me">⌖</i></div>
      ${groundStill(state.fort ?? 0)}
      <div class="wm-creed">${esc(def.flavour)}</div>
      ${ownBanner}
      <div class="wm-stat"><span>Held by</span><b${mine ? ' class="own"' : ''}>${esc(
        state.owner >= 0 ? nationName(c, state.owner) : 'Nobody',
      )}${mine ? ' — yours' : ''}</b></div>
      ${this.ownerBlock(c, state.owner, mine)}
      <div class="wm-stat"${WorldMap.tip([
        `${terrain.name}. A defender here fights at ×${terrain.defence.toFixed(2)}.`,
        isCoastal(p) ? 'It stands on the water.' : '',
      ])}><span>Ground</span><b>${esc(terrain.name)}${isCoastal(p) ? ' · on the sea' : ''} ·×${terrain.defence.toFixed(2)}</b></div>
      <!--
        The climate, under the ground it belongs to.

        The owner's ask: clicking a province should tell you the climate.

        A full-width row rather than a wm-stat pair, because it is a sentence and
        not a figure — and because what it is really answering is "what will a
        hold here be short of", which is the question the Ground row above looks
        like it answers and does not. Quoted straight from PROVINCE_TERRAIN, so
        it cannot drift from the mix sim/map.ts actually generates.

        (No backticks in this comment: it lives inside a template literal, and a
        backtick in here ends the template. See the chart2 write-up.)
      -->
      <div class="wm-climate"${WorldMap.tip([
        'What a settlement built on this ground is made of.',
        'Wood, stone and ore are mixed differently in every country — this is what you would be building with.',
      ])}>${esc(terrain.climate)}</div>
      <div class="wm-stat"><span>Walls</span><b>${state.fort} · defence ×${defenceMultiplier(c, p).toFixed(2)}</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `${rankName(holdingRank(c, p))}.`,
        standing > 0
          ? `It keeps ${standing} points on the walls whatever happens to it — that is the floor, and it is back the month after any fight.`
          : 'A village keeps no standing garrison. What is here is levy, and it can be ground to nothing.',
        standing > 0
          ? 'A siege still empties it: nothing regrows while somebody is sitting outside the gate.'
          : '',
        `${GARRISON_BASE} for the ground, +${state.fort * GARRISON_PER_FORT} for the walls, +${tierGarrison(tierOf(c, p))} for the ${tierName(tierOf(c, p))}${
          standing > 0 ? `, +${standing} standing` : ''
        }.`,
        state.barracks > 0 ? `${Math.round(state.barracks)} of that is your own yards and towers.` : '',
      ])}><span>Garrison</span><b>${Math.round(state.garrison)} / ${garrisonCap(c, p)}${
        standing > 0 ? ` <i class="wm-dim">${standing} standing</i>` : ''
      }</b></div>
      <div class="wm-stat"${WorldMap.tip([
        mine
          ? 'Straight into your treasury at the next council.'
          : 'Straight into its holder’s treasury each month.',
        // The tier first, because it is the term the player can actually move.
        // A Hamlet paying badly and a Great City paying badly are two different
        // problems and the card price alone cannot tell them apart.
        owned
          ? `${tierName(tierOf(c, p))}, which multiplies everything below it by ×${tierYield(tierOf(c, p)).toFixed(2)}.`
          : '',
        `The bare ground is worth ${def.wealth}g · ${def.levy} levies${
          def.resource ? ` · ${def.yield} ${def.resource}` : ''
        } before the tier, once it is settled and nobody is sitting on it.`,
        short ? 'It is not yielding all of that — see below.' : 'It is yielding all of that.',
        // Said here as well as in the row below, because this is the row whose
        // number looks wrong to anyone who remembers what the card says.
        toMarket > 0.005
          ? `And ${toMarket.toFixed(1)}g of the gold goes to the market rather than the treasury — see “To market”.`
          : '',
      ])}><span>${mine ? 'Pays you' : 'Pays'}</span><b${mine && !short ? ' class="own"' : ''}>${pays}</b></div>
      ${short ? `<div class="wm-short">${esc(yieldCuts(c, p))}</div>` : ''}
      ${tradeRow}
      ${state.unrest > 0.01 ? `<div class="wm-stat"><span>Unrest</span><b class="bad">${Math.round(state.unrest * 100)}%</b></div>` : ''}
      ${state.claimedBy >= 0 && state.claimedBy !== state.owner ? `<div class="wm-stat"${WorldMap.tip(['The crown that lost this ground last still says it is theirs.', 'That is a reason for a war, and they have it.'])}><span>Claimed by</span><b>${esc(nationName(c, state.claimedBy))}</b></div>` : ''}
      <div class="wm-stat"${WorldMap.tip([
        seen
          ? 'Your riders can see this ground: anything standing here is in your warning list.'
          : 'Beyond your riders. You hold nothing within a march of it, so what happens here happens unseen.',
      ])}><span>Riders</span><b>${seen ? 'watching' : 'blind'}</b></div>
      ${roadRow}
      ${harbourRow}
      ${state.siegeBy >= 0 ? `<div class="wm-stat"><span>Besieged by</span><b class="bad">${esc(nationName(c, state.siegeBy))} · ${months(state.siegeT)} so far</b></div>` : ''}
      ${warns.length > 0 ? `<div class="wm-sub">What is coming</div>${warns.map((w) => this.warnRow(c, w)).join('')}` : ''}
      ${camp ? `<div class="wm-sub">Brigands</div>${this.campRow(c, camp)}` : ''}
      <div class="wm-sub">Hosts here</div>
      ${hostList || '<div class="wm-hint">Nobody in the field.</div>'}
      ${
        inside.length > 0
          ? `<div class="wm-sub">Inside the walls</div>${inside.map((a: Army) => this.hostRow(c, a)).join('')}`
          : ''
      }
      ${hullsHere ? `<div class="wm-sub">In the roads</div>${hullsHere}` : ''}
      ${actions.length ? `<div class="wm-sub">The province</div><div class="wm-acts">${actions.join('')}</div>` : ''}
      ${
        harbourActs.length
          ? `<div class="wm-sub">The harbour</div><div class="wm-acts">${harbourActs.join('')}</div>`
          : ''
      }
      ${army ? this.armyBlock(c, army) : ''}`;
  }

  /**
   * The crown that holds the clicked province, as an actor rather than a name.
   *
   * The reference genre's core loop: click ground, see whose it is and where
   * you stand with them, and act — declare, ally, ask for peace — without going
   * to find the same crown again under another tab. The Crowns tab remains the
   * ledger of everybody; this is the one crown the click was about, with the
   * same buttons wired through the same delegated handlers.
   */
  private ownerBlock(c: CampaignState, owner: number, mine: boolean): string {
    if (mine || owner < 0 || owner === BANDIT_NATION || !c.nations[owner]?.alive) return '';
    const r = diplomaticView(c, c.playerNation).find((v) => v.nation === owner);
    if (!r) return '';
    const wars = warView(c, c.playerNation);
    const live = !c.pending && !c.over;
    return `<div class="wm-sub">The crown</div>
      <div class="wm-crown open${r.atWar ? ' war' : ''}">
        <div class="wm-crownhead" data-act="opencrown" data-nation="${r.nation}"${this.crownTip(r)}>
          <i class="wm-swatch" style="background:${hex(nationAccent(r.nation))}"></i>
          <span>${esc(r.name)}<em>${esc(this.crownMarks(c, r))}</em></span>
          <b class="${r.theirOpinion >= 0 ? 'up' : 'down'}">${r.theirOpinion}</b>
        </div>
        <div class="wm-acts">${this.crownActs(r, wars, live).join('')}</div>
      </div>`;
  }

  /** One host, in a list of hosts. Clicking it makes it the selected host. */
  private hostRow(c: CampaignState, a: Army): string {
    /**
     * Held, and that is the *group* and not only the lead host.
     *
     * The column has to agree with the board about what a gesture picked up.
     * A box that lights four banners on the chart and one row in the panel is a
     * screen telling the player two different things about one selection.
     */
    const sel =
      a.id === this.selectedArmy ||
      (this.groupArmies.length > 0 && this.groupArmies.includes(a.id));
    const marching = a.moveTo >= 0 ? ` → ${esc(provinceName(a.moveTo))} (${months(a.moveT)})` : '';
    const stance =
      a.sheltered ? ' · in the walls'
      : a.stance === ArmyStance.Besieging ? ' · besieging'
      : a.stance === ArmyStance.Assaulting ? ' · storming' : '';
    return `<div class="wm-army${sel ? ' sel' : ''}${a.owner === c.playerNation ? ' mine' : ''}"
              data-army="${a.id}"${WorldMap.tip([
                a.name,
                `${nationName(c, a.owner)} · ${Math.round(armyPoints(a))} pts in the field`,
                `${armyModels(a)} models · ${armySummary(a)}`,
                `Supply ${Math.round(a.supply * 100)}% · ${a.victories} victories`,
                a.moveTo >= 0
                  ? `On ${roadWord(c, a.moveFrom, a.moveTo)} to ${provinceName(a.moveTo)}, arriving ${this.arrivalLine(a.moveT)}`
                  : 'Standing.',
              ])}>
        <!--
          The arm, above the name. It is the fact you want before you have
          decided to ask for the roster, which is why it is not in the tooltip
          with everything else.
        -->
        <div class="wm-armline"><svg viewBox="0 0 12 12">${armGlyph(
          armOf(a),
        )}</svg>${ARM_WORD[armOf(a)]} · ${armyModels(a)} ${armyModels(a) === 1 ? 'man' : 'men'}</div>
        <b>${esc(a.name)}${
          sel && this.groupArmies.length > 1 ? ' <i class="wm-held">held</i>' : ''
        }</b>
        <span>${esc(nationName(c, a.owner))} · ${armyModels(a)} models · ${Math.round(armyPoints(a))} pts${stance}${marching}</span>
        <span class="wm-dim">${esc(armySummary(a))}</span>
      </div>`;
  }

  /**
   * Everything about one host, and everything it can be told to do.
   *
   * All four numbers that decide what a host is worth in a fight are here and
   * were not anywhere before: raw strength, what veterancy adds to it, what
   * hunger takes off it, and the standing order it is currently under. A player
   * who cannot see supply cannot understand why a host that outnumbers the enemy
   * lost, and `armyPoints` is the function that decides it.
   */
  private armyBlock(c: CampaignState, a: Army, terse = false): string {
    const mine = a.owner === c.playerNation;
    const raw = rawPoints(a);
    const vet = Math.min(MAX_VETERANCY, a.victories * VETERANCY_PER_WIN);
    const fed = 0.6 + 0.4 * Math.max(0, Math.min(1, a.supply));
    const orders: string[] = [];
    if (a.aboard >= 0) {
      // A host aboard a hull is not standing anywhere. Its `at` follows the ship
      // and means nothing on land, so saying "standing at Saltmere" would be a
      // lie the panel told twice a month.
      const f = findFleet(c, a.aboard);
      orders.push(
        `<div class="wm-stat wm-click" data-act="focusfleet" data-fleet2="${a.aboard}"${WorldMap.tip([
          'This host is at sea. It goes where the fleet goes and can be given no order of its own until it is put ashore.',
          f && isSailing(f)
            ? `${provinceName(f.moveFrom)} → ${provinceName(f.moveTo)}, landfall ${this.arrivalLine(f.moveT)}.`
            : f
              ? `Lying off ${provinceName(f.at)}, waiting to be put ashore.`
              : '',
          'Click for the fleet.',
        ])}><span>Aboard</span><b>${esc(f ? f.name : 'a fleet')}</b></div>`,
      );
    } else if (a.moveTo >= 0 && terse) {
      // The panel above has already said where it is going and when it lands.
    } else if (a.moveTo >= 0) {
      orders.push(
        `<div class="wm-stat"><span>Marching</span><b>${esc(provinceName(a.moveFrom))} → ${esc(provinceName(a.moveTo))}</b></div>
         <div class="wm-stat"><span>Arrives</span><b>${esc(this.arrivalLine(a.moveT))}</b></div>
         ${meter(1 - a.moveT / Math.max(1, a.moveTotal))}`,
      );
    } else if (a.sheltered) {
      orders.push(
        `<div class="wm-stat"${WorldMap.tip([
          'Inside the walls. It cannot be met in the open field and it cannot be given a road.',
          'If somebody comes for this holding, it is the defence — at the walls’ own multiplier.',
          'Bring it out before it can march, besiege or run anybody down.',
        ])}><span>Order</span><b class="own">Inside the walls of ${esc(provinceName(a.at))}</b></div>`,
      );
    } else if (a.stance === ArmyStance.Besieging) {
      orders.push(
        `<div class="wm-stat"><span>Order</span><b>Sitting down before ${esc(provinceName(a.at))} · ${months(a.siegeTurns)}</b></div>`,
      );
    } else if (a.stance === ArmyStance.Assaulting) {
      orders.push(`<div class="wm-stat"><span>Order</span><b class="bad">Storming the walls</b></div>`);
    } else {
      orders.push(
        `<div class="wm-stat"><span>Order</span><b>Standing at ${esc(provinceName(a.at))}${a.idleTurns > 1 ? ` · idle ${months(a.idleTurns)}` : ''}</b></div>`,
      );
    }
    if (a.intercepting >= 0) {
      const q = findArmy(c, a.intercepting);
      orders.push(
        `<div class="wm-stat"><span>Chasing</span><b>${esc(q ? q.name : 'a host that is gone')}</b></div>`,
      );
    }
    if (a.objective >= 0 && a.objective !== a.at && a.objective !== a.moveTo) {
      orders.push(
        `<div class="wm-stat"><span>Set out for</span><b>${esc(provinceName(a.objective))}</b></div>`,
      );
    }

    return `
      <div class="wm-sub">${esc(a.name)}</div>
      <div class="wm-stat"${WorldMap.tip([
        `${Math.round(raw)} points of men,`,
        `×${(1 + vet).toFixed(2)} for ${a.victories} victories (never past ×${(1 + MAX_VETERANCY).toFixed(2)}),`,
        `×${fed.toFixed(2)} for supply.`,
        `That is ${Math.round(armyPoints(a))} in the field.`,
      ])}><span>Strength</span><b>${Math.round(armyPoints(a))} pts <i class="wm-dim">${Math.round(raw)} raw</i></b></div>
      <div class="wm-stat"><span>Models</span><b>${armyModels(a)}</b></div>
      <div class="wm-stat"${WorldMap.tip([
        'Below half, a host bleeds men every month.',
        'It is fed by standing on friendly ground and starved by sitting on somebody else’s.',
      ])}><span>Supply</span><b class="${a.supply < 0.5 ? 'bad' : ''}">${Math.round(a.supply * 100)}%</b></div>
      ${meter(a.supply, a.supply < 0.5 ? 'bad' : '')}
      <div class="wm-stat"${WorldMap.tip([
        `${a.victories} battles won, worth +${Math.round(VETERANCY_PER_WIN * 100)}% each,`,
        `capped at +${Math.round(MAX_VETERANCY * 100)}%.`,
      ])}><span>Veterancy</span><b>+${Math.round(vet * 100)}%<i class="wm-dim"> · ${a.victories} won</i></b></div>
      <div class="wm-dim wm-roster">${esc(armySummary(a))}</div>
      ${orders.join('')}
      ${mine ? this.armyOrders(c, a) : `<div class="wm-hint">Not yours to command.</div>`}`;
  }

  /** The buttons for a host of your own. */
  private armyOrders(c: CampaignState, army: Army): string {
    if (c.pending || c.over) return '';
    /**
     * The gesture, stated before the buttons rather than under them.
     *
     * It was below the neighbour list to begin with, which put it under three
     * screenfuls of orders on a scrolling column — a hint nobody scrolls to is
     * a hint that does not exist. It is also said on the board the moment a host
     * is selected; see `sayHostSelected`. Two places, because this is the one
     * control on this screen with no button to advertise it.
     */
    const hint =
      army.moveTo < 0 && army.aboard < 0 && !army.sheltered
        ? `<div class="wm-order-hint"><b>Right-click the map</b> to march anywhere the roads reach.
             Hover first: it draws the route and counts the months.</div>`
        : army.sheltered
          ? `<div class="wm-order-hint">They are <b>inside the walls</b>. Bring them out before they can be sent anywhere.</div>`
          : '';
    const acts: string[] = [];
    /**
     * Behind the gates, and back out of them.
     *
     * First in the list, above the march buttons, because it is the one order
     * on this panel that decides whether any of the others are legal — and
     * because it is the answer to a host that is about to be caught in the open
     * by something twice its size, which is a decision made in a hurry.
     */
    if (army.sheltered) {
      acts.push(
        `<span class="wm-btn" data-act="sally" data-army2="${army.id}"${WorldMap.tip([
          `Bring them back out into the field at ${provinceName(army.at)}.`,
          'They can march, besiege and run people down again — and they can be met in the open.',
        ])}>Bring them out</span>`,
      );
    } else if (army.moveTo < 0 && army.aboard < 0) {
      const why = shelterRefusal(c, army);
      const room = shelterCapacity(c, army.at);
      const used = shelteredPoints(c, army.at);
      acts.push(
        `<span class="wm-btn${why ? ' off' : ''}" data-act="shelter" data-army2="${army.id}"${WorldMap.tip([
          `Put them inside the walls of ${provinceName(army.at)}.`,
          'They cannot be caught in the open field, and they are the defence if somebody comes.',
          'They cannot march, besiege or run anybody down until they are brought out.',
          room > 0 ? `These walls hold ${room} points; ${used} are inside.` : '',
          why,
        ])}>Take the walls${room > 0 ? ` <i>${Math.max(0, room - used)} pts of room</i>` : ''}</span>`,
      );
    }
    if (army.moveTo >= 0) {
      // Halting reverses the lane rather than cancelling it: the ground already
      // covered has to be walked back. Say so on the button, or it looks free.
      acts.push(
        `<span class="wm-btn" data-act="halt" data-army2="${army.id}"${WorldMap.tip([
          `Turn back toward ${provinceName(army.moveFrom)}.`,
          `It does not teleport: ${months(army.moveTotal - army.moveT)} of walking to undo.`,
        ])}>Turn back <i>${months(army.moveTotal - army.moveT)} home</i></span>`,
      );
    } else if (army.sheltered) {
      // Everything below is an order for a host in the field. A host inside the
      // walls has exactly one order, and it is the one already on the list.
    } else {
      if (c.provinces[army.at].owner !== c.playerNation) {
        acts.push(
          army.stance === ArmyStance.Besieging
            ? `<span class="wm-btn" data-act="assault" data-army2="${army.id}"${WorldMap.tip(['Go over the walls now, and pay for it in men.'])}>Storm the walls</span>`
            : `<span class="wm-btn" data-act="besiege" data-army2="${army.id}"${WorldMap.tip([
                'Sit down and starve them out.',
                `About ${months(Math.max(1, c.provinces[army.at].fort) * SIEGE_TURNS_PER_FORT)} against these walls,`,
                'or sooner if the garrison starves first.',
              ])}>Sit down and wait</span>`,
        );
      }
      if (rawPoints(army) < MAX_ARMY_POINTS * 0.9 && c.provinces[army.at].owner === c.playerNation) {
        const bill = raisePrice(c, c.playerNation, 400);
        const canPay = c.nations[c.playerNation].treasury.gold >= bill.gold;
        acts.push(
          `<span class="wm-btn${canPay ? '' : ' off'}" data-act="reinforce" data-army2="${army.id}"${WorldMap.tip([
            'Four hundred points of fresh men, where it stands.',
            'They arrive fed, and they have won nothing.',
            canPay ? '' : 'The treasury will not stand it.',
          ])}>Reinforce <i>400 pts · ${Math.round(bill.gold)}g</i></span>`,
        );
      }
      const stackmate = armiesIn(c, army.at).find(
        (o) => o.id !== army.id && o.owner === c.playerNation && o.moveTo < 0,
      );
      if (stackmate) {
        acts.push(
          `<span class="wm-btn" data-act="merge" data-army2="${army.id}" data-with="${stackmate.id}">Fold in ${esc(stackmate.name)}</span>`,
        );
      }
      // Anything on a road that touches this ground can be run down from here.
      for (const quarry of c.armies) {
        if (!canIntercept(c, army, quarry)) continue;
        acts.push(
          `<span class="wm-btn" data-act="intercept" data-chase="${army.id}" data-quarry="${quarry.id}"${WorldMap.tip([
            `Go out onto ${roadWord(c, quarry.moveFrom, quarry.moveTo)} and meet them on it.`,
            `${quarry.name} — ${Math.round(armyPoints(quarry))} pts against your ${Math.round(armyPoints(army))}.`,
            'Nothing changes hands on a road. The loser is thrown back the way it came.',
          ])}>Run down ${esc(quarry.name)} <i>${Math.round(armyPoints(quarry))} pts</i></span>`,
        );
      }
      acts.push(`<span class="wm-btn ghost" data-act="disband" data-army2="${army.id}">Stand it down</span>`);
    }

    /**
     * Every road out of here, including the ones that are shut.
     *
     * This list used to be filtered by `canMarch` and the refused neighbours
     * simply were not on it — which on the old single-landmass board was almost
     * never wrong and on this one is the commonest case. A player looking at a
     * host beside ground they own, with no button offering to march to it and no
     * sentence saying why, has been told nothing. `marchRefusal` is the
     * simulation's own answer, out of the same clause list `canMarch` enforces,
     * and it distinguishes the two kinds of no: *go round*, when the destination
     * is on the same walkable island, and *take ship*, when it is not.
     */
    const marchable =
      army.moveTo < 0 && !army.sheltered
        ? neighbours(c, army.at)
            .map((q) => {
              const camp = campAt(c, q);
              const why = canOrderMarch(c, army, q) ? '' : orderRefusal(c, army, q) || 'They cannot go there.';
              const turns = why ? 0 : marchTurns(c, army, q);
              return `<span class="wm-btn march${why ? ' off' : ''}" data-act="march" data-army2="${army.id}" data-to="${q}"${WorldMap.tip([
                `${provinceName(q)} — ${why ? 'no march is possible' : `${months(turns)} by ${roadWord(c, army.at, q)}`}.`,
                mayAttack(c, c.playerNation, q) ? 'You are at war with whoever holds it.' : '',
                camp ? `Brigands there, ${Math.round(camp.strength)} strong. Arriving means a fight.` : '',
                why,
              ])}>
                 ${esc(provinceName(q))} <i>${why ? esc(why) : months(turns)}${
                   !why && mayAttack(c, c.playerNation, q) ? ' · hostile' : ''
                 }${!why && camp ? ' · brigands' : ''}</i></span>`;
            })
            .join('')
        : '';

    /**
     * Getting aboard. A host standing in a harbour beside a fleet of its own is
     * one click from three quarters of the board, and until this button existed
     * the only crowns that ever crossed water were the ones the AI sailed.
     */
    const aboard =
      army.moveTo < 0 && army.aboard < 0 && !army.sheltered
        ? fleetsAt(c, army.at)
            .filter((f) => f.owner === c.playerNation)
            .map((f) => {
              const why = embarkRefusal(c, f, army);
              return `<span class="wm-btn${why ? ' off' : ''}" data-act="embark"
                  data-fleet2="${f.id}" data-army2="${army.id}"${WorldMap.tip([
                `Put ${army.name} aboard ${f.name}.`,
                `She lifts ${fleetCapacity(f)} points and is carrying ${Math.round(cargoPoints(c, f))}; this host is ${Math.round(rawPoints(army))}.`,
                'Men at sea can be given no orders of their own until they are put ashore.',
                why,
              ])}>Aboard ${esc(f.name)} <i>${f.ships} hulls · ${Math.round(
                fleetCapacity(f) - cargoPoints(c, f),
              )} pts of room</i></span>`;
            })
            .join('')
        : '';

    return `${hint}${acts.length ? `<div class="wm-sub">Orders</div><div class="wm-acts">${acts.join('')}</div>` : ''}
      ${aboard ? `<div class="wm-sub">Take ship</div><div class="wm-acts">${aboard}</div>` : ''}
      ${marchable ? `<div class="wm-sub">Neighbours</div><div class="wm-acts">${marchable}</div>` : ''}`;
  }

  /**
   * A host on a road: whose, how strong, where from, where to, when it lands —
   * and, if you have somebody in reach, the order to go and meet it.
   */
  private marchPanel(c: CampaignState): string {
    const a = findArmy(c, this.focusArmy);
    if (!a || a.moveTo < 0) return this.provincePanel(c);
    const lane = marchesInProgress(c).find((m) => m.armyId === a.id);
    const mine = a.owner === c.playerNation;
    const hostile = !mine && atWar(c, c.playerNation, a.owner);
    const chasers = mine ? [] : armiesOf(c, c.playerNation).filter((k) => canIntercept(c, k, a));

    return `
      <div class="wm-title small">On the march
        <i class="wm-look" data-act="lookprov" data-prov2="${a.moveTo}" title="Show me">⌖</i></div>
      <div class="wm-creed">${esc(a.name)} of ${esc(nationName(c, a.owner))}${
        mine ? ' — yours' : hostile ? ' — you are at war with them' : ' — you are not at war with them'
      }.</div>
      <div class="wm-stat"><span>Crown</span><b><i class="wm-swatch" style="background:${hex(nationAccent(a.owner))}"></i>${esc(nationName(c, a.owner))}</b></div>
      <div class="wm-stat"><span>Strength</span><b>${Math.round(armyPoints(a))} pts · ${armyModels(a)} models</b></div>
      <div class="wm-stat"><span>From</span><b>${esc(provinceName(a.moveFrom))}</b></div>
      <div class="wm-stat"><span>To</span><b>${esc(provinceName(a.moveTo))}${
        c.provinces[a.moveTo].owner === c.playerNation ? ' <i class="bad">— yours</i>' : ''
      }</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `${roadWord(c, a.moveFrom, a.moveTo)}, ${months(a.moveTotal)} end to end.`,
      ])}><span>By</span><b>${esc(roadWord(c, a.moveFrom, a.moveTo))}</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `${months(a.moveT)} of realm time, which is ${Math.round(MONTH_SECONDS)} seconds a month.`,
        'The minutes are at normal pace. Running the clock faster does not slow them down.',
      ])}><span>Arrives</span><b>${esc(this.arrivalLine(a.moveT))}</b></div>
      ${meter(lane ? lane.progress : 0, hostile ? 'bad' : '')}
      <div class="wm-hint">${Math.round((lane ? lane.progress : 0) * 100)}% of the way. A month is ${Math.round(MONTH_SECONDS)} seconds of the realm's own time.</div>
      ${this.armyBlock(c, a, true)}
      ${
        chasers.length > 0
          ? `<div class="wm-sub">Run it down</div><div class="wm-acts">${chasers
              .map(
                (k) =>
                  `<span class="wm-btn" data-act="intercept" data-chase="${k.id}" data-quarry="${a.id}"${WorldMap.tip([
                    `${k.name} stands at ${provinceName(k.at)}, on one end of that road.`,
                    `${Math.round(armyPoints(k))} pts against their ${Math.round(armyPoints(a))}.`,
                    'Nothing changes hands on a road. The loser goes home.',
                  ])}>${esc(k.name)} <i>${Math.round(armyPoints(k))} pts · from ${esc(provinceName(k.at))}</i></span>`,
              )
              .join('')}</div>`
          : mine
            ? ''
            : `<div class="wm-hint">You have nobody standing on either end of that road, and only a host that is standing still can run one down.</div>`
      }`;
  }

  /**
   * A fleet, in full: what it is, what it is carrying, where it is bound, how
   * much sea is left, and everything it can be told to do.
   *
   * The two states are genuinely different panels and it matters that they read
   * that way. **Under way**, a fleet has exactly one order available to it —
   * turn round — and everything else on the panel is a countdown; the hosts
   * aboard are stated as *idle*, because they are, and a player who does not
   * know that will keep looking for them on land. **In harbour**, it is a list
   * of coasts it can reach, a list of hosts it can lift, and the price of more
   * hulls.
   *
   * Every refusal here comes out of `fleets.ts`. `sailRefusal`, `landingRefusal`,
   * `embarkRefusal` and `fleetRefusal` each return the player-facing sentence or
   * the empty string, so the greyed button and the enforced rule are the same
   * clause list — the discipline the diplomacy panel set and the march buttons
   * follow.
   */
  private fleetPanel(c: CampaignState): string {
    const f = findFleet(c, this.focusFleet);
    if (!f) return this.provincePanel(c);
    const mine = f.owner === c.playerNation;
    const hostile = !mine && atWar(c, c.playerNation, f.owner);
    const sailing = isSailing(f);
    const hosts = cargoArmies(c, f);
    const orders = mine && !c.pending && !c.over;
    const where = sailing ? f.moveTo : f.at;
    const acts: string[] = [];

    if (orders && sailing) {
      const back = Math.max(1, f.moveTotal - f.moveT);
      acts.push(
        `<span class="wm-btn" data-act="putabout" data-fleet2="${f.id}"${WorldMap.tip([
          `Put about for ${provinceName(f.moveFrom)}.`,
          `The sea already crossed is still to cross: ${months(back)} of it.`,
        ])}>Put about <i>${months(back)} back</i></span>`,
      );
    } else if (orders) {
      const why = landingRefusal(c, f);
      if (f.cargo.length > 0) {
        acts.push(
          `<span class="wm-btn${why ? ' off' : ''}" data-act="land" data-fleet2="${f.id}"${WorldMap.tip([
            `Put every host aboard onto the beach at ${provinceName(f.at)}.`,
            'They arrive hungry, and if the ground is hostile they arrive in a battle.',
            why,
          ])}>Put them ashore <i>${f.cargo.length} host${f.cargo.length === 1 ? '' : 's'}</i></span>`,
        );
      }
      // More hulls, into this fleet, at the harbour it lies in.
      const room = MAX_FLEET_SHIPS - f.ships;
      if (room > 0) {
        const want = Math.min(room, Math.max(1, maxAffordableShips(c, c.playerNation)));
        const bad = c.provinces[f.at].port <= 0 || c.provinces[f.at].owner !== c.playerNation;
        const poor = maxAffordableShips(c, c.playerNation) < 1;
        acts.push(
          `<span class="wm-btn${bad || poor ? ' off' : ''}" data-act="raisefleet"
             data-prov2="${f.at}" data-ships="${want}" data-fleet2="${f.id}"${WorldMap.tip([
            `Lay down ${want} more hull${want === 1 ? '' : 's'} at ${provinceName(f.at)}.`,
            bad ? 'They can only be built in a harbour of your own.' : '',
            poor ? 'The treasury and the timber yard will not stand one.' : '',
          ])}>More hulls <i>${want} · to ${f.ships + want}</i></span>`,
        );
      }
      acts.push(
        `<span class="wm-btn ghost" data-act="payoff" data-fleet2="${f.id}"${WorldMap.tip([
          'Pay off the crews. Anything aboard is put ashore first.',
          `It costs you ${(f.ships * SHIP_UPKEEP_GOLD).toFixed(1)}g a month to keep them.`,
        ])}>Pay them off</span>`,
      );
      // Anything on the water that can be run down from here.
      for (const q of c.fleets) {
        if (!canInterceptFleet(c, f, q)) continue;
        acts.push(
          `<span class="wm-btn" data-act="chase" data-fleet2="${f.id}" data-quarry="${q.id}"${WorldMap.tip([
            `${q.name} of ${nationName(c, q.owner)} — ${fleetPoints(q)} pts of hulls against your ${fleetPoints(f)}.`,
            q.cargo.length > 0 ? 'There are men aboard her. If she goes down they go down with her.' : '',
          ])}>Run down ${esc(q.name)} <i>${q.ships} hulls</i></span>`,
        );
      }
    }

    // The coasts a lane runs to, priced in months, refused ones greyed with the
    // simulation's own sentence rather than left off the list.
    const courses = orders && !sailing
      ? seaNeighbours(c, f.at)
          .map((q) => {
            const why = sailRefusal(c, f, q);
            const m = why ? 0 : sailMonths(c, f, q);
            const enemy = c.provinces[q].owner >= 0 && atWar(c, c.playerNation, c.provinces[q].owner);
            return `<span class="wm-btn march${why ? ' off' : ''}" data-act="sail"
                data-fleet2="${f.id}" data-to="${q}"${WorldMap.tip([
              `${provinceName(q)} — ${months(m)} under sail.`,
              c.provinces[q].owner >= 0 ? `${nationName(c, c.provinces[q].owner)} holds that coast.` : 'Nobody holds that coast.',
              (c.provinces[q].port ?? 0) > 0 ? 'There is a harbour there.' : '',
              enemy ? 'You are at war with them, so your men may land.' : '',
              why,
            ])}>${esc(provinceName(q))} <i>${months(m)}${enemy ? ' · hostile' : ''}</i></span>`;
          })
          .join('')
      : '';

    // Hosts standing in the same harbour, and whether this fleet can lift them.
    const loadable = orders && !sailing
      ? armiesStandingIn(c, f.at)
          .filter((a) => a.owner === c.playerNation)
          .map((a) => {
            const why = embarkRefusal(c, f, a);
            return `<span class="wm-btn${why ? ' off' : ''}" data-act="embark"
                data-fleet2="${f.id}" data-army2="${a.id}"${WorldMap.tip([
              `${a.name} — ${Math.round(rawPoints(a))} points of men to lift.`,
              `${f.name} can carry ${Math.round(fleetCapacity(f) - cargoPoints(c, f))} more.`,
              why,
            ])}>Take ${esc(a.name)} aboard <i>${Math.round(rawPoints(a))} pts</i></span>`;
          })
          .join('')
      : '';

    return `
      <div class="wm-title small">${esc(f.name)}
        <i class="wm-look" data-act="lookprov" data-prov2="${where}" title="Show me">⌖</i></div>
      <div class="wm-creed">${esc(nationName(c, f.owner))}${
        mine ? ' — yours' : hostile ? ' — you are at war with them' : ' — you are not at war with them'
      }. ${esc(fleetSummary(c, f))}.</div>
      <div class="wm-stat"><span>Crown</span><b><i class="wm-swatch" style="background:${hex(nationAccent(f.owner))}"></i>${esc(nationName(c, f.owner))}</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `${f.ships} hulls, ${fleetPoints(f)} points of fight at sea.`,
        `They lift ${fleetCapacity(f)} points of men, and are carrying ${Math.round(cargoPoints(c, f))}.`,
        `Upkeep ${(f.ships * SHIP_UPKEEP_GOLD).toFixed(1)}g a month.`,
      ])}><span>Hulls</span><b>${f.ships} · ${fleetPoints(f)} pts at sea</b></div>
      <div class="wm-stat"${WorldMap.tip([
        'What is aboard, in raw points. A fleet lifts men, not veterans.',
      ])}><span>Carrying</span><b class="${f.cargo.length > 0 ? 'own' : ''}">${Math.round(
        cargoPoints(c, f),
      )} / ${fleetCapacity(f)} pts</b></div>
      ${meter(fleetCapacity(f) > 0 ? cargoPoints(c, f) / fleetCapacity(f) : 0)}
      ${
        sailing
          ? `<div class="wm-stat"><span>From</span><b>${esc(provinceName(f.moveFrom))}</b></div>
             <div class="wm-stat"><span>Bound for</span><b>${esc(provinceName(f.moveTo))}${
               c.provinces[f.moveTo].owner === c.playerNation && !mine
                 ? ' <i class="bad">— yours</i>'
                 : ''
             }</b></div>
             <div class="wm-stat"${WorldMap.tip([
               'Months of realm time before she makes landfall.',
               'Nothing aboard can be given an order until she does.',
             ])}><span>Landfall</span><b>${esc(this.arrivalLine(f.moveT))}</b></div>
             ${meter(voyageProgress(f), hostile ? 'bad' : '')}
             <div class="wm-hint">${Math.round(voyageProgress(f) * 100)}% of the way across.
               A fleet under way is doing nothing: she cannot be given a new course, cannot fight
               on land, and cannot put a man ashore until she arrives.</div>`
          : `<div class="wm-stat"><span>Lying off</span><b>${esc(provinceName(f.at))}${
              (c.provinces[f.at].port ?? 0) > 0
                ? ` · harbour ${c.provinces[f.at].port}`
                : ' · no harbour'
            }</b></div>
             <div class="wm-hint">In harbour, and ready for orders.</div>`
      }
      ${
        hosts.length > 0
          ? `<div class="wm-sub">Aboard</div>${hosts
              .map(
                (a) =>
                  `<div class="wm-army mine" data-army="${a.id}"${WorldMap.tip([
                    a.name,
                    `${Math.round(rawPoints(a))} pts of men, ${armyModels(a)} models`,
                    'At sea. It can be given no orders of its own until it is put ashore.',
                  ])}><b>${esc(a.name)}</b>
                    <span>${Math.round(rawPoints(a))} pts · ${armyModels(a)} models · at sea</span>
                    <span class="wm-dim">${esc(armySummary(a))}</span></div>`,
              )
              .join('')}`
          : mine && !sailing
            ? `<div class="wm-hint">Empty. She is an escort until somebody goes aboard.</div>`
            : ''
      }
      ${acts.length ? `<div class="wm-sub">Orders</div><div class="wm-acts">${acts.join('')}</div>` : ''}
      ${courses ? `<div class="wm-sub">Stand out for</div><div class="wm-acts">${courses}</div>` : ''}
      ${loadable ? `<div class="wm-sub">Take aboard</div><div class="wm-acts">${loadable}</div>` : ''}
      ${!mine ? `<div class="wm-hint">Not yours to command.</div>` : ''}`;
  }

  /**
   * The markets: the graph, your factors, and the two orders you can give them.
   *
   * Everything here is read, never derived. `controlIn`, `nodeIncome`,
   * `nodeLeader`, `upstreamOf`/`downstreamOf` and the node's own `outflow` are
   * what the simulation used to move the money last month, and `postReason` is
   * the sentence a refused button is greyed with. The one judgement on the panel
   * — where a factor would earn most — is `tradeOptions`, which is *literally*
   * the ranking the AI council uses, so the hint and the opponent cannot
   * disagree about what a good posting is.
   */
  private tradePanel(c: CampaignState): string {
    const nodes = c.trade?.nodes ?? [];
    if (nodes.length === 0) return `<div class="wm-hint">This realm has no markets.</div>`;
    const me = c.playerNation;
    const mine = merchantsOf(c, me);
    const cap = merchantCap(c, me);
    const spare = freeMerchants(c, me);

    let held = 0;
    let led = 0;
    for (let k = 0; k < nodes.length; k++) {
      if (controlIn(c, k, me) < 0.02) continue;
      held++;
      if (nodeLeader(c, k).nation === me) led++;
    }

    /**
     * A factor on the road is doing nothing at all, and the panel has to say so
     * in those words. `travel` is the whole reason a merchant is scarce rather
     * than a radio button: moving one costs two or three months of whatever it
     * was doing, so a player who cannot see the months does not understand why
     * they should not keep re-posting.
     */
    const factorRow = (m: Merchant): string => {
      const share = controlIn(c, m.node, me);
      const earning = nodeIncome(c, m.node, me);
      return `<div class="wm-factor${m.travel > 0 ? ' travelling' : ''}"
          data-act="market" data-node="${m.node}"${WorldMap.tip([
        `${MISSION_NAMES[m.mission] ?? 'posted'} in ${nodeName(c, m.node)}`,
        m.mission === MerchantMission.Steer && m.toward >= 0
          ? `Pushing what leaves toward ${nodeName(c, m.toward)}, and draining this market to do it.`
          : 'Keeping value in this market, and taking your share of it.',
        m.travel > 0
          ? `Still on the road — ${months(m.travel)} yet, and doing nothing at all until he arrives.`
          : `You hold ${Math.round(share * 100)}% here and took ${earning.toFixed(1)}g last month.`,
        'Click for the market.',
      ])}>
        <b>${esc(nodeName(c, m.node))}</b>
        <span>${esc(MISSION_NAMES[m.mission] ?? 'posted')}${
          m.mission === MerchantMission.Steer && m.toward >= 0
            ? ` → ${esc(nodeName(c, m.toward))}`
            : ''
        }</span>
        <span class="${m.travel > 0 ? 'wm-onroad' : 'wm-dim'}">${
          m.travel > 0
            ? `on the road — ${months(m.travel)} yet, idle`
            : `${Math.round(share * 100)}% control · ${earning.toFixed(1)}g a month`
        }</span>
      </div>`;
    };

    const advice = tradeOptions(c, me)
      .filter((o) => canPost(c, me, o.node, o.mission, o.toward))
      .slice(0, 4)
      .map((o) => {
        const away = travelMonths(c, me, o.node);
        const there = merchantAt(c, me, o.node);
        return `<span class="wm-btn" data-act="${o.mission === MerchantMission.Steer ? 'steer' : 'collect'}"
            data-node="${o.node}" data-toward="${o.toward}"${WorldMap.tip([
          `${o.mission === MerchantMission.Steer ? `Steer ${nodeName(c, o.node)} toward ${nodeName(c, o.toward)}` : `Collect in ${nodeName(c, o.node)}`}.`,
          `The council reckons it worth about ${o.gain.toFixed(1)}g a month. This is the same ranking every other crown's council uses.`,
          there
            ? 'You already have a factor there, so retasking him is free.'
            : `${months(away)} on the road before he is in place, doing nothing on the way.`,
        ])}>${esc(nodeName(c, o.node))} <i>${
          o.mission === MerchantMission.Steer ? `steer → ${esc(nodeName(c, o.toward))}` : 'collect'
        } · +${o.gain.toFixed(1)}g${there ? '' : ` · ${months(away)} out`}</i></span>`;
      })
      .join('');

    const list = nodes
      .map((node) => {
        const share = controlIn(c, node.id, me);
        const lead = nodeLeader(c, node.id);
        const st = c.trade.states[node.id];
        const there = merchantAt(c, me, node.id);
        return { node, share, lead, st, there };
      })
      .sort((a, b) => b.share - a.share || (b.st?.value ?? 0) - (a.st?.value ?? 0))
      .map(
        (r) =>
          `<div class="wm-noderow${r.share >= 0.02 ? ' held' : ''}${
            this.focusNode === r.node.id ? ' sel' : ''
          }" data-act="market" data-node="${r.node.id}"${WorldMap.tip([
            `${nodeName(c, r.node.id)} — ${(r.st?.value ?? 0).toFixed(1)}g passes through a month`,
            r.lead.nation >= 0
              ? `${nationName(c, r.lead.nation)} leads it with ${Math.round(r.lead.share * 100)}%`
              : 'Nobody has a hold on it',
            r.there ? `Your factor is ${MISSION_NAMES[r.there.mission] ?? 'posted'} there.` : '',
          ])}>
            <b>${esc(nodeName(c, r.node.id))}${r.there ? '<i class="wm-fmark">✦</i>' : ''}</b>
            <span>${(r.st?.value ?? 0).toFixed(1)}g through · ${
              r.lead.nation >= 0 ? esc(nationName(c, r.lead.nation)) : 'nobody'
            } leads</span>
            <span class="${r.share >= 0.02 ? 'wm-yours' : 'wm-dim'}">${Math.round(
              r.share * 100,
            )}% yours · ${nodeIncome(c, r.node.id, me).toFixed(1)}g</span>
          </div>`,
      )
      .join('');

    return `
      <div class="wm-title small">The markets</div>
      <div class="wm-creed">A third of every province's gold goes to the market its ground trades
        through, and comes back only through control of that market — which may be somebody else's.</div>
      <div class="wm-stat"${WorldMap.tip([
        'What the markets paid you at the last council.',
        `The whole board's trade is worth ${realmTradeValue(c).toFixed(1)}g a month.`,
      ])}><span>Takings</span><b class="${held > 0 ? 'own' : ''}">${tradeIncomeOf(c, me).toFixed(
        1,
      )}g <i class="wm-dim">of ${realmTradeValue(c).toFixed(0)}g on the board</i></b></div>
      <div class="wm-stat"><span>Markets</span><b>${held} held · ${led} led · ${nodes.length} on the board</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `Two to begin with, and one more for every ${7} provinces you hold, never past ${6}.`,
        'This is the scarce thing in the whole trade layer. Everything else is arithmetic.',
      ])}><span>Factors</span><b class="${spare > 0 ? 'own' : ''}">${mine.length} / ${cap}${
        spare > 0 ? ` · ${spare} spare` : ' · none spare'
      }</b></div>
      ${this.focusNode >= 0 ? this.marketCard(c, this.focusNode) : ''}
      <div class="wm-sub">Your factors</div>
      ${mine.length ? mine.map(factorRow).join('') : '<div class="wm-hint">Every factor is at home and idle. They earn nothing there.</div>'}
      ${
        advice
          ? `<div class="wm-sub"${WorldMap.tip([
              'Ranked by what the council reckons each posting worth, in gold a month.',
              'This is the same judgement every rival crown uses, not a second opinion written for the player.',
            ])}>Where one would earn most</div><div class="wm-acts">${advice}</div>`
          : ''
      }
      ${this.orderBook(c)}
      <div class="wm-sub">Every market</div>
      ${list}`;
  }

  /**
   * The realm's order book: what is for sale, what is wanted, and your own hand.
   *
   * Three rules of the simulation's are on screen here and none of them is
   * re-derived. **Reach** is `bookView`, which asks `nationReach` and walks the
   * combined graph *once* for the whole section — `capitalDistance` is a
   * breadth-first search per call and this panel repaints whenever the realm
   * moves, so a per-row call would be a search per row per frame. **The sort**
   * is `matchMarket`'s, tie-break included, so the ask this panel calls the
   * cheapest is the one the council will actually fill first. **The refusal**
   * is `postRefusal`, and the greyed button carries its sentence verbatim
   * rather than a paraphrase — the standing pattern on this screen, and the
   * only way the button and the reason cannot drift apart.
   *
   * Everything the player does here leaves as a command. The panel writes
   * nothing.
   */
  private orderBook(c: CampaignState): string {
    const me = c.playerNation;
    const view = bookView(c, me);
    const sides = MARKET_KEYS.map((k) => bookFor(c, view, k));
    const orders = !c.pending && !c.over;
    const held = c.nations[me].treasury;
    const mine = ordersOf(c, me);

    /** One line of your own hand, with the way out of it. */
    const orderRow = (o: MarketOrder): string => {
      const sell = o.kind === OrderKind.Sell;
      const age = Math.max(0, c.turn - o.placedTurn);
      const left = Math.max(0, ORDER_TTL_MONTHS - age);
      const worth = (o.amount * o.price) / 10;
      return `<div class="wm-order${sell ? ' sell' : ''}"${WorldMap.tip([
        sell
          ? `${o.amount} ${RESOURCE_LABELS[o.key]} on the table at ${goldPrice(o.price)} each.`
          : `A standing bid for ${o.amount} ${RESOURCE_LABELS[o.key]} at up to ${goldPrice(o.price)} each.`,
        sell
          ? 'The goods left the treasury when the order went up. They come home if you take it back, or when it lapses.'
          : 'No gold is set aside. A bid pays only as it fills, and only as far as the treasury stretches that month.',
        'The book is settled once a month, at the council.',
        `It lapses after ${months(ORDER_TTL_MONTHS)} on the book.`,
      ])}>
        <b>${sell ? 'Selling' : 'Wanting'} ${o.amount} ${esc(RESOURCE_LABELS[o.key])}
          <i>${goldPrice(o.price)} each</i></b>
        <span>${
          sell
            ? `${worth.toFixed(1)}g if it all sells · the goods are already out of the treasury`
            : `${worth.toFixed(1)}g if it all fills · paid a fill at a time`
        }</span>
        <span class="wm-dim">${age === 0 ? 'posted this month' : `posted ${months(age)} ago`} · ${
          left === 0 ? 'lapses at this council' : `lapses in ${months(left)}`
        }</span>
        ${
          orders
            ? `<div class="wm-acts"><span class="wm-btn ghost" data-act="mkcancel" data-order="${o.id}"${WorldMap.tip([
                sell
                  ? 'Off the book, and the goods back into the treasury in full.'
                  : 'Off the book. Nothing was ever set aside for it.',
              ])}>${sell ? 'Take them back' : 'Withdraw it'}</span></div>`
            : ''
        }
      </div>`;
    };

    // ── The visible book ────────────────────────────────────────────────────
    // Only what a crown you may actually deal with has posted. What the rest of
    // the realm is offering is counted, never listed: an ask you cannot reach
    // is not a price, it is an argument for a navy, and it is made as one under
    // the reach figure rather than as a row you would try to click.
    const SHOWN_PER_SIDE = 3;
    let beyondReach = 0;
    let beyondWar = 0;
    const rowsFor = (list: MarketOrder[], ask: boolean): string =>
      list
        .slice(0, SHOWN_PER_SIDE)
        .map((o) => {
          const own = o.nation === me;
          const p = view.partners[o.nation];
          return `<div class="wm-bookrow${ask ? ' ask' : ' bid'}${own ? ' mine' : ''}"${
            own ? '' : ` data-act="mkmatch" data-order="${o.id}"`
          }${WorldMap.tip([
            own
              ? `Your own ${ask ? 'ask' : 'bid'}.`
              : ask
                ? `${nationName(c, o.nation)} will sell ${o.amount} ${RESOURCE_LABELS[o.key]} at ${goldPrice(o.price)} each.`
                : `${nationName(c, o.nation)} wants ${o.amount} ${RESOURCE_LABELS[o.key]} and will pay up to ${goldPrice(o.price)} each.`,
            own ? '' : `Their seat is ${p?.hops ?? -1} hops from yours; your merchants cover ${view.reach}.`,
            !own && ask
              ? 'Every trade settles at the seller’s price, so the cheapest ask is the one a bid takes first.'
              : '',
            own ? '' : 'Click to set the form below against this order.',
          ])}>
            <span class="wm-bookside">${ask ? 'asks' : 'bids'}</span>
            <b>${goldPrice(o.price)}</b>
            <span class="wm-bookamt">${o.amount}</span>
            <span class="wm-dim">${own ? 'you' : esc(nationName(c, o.nation))}</span>
          </div>`;
        })
        .join('') +
      (list.length > SHOWN_PER_SIDE
        ? `<div class="wm-bookmore">and ${list.length - SHOWN_PER_SIDE} more ${ask ? 'asks' : 'bids'}</div>`
        : '');

    const blocks = MARKET_KEYS.map((k, i) => {
      const s = sides[i];
      beyondReach += s.beyondReach;
      beyondWar += s.beyondWar;
      if (s.asks.length === 0 && s.bids.length === 0) return '';
      const best = marketPrice(s);
      return `<div class="wm-bookgood">
        <b>${esc(RESOURCE_LABELS[k])}<i>${
          best.ask > 0 ? `cheapest ${goldPrice(best.ask)}` : 'none for sale'
        }${best.bid > 0 ? ` · best bid ${goldPrice(best.bid)}` : ''}</i></b>
        ${rowsFor(s.asks, true)}
        ${rowsFor(s.bids, false)}
      </div>`;
    }).join('');

    // ── The form ────────────────────────────────────────────────────────────
    const d = this.draft;
    const sell = d.kind === OrderKind.Sell;
    const label = RESOURCE_LABELS[d.key];
    const why = postRefusal(c, me, d.kind, d.key, d.amount, d.price);
    const total = (d.amount * d.price) / 10;
    const step = (act: string, delta: number, text: string, tip: string): string =>
      `<span class="wm-btn tiny" data-act="${act}" data-d="${delta}"${WorldMap.tip([tip])}>${text}</span>`;

    const goods = MARKET_KEYS.map((k, i) => {
      const s = sides[i];
      const best = marketPrice(s);
      return `<span class="wm-good${k === d.key ? ' on' : ''}" data-act="mkgood" data-key="${k}"${WorldMap.tip([
        `${RESOURCE_LABELS[k]} — you hold ${Math.floor(held[k])}.`,
        best.ask > 0
          ? `The cheapest ask on the book is ${goldPrice(best.ask)}.`
          : 'Nobody within reach is selling any.',
        best.bid > 0 ? `The best bid is ${goldPrice(best.bid)}.` : 'Nobody within reach is bidding.',
        `The crowns price it around ${goldPrice(MARKET_PRICES[k] ?? 0)}.`,
      ])}>${esc(RESOURCE_LABELS[k])}<i>${Math.floor(held[k])}</i></span>`;
    }).join('');

    const form = `
      <div class="wm-acts">
        <span class="wm-btn${sell ? ' on' : ''}" data-act="mkside" data-kind="${OrderKind.Sell}"${WorldMap.tip([
          'Put goods on the table at a price of your choosing.',
          'They leave the treasury the moment the order goes up — that is what makes an ask a real offer rather than a promise.',
        ])}>Sell</span>
        <span class="wm-btn${sell ? '' : ' on'}" data-act="mkside" data-kind="${OrderKind.Buy}"${WorldMap.tip([
          'A standing bid: the most you will pay a unit.',
          'No gold is set aside. It fills at the council, at the seller’s price, and only as far as the treasury stretches.',
        ])}>Buy</span>
      </div>
      <div class="wm-goods">${goods}</div>
      <div class="wm-step"${WorldMap.tip([
        sell
          ? `You hold ${Math.floor(held[d.key])} ${label}.`
          : 'How many units you want. A bid fills a unit at a time, cheapest ask first.',
        `No single order carries more than ${MAX_ORDER_UNITS} units.`,
      ])}>
        ${step('mkamt', -10, '−10', 'Ten fewer units.')}
        ${step('mkamt', -1, '−1', 'One fewer unit.')}
        <b>${d.amount}<i>${esc(label)}</i></b>
        ${step('mkamt', 1, '+1', 'One more unit.')}
        ${step('mkamt', 10, '+10', 'Ten more units.')}
        ${
          sell
            ? `<span class="wm-btn tiny ghost" data-act="mkamt" data-all="1"${WorldMap.tip([
                `Everything the treasury holds: ${Math.floor(held[d.key])} ${label}.`,
              ])}>All</span>`
            : ''
        }
      </div>
      <div class="wm-step"${WorldMap.tip([
        sell
          ? 'Your asking price a unit. A buyer takes the cheapest ask first, and pays exactly what you asked.'
          : 'The most you will pay a unit. Bidding high buys you priority, not a worse price — you still pay the seller’s.',
        `Between ${goldPrice(MIN_PRICE_TENTHS)} and ${goldPrice(MAX_PRICE_TENTHS)}.`,
      ])}>
        ${step('mkprice', -10, '−1.0', 'A gold less a unit.')}
        ${step('mkprice', -1, '−0.1', 'A tenth less a unit.')}
        <b>${goldPrice(d.price)}<i>a unit</i></b>
        ${step('mkprice', 1, '+0.1', 'A tenth more a unit.')}
        ${step('mkprice', 10, '+1.0', 'A gold more a unit.')}
        <span class="wm-btn tiny ghost" data-act="mkprice" data-seed="1"${WorldMap.tip([
          sell
            ? 'Back to what the book would pay you for it today — the best bid within reach, or what the crowns price it at when nobody is bidding.'
            : 'Back to what the book is asking today — the cheapest ask within reach, or what the crowns price it at when nobody is selling.',
        ])}>Market</span>
      </div>
      <div class="wm-acts">
        <span class="wm-btn${why ? ' off' : ''}" data-act="mkpost"${WorldMap.tip([
          sell
            ? `Put ${d.amount} ${label} on the book at ${goldPrice(d.price)} each — ${total.toFixed(1)}g if it all sells.`
            : `Bid for ${d.amount} ${label} at up to ${goldPrice(d.price)} each — ${total.toFixed(1)}g if it all fills.`,
          sell
            ? 'The goods leave the treasury now, and come home only if you take the order back or it lapses.'
            : 'Nothing leaves the treasury until a match lands at the council.',
          `You may keep ${MAX_LIVE_ORDERS} orders on the book at once; you have ${mine.length}.`,
          why,
        ])}>${sell ? 'Put them on the book' : 'Post the bid'}
          <i>${esc(why || `${d.amount} ${label} · ${goldPrice(d.price)} each · ${total.toFixed(1)}g`)}</i></span>
      </div>`;

    const closed = view.nearestClosed;
    return `
      <div class="wm-sub"${WorldMap.tip([
        'The realm’s book. Any crown may put goods up at a price of its choosing and any crown may bid.',
        'It is settled once a month at the council: the cheapest ask first, and every trade at the seller’s price.',
      ])}>The book</div>
      <div class="wm-stat"${WorldMap.tip([
        `Your merchants cover ${view.reach} hops between capitals, on the roads and the lanes together.`,
        `${BASE_REACH_HOPS} of that is what they manage on their own; the rest is your ${view.hulls} hull${view.hulls === 1 ? '' : 's'}, at one hop every ${HULLS_PER_REACH}.`,
        'A pair may trade if either side reaches the other, so a crown with a great navy comes to you.',
      ])}><span>Merchants reach</span><b class="${view.hulls > 0 ? 'own' : ''}">${view.reach} hops <i class="wm-dim">${view.hulls} hulls</i></b></div>
      <div class="wm-stat"${WorldMap.tip([
        'Crowns whose book you can deal on: alive, at peace with you, and within reach of one of you.',
        'A war closes a book outright, however close the capital is.',
      ])}><span>Books open to you</span><b class="${view.open > 0 ? 'own' : ''}">${view.open} of ${view.crowns} crowns</b></div>
      ${
        closed
          ? `<div class="wm-hint">${esc(nationName(c, closed.nation))} keeps a book ${closed.hops} hops off — ${closed.hulls} more hull${
              closed.hulls === 1 ? '' : 's'
            } and your merchants would reach it.</div>`
          : ''
      }
      ${
        beyondReach + beyondWar > 0
          ? `<div class="wm-hint">${beyondReach > 0 ? `${beyondReach} order${beyondReach === 1 ? '' : 's'} stand${beyondReach === 1 ? 's' : ''} on books too far off to deal on` : ''}${
              beyondReach > 0 && beyondWar > 0 ? ', and ' : ''
            }${beyondWar > 0 ? `${beyondWar} behind a war` : ''}.</div>`
          : ''
      }
      <div class="wm-sub tight">Your orders</div>
      ${
        mine.length
          ? mine.map(orderRow).join('')
          : '<div class="wm-hint">Nothing of yours is on the book.</div>'
      }
      <div class="wm-sub tight">Post an order</div>
      ${
        orders
          ? form
          : '<div class="wm-hint">The council is sitting. The book is settled with it, and takes no new orders until it rises.</div>'
      }
      <div class="wm-sub tight"${WorldMap.tip([
        'Only what crowns you may actually deal with have posted.',
        'Asks are listed cheapest first, which is the order the council fills them in.',
      ])}>On the book</div>
      ${blocks || '<div class="wm-hint">Nobody within reach is offering anything, or wanting it.</div>'}`;
  }

  /**
   * What to start a price at, in tenths.
   *
   * The price that would fill *today*: an ask has to be met by a bid, so a sell
   * seeds from the best bid on the book, and a bid from the cheapest ask. Where
   * that side is empty there is no market price to read and the crowns' own
   * anchor (`MARKET_PRICES`, which is what the AI councils price around) is the
   * honest answer — a starting point, never a rule; the player may ask
   * anything between the two bounds.
   *
   * This walks the graph, so it is called on a *click* and never on a render.
   */
  private seedPrice(c: CampaignState, key: ResourceKey, kind: number): number {
    const best = marketPrice(bookFor(c, bookView(c, c.playerNation), key));
    const want = kind === OrderKind.Sell ? best.bid : best.ask;
    return Math.max(
      MIN_PRICE_TENTHS,
      Math.min(MAX_PRICE_TENTHS, want || MARKET_PRICES[key] || MIN_PRICE_TENTHS),
    );
  }

  /**
   * Hold the draft inside the book's own bounds.
   *
   * Not a second copy of the rules: `postRefusal` is still what decides whether
   * an order may be posted, and it is what greys the button. This only keeps
   * the *form* from showing a number the book could never carry — a negative
   * amount off the bottom of a stepper, a price of nought.
   */
  private clampDraft(): void {
    const d = this.draft;
    if (!MARKET_KEYS.includes(d.key)) d.key = MARKET_KEYS[0];
    d.amount = Math.max(1, Math.min(MAX_ORDER_UNITS, Math.round(d.amount || 0)));
    d.price = Math.max(MIN_PRICE_TENTHS, Math.min(MAX_PRICE_TENTHS, Math.round(d.price || 0)));
  }

  /**
   * One market, and — the point of the card — *why* it is leaking.
   *
   * `collectShare` and `steerShare` exist in the node's state specifically so a
   * panel can answer that. A market that retains half of what passes through it
   * is either one nobody is working, or one somebody is actively dragging value
   * out of, and those are opposite problems with opposite answers. The second is
   * an act of war and reads as one here: it is named, the crowns doing it are
   * named, and it is the only red on the card.
   */
  private marketCard(c: CampaignState, id: number): string {
    const node = tradeNode(c, id);
    if (!node) return '';
    const me = c.playerNation;
    const st = c.trade.states[id];
    const lead = nodeLeader(c, id);
    const share = controlIn(c, id, me);
    const there = merchantAt(c, me, id);
    const away = travelMonths(c, me, id);
    const orders = !c.pending && !c.over;

    // Who is dragging value out of this market. Read off the merchants standing
    // in it, not inferred from the shares — the state says exactly who.
    const steerers = (c.trade.merchants ?? []).filter(
      (m) => m.node === id && m.mission === MerchantMission.Steer && m.travel <= 0,
    );
    const drainers = [...new Set(steerers.map((m) => m.nation))]
      .filter((nt) => nt !== me)
      .map((nt) => nationName(c, nt));

    const holders = c.nations
      .map((_, nt) => ({ nt, v: controlIn(c, id, nt) }))
      .filter((r) => r.v >= 0.02)
      .sort((a, b) => b.v - a.v)
      .slice(0, 5)
      .map(
        (r) =>
          `<div class="wm-stat"><span><i class="wm-swatch" style="background:${hex(
            nationAccent(r.nt),
          )}"></i>${esc(nationName(c, r.nt))}${r.nt === me ? ' — you' : ''}</span><b${
            r.nt === me ? ' class="own"' : ''
          }>${Math.round(r.v * 100)}% · ${nodeIncome(c, id, r.nt).toFixed(1)}g</b></div>`,
      )
      .join('');

    const roadsOut = node.outgoing
      .map((to, i) => {
        const gold = st?.outflow[i] ?? 0;
        return `<div class="wm-stat wm-click" data-act="market" data-node="${to}"${WorldMap.tip([
          `${gold.toFixed(1)} gold went from ${nodeName(c, id)} to ${nodeName(c, to)} last month.`,
          'That is what actually moved, not what the road is worth.',
          'Click to look at the far end.',
        ])}><span>→ ${esc(nodeName(c, to))}</span><b>${gold.toFixed(1)}g</b></div>`;
      })
      .join('');

    const roadsIn = upstreamOf(c, id)
      .map(
        (from) =>
          `<div class="wm-stat wm-click" data-act="market" data-node="${from}"><span>← ${esc(
            nodeName(c, from),
          )}</span><b>${(c.trade.states[from]?.outflow[
            tradeNode(c, from)?.outgoing.indexOf(id) ?? -1
          ] ?? 0).toFixed(1)}g</b></div>`,
      )
      .join('');

    const acts: string[] = [];
    if (orders) {
      const collectWhy = postReason(c, me, id, MerchantMission.Collect, -1);
      const already = there && there.mission === MerchantMission.Collect;
      acts.push(
        `<span class="wm-btn${collectWhy || already ? ' off' : ''}" data-act="collect" data-node="${id}"${WorldMap.tip([
          `Send a factor to collect in ${nodeName(c, id)}.`,
          'More of what passes through stays here, and you take your control share of it.',
          already ? 'He is already collecting here.' : '',
          there
            ? 'Retasking a factor already in this market costs nothing.'
            : `${months(away)} on the road first, and he earns nothing on the way.`,
          collectWhy,
        ])}>Collect here${there ? '' : ` <i>${months(away)} out</i>`}</span>`,
      );
      for (const to of downstreamOf(c, id)) {
        const why = postReason(c, me, id, MerchantMission.Steer, to);
        const same = there && there.mission === MerchantMission.Steer && there.toward === to;
        acts.push(
          `<span class="wm-btn${why || same ? ' off' : ''}" data-act="steer" data-node="${id}"
             data-toward="${to}"${WorldMap.tip([
            `Take nothing here. Push what leaves toward ${nodeName(c, to)}.`,
            'It drains this market as well as aiming it — which robs whoever is collecting here. That is a weapon, and against a crown you are at peace with it is only a slower way to make money.',
            `You hold ${Math.round(controlIn(c, to, me) * 100)}% of ${nodeName(c, to)}, which is where the money would land.`,
            same ? 'He is already steering that way.' : '',
            why,
          ])}>Steer → ${esc(nodeName(c, to))}</span>`,
        );
      }
      if (there) {
        acts.push(
          `<span class="wm-btn ghost" data-act="recall" data-node="${id}"${WorldMap.tip([
            'Bring him home. He is available again at once, and in place somewhere else only after the road.',
          ])}>Recall the factor</span>`,
        );
      }
    }

    const drained = (st?.steerShare ?? 0) > 0.02;
    const terminal = isTerminal(c, id);
    return `<div class="wm-market${drained ? ' drained' : ''}">
      <b>${esc(nodeName(c, id))}<i class="wm-nodekind">${
        terminal ? 'terminal market' : node.depth === 0 ? 'on salt water' : `${node.depth} from the sea`
      }</i></b>
      <div class="wm-stat"${WorldMap.tip([
        terminal
          ? 'Nothing flows out of a terminal. Everything that reaches it is taken here, which is what makes one worth a war.'
          : node.depth === 0
            ? 'It stands on salt water. Value still runs from here, but by lane rather than downhill — over water it runs from the lesser market to the greater.'
            : `Value runs downhill from here, ${node.depth} step${node.depth === 1 ? '' : 's'} to salt water.`,
        `${node.provinces.length} provinces trade through it.`,
        `${(st?.local ?? 0).toFixed(1)}g of that is grown in its own provinces; the rest flowed in from upstream.`,
      ])}><span>Through it</span><b>${(st?.value ?? 0).toFixed(1)}g a month</b></div>
      <div class="wm-stat"${WorldMap.tip([
        'The share of everything passing through that is taken in this market rather than passed on down the roads.',
        `Collectors hold ${Math.round((st?.collectShare ?? 0) * 100)}% between them, which is what keeps value here.`,
        `Steerers hold ${Math.round((st?.steerShare ?? 0) * 100)}%, which is what drags it out.`,
      ])}><span>Taken here</span><b class="${drained ? 'bad' : ''}">${Math.round(
        (st?.retained ?? 0) * 100,
      )}%</b></div>
      ${meter(st?.retained ?? 0, drained ? 'bad' : '')}
      ${
        drained
          ? `<div class="wm-drain">${
              drainers.length
                ? `${esc(drainers.join(', '))} ${drainers.length === 1 ? 'is' : 'are'} steering value out of this market — ${Math.round(
                    (st?.steerShare ?? 0) * 100,
                  )}% of it pushed down the roads they choose, and taken out of the pockets of whoever collects here.`
                : `Somebody is steering ${Math.round((st?.steerShare ?? 0) * 100)}% of this market's value out of it and down a road of their choosing.`
            }</div>`
          : ''
      }
      <div class="wm-stat"><span>You hold</span><b class="${share >= 0.02 ? 'own' : ''}">${Math.round(
        share * 100,
      )}% · ${nodeIncome(c, id, me).toFixed(1)}g a month</b></div>
      <div class="wm-stat"><span>Led by</span><b${lead.nation === me ? ' class="own"' : ''}>${esc(
        lead.nation >= 0 ? nationName(c, lead.nation) : 'nobody',
      )}${lead.nation >= 0 ? ` · ${Math.round(lead.share * 100)}%` : ''}</b></div>
      ${holders ? `<div class="wm-sub tight">Who holds it</div>${holders}` : ''}
      ${roadsIn ? `<div class="wm-sub tight">Roads in</div>${roadsIn}` : ''}
      ${
        roadsOut
          ? `<div class="wm-sub tight"${WorldMap.tip([
              'What actually went down each road last month, in gold.',
            ])}>Roads out</div>${roadsOut}`
          : `<div class="wm-sub tight">Roads out</div><div class="wm-hint">None. This is where the money stops.</div>`
      }
      ${acts.length ? `<div class="wm-sub tight">Orders</div><div class="wm-acts">${acts.join('')}</div>` : ''}
      <div class="wm-acts"><span class="wm-btn ghost" data-act="markets">All markets</span>
        <span class="wm-btn ghost" data-act="lookprov" data-prov2="${node.seat}">Show me the fair</span></div>
    </div>`;
  }

  /**
   * A warning, in full.
   *
   * `c.warnings` carries the kind, the crown, both ends and an *exact* strength,
   * because the strategic layer does not lie to the player about a thing it has
   * already decided. All of it goes on screen — a warning that has been rounded
   * off for presentation is a warning that cannot be planned against.
   */
  private warnPanel(c: CampaignState): string {
    const p = this.focusProv;
    const all = warningsFor(c, p);
    if (all.length === 0) return this.provincePanel(c);
    const state = c.provinces[p];
    const standing = armiesStandingIn(c, p).filter((a) => a.owner === c.playerNation);
    const hold = standing.reduce((n, a) => n + armyPoints(a), 0) + (state.owner === c.playerNation ? state.garrison * defenceMultiplier(c, p) : 0);
    const worst = all.reduce((n, w) => Math.max(n, w.strength), 0);

    return `
      <div class="wm-title small">Riders from ${esc(provinceName(p))}
        <i class="wm-look" data-act="lookprov" data-prov2="${p}" title="Show me">⌖</i></div>
      <div class="wm-creed">What your scouts have actually seen. The numbers are exact — nothing here is a guess.</div>
      ${all.map((w) => this.warnCard(c, w)).join('')}
      <div class="wm-sub">Against it</div>
      <div class="wm-stat"${WorldMap.tip([
        'Your hosts standing here, plus the garrison behind whatever walls there are.',
        'A host on a road defends nothing.',
      ])}><span>You hold</span><b class="${hold < worst ? 'bad' : ''}">${Math.round(hold)} pts</b></div>
      <div class="wm-stat"><span>Worst of them</span><b>${worst} pts</b></div>
      <div class="wm-acts"><span class="wm-btn" data-act="tab" data-tab="ground">The ground itself</span></div>`;
  }

  /**
   * The host a warning is about — or nothing, when the warning is not about one.
   *
   * `MarchWarning.armyId` is **not always an army id**. It is an id into
   * whichever list `kind` names, and the sea layer adds a `Landing` kind whose
   * id indexes fleets. Looking it up in `c.armies` regardless would not fail
   * loudly: it would find whichever host happens to hold that number, name the
   * wrong army in the card, and offer intercept buttons that order a chase
   * against something else entirely.
   *
   * So this is an allow-list of the kinds that genuinely carry an army, not a
   * deny-list of the ones that do not. A kind nobody has written yet returns
   * nothing and the card simply omits the host, which is the safe direction to
   * be wrong in; a deny-list would silently start mis-resolving the moment
   * somebody added a fourth id space.
   */
  private static warningArmyOf(c: CampaignState, w: MarchWarning): Army | undefined {
    if (w.kind !== WarnKind.March && w.kind !== WarnKind.Massing) return undefined;
    return w.armyId >= 0 ? findArmy(c, w.armyId) : undefined;
  }

  private warnCard(c: CampaignState, w: MarchWarning): string {
    const kind =
      w.kind === WarnKind.March ? 'On the march'
      : w.kind === WarnKind.Massing ? 'Massing'
      : w.kind === WarnKind.Landing ? 'A landing, by sea' : 'Brigands';
    const convoy = w.kind === WarnKind.Landing ? findFleet(c, w.armyId) : undefined;
    const who = w.nation === BANDIT_NATION ? 'Nobody’s men' : nationName(c, w.nation);
    const known = Math.max(0, c.turn - w.sinceTurn);
    const army = WorldMap.warningArmyOf(c, w);
    const chasers = army ? armiesOf(c, c.playerNation).filter((k) => canIntercept(c, k, army)) : [];
    return `<div class="wm-warncard ${
      w.kind === WarnKind.March
        ? 'march'
        : w.kind === WarnKind.Massing
          ? 'massing'
          : w.kind === WarnKind.Landing
            ? 'landing'
            : 'camp'
    }">
      <b>${kind} · ${esc(who)}</b>
      <div class="wm-stat"><span>Strength</span><b>${w.strength} pts</b></div>
      ${
        w.kind === WarnKind.Bandits
          ? `<div class="wm-stat"><span>Sitting on</span><b>${esc(provinceName(w.from))}</b></div>`
          : `<div class="wm-stat"><span>From</span><b>${esc(provinceName(w.from))}</b></div>
             <div class="wm-stat"><span>To</span><b>${esc(provinceName(w.to))}</b></div>`
      }
      ${
        w.kind === WarnKind.Landing
          ? `<div class="wm-stat"${WorldMap.tip([
              'Men at sea, bound for a coast you hold. Every convoy is visible for its whole voyage.',
              'Nothing you have on land can touch them until they are ashore. A fleet of your own can.',
            ])}><span>Aboard</span><b>${esc(convoy ? convoy.name : 'a fleet')}${
              convoy ? ` · ${convoy.ships} hulls` : ''
            }</b></div>
            <div class="wm-stat"${WorldMap.tip([
              'Months of sea left. They can be met on the water until the last of them.',
            ])}><span>Makes landfall</span><b class="${w.turns <= 1 ? 'bad' : ''}">${esc(
              this.arrivalLine(w.turns),
            )}</b></div>`
          : ''
      }
      ${
        w.kind === WarnKind.March
          ? `<div class="wm-stat"><span>By</span><b>${esc(roadWord(c, w.from, w.to))}</b></div>
             <div class="wm-stat"${WorldMap.tip([
               'Months of realm time, the date it lands, and what that is in minutes at normal pace.',
               'Running the clock faster does not slow them down.',
             ])}><span>Arrives</span><b class="${w.turns <= 1 ? 'bad' : ''}">${esc(this.arrivalLine(w.turns))}</b></div>`
          : w.kind === WarnKind.Massing
            ? `<div class="wm-stat"${WorldMap.tip([
                'A host standing one march from ground you hold.',
                'It has no orders you can see yet — which is exactly when it is cheapest to answer.',
                atWar(c, c.playerNation, w.nation)
                  ? 'You are at war with them.'
                  : 'You are not at war with them. A host is a host; a herald is not something a picket knows about.',
              ])}><span>Standing</span><b>${atWar(c, c.playerNation, w.nation) ? 'at war with you' : 'at peace, for now'}</b></div>
               <div class="wm-stat"><span>Arrives</span><b>one march away</b></div>`
            : ''
      }
      <div class="wm-stat"${WorldMap.tip([
        'How long this has been in your warning list.',
        'Nothing can reach ground you hold without having stood here for a month first.',
      ])}><span>Known for</span><b>${known === 0 ? 'this month' : months(known)}</b></div>
      <div class="wm-acts">
        ${army ? `<span class="wm-btn" data-act="focusarmy" data-army2="${army.id}">${army.moveTo >= 0 ? 'Watch it march' : 'Look at the host'}</span>` : ''}
        ${convoy ? `<span class="wm-btn" data-act="focusfleet" data-fleet2="${convoy.id}">Watch the convoy</span>` : ''}
        ${w.kind === WarnKind.Bandits ? `<span class="wm-btn" data-act="focuscamp" data-prov2="${w.from}">The camp</span>` : ''}
        ${chasers
          .map(
            (k) =>
              `<span class="wm-btn" data-act="intercept" data-chase="${k.id}" data-quarry="${army?.id ?? -1}">Run it down with ${esc(k.name)}</span>`,
          )
          .join('')}
      </div>
    </div>`;
  }

  /** A camp, as one row inside the province panel. */
  private campRow(c: CampaignState, camp: BanditCamp): string {
    const scale = campYieldScale(c, camp.at);
    return `<div class="wm-warnrow camp" data-act="focuscamp" data-prov2="${camp.at}"
        ${WorldMap.tip([
          `${Math.round(camp.strength)} points of brigands, and growing every month.`,
          `The province pays ${Math.round(scale * 100)}% of what it should.`,
          camp.bounty > 0 ? `A bounty is out — ${months(camp.bounty)} of it left.` : 'No bounty out.',
          'Click for what it costs and what to do about it.',
        ])}>
        Brigands, ${Math.round(camp.strength)} strong — the province pays ${Math.round(scale * 100)}%${
          camp.bounty > 0 ? `, bounty out (${months(camp.bounty)})` : ''
        }
      </div>`;
  }

  /** A warning, as one row inside the province panel. */
  private warnRow(c: CampaignState, w: MarchWarning): string {
    const urgent = w.kind === WarnKind.March && w.turns <= 1;
    return `<div class="wm-warnrow${urgent ? ' urgent' : ''}" data-act="focuswarn" data-prov2="${w.to}"
        ${WorldMap.tip([this.warnLine(c, w), 'Click for the whole of it.'])}>
        ${esc(this.warnLine(c, w))}
      </div>`;
  }

  /**
   * Brigands: what they are worth, what they are costing, and the two ways to be
   * rid of them.
   */
  private campPanel(c: CampaignState): string {
    const camp = campAt(c, this.focusProv);
    if (!camp) return this.provincePanel(c);
    const p = camp.at;
    const def = provinceDef(p);
    const state = c.provinces[p];
    const mine = state.owner === c.playerNation;
    const scale = campYieldScale(c, p);
    const loot = campLoot(camp, CAMP_LOOT_GOLD, CAMP_LOOT_IRON);
    const me = c.nations[c.playerNation];
    const canPay = me.treasury.gold >= BOUNTY_GOLD;
    const sendable = armiesOf(c, c.playerNation).filter((a) => canOrderMarch(c, a, p));
    const standing = armiesStandingIn(c, p).filter((a) => a.owner === c.playerNation);

    let bounty: string;
    if (!mine) bounty = `<span class="wm-btn off">Post a bounty <i>not your ground</i></span>`;
    else if (camp.bounty > 0) {
      bounty = `<span class="wm-btn off">Post a bounty <i>one is already out · ${months(camp.bounty)} left</i></span>`;
    } else {
      bounty = `<span class="wm-btn${canPay ? '' : ' off'}" data-act="bounty" data-prov2="${p}"${WorldMap.tip([
        `${BOUNTY_GOLD} gold, and somebody goes into the hills for ${BOUNTY_TURNS} months.`,
        `It takes about ${Math.ceil(camp.strength / 33)} months off this camp — it grows while it is being hunted.`,
        'Slower and dearer than marching on it, and available when you have nobody to march.',
        canPay ? '' : 'You cannot pay it.',
      ])}>Post a bounty <i>${BOUNTY_GOLD}g · ${BOUNTY_TURNS} months</i></span>`;
    }

    return `
      <div class="wm-title small">The camp on ${esc(def.name)}
        <i class="wm-look" data-act="lookprov" data-prov2="${p}" title="Show me">⌖</i></div>
      <div class="wm-creed">Nobody's men, hostile to everyone, and taking their share for as long as they are left alone.</div>
      <div class="wm-stat"${WorldMap.tip([
        'What it fights with, and what it is worth clearing.',
        'It puts on strength every month it is not answered.',
      ])}><span>Strength</span><b>${Math.round(camp.strength)} pts</b></div>
      <div class="wm-stat"><span>Standing since</span><b>${months(Math.max(0, c.turn - camp.founded))}</b></div>
      <div class="wm-stat"${WorldMap.tip([
        `${provinceName(p)} pays ${Math.round(scale * 100)}% of what it should while they sit there.`,
        `That is ${Math.round(def.wealth * (1 - scale))} gold and ${Math.round(def.levy * (1 - scale))} levies a month, gone.`,
        'And they take men off the garrison every month besides.',
      ])}><span>The province pays</span><b class="bad">${Math.round(scale * 100)}% of ${def.wealth}g</b></div>
      <div class="wm-stat"><span>Costing you</span><b class="bad">−${Math.round(def.wealth * (1 - scale))}g · −${Math.round(def.levy * (1 - scale))} levies each month</b></div>
      <div class="wm-stat"${WorldMap.tip(['What is in the camp, for whoever clears it.'])}><span>Worth clearing</span><b>${loot.gold}g · ${loot.iron} iron</b></div>
      ${camp.bounty > 0 ? `<div class="wm-stat"><span>Bounty</span><b>out · ${months(camp.bounty)} left</b></div>` : ''}
      <div class="wm-stat"><span>Held by</span><b>${esc(state.owner >= 0 ? nationName(c, state.owner) : 'Nobody')}</b></div>
      <div class="wm-sub">Be rid of them</div>
      <div class="wm-acts">${bounty}</div>
      ${
        standing.length > 0
          ? `<div class="wm-hint">${esc(standing[0].name)} is standing on them already; the fight comes at the next council.</div>`
          : sendable.length > 0
            ? `<div class="wm-sub">Or send a host</div><div class="wm-acts">${sendable
                .map(
                  (a) =>
                    `<span class="wm-btn march" data-act="marcharmy" data-army2="${a.id}" data-to="${p}"${WorldMap.tip([
                      `${a.name}, ${Math.round(armyPoints(a))} pts, from ${provinceName(a.at)}.`,
                      `${months(marchTurns(c, a, p))} away.`,
                      mine ? 'On your own ground half the garrison comes out with it.' : '',
                    ])}>${esc(a.name)} <i>${months(marchTurns(c, a, p))} · ${Math.round(armyPoints(a))} pts</i></span>`,
                )
                .join('')}</div>`
            : `<div class="wm-hint">You have no host within a march of it.</div>`
      }`;
  }

  /**
   * Wars and envoys.
   *
   * `warView` returns the ledger *and* `availableTerms` — the exact list the
   * council chooses from — so the player and the AI are reading one table. That
   * is the only way an AI peace ever reads as fair, and it is why the terms are
   * printed with their war-score costs rather than as a set of buttons that
   * mysteriously do or do not work.
   */
  private warsPanel(c: CampaignState, wars: WarView[], offers: DiploOffer[]): string {
    const crowns = diplomaticView(c, c.playerNation);
    const live = !c.pending && !c.over;
    return `
      <div class="wm-title small">The Council</div>
      <div class="wm-creed">Every war you are in, what you could ask for today, and everything waiting on your word.</div>
      ${
        offers.length > 0
          ? `<div class="wm-sub">On your table</div>${offers.map((o) => this.offerCard(c, o, live)).join('')}`
          : `<div class="wm-sub">On your table</div><div class="wm-hint">Nothing waiting.</div>`
      }
      <div class="wm-sub">Wars</div>
      ${wars.length > 0 ? wars.map((w) => this.warCard(w, live)).join('') : '<div class="wm-hint">You are at peace with the whole realm.</div>'}
      <div class="wm-sub">The powers</div>
      ${crowns.map((r) => this.crownRow(c, r, wars, live)).join('')}`;
  }

  /**
   * An offer, and both answers.
   *
   * Silence is an answer here — `expireOffers` runs a lapsed call to arms
   * straight through `refuseCall` — so the panel says what it will cost to do
   * nothing rather than leaving the player to find out.
   */
  private offerCard(c: CampaignState, o: DiploOffer, live: boolean): string {
    const lapses = Math.max(0, o.expires - c.turn);
    const call = o.kind === OfferKind.CallToArms;
    return `<div class="wm-offer${call ? ' urgent' : ''}">
      <b>${esc(offerText(c, o))}</b>
      <span class="wm-dim">${lapses <= 0 ? 'It lapses at the next council.' : `Lapses in ${months(lapses)}.`}${
        call ? ' Saying nothing costs exactly what refusing costs: the alliance, and the realm’s opinion of you.' : ''
      }</span>
      ${
        live
          ? `<div class="wm-acts">
              <span class="wm-btn" data-act="answer" data-offer="${o.id}" data-yes="1">Accept</span>
              <span class="wm-btn ghost" data-act="answer" data-offer="${o.id}" data-yes="0">Refuse</span>
            </div>`
          : ''
      }
    </div>`;
  }

  private warCard(w: WarView, live: boolean): string {
    const open = this.openWar === w.warId;
    const winning = w.score >= 0;
    return `<div class="wm-war${winning ? ' up' : ' down'}">
      <b data-act="openwar" data-war="${w.warId}"${WorldMap.tip([
        `Against ${w.enemyName}, ${months(w.turns)} so far.`,
        `Declared as: ${CB_NAMES[w.cb] ?? 'no pretext'}.`,
        `${w.battlesWon} battles won, ${w.battlesLost} lost.`,
        `${w.provincesTaken} provinces taken, ${w.provincesLost} lost.`,
        'Click for the terms you could ask for today.',
      ])}>${esc(w.enemyName)}<i>${winning ? '+' : ''}${w.score}</i></b>
      <div class="wm-scorebar"><i class="${winning ? 'up' : 'down'}"
        style="width:${Math.min(50, Math.abs(w.score) / 2).toFixed(1)}%;${winning ? 'left:50%' : `left:${(50 - Math.min(50, Math.abs(w.score) / 2)).toFixed(1)}%`}"></i></div>
      <span class="wm-dim">${esc(CB_NAMES[w.cb] ?? 'No pretext')} · ${months(w.turns)} · battles ${w.battlesWon}–${w.battlesLost} · ground ${w.provincesTaken}–${w.provincesLost}</span>
      ${
        open
          ? `<div class="wm-acts">${w.options
              .map(
                (t) =>
                  `<span class="wm-btn${live ? '' : ' off'}" data-act="peace" data-war="${w.warId}"
                     data-term="${t.term}" data-prov2="${t.province}"${WorldMap.tip([
                       `${TERM_NAMES[t.term] ?? t.label}.`,
                       `${t.cost} of your ${Math.max(0, w.score)} war score.`,
                       'This is the same list the council chooses from.',
                       'They may still refuse; a refusal costs you nothing but the asking.',
                     ])}>${esc(t.label)} <i>${t.cost} score</i></span>`,
              )
              .join('')}</div>`
          : `<div class="wm-hint">${w.options.length} term${w.options.length === 1 ? '' : 's'} available — click the name.</div>`
      }
    </div>`;
  }

  /**
   * The diplomatic verbs for one crown, as buttons.
   *
   * Extracted from `crownRow` so the *province* panel can offer them too:
   * clicking somebody's ground and being told who holds it, and then having to
   * go find the same crown again under another tab to say anything to them, is
   * the round trip the reference genre never asks for. Same markup, same
   * delegated handlers, one authority on what is offered.
   */
  private crownActs(r: CrownView, wars: WarView[], live: boolean): string[] {
    const acts: string[] = [];
    const verb = (
      act: number,
      label: string,
      blocked: string,
      tipLines: string[],
    ): void => {
      acts.push(
        `<span class="wm-btn${blocked || !live ? ' off' : ''}" data-act="diplo" data-diplo="${act}"
           data-nation="${r.nation}"${WorldMap.tip([...tipLines, blocked])}>${label}</span>`,
      );
    };
    if (r.atWar) {
      const war = wars.find((w) => w.enemy === r.nation);
      if (war) {
        acts.push(
          `<span class="wm-btn" data-act="openwar" data-war="${war.warId}">Terms <i>${war.score >= 0 ? '+' : ''}${war.score}</i></span>`,
        );
      }
    } else {
      verb(DiploAction.ProposeAlliance, 'Alliance', r.allianceBlocked, [
        `They ally at +${20} opinion; they think ${r.theirOpinion} of you.`,
        'A marriage first is worth +20, which is usually the whole of the gap.',
      ]);
      verb(DiploAction.ProposeMarriage, 'Marriage', r.marriageBlocked, [
        'Nearly free, worth +20 opinion, and in twenty years an argument about a throne.',
      ]);
      const cb = r.casusBelli[0] ?? CasusBelli.None;
      acts.push(
        `<span class="wm-btn${r.warBlocked || !live ? ' off' : ''}" data-act="declare"
           data-nation="${r.nation}" data-cb="${cb}"${WorldMap.tip([
             `Declared as: ${CB_NAMES[cb] ?? 'no pretext'}.`,
             `Your reasons: ${r.casusBelli.map((k) => CB_NAMES[k]).join(', ')}.`,
             cb === CasusBelli.None ? 'No pretext at all. The whole realm will mark it.' : '',
             r.warBlocked,
           ])}>Declare war</span>`,
      );
    }
    if (r.allied) verb(DiploAction.BreakAlliance, 'Break the alliance', '', ['They will remember it, and so will everyone else.']);
    if (r.theirSubjection !== SubjectKind.None) {
      verb(DiploAction.AnnexSubject, 'Annex', r.annexBlocked, ['Its ground, its hosts and its own subjects come across.']);
      verb(DiploAction.ReleaseSubject, 'Release', '', ['Costs nothing, and they remember it for a long time.']);
    } else if (!r.atWar) {
      verb(DiploAction.DemandVassalage, 'Demand the knee', '', [
        'Only a very small crown that likes you a great deal ever says yes.',
      ]);
    }
    return acts;
  }

  /** The one-line summary of where you and this crown stand. */
  private crownMarks(c: CampaignState, r: CrownView): string {
    const marks = [
      r.atWar ? 'at war' : '',
      r.allied ? 'allied' : '',
      r.married ? 'married' : '',
      r.truceTurns > 0 ? `truce ${months(r.truceTurns)}` : '',
      r.theirSubjection !== SubjectKind.None ? (SUBJECT_NAMES[r.theirSubjection] ?? '').toLowerCase() : '',
      r.liege >= 0 && r.liege !== c.playerNation ? `sworn to ${nationName(c, r.liege)}` : '',
    ].filter(Boolean);
    return marks.join(' · ') || r.confessionName;
  }

  /** The hover card for a crown's head row: opinion, its terms, its weight. */
  private crownTip(r: CrownView): string {
    return WorldMap.tip([
      `${r.name} — ${r.confessionName}`,
      `They think ${r.theirOpinion} of you; you think ${r.yourOpinion} of them.`,
      ...r.terms.map((t) => `· ${t.value >= 0 ? '+' : ''}${t.value}  ${t.label}`),
      `${r.provinces} provinces, ${r.strength} pts of strength.`,
    ]);
  }

  private crownRow(c: CampaignState, r: CrownView, wars: WarView[], live: boolean): string {
    const open = this.openCrown === r.nation;
    const acts = this.crownActs(r, wars, live);

    return `<div class="wm-crown${open ? ' open' : ''}${r.atWar ? ' war' : ''}">
      <div class="wm-crownhead" data-act="opencrown" data-nation="${r.nation}"${this.crownTip(r)}>
        <i class="wm-swatch" style="background:${hex(nationAccent(r.nation))}"></i>
        <span>${esc(r.name)}<em>${esc(this.crownMarks(c, r))}</em></span>
        <b class="${r.theirOpinion >= 0 ? 'up' : 'down'}">${r.theirOpinion}</b>
      </div>
      ${open ? `<div class="wm-acts">${acts.join('')}</div>` : ''}
    </div>`;
  }

  /**
   * The chronicle, and what used to be the End Turn button.
   *
   * There is nothing to press here any more. The realm keeps its own time, so
   * what belonged in this corner was never a button — it was the answer to "how
   * long have I got", which is what the player was actually reading off the
   * button all along. Orders are given whenever; they take effect when the
   * council next sits.
   */
  private logPanel(c: CampaignState): string {
    const lines = c.log.slice(-8).reverse().map((l) => `<div>${esc(l)}</div>`).join('');
    return `
      <div class="wm-chron">${lines}</div>
      <div class="wm-turn">
        ${c.over
          ? `<span class="wm-won">${esc(nationName(c, c.winner))} holds the realm.</span>`
          : `<span class="wm-clock"><span class="wm-t-long">${esc(this.dateLine)}</span><i class="wm-t-wait">${esc(
              this.waitWord(c),
            )}</i></span>`}
      </div>`;
  }

  /**
   * The war menu used to be here, as `battlePrompt`.
   *
   * It lives in `ui/warcall.ts` now and is driven from the frame loop, because
   * a panel that only exists on the world map is a panel the player does not
   * see when the march they ordered ten minutes ago actually lands and they are
   * standing in their own town. It is the same panel in the same place on both
   * screens, and it answers itself — `Cmd.CampaignAutoResolve` and the door
   * into the battle both hang off it now, so the two cases that used to be in
   * `act` for them have gone with the markup that raised them.
   */

  // ── Wiring ───────────────────────────────────────────────────────────────

  /**
   * Find everything again and hang the handlers back on it.
   *
   * `rebuilt` is false on the one path that did **not** replace the chart —
   * `paintSelection`. Everything here is still worth doing on that path (the
   * columns and the counter overlay are new markup and their handlers went with
   * the old), except the zoom cache: see the note at `appliedK` below.
   */
  private wire(c: CampaignState, rebuilt = true): void {
    this.chart = this.el.querySelector('.wm-chart');
    this.plotEl = this.el.querySelector('.wm-plot');
    /**
     * `.wm-marquee` and not `.wm-band`: the chart already has a `.wm-band`, and
     * it is the province outline in `territoryLayer` — a hundred and fifty of
     * them, the first of which is what `querySelector('.wm-band')` would have
     * handed back. A marquee written into a province's own border is a bug that
     * would have looked like the border layer having gone mad.
     */
    this.bandEl = this.el.querySelector('.wm-marquee');
    // The markup was just replaced and the marquee's rect went with it. A drag
    // in flight across a repaint has to keep its box.
    this.drawBand();
    this.saidEl = this.el.querySelector('.wm-say');
    // The markup was just replaced, so the sheet's `on` marks went with it.
    this.applyNarrow();
    this.setSheet(this.sheet);
    /**
     * Everything held to a constant size on the glass, found by the attribute
     * that says so rather than by class. `.wm-counter` was the whole list until
     * the towns joined it — a town is not clickable and must not take the
     * counter's hover and hit-target rules, but it is scaled by exactly the same
     * arithmetic, and `data-cx` is what both of them actually have in common.
     */
    this.counters = Array.from(this.el.querySelectorAll<SVGGElement>('[data-cx]'));
    this.hatchPats = Array.from(this.el.querySelectorAll<SVGPatternElement>('pattern[id^="wmh-"]'));
    this.clockEls = {
      date: this.el.querySelector('.wm-t-date'),
      council: this.el.querySelector('.wm-t-council'),
      long: this.el.querySelector('.wm-t-long'),
      wait: this.el.querySelector('.wm-t-wait'),
      drawn: this.el.querySelector('.wm-t-drawn'),
    };
    // The markup was just replaced, so whatever scale the old counters carried
    // went with it. Force the rewrite rather than trusting the cached zoom —
    // unless nothing was replaced, in which case every mark still carries the
    // right transform and `paintSelection` has already scaled the few it made.
    if (rebuilt) this.appliedK = -1;
    this.wireView();
    this.applyView();

    for (const el of this.el.querySelectorAll('[data-prov]')) {
      const p = Number((el as SVGElement).dataset.prov);
      (el as HTMLElement).onclick = (ev: MouseEvent) => {
        // A drag that happened to end over a province is a pan, not a click.
        if (this.moved > 4) return;
        /**
         * **Shift-click adds a province's hosts to the group**, and shift-click
         * again takes them out.
         *
         * The same modifier the settlement uses to add a company to a selection,
         * which is the only reason it needs no teaching. It is the keyboard half
         * of the marquee: a box is the fast way to take four hosts standing
         * near each other, and this is the only way to take two that are a
         * continent apart.
         */
        if (ev.shiftKey) {
          const here = armiesIn(c, p)
            .filter((a) => a.owner === c.playerNation && a.moveTo < 0 && a.aboard < 0)
            .map((a) => a.id);
          if (here.length > 0) {
            const have = this.orderedHosts(c).map((a) => a.id);
            const all = here.every((id) => have.includes(id));
            this.setGroup(
              all ? have.filter((id) => !here.includes(id)) : [...have, ...here.filter((id) => !have.includes(id))],
            );
            const n = this.orderedHosts(c).length;
            this.say(
              n === 0
                ? 'Nothing held.'
                : n === 1
                  ? `${findArmy(c, this.selectedArmy)?.name ?? 'One host'} held.`
                  : `${n} hosts held. Right-click anywhere the roads reach.`,
            );
            this.focus = 'province';
            this.draw(c);
          }
          return;
        }
        // An ordinary click is a fresh selection, so it puts any group down.
        this.groupArmies = [];
        this.selectedProvince = p;
        /**
         * Selecting a province you have a host in selects the host too; that is
         * almost always what the click meant. **Clicking it again steps to the
         * next of your hosts standing there**, and wraps.
         *
         * A province holding two of your hosts could otherwise only ever offer
         * the first one from the map: `hostLayer` draws one banner per crown per
         * province with a count on it, not one banner a host — which is right,
         * because four legible marks beat sixteen illegible ones — so there was
         * no second thing on the chart to click. Picking the second host meant
         * leaving the map and opening the column, which is exactly the "the
         * levy ui is the one that needs the most improvement" complaint in
         * miniature.
         *
         * Cycling rather than fanning the stack out is the idiom the games this
         * is aimed at use, and it costs no new state: the selection *is* the
         * cursor. `findIndex` answers -1 when nothing of yours here is selected
         * — a first click, or a click on different ground — and `-1 + 1` is 0,
         * so the ordinary case falls out of the same line as the cycle.
         */
        const mine = armiesIn(c, p).filter((a) => a.owner === c.playerNation);
        const held = mine.findIndex((a) => a.id === this.selectedArmy);
        this.selectedArmy = mine.length > 0 ? mine[(held + 1) % mine.length].id : -1;
        this.sayHostSelected(c, this.selectedArmy);
        this.focus = 'province';
        // On a phone the detail column is a shut sheet, so a tap on the ground
        // would otherwise do something the player cannot see — the same rule
        // `syncSheetForBuilding` follows in the settlement.
        if (this.narrow) this.sheet = 'right';
        this.draw(c);
      };
      /**
       * **Right-click marches.**
       *
       * The order used to exist only as a list of buttons in the province
       * column, one per *neighbouring* province — so moving a host anywhere real
       * meant reading a panel, pressing a name, waiting out the months, reading
       * the panel again. `orderMarch` has walked a full route to any destination
       * since the march rework; nothing but the UI was ever asking it for one.
       *
       * Right-click and not left, because left already means "look at this" on
       * this map and because it is the gesture the settlement already uses for
       * exactly this — right-click moves the selected companies — and the one
       * every game this layer is modelled on uses. The pan guard is the same one
       * the click carries: a right-drag across the board is a pan that happened
       * to end over a province, not an order.
       */
      (el as HTMLElement).oncontextmenu = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.orderMarchTo(c, p);
      };
      (el as HTMLElement).onmouseenter = () => this.plotTo(c, p);
      (el as HTMLElement).onmouseleave = () => this.plotTo(c, -1);
    }
    /**
     * The pips under a banner: one host each, taken directly.
     *
     * `stopPropagation` is the load-bearing line. A pip is drawn *inside* the
     * banner's counter — which is what glues it to the mark at every zoom — so
     * the counter's own click is on the way up from it, and without this a
     * click on the third pip would select the third host and then immediately
     * cycle to the fourth.
     */
    for (const el of this.el.querySelectorAll('[data-hostpick]')) {
      (el as SVGElement).onclick = (ev: MouseEvent) => {
        ev.stopPropagation();
        // Same drag guard the province fills carry: a box drawn from a banner
        // ends over one of these as often as not.
        if (this.moved > 4) return;
        const id = Number((el as SVGElement).dataset.hostpick);
        const a = findArmy(c, id);
        if (!a || a.owner !== c.playerNation) return;
        if (ev.shiftKey) {
          const have = this.orderedHosts(c).map((q) => q.id);
          this.setGroup(have.includes(id) ? have.filter((q) => q !== id) : [...have, id]);
        } else {
          this.setGroup([id]);
        }
        this.selectedProvince = a.at;
        this.sayHostSelected(c, this.selectedArmy);
        this.focus = 'province';
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-army]')) {
      (el as HTMLElement).onclick = () => {
        this.selectedArmy = Number((el as HTMLElement).dataset.army);
        this.sayHostSelected(c, this.selectedArmy);
        this.focus = 'province';
        this.draw(c);
      };
    }
    /**
     * The campaign's own marks.
     *
     * Each one takes the *same* drag guard the province fills take, because a
     * counter sitting in the middle of the map is exactly where a pan is likely
     * to end, and a pan that selects a host reads as the map being twitchy.
     */
    for (const el of this.el.querySelectorAll('[data-march]')) {
      (el as SVGElement).onclick = () => {
        if (this.moved > 4) return;
        const id = Number((el as SVGElement).dataset.march);
        const a = findArmy(c, id);
        this.focus = 'march';
        this.focusArmy = id;
        if (a) this.selectedProvince = a.moveTo >= 0 ? a.moveTo : a.at;
        // Only a host of your own may stay selected. A stale selection would be
        // dragged back into view by `update`, which follows the selected host,
        // and the column would quietly point somewhere else on the next frame.
        this.selectedArmy = a && a.owner === c.playerNation ? id : -1;
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-fleet]')) {
      (el as SVGElement).onclick = () => {
        if (this.moved > 4) return;
        const id = Number((el as SVGElement).dataset.fleet);
        const f = findFleet(c, id);
        this.focus = 'fleet';
        this.focusFleet = id;
        // The ground the column follows is where the hulls are, or where they
        // are bound — which is the province a player looking at a convoy is
        // actually interested in.
        if (f) this.selectedProvince = f.moveTo >= 0 ? f.moveTo : f.at;
        this.selectedArmy = -1;
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-market]')) {
      (el as SVGElement).onclick = () => {
        if (this.moved > 4) return;
        const id = Number((el as SVGElement).dataset.market);
        this.focus = 'market';
        this.focusNode = id;
        const seat = tradeNode(c, id)?.seat ?? -1;
        if (seat >= 0) this.selectedProvince = seat;
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-camp]')) {
      (el as SVGElement).onclick = () => {
        if (this.moved > 4) return;
        this.focus = 'camp';
        this.focusProv = Number((el as SVGElement).dataset.camp);
        this.selectedProvince = this.focusProv;
        this.selectedArmy = -1;
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-warn]')) {
      (el as SVGElement).onclick = () => {
        if (this.moved > 4) return;
        this.focus = 'warn';
        this.focusProv = Number((el as SVGElement).dataset.warn);
        this.selectedProvince = this.focusProv;
        this.selectedArmy = -1;
        this.draw(c);
      };
    }
    for (const el of this.el.querySelectorAll('[data-act]')) {
      (el as HTMLElement).onclick = (ev) => {
        ev.stopPropagation();
        this.act(c, el as HTMLElement);
      };
    }

    /**
     * Put the route preview back.
     *
     * A rebuild throws the whole chart away, `.wm-plot` with it, and the cursor
     * does not move just because the board did — so without this the preview
     * blinks out under a stationary pointer the moment a host arrives or a month
     * turns, and never comes back until the player waggles the mouse. `plotProv`
     * survives the rebuild precisely so this line can.
     *
     * The board's reply is put back for the same reason and by the same rule:
     * `paintClock` would restore it on the next frame anyway, and one blank
     * frame in the middle of a sentence is one frame of flicker.
     */
    this.drawPlot(c);
    this.paintSaid();
  }

  /**
   * Drag to pan, wheel to zoom.
   *
   * The handlers go on the *stage* rather than on the chart, so that a drag that
   * starts on a mountain, a name or a road pans exactly as one that starts on
   * open ground — those layers are all `pointer-events: none`, so without this
   * the map would only be draggable in some places, which reads as the map being
   * broken rather than as a layering decision.
   */
  private wireView(): void {
    const stage = this.el.querySelector('.wm-stage') as HTMLElement | null;
    if (!stage) return;

    stage.onwheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const box = stage.getBoundingClientRect();
      // Where the cursor is inside the *drawn* chart, which is letterboxed
      // inside the stage by preserveAspectRatio. Getting this wrong makes the
      // map creep away from the cursor as you zoom.
      const draw = this.drawnRect(box);
      const ax = (ev.clientX - draw.x) / draw.w;
      const ay = (ev.clientY - draw.y) / draw.h;
      this.zoomBy(ev.deltaY > 0 ? 1.18 : 1 / 1.18, Math.max(0, Math.min(1, ax)), Math.max(0, Math.min(1, ay)));
    };

    // The board's own right-click is an order; the browser's is a menu over the
    // top of it. This is on the stage rather than on each province so that a
    // right-click on open sea does not pop one either.
    stage.oncontextmenu = (ev: MouseEvent) => ev.preventDefault();

    /**
     * Fingers on the chart.
     *
     * The same recogniser the battlefield uses, given a `Viewport` this screen
     * can satisfy — one finger drags, two pinch about their midpoint, a tap is a
     * click on whatever is under it, and a long press is the march order. It is
     * *not* a copy: `HOLD_MS` and `SLOP` are one pair of numbers, or the two
     * boards would feel like two games.
     *
     * Three things this had to get right and only one of them is obvious:
     *
     *   • `.wm-stage` needs `touch-action: none`, which `TouchInput` sets, or
     *     the browser takes the drag as a page scroll and the handler never runs.
     *   • **The pan has to arm `this.moved`.** The province click path is
     *     guarded by it and it was written only by `onmousemove`; without this
     *     every pan ends by selecting whatever province the finger stopped over.
     *   • A long press wants a `contextmenu`, not a right *button*: that is what
     *     every province binds to order a march. `onHoldAt` is the hook for
     *     exactly that difference.
     */
    // The stage this was bound to has just been thrown away with the markup.
    this.touch?.dispose();
    this.touch = new TouchInput(
      stage,
      {
        panPixels: (dx, dy) => {
          const draw = this.drawnRect(stage.getBoundingClientRect());
          if (draw.w <= 0 || draw.h <= 0) return;
          this.view = this.clampView({
            x: this.view.x + (dx / draw.w) * this.view.w,
            y: this.view.y + (dy / draw.h) * this.view.h,
            w: this.view.w,
            h: this.view.h,
          });
          this.applyView();
        },
        zoomAt: (factor, ax, ay) => this.zoomBy(1 / factor, ax, ay),
      },
      {
        onPan: () => {
          // The drag guard the click path already reads. Anything over the slop
          // is a pan, and a pan is not a selection.
          this.moved = 99;
        },
        onHoldAt: (x, y) => {
          const el = document.elementFromPoint(x, y);
          const cell = el?.closest?.('[data-prov]') as HTMLElement | null;
          if (!cell) return;
          // Straight to the order, rather than through a synthetic event: the
          // guard `moved` is about to be cleared by the touch end and a
          // dispatched `contextmenu` would race it.
          this.moved = 0;
          if (this.state) this.orderMarchTo(this.state, Number(cell.dataset.prov));
        },
      },
    );

    stage.onmousedown = (ev: MouseEvent) => {
      /**
       * **A drag is either a pan or a box, and which one is decided here.**
       *
       * Two ways in, because the two answer different questions:
       *
       *   • **Shift and drag, anywhere.** The modifier the settlement's own box
       *     select uses, and the one every game of this kind uses for "add to
       *     what I have". It works over sea, over a province you do not own,
       *     over a mountain — anywhere a player can put a corner.
       *   • **Drag begun on one of your own banners.** Nobody grabs an army to
       *     move the paper. This is the discoverable half: the first time a
       *     player tries to drag a host, the box appears, and that is the whole
       *     of the teaching.
       *
       * Everything else is still a pan, and the pan is still the default —
       * which matters, because on a board three screens wide panning is what
       * the left button is mostly for.
       */
      const onOwnBanner = Boolean((ev.target as Element | null)?.closest?.('.wm-hostflag.mine'));
      if (ev.button === 0 && (ev.shiftKey || onOwnBanner)) {
        const q = this.mapPointAt(ev.clientX, ev.clientY, stage);
        this.band = { x0: q.x, y0: q.y, x1: q.x, y1: q.y };
        this.panning = false;
        this.moved = 0;
        this.drawBand();
        return;
      }
      this.panning = true;
      this.moved = 0;
      this.panFrom = { x: ev.clientX, y: ev.clientY };
      this.panView = { ...this.view };
      stage.classList.add('dragging');
    };

    stage.onmousemove = (ev: MouseEvent) => {
      if (this.band) {
        const q = this.mapPointAt(ev.clientX, ev.clientY, stage);
        this.band.x1 = q.x;
        this.band.y1 = q.y;
        /**
         * Arm the drag guard the moment the box is real.
         *
         * The banner the drag started on carries the province click, and that
         * click fires on mouseup wherever the cursor ended. Without this, every
         * box drawn from a banner ended by selecting whatever province the
         * cursor stopped over and throwing the box's own selection away one
         * frame later — the same fault the pan had, in a new place.
         */
        const box = this.drawnRect(stage.getBoundingClientRect());
        const px = (Math.abs(this.band.x1 - this.band.x0) / this.view.w) * box.w;
        const py = (Math.abs(this.band.y1 - this.band.y0) / this.view.h) * box.h;
        if (px + py > 5) this.moved = 99;
        this.drawBand();
        return;
      }
      if (!this.panning) return;
      const dx = ev.clientX - this.panFrom.x;
      const dy = ev.clientY - this.panFrom.y;
      this.moved = Math.max(this.moved, Math.abs(dx) + Math.abs(dy));
      const draw = this.drawnRect(stage.getBoundingClientRect());
      this.view = this.clampView({
        x: this.panView.x - (dx / draw.w) * this.panView.w,
        y: this.panView.y - (dy / draw.h) * this.panView.h,
        w: this.panView.w,
        h: this.panView.h,
      });
      this.applyView();
    };

    const stop = (): void => {
      if (this.band && this.state) this.closeBand(this.state);
      this.band = null;
      this.panning = false;
      stage.classList.remove('dragging');
      // Cleared on the next tick so the click that follows this mouseup can
      // still see that it was a drag.
      setTimeout(() => {
        this.moved = 0;
      }, 0);
    };
    stage.onmouseup = stop;
    stage.onmouseleave = stop;
  }

  /**
   * Order the selected host to `to`, or say why not.
   *
   * The refusal is the simulation's own sentence — `orderRefusal` out of
   * `armies.ts` — rather than a second set of words that can drift from the
   * rules. It is shown *on the board*, beside the province the player pointed
   * at, because a right-click that does nothing and puts an explanation in a
   * column on the far side of the screen has not explained anything.
   */
  /**
   * Every host the next right-click will be an order to.
   *
   * The group if there is one, the single selected host otherwise, and always
   * filtered to hosts that still exist and are still yours — a host can be
   * destroyed or folded into another between the drag that selected it and the
   * order that uses it.
   */
  private orderedHosts(c: CampaignState): Army[] {
    const ids = this.groupArmies.length > 0 ? this.groupArmies : [this.selectedArmy];
    const out: Army[] = [];
    for (const id of ids) {
      if (id < 0) continue;
      const a = findArmy(c, id);
      if (a && a.owner === c.playerNation) out.push(a);
    }
    return out;
  }

  /**
   * Take a set of hosts as the group, and keep the invariant the field's own
   * note describes: the lead is the selection, and a group of one is not a
   * group.
   */
  private setGroup(ids: number[]): void {
    const uniq = [...new Set(ids)];
    this.groupArmies = uniq.length > 1 ? uniq : [];
    this.selectedArmy = uniq.length > 0 ? uniq[0] : -1;
    this.saidArmy = -1;
  }

  /**
   * Where a point on the glass is on the chart.
   *
   * The inverse of what `applyView` does: the chart's `viewBox` *is* `this.view`
   * and `drawnRect` is where the letterboxed drawing actually sits inside the
   * stage, so this is those two composed. Anything that reads a mouse position
   * as ground has to go through here rather than through `getBBox`, which would
   * be a layout read per province per frame of a drag.
   */
  private mapPointAt(clientX: number, clientY: number, stage: HTMLElement): Pt {
    const draw = this.drawnRect(stage.getBoundingClientRect());
    if (draw.w <= 0 || draw.h <= 0) return { x: 0, y: 0 };
    return {
      x: this.view.x + ((clientX - draw.x) / draw.w) * this.view.w,
      y: this.view.y + ((clientY - draw.y) / draw.h) * this.view.h,
    };
  }

  /**
   * Draw the marquee, straight into its own group.
   *
   * Never through `signature`, for the reason `drawPlot` is not: this changes on
   * every mouse move of a drag, and a full rebuild of the board per frame would
   * throw away the very element the drag is happening on.
   */
  private drawBand(): void {
    const g = this.bandEl;
    if (!g) return;
    const b = this.band;
    if (!b) {
      if (g.innerHTML !== '') g.innerHTML = '';
      return;
    }
    const x = Math.min(b.x0, b.x1);
    const y = Math.min(b.y0, b.y1);
    const w = Math.abs(b.x1 - b.x0);
    const h = Math.abs(b.y1 - b.y0);
    g.innerHTML =
      `<rect class="wm-marqbox" x="${x.toFixed(2)}" y="${y.toFixed(2)}" ` +
      `width="${w.toFixed(2)}" height="${h.toFixed(2)}" />`;
  }

  /**
   * Close the marquee: take every host of yours standing inside it.
   *
   * **Standing** hosts only. A host already on the road cannot be given a march
   * order at all — `orderMarch` refuses it — so putting one in the group would
   * be selecting something the very next gesture is going to silently skip, and
   * the player would count four banners and watch three of them move.
   */
  private closeBand(c: CampaignState): void {
    const b = this.band;
    this.band = null;
    this.drawBand();
    if (!b) return;
    const x0 = Math.min(b.x0, b.x1);
    const x1 = Math.max(b.x0, b.x1);
    const y0 = Math.min(b.y0, b.y1);
    const y1 = Math.max(b.y0, b.y1);
    // A box smaller than a banner is a click that wobbled, not a selection.
    if (x1 - x0 < this.view.w * 0.012 && y1 - y0 < this.view.h * 0.012) return;
    const caught: number[] = [];
    for (const a of c.armies) {
      if (a.owner !== c.playerNation || a.moveTo >= 0 || a.aboard >= 0) continue;
      const s = provinceSite(a.at);
      if (s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) caught.push(a.id);
    }
    if (caught.length === 0) {
      this.say('Nothing of yours in the box.');
      return;
    }
    this.setGroup(caught);
    this.selectedProvince = findArmy(c, caught[0])?.at ?? this.selectedProvince;
    this.focus = 'province';
    this.say(
      caught.length === 1
        ? `${findArmy(c, caught[0])?.name ?? 'One host'} is yours. Right-click to march.`
        : `${caught.length} hosts taken up. Right-click anywhere the roads reach — they each walk their own road.`,
    );
    this.draw(c);
  }

  private orderMarchTo(c: CampaignState, to: number): void {
    if (this.moved > 4) return;
    const hosts = this.orderedHosts(c);
    if (hosts.length === 0) {
      this.say('Choose one of your hosts first — click its banner, or drag a box over several.');
      return;
    }
    if (hosts.length > 1) {
      this.orderGroupTo(c, hosts, to);
      return;
    }
    const army = hosts[0];
    if (to === army.at) {
      this.say(`${army.name} is already at ${provinceName(to)}.`);
      return;
    }
    if (!canOrderMarch(c, army, to)) {
      this.say(orderRefusal(c, army, to) || 'They cannot go there.');
      return;
    }
    this.cb.onCommand(cmd(Cmd.CampaignMoveArmy, army.id, to));
    this.say(`${army.name} marches for ${provinceName(to)} — ${months(marchTurns(c, army, to))}.`);
    // The route the plot was showing is now the route they are walking, and
    // `laneLayer` draws that. Drop the preview so the two are never both up.
    this.plotProv = -1;
    this.forceRepaint();
  }

  /**
   * Several hosts, one destination — and what that *means*.
   *
   * Four hosts right-clicked onto one province is either four columns down one
   * road or a real formation, and the two genres this board borrows from do not
   * answer it the same way. Total War would make it a formation: one order, one
   * arrival, a facing, and hosts that wait for each other. This board's answer
   * is the other one, and it is not a shortcut — it is what the rest of the
   * simulation already says.
   *
   * A march on this map is `moveFrom`, `moveTo`, `moveT` on **one** army.
   * Interception is a thing that happens to one host on one road; the running
   * fight, `regroup` and `standDown` are all written about one host being
   * thrown off one road; `MAX_ARMY_POINTS` is a *cap on a host*, and four hosts
   * that arrived as one object would quietly be an army of four times the legal
   * size with none of the cost of building one. So the group is a gesture, not
   * an object: each host takes its own route at its own pace, can be
   * intercepted on its own, and arrives when its own roads let it. If a player
   * wants one bigger host at the far end, `CampaignMergeArmies` is still the
   * verb for that, and it is a decision with a price rather than a side effect
   * of having dragged a box.
   *
   * The whole gesture goes out as **one** `Cmd.CampaignMarchGroup` rather than
   * as four `CampaignMoveArmy`s; the argument is on the command itself.
   */
  private orderGroupTo(c: CampaignState, hosts: Army[], to: number): void {
    const able = hosts.filter((a) => a.at !== to && canOrderMarch(c, a, to));
    const stuck = hosts.filter((a) => !able.includes(a));
    if (able.length === 0) {
      // One refusal, and it is the simulation's own sentence about the first
      // host that could not go — not a summary of four of them, which is a
      // paragraph nobody reads off the bottom of a map.
      const why = orderRefusal(c, hosts[0], to);
      this.say(
        hosts.every((a) => a.at === to)
          ? `They are already at ${provinceName(to)}.`
          : why || 'None of them can go there.',
      );
      return;
    }
    this.cb.onCommand(cmd(Cmd.CampaignMarchGroup, to, 0, 0, 0, able.map((a) => a.id).join(',')));
    const longest = Math.max(...able.map((a) => marchTurns(c, a, to)));
    const tail =
      stuck.length > 0
        ? ` ${stuck.length} stay${stuck.length === 1 ? 's' : ''} — ${orderRefusal(c, stuck[0], to) || 'no road'}`
        : '';
    this.say(
      `${able.length} hosts march for ${provinceName(to)} — the last of them in ${months(longest)}.${tail}`,
    );
    this.plotProv = -1;
    this.forceRepaint();
  }

  /**
   * The board's reply to selecting one of your hosts.
   *
   * The teaching moment for right-click-to-march is the instant a player picks
   * a host up, and it is the only moment when a line across the bottom of the
   * board is certainly worth its space. Said once per selection — `plotProv`
   * and the selection id together are what make "once" mean once — and only for
   * a host that is actually free to be given an order.
   */
  private sayHostSelected(c: CampaignState, id: number): void {
    if (id < 0 || id === this.saidArmy) return;
    this.saidArmy = id;
    const a = findArmy(c, id);
    if (!a || a.owner !== c.playerNation) return;
    if (a.moveTo >= 0) {
      this.say(`${a.name} is on the road to ${provinceName(a.moveTo)}.`);
      return;
    }
    if (a.aboard >= 0) return;
    this.say(`${a.name} is yours. Right-click anywhere the roads reach to march there.`);
  }

  /** Put a sentence on the board for a few seconds. */
  private say(text: string): void {
    this.said = text;
    this.saidUntil = performance.now() + 4200;
    this.paintSaid();
  }

  /**
   * Paint that sentence, and clear it when its time is up.
   *
   * Called from `paintClock`, which runs every frame and is already the place
   * this screen patches text in without rebuilding the board.
   */
  private paintSaid(): void {
    const el = this.saidEl;
    if (!el) return;
    const live = this.said !== '' && performance.now() < this.saidUntil;
    const text = live ? this.said : '';
    if (el.textContent !== text) el.textContent = text;
    el.classList.toggle('on', live);
  }

  /**
   * Plot the route the selected host would take to the province under the
   * cursor, and how long it would take them.
   *
   * Written straight into `.wm-plot` rather than folded into `signature`,
   * because the signature drives a full markup rebuild of a hundred and
   * twenty-seven provinces and this changes on every mouseover. It is the same
   * argument the counters make about `applyView`.
   */
  private plotTo(c: CampaignState, to: number): void {
    if (this.plotProv === to) return;
    this.plotProv = to;
    this.drawPlot(c);
  }

  private drawPlot(c: CampaignState): void {
    const g = this.plotEl;
    if (!g) return;
    const to = this.plotProv;
    /**
     * Every host the order would go to, not only the lead one.
     *
     * A group whose preview drew one route would be a preview of a different
     * order from the one the right-click gives — and the case where it matters
     * is precisely the case the gesture exists for: hosts in two provinces,
     * whose roads to the same destination are not the same roads.
     */
    const hosts = this.orderedHosts(c).filter((a) => a.moveTo < 0);
    if (to < 0 || hosts.length === 0) {
      if (g.innerHTML !== '') g.innerHTML = '';
      return;
    }

    /**
     * The zoom, folded into every radius this layer draws.
     *
     * The pip and the destination ring carried hard-coded radii in **map**
     * units — 1.1 and 3.2 — while every stroke width and every letter on the
     * same layer was already multiplied by `--wm-k` to hold it still on the
     * glass. So the preview was the one thing on the chart that changed size
     * with the zoom: at the fit view a route pip is a pixel and a half and
     * invisible, and at the deepest view it is a gold blob eleven pixels across
     * sitting on top of the province it is supposed to be marking. Photographed
     * at both ends, which is the only way this kind of fault is ever found.
     *
     * `k` is exactly what `applyView` writes into `--wm-k`, so the circles now
     * obey the same rule as the lines they are drawn on — and `applyView`
     * redraws this layer when the zoom moves, in the block that already only
     * runs then.
     */
    const k = this.view.w / VIEW.w;
    const out: string[] = [];
    let anyLegal = false;
    let longest = 0;
    for (const army of hosts) {
      if (to === army.at) continue;
      const path = marchPath(c, army, to);
      const legal = canOrderMarch(c, army, to);
      const col = legal ? '#e6cf92' : '#9c3336';
      if (legal) {
        anyLegal = true;
        longest = Math.max(longest, marchTurns(c, army, to));
      }

      // No walkable route at all: mark the destination and let the tooltip and
      // the refusal line carry the reason. Drawing a straight line from the host
      // to a province it cannot reach would be drawing a road that is not there.
      if (path.length > 0) {
        let from = army.at;
        for (const step of path) {
          const pts = roadPoints(from, step);
          const fwd = from < step ? pts : [...pts].reverse();
          out.push(
            `<path class="wm-plotline" d="${fwd
              .map((q, k) => `${k === 0 ? 'M' : 'L'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`)
              .join('')}" stroke="${col}" />`,
          );
          from = step;
        }
        // A pip on every province the route passes through, so the *number of
        // borders crossed* is visible — which is what a march actually costs.
        for (const step of path.slice(0, -1)) {
          const q = provinceSite(step);
          out.push(
            `<circle class="wm-plotpip" cx="${q.x.toFixed(2)}" cy="${q.y.toFixed(2)}" ` +
              `r="${(3.2 * k).toFixed(3)}" fill="${col}" />`,
          );
        }
      }
    }

    if (out.length === 0 && hosts.every((a) => a.at === to)) {
      if (g.innerHTML !== '') g.innerHTML = '';
      return;
    }

    const col = anyLegal ? '#e6cf92' : '#9c3336';
    const end = provinceSite(to);
    out.push(
      `<circle class="wm-plotend" cx="${end.x.toFixed(2)}" cy="${end.y.toFixed(2)}"
        r="${(6.5 * k).toFixed(3)}" fill="none" stroke="${col}" />`,
    );
    /**
     * The months, and for a group it is the **longest** of them.
     *
     * Quoting the lead host's march would be quoting the number that is true of
     * one counter and useless about the gesture: what a player wants to know
     * before pressing is when the last of them is on the ground.
     */
    const label = anyLegal
      ? hosts.length > 1
        ? `${hosts.length} hosts · ${months(longest)}`
        : months(longest)
      : 'no road';
    out.push(
      `<text class="wm-plottext" x="${end.x.toFixed(2)}" y="${(end.y - 10.5 * k).toFixed(2)}"
        fill="${col}">${esc(label)}</text>`,
    );
    g.innerHTML = out.join('');
  }

  /**
   * Back to your own seat, at a working zoom.
   *
   * Every map of this kind has this key and this game did not: the board is
   * three screens wide, a click on a far province takes the column with it, and
   * there was no way back to your own capital except panning until you found
   * it. Paradox's is Backspace and it has been since the first of these games;
   * Home is the one a person guesses. Both, plus a button, because a control
   * nobody can find is worth what a control that does not exist is worth.
   */
  home(c: CampaignState): void {
    this.clearFocus();
    this.selectedProvince = c.capital;
    this.lookAt(c.capital);
    this.say('Your seat.');
    this.draw(c);
  }

  /**
   * Put the board down: no host held, no province chosen, no panel following
   * anything.
   *
   * The complaint this answers is "once you click you cannot snap back". The
   * column follows whatever was last touched — a march, a camp, a fleet, a
   * market — and every one of those focus modes is sticky until something else
   * takes it. Escape is what every game of this kind uses to let go, and this
   * screen had no Escape of its own at all: it fell straight through to
   * `main.ts`, which read it as "close a screen" and shut the whole map.
   */
  /**
   * Put a province on the chart from *outside* this screen.
   *
   * The Levies screen is a list of provinces and every row of it is a place on
   * the board; a row that could not take you there would be a list of names.
   * It is `lookAt` plus a selection rather than a new mechanism, so a province
   * arrived at this way is in exactly the state one clicked on the chart is.
   */
  showProvince(c: CampaignState, p: number): void {
    this.clearFocus();
    this.selectedProvince = p;
    this.lookAt(p);
    this.draw(c);
  }

  /** Turn the levy marks on from outside — the Levies screen's own button. */
  showLevyMarks(c: CampaignState): void {
    this.levyMarks = true;
    this.say('Every province of yours, and what it sends the muster each month.');
    this.draw(c);
  }

  private clearFocus(): void {
    this.selectedArmy = -1;
    // A group is part of the selection, so it lets go with the rest of it.
    this.groupArmies = [];
    this.band = null;
    this.drawBand();
    this.saidArmy = -1;
    this.focus = 'province';
    this.plotProv = -1;
    this.openCrown = -1;
    this.openWar = -1;
    if (this.plotEl) this.plotEl.innerHTML = '';
  }

  /**
   * Escape, handled here before `main.ts` sees it.
   *
   * Returns true when it was used, so the caller can stop: the first Escape
   * lets go of whatever is held, and only an Escape with nothing left to let go
   * of closes the map. That ordering is the whole of why this exists — a single
   * key that means both "deselect" and "leave" has to spend itself on the
   * smaller meaning first.
   */
  escape(c: CampaignState): boolean {
    const had = this.selectedArmy >= 0 || this.focus !== 'province' || this.selectedProvince >= 0;
    if (!had) return false;
    this.clearFocus();
    this.selectedProvince = -1;
    this.say('');
    this.draw(c);
    return true;
  }

  /**
   * Open one sheet, or none. Only one at a time, exactly as the settlement HUD
   * does it, and for the same reason: the map is the screen and a panel is
   * something pulled up over it and put away again.
   */
  private setSheet(id: string): void {
    this.sheet = id;
    for (const k of ['left', 'right', 'chron']) {
      this.el.classList.toggle(`sheet-${k}`, k === id);
    }
    for (const el of Array.from(this.el.querySelectorAll('.wm-sheettab'))) {
      const t = el as HTMLElement;
      if (t.dataset.sheet !== undefined) t.classList.toggle('on', t.dataset.sheet === id);
    }
  }

  /**
   * Turn the phone layout on and off from one breakpoint.
   *
   * A class rather than a bare `@media` block because the gesture layer and the
   * hover card need the flag in *script* as well as in CSS, and two authorities
   * for one number is how they end up disagreeing. `NARROW_PX` is that number
   * and `touch.ts` exports it.
   */
  private applyNarrow(): void {
    const want = isNarrow();
    /**
     * The class is written **every** time, and the flag only decides whether
     * anything else has to happen.
     *
     * It was the other way round — an early return on `want === this.narrow`,
     * with the class written only on a transition — and that makes the class a
     * cache of the flag rather than a statement of it. The two then diverge for
     * good the first time a repaint or a throttled frame drops one of them:
     * measured, a window resized down to 390 sat with the media query saying
     * narrow, the flag saying narrow, and no class on the element at all, with
     * nothing able to put it back. `classList.toggle` with an explicit boolean
     * is idempotent and costs nothing, so there is no reason to guard it.
     */
    this.el.classList.toggle('narrow', want);
    if (want === this.narrow) return;
    this.narrow = want;
    // Coming back to a wide window must not leave a sheet class behind: the
    // desktop rules do not mention them, so a stale one is invisible until the
    // window is narrowed again and the wrong panel is open.
    if (!want) this.setSheet('');
  }

  /** Where the chart actually is inside the stage, after letterboxing. */
  private drawnRect(box: DOMRect): { x: number; y: number; w: number; h: number } {
    const aspect = VIEW.w / VIEW.h;
    let w = box.width;
    let h = w / aspect;
    if (h > box.height) {
      h = box.height;
      w = h * aspect;
    }
    return { x: box.left + (box.width - w) / 2, y: box.top + (box.height - h) / 2, w, h };
  }

  /**
   * Put a province on screen, zooming in if the whole realm is showing.
   *
   * Selecting a province from a panel — or having the campaign select one for
   * you, which it does when your host moves — has to move the view, or the
   * selection happens somewhere the player cannot see.
   */
  private lookAt(p: number): void {
    const s = provinceSite(p);
    const w = Math.min(this.view.w, 150);
    const h = w * (VIEW.h / VIEW.w);
    this.view = this.clampView({ x: s.x - w / 2, y: s.y - h / 2, w, h });
    this.applyView();
  }

  private act(c: CampaignState, el: HTMLElement): void {
    const what = el.dataset.act;
    // A button that names its own host beats the selection, so an order given
    // from the march panel cannot land on whatever happened to be selected.
    const army = el.dataset.army2 !== undefined ? Number(el.dataset.army2) : this.selectedArmy;
    const prov = el.dataset.prov2 !== undefined ? Number(el.dataset.prov2) : this.selectedProvince;
    /** Change what the column is about, and redraw at once. */
    const refocus = (f: FocusKind): void => {
      this.focus = f;
      this.draw(c);
    };
    switch (what) {
      case 'zoomin':
        this.zoomBy(1 / 1.5);
        return;
      case 'zoomout':
        this.zoomBy(1.5);
        return;
      case 'zoomfit':
        this.view = { x: VIEW.x, y: VIEW.y, w: VIEW.w, h: VIEW.h };
        this.applyView();
        return;
      case 'view': {
        /**
         * Checked against the list of modes rather than a hand-written set of
         * strings.
         *
         * The set was `crowns || trade || reach`, with everything else falling
         * back to `relations` — so adding the Land view put a tab on the screen
         * that quietly did nothing, and the fallback made it look like a
         * rendering bug rather than a missing case. A mode is a member of
         * `MAP_VIEWS` now, and the tab list and this switch read the same array.
         */
        const want = el.dataset.view as MapView | undefined;
        this.mapView = want && MAP_VIEWS.includes(want) ? want : 'relations';
        this.draw(c);
        return;
      }
      case 'look':
        this.lookAt(this.selectedProvince);
        return;
      case 'levies':
        this.cb.onOpenLevies();
        return;
      case 'levymarks':
        this.levyMarks = !this.levyMarks;
        this.say(
          this.levyMarks
            ? 'Every province of yours, and what it sends the muster each month.'
            : 'Levy marks put away.',
        );
        this.draw(c);
        return;
      case 'lookprov':
        this.selectedProvince = prov;
        this.lookAt(prov);
        refocus('province');
        return;
      case 'tab':
        refocus(
          el.dataset.tab === 'realm'
            ? 'realm'
            : el.dataset.tab === 'market'
              ? 'market'
              : el.dataset.tab === 'muster'
                ? 'muster'
                : 'province',
        );
        return;
      case 'market':
        this.focusNode = Number(el.dataset.node);
        refocus('market');
        return;
      case 'markets':
        // Back out of one market to the whole graph.
        this.focusNode = -1;
        refocus('market');
        return;
      case 'focusfleet': {
        const id = Number(el.dataset.fleet2);
        const f = findFleet(c, id);
        this.focusFleet = id;
        if (f) this.selectedProvince = f.moveTo >= 0 ? f.moveTo : f.at;
        refocus('fleet');
        return;
      }
      case 'focusarmy': {
        const id = Number(el.dataset.army2);
        const a = findArmy(c, id);
        this.focusArmy = id;
        if (a) this.selectedProvince = a.moveTo >= 0 ? a.moveTo : a.at;
        if (a && a.owner === c.playerNation) this.selectedArmy = id;
        refocus(a && a.moveTo >= 0 ? 'march' : 'province');
        return;
      }
      case 'focuscamp':
        this.focusProv = prov;
        this.selectedProvince = prov;
        refocus('camp');
        return;
      case 'focuswarn':
        this.focusProv = prov;
        this.selectedProvince = prov;
        refocus('warn');
        return;
      case 'openwar': {
        const id = Number(el.dataset.war);
        this.openWar = this.openWar === id ? -1 : id;
        refocus('realm');
        return;
      }
      case 'opencrown': {
        const n = Number(el.dataset.nation);
        this.openCrown = this.openCrown === n ? -1 : n;
        refocus('realm');
        return;
      }
      case 'crown':
        this.openCrown = Number(el.dataset.nation);
        refocus('realm');
        return;
      case 'sheet': {
        const want = el.dataset.sheet ?? '';
        this.setSheet(this.sheet === want ? '' : want);
        return;
      }
      case 'home':
        this.home(c);
        return;
      /**
       * Put the panels away.
       *
       * The columns can be made narrower and they cannot be made *absent*, and
       * at some point every strategic map wants to be looked at rather than
       * consulted. This is the same verb those games bind to a key: the board
       * fills the window, the chrome is gone, and one press brings it back.
       *
       * A class on the root rather than a rebuild, deliberately: the panels
       * still exist, still hold their scroll position and their open sheet, and
       * come back exactly as they were left. Rebuilding them would lose all
       * three and cost a repaint of a hundred and fifty polygons for what is a
       * layout change.
       */
      case 'bare':
        this.bare = !this.bare;
        this.el?.classList.toggle('wm-bare', this.bare);
        return;
      /**
       * The key, on request.
       *
       * It used to be always on the sheet, bottom left, a plate as tall as a
       * third of the board. A key is the piece of furniture with the shortest
       * useful life on any map: it is read once, early, by somebody wondering
       * what a hatch means, and never again — and it was paying for that one
       * reading with a permanent corner of the board.
       *
       * Repaints rather than toggling a class, because the plate is drawn
       * inside the furniture SVG and its size depends on the rows in it.
       */
      case 'key':
        this.showKey = !this.showKey;
        this.paint(c);
        return;
      case 'endturn':
        this.cb.onCommand(cmd(Cmd.CampaignEndTurn));
        break;
      case 'raise': {
        // Spend what the treasury will actually stand, not what it looks like
        // it will: iron bought in from somewhere else costs a premium.
        const points = maxAffordablePoints(c, c.playerNation);
        if (points < MIN_ARMY_POINTS) break;
        this.cb.onCommand(cmd(Cmd.CampaignRaiseArmy, prov, points));
        break;
      }
      case 'fortify':
        this.cb.onCommand(cmd(Cmd.CampaignFortify, prov));
        break;
      case 'march':
        this.cb.onCommand(cmd(Cmd.CampaignMoveArmy, army, Number(el.dataset.to)));
        break;
      case 'besiege':
        this.cb.onCommand(cmd(Cmd.CampaignBesiege, army));
        break;
      case 'shelter':
        this.cb.onCommand(cmd(Cmd.CampaignShelterArmy, army));
        break;
      case 'sally':
        this.cb.onCommand(cmd(Cmd.CampaignSallyArmy, army));
        break;
      case 'assault':
        this.cb.onCommand(cmd(Cmd.CampaignAssault, army));
        break;
      case 'reinforce':
        this.cb.onCommand(cmd(Cmd.CampaignReinforce, army, 400));
        break;
      case 'merge':
        this.cb.onCommand(cmd(Cmd.CampaignMergeArmies, army, Number(el.dataset.with)));
        break;
      case 'disband':
        this.cb.onCommand(cmd(Cmd.CampaignDisbandArmy, army));
        this.selectedArmy = -1;
        break;
      case 'marcharmy':
        // A march ordered from somewhere other than the host's own panel — the
        // camp panel's "send a host", which names both ends.
        this.cb.onCommand(cmd(Cmd.CampaignMoveArmy, army, Number(el.dataset.to)));
        this.selectedArmy = army;
        break;
      case 'halt':
        this.cb.onCommand(cmd(Cmd.CampaignHalt, army));
        break;
      case 'intercept':
        this.cb.onCommand(
          cmd(Cmd.CampaignIntercept, Number(el.dataset.chase), Number(el.dataset.quarry)),
        );
        break;
      case 'bounty':
        this.cb.onCommand(cmd(Cmd.CampaignBounty, prov));
        break;
      // ── The sea ───────────────────────────────────────────────────────────
      // Every one of these goes out as a command, exactly like a march. The
      // legality was already decided by whichever `*Refusal` reader greyed the
      // button; the command re-checks it, which is the second line of defence
      // the simulation keeps and the UI must not duplicate.
      case 'buildport':
        this.cb.onCommand(cmd(Cmd.CampaignBuildPort, prov));
        break;
      case 'raisefleet':
        this.cb.onCommand(
          cmd(
            Cmd.CampaignRaiseFleet,
            prov,
            Number(el.dataset.ships),
            Number(el.dataset.fleet2 ?? -1) >= 0 ? Number(el.dataset.fleet2) : 0,
          ),
        );
        break;
      case 'sail':
        this.cb.onCommand(
          cmd(Cmd.CampaignSailFleet, Number(el.dataset.fleet2), Number(el.dataset.to)),
        );
        break;
      case 'embark':
        this.cb.onCommand(cmd(Cmd.CampaignEmbark, Number(el.dataset.fleet2), army));
        break;
      case 'land':
        this.cb.onCommand(
          cmd(Cmd.CampaignFleetAction, Number(el.dataset.fleet2), FleetAction.Disembark),
        );
        break;
      case 'putabout':
        this.cb.onCommand(
          cmd(Cmd.CampaignFleetAction, Number(el.dataset.fleet2), FleetAction.Halt),
        );
        break;
      case 'payoff':
        this.cb.onCommand(
          cmd(Cmd.CampaignFleetAction, Number(el.dataset.fleet2), FleetAction.Disband),
        );
        this.focus = 'province';
        break;
      case 'chase':
        this.cb.onCommand(
          cmd(Cmd.CampaignFleetIntercept, Number(el.dataset.fleet2), Number(el.dataset.quarry)),
        );
        break;
      // ── The markets ───────────────────────────────────────────────────────
      case 'collect':
        this.cb.onCommand(cmd(Cmd.CampaignTradeCollect, Number(el.dataset.node)));
        break;
      case 'steer':
        this.cb.onCommand(
          cmd(Cmd.CampaignTradeSteer, Number(el.dataset.node), Number(el.dataset.toward)),
        );
        break;
      case 'recall':
        this.cb.onCommand(cmd(Cmd.CampaignTradeRecall, Number(el.dataset.node)));
        break;
      // ── The book ──────────────────────────────────────────────────────────
      // The four that only move the draft redraw at once and issue nothing; the
      // two that touch the realm are commands like every other order on this
      // screen, and were greyed by `postRefusal` before they could be clicked.
      case 'mkside':
        this.draft.kind = Number(el.dataset.kind) === OrderKind.Buy ? OrderKind.Buy : OrderKind.Sell;
        this.draft.price = this.seedPrice(c, this.draft.key, this.draft.kind);
        this.clampDraft();
        this.draw(c);
        return;
      case 'mkgood': {
        const key = el.dataset.key as ResourceKey | undefined;
        if (!key || !MARKET_KEYS.includes(key)) return;
        this.draft.key = key;
        this.draft.price = this.seedPrice(c, key, this.draft.kind);
        this.clampDraft();
        this.draw(c);
        return;
      }
      case 'mkamt':
        this.draft.amount =
          el.dataset.all === '1'
            ? Math.floor(c.nations[c.playerNation].treasury[this.draft.key])
            : this.draft.amount + Number(el.dataset.d);
        this.clampDraft();
        this.draw(c);
        return;
      case 'mkprice':
        this.draft.price =
          el.dataset.seed === '1'
            ? this.seedPrice(c, this.draft.key, this.draft.kind)
            : this.draft.price + Number(el.dataset.d);
        this.clampDraft();
        this.draw(c);
        return;
      case 'mkmatch': {
        // Clicking somebody else's line sets the form against it: the other
        // side, their good, their price. The amount is theirs too, clamped by
        // what you could actually put behind it.
        const o = findOrder(c, Number(el.dataset.order));
        if (!o) return;
        this.draft.kind = o.kind === OrderKind.Sell ? OrderKind.Buy : OrderKind.Sell;
        this.draft.key = o.key;
        this.draft.price = o.price;
        this.draft.amount = o.amount;
        this.clampDraft();
        this.draw(c);
        return;
      }
      case 'mkpost':
        this.cb.onCommand(
          cmd(
            this.draft.kind === OrderKind.Sell ? Cmd.MarketPostSell : Cmd.MarketPostBuy,
            RESOURCE_KEYS.indexOf(this.draft.key),
            this.draft.amount,
            this.draft.price,
          ),
        );
        break;
      case 'mkcancel':
        this.cb.onCommand(cmd(Cmd.MarketCancel, Number(el.dataset.order)));
        break;
      case 'answer':
        this.cb.onCommand(
          cmd(Cmd.CampaignAnswerOffer, Number(el.dataset.offer), el.dataset.yes === '1' ? 1 : 0),
        );
        break;
      case 'peace':
        this.cb.onCommand(
          cmd(
            Cmd.CampaignOfferPeace,
            Number(el.dataset.war),
            Number(el.dataset.term),
            Number(el.dataset.prov2),
          ),
        );
        break;
      case 'declare':
        this.cb.onCommand(
          cmd(Cmd.CampaignDeclareWar, Number(el.dataset.nation), Number(el.dataset.cb)),
        );
        break;
      case 'diplo':
        this.cb.onCommand(
          cmd(Cmd.CampaignDiplomacy, Number(el.dataset.diplo), Number(el.dataset.nation)),
        );
        break;
      case 'ledger':
        this.cb.onOpenLedger();
        break;
      case 'city':
        this.cb.onEnterCity();
        break;
      case 'hold':
        this.cb.onEnterHold(Number(el.dataset.prov2));
        break;
      case 'quit':
        this.cb.onQuit();
        break;
      default:
        break;
    }
    // The command lands on the next tick; force a redraw once it has.
    this.forceRepaint();
  }

  private injectStyle(): void {
    const s = document.createElement('style');
    s.textContent = `
      /*
       * Below the resource strip, not over it.
       *
       * This screen was 'position: absolute; inset: 0; z-index: 80' — edge to
       * edge and above everything the HUD owns — so the realm map covered the
       * one bar the settlement calls 'the piece of furniture that is on screen
       * in every mode and never moves'. It also covered the Ledger (75) and the
       * character sheet (70), which is why both of them opened invisibly from
       * here: the key worked, the screen was behind the map.
       *
       * So the map starts under the 40px strip and sits at 50: over the whole
       * settlement HUD, which is about a town this screen is not looking at,
       * and under the strip (55), the tooltip (60), the sheet (70), the Ledger
       * (75), the war menu (92) and the realm's Chronicle (95).
       */
      #worldmap { position: absolute; top: 40px; left: 0; right: 0; bottom: 0;
        z-index: 50; pointer-events: auto;
        background: radial-gradient(ellipse at 50% 40%, #22201c 0%, #100f0d 64%, #070706 100%);
        color: var(--bone); font-size: 12.5px; overflow: hidden; }
      #worldmap.gone { display: none; }
      #worldmap .wm-grid { display: grid; height: 100%; overflow: hidden;
        grid-template-columns: 214px 1fr 278px; grid-template-rows: minmax(0, 1fr) 98px;
        grid-template-areas: "left map right" "left log right"; gap: 0; }
      /* **The map gets the screen back.**

         Measured on a 1600x900 window before this: 268 + 320 of side panel and
         a 132px log left the board about 46% of the screen, and the owner's
         words for that were that the chrome "takes up most of the map space".
         It is a strategic map; the map is the screen's job and the columns are
         a margin around it.

         214 / 278 / 98 hands the board about 1108x760 — a sixth more map by
         area — without either column losing a figure it was carrying, which is
         why this is a resize and not a cull. It went to 206/252 first and that
         was too far by a hair: the right column's tab row clipped "Wars &
         Envoys", and a panel that cuts off a word it is trying to say looks
         broken in a way that a slightly wider one does not. What the columns cannot do at any
         width is get out of the way entirely, and that is what the button
         below is for. */
      #worldmap .wm-left { grid-area: left; padding: 20px 18px; overflow-y: auto;
        border-right: 1px solid rgba(200,164,76,0.16); background: rgba(0,0,0,0.28); }
      #worldmap .wm-right { grid-area: right; padding: 20px 18px; overflow-y: auto;
        border-left: 1px solid rgba(200,164,76,0.16); background: rgba(0,0,0,0.28); }
      /* min-height:0 is load-bearing. A grid row of 1fr has an automatic
         minimum of its content, and an SVG with width:100% sizes its height
         from the viewBox aspect ratio — so the map grew to a square as wide as
         the column and shoved the chronicle and the end-turn button clean off
         the bottom of the screen. */
      #worldmap .wm-map { grid-area: map; display: flex; align-items: center;
        justify-content: center; min-height: 0; min-width: 0; overflow: hidden; }
      /* Two SVGs, stacked. The lower one pans and zooms; the upper one carries
         the cartographer's furniture and does not move, because a compass that
         scrolls off the top of the screen is not a compass. */
      #worldmap .wm-stage { position: relative; width: 100%; height: 100%;
        cursor: grab; user-select: none; }
      #worldmap .wm-stage.dragging { cursor: grabbing; }
      #worldmap .wm-stage > svg { position: absolute; inset: 0; width: 100%; height: 100%; }
      #worldmap .wm-frame { pointer-events: none; }
      /* The unit on a number that has two kinds of quantity beside each other.
         Small and dim: it is there to be read once and then ignored. */
      #worldmap .wm-unit { font-size: 9px; letter-spacing: 0.06em; color: var(--bone-deep);
        font-weight: 400; margin-left: 1px; }
      #worldmap .wm-zoom { position: absolute; right: 14px; top: 14px; display: flex;
        flex-direction: column; gap: 4px; z-index: 2; }
      #worldmap .wm-zoom span { width: 26px; height: 26px; display: flex; align-items: center;
        justify-content: center; cursor: pointer; font-size: 15px; line-height: 1;
        color: var(--gold-pale); background: rgba(12,12,10,0.72);
        border: 1px solid rgba(200,164,76,0.3); }
      #worldmap .wm-zoom span:hover { background: rgba(200,164,76,0.24); }

      /* ── The view switch and its key ─────────────────────────────────────
         Top-left, opposite the zoom buttons.

         Not bottom-left, which is where it went first and where it landed on top
         of the cartographer's own legend — "The Key", the frontier and ford and
         old-road rows — and on the colophon under it. Two keys in one corner,
         one drawn on the chart and one floating over it, and the reader has to
         work out that they are different objects. The furniture layer owns three
         of the four corners already: the cartouche is top-centre, the compass
         top-right, the mini-map bottom-right, the legend and the colophon
         bottom-left. Top-left is the corner it does not want.

         max-height and overflow-y are not decoration. The key is one row per
         relation that exists on the board, seven at the outside, plus a line for
         the brigands; on a short window that is taller than the map, and a key
         that covers the thing it explains is worse than no key. Capping it at
         two-fifths of the stage and letting it scroll is the same rule every
         other overflowing panel here follows.

         The container takes pointer-events:none and gives it back to the
         controls. The panel sits *on* the chart, and a dead rectangle over the
         sea that swallows a drag is a map that has stopped panning for no
         visible reason — the frontier tooltips and the town labels are all
         pointer-transparent for exactly this reason. */
      #worldmap .wm-view { position: absolute; left: 14px; top: 14px; z-index: 2;
        display: flex; flex-direction: column; gap: 6px; align-items: flex-start;
        pointer-events: none; max-width: 220px; }
      #worldmap .wm-viewtabs { display: flex; pointer-events: auto;
        border: 1px solid rgba(200,164,76,0.3); background: rgba(12,12,10,0.82); }
      #worldmap .wm-viewtabs span { padding: 4px 10px; cursor: pointer; font-size: 10px;
        font-family: 'Cinzel', Georgia, serif; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--bone-deep); line-height: 1.4; }
      #worldmap .wm-viewtabs span + span { border-left: 1px solid rgba(200,164,76,0.22); }
      #worldmap .wm-viewtabs span:hover { color: var(--gold-pale); background: rgba(200,164,76,0.14); }
      #worldmap .wm-viewtabs span.on { color: var(--gold); background: rgba(200,164,76,0.2); }
      /* The key is a reference, not a headline: it is read once when somebody
         wonders what a hatch means and ignored for the rest of the evening. It
         was set at the same weight as the panels that carry live figures, and
         at 40vh it could run a third of the way down the screen. Tighter rows,
         a smaller swatch and half the height — the map is what the screen is
         for. */
      /* The climate line: a sentence in the column of figures, so it is set as
         one — italic, quieter than the numbers, and allowed to wrap. */
      #worldmap .wm-climate { color: var(--bone-dim); font-size: 11px; font-style: italic;
        line-height: 1.5; padding: 3px 0 5px; border-bottom: 1px solid rgba(200,164,76,0.10);
        margin-bottom: 3px; }
      #worldmap .wm-keylist { pointer-events: auto; display: flex; flex-direction: column;
        gap: 1px; padding: 5px 7px; background: rgba(12,12,10,0.82);
        border: 1px solid rgba(200,164,76,0.18); max-height: 22vh; overflow-y: auto; }
      #worldmap .wm-keyrow { display: flex; align-items: center; gap: 5px;
        font-size: 9.5px; color: var(--bone-dim); line-height: 1.35; white-space: nowrap; }
      /* Your own line is the one the eye is looking for, so it is the one that is
         not grey. Everything else in the key is deliberately quieter than it. */
      #worldmap .wm-keyrow.own { color: var(--gold-pale); }
      #worldmap .wm-keyrow.dimline { color: var(--bone-deep); font-style: italic;
        white-space: normal; max-width: 168px; line-height: 1.45; }
      #worldmap .wm-keyrow b { font-weight: 500; font-variant-numeric: tabular-nums;
        min-width: 15px; text-align: right; color: var(--bone); }
      #worldmap .wm-keyrow.own b { color: var(--gold); }
      /* A realm cut in pieces is a warning, not a statistic — the only red line
         the key ever shows, and it appears only when it is true. */
      #worldmap .wm-keyrow.cut { color: var(--oxblood-light, #a8362c); }
      #worldmap .wm-keyrow.cut b { color: var(--oxblood-light, #a8362c); }
      /* A fixed box, not a scaled one: this swatch is a sample of the map at the
         map's own pattern scale, and letting it grow with the font would show a
         different hatch density than the board does. */
      #worldmap .wm-keysw { width: 9px; height: 9px; flex: none; display: block; }
      #worldmap .wm-look { float: right; font-style: normal; cursor: pointer;
        color: var(--gold-deep); font-size: 15px; line-height: 1; }
      #worldmap .wm-look:hover { color: var(--gold-pale); }
      #worldmap .wm-log { grid-area: log; display: flex; align-items: stretch;
        border-top: 1px solid rgba(200,164,76,0.16); background: rgba(0,0,0,0.34); }
      #worldmap .wm-chron { flex: 1; padding: 12px 18px; overflow-y: auto; line-height: 1.7;
        color: var(--bone-dim); font-size: 12px; }
      #worldmap .wm-chron div:first-child { color: var(--bone); }
      #worldmap .wm-turn { display: flex; align-items: center; padding: 0 24px;
        border-left: 1px solid rgba(200,164,76,0.16); }
      #worldmap .wm-title { font-family: 'Cinzel', Georgia, serif; font-size: 19px;
        letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold); margin-bottom: 6px; }
      #worldmap .wm-title.small { font-size: 16px; }
      #worldmap .wm-creed { font-style: italic; color: var(--bone-deep); line-height: 1.6;
        margin-bottom: 16px; font-size: 11.5px; }
      #worldmap .wm-sub { font-family: 'Cinzel', Georgia, serif; font-size: 10px;
        letter-spacing: 0.22em; text-transform: uppercase; color: var(--gold-deep);
        margin: 18px 0 8px; border-top: 1px solid rgba(200,164,76,0.14); padding-top: 12px; }
      #worldmap .wm-stat { display: flex; justify-content: space-between; padding: 3px 0;
        font-variant-numeric: tabular-nums; }
      #worldmap .wm-stat span { color: var(--bone-deep); }
      #worldmap .wm-stat b { font-weight: 500; text-align: right; }
      #worldmap .wm-stat b.bad { color: var(--blood, #a8362c); }
      /* Yours, in the one colour on this screen that means yours — the same gold
         the relations view tints your ground with. */
      #worldmap .wm-stat b.own { color: var(--gold-pale); }
      /* The banner at the head of a province you hold: that you have it, and how
         you came by it. Gold rule down the left, the same mark the crown rows
         and the host rows use to say "this one is mine". */
      #worldmap .wm-own { margin: 0 0 12px; padding: 7px 10px;
        border-left: 2px solid var(--gold); background: rgba(200,164,76,0.1);
        font-family: 'Cinzel', Georgia, serif; font-size: 11px; letter-spacing: 0.18em;
        text-transform: uppercase; color: var(--gold); }
      #worldmap .wm-own em { display: block; margin-top: 3px; font-family: inherit;
        font-style: italic; font-size: 10.5px; letter-spacing: 0.02em;
        text-transform: none; color: var(--bone-dim); line-height: 1.5; }
      /* Why the takings are short. Sits directly under the Pays row it explains,
         indented past the label column so it reads as a footnote to that figure
         and not as another statistic. */
      #worldmap .wm-short { margin: -1px 0 4px; padding-left: 1px; font-size: 10.5px;
        font-style: italic; color: var(--oxblood-light, #a8362c); line-height: 1.5; }
      #worldmap .wm-stat i { font-style: normal; font-size: 11px; }
      #worldmap .wm-stat i.up { color: #7d9436; }
      #worldmap .wm-stat i.down { color: #a8362c; }
      #worldmap .wm-rival { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
      #worldmap .wm-rival i { width: 9px; height: 9px; border-radius: 2px; flex: none; }
      #worldmap .wm-rival span { flex: 1; color: var(--bone-dim); }
      /* The plain race, under the proper name: which power is which, at a glance. */
      #worldmap .wm-rival span em { display: block; font-style: normal; font-size: 9.5px;
        letter-spacing: 0.1em; text-transform: uppercase; color: var(--bone-deep); }
      #worldmap .wm-race { font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--gold); margin-top: 2px; }
      #worldmap .wm-rival.me span { color: var(--gold-pale); }
      #worldmap .wm-rival b { font-variant-numeric: tabular-nums; font-weight: 500; }
      #worldmap .wm-army { padding: 7px 9px; margin-bottom: 6px; cursor: pointer;
        background: rgba(255,255,255,0.03); border-left: 2px solid rgba(200,164,76,0.2); }
      #worldmap .wm-army.mine { border-left-color: var(--gold); }
      #worldmap .wm-army.sel { background: rgba(200,164,76,0.14); }
      #worldmap .wm-army b { display: block; font-weight: 500; }
      #worldmap .wm-army span { display: block; font-size: 11px; color: var(--bone-deep); }
      #worldmap .wm-army .wm-dim { color: rgba(200,190,170,0.4); font-size: 10.5px; }
      #worldmap .wm-acts { display: flex; flex-wrap: wrap; gap: 6px; }
      #worldmap .wm-btn { padding: 7px 10px; cursor: pointer; font-size: 11.5px;
        background: rgba(255,255,255,0.035); border: 1px solid rgba(200,164,76,0.2);
        clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%); }
      #worldmap .wm-btn:hover { background: rgba(200,164,76,0.16); border-color: var(--gold); }
      #worldmap .wm-btn.ghost { border-color: rgba(200,164,76,0.1); color: var(--bone-deep); }
      #worldmap .wm-btn.off { opacity: 0.35; pointer-events: none; }
      #worldmap .wm-btn.march { border-color: rgba(200,164,76,0.14); }
      #worldmap .wm-btn i { font-style: normal; display: block; font-size: 10px;
        color: var(--bone-deep); margin-top: 2px; }
      #worldmap .wm-seal { font-family: 'Cinzel', Georgia, serif; font-size: 12px;
        letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-pale);
        padding: 11px 20px; cursor: pointer; border: 1px solid var(--gold-deep);
        background: rgba(200,164,76,0.1); white-space: nowrap; }
      #worldmap .wm-seal:hover { background: rgba(200,164,76,0.24); }
      #worldmap .wm-seal.off { opacity: 0.35; pointer-events: none; }
      #worldmap .wm-won { font-family: 'Cinzel', Georgia, serif; color: var(--gold);
        letter-spacing: 0.14em; }
      /* What stands where the End Turn seal used to. A reading, not a control. */
      #worldmap .wm-clock { font-family: 'Cinzel', Georgia, serif; font-size: 13px;
        letter-spacing: 0.12em; color: var(--gold); text-align: center; white-space: nowrap; }
      #worldmap .wm-clock i { display: block; font-family: Inter, system-ui, sans-serif;
        font-style: normal; font-size: 10.5px; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--bone-deep); margin-top: 5px; }
      #worldmap .wm-ghost { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--bone-deep); cursor: pointer; }
      #worldmap .wm-ghost:hover { color: var(--bone); }
      #worldmap .wm-foot { margin-top: 24px; }
      #worldmap .wm-hint { color: var(--bone-deep); font-size: 11px; line-height: 1.6; margin-top: 10px; }

      /* ── The chart ──────────────────────────────────────────────────────
         Everything below is drawn in map units (the view box is about 156
         across), so widths look absurd until you remember one unit is roughly
         seven screen pixels. The only elements that take a pointer are the
         territory fills: every other layer is pointer-events:none so that a
         click on a name, a road or a mountain still lands on the ground it is
         drawn over. That is what keeps the whole province clickable. */
      #worldmap .wm-chart text { pointer-events: none; }
      #worldmap .wm-cell { cursor: pointer; transition: filter 120ms ease, fill-opacity 120ms ease; }
      /* The hover *adds* weight rather than only brightening, because the fill
         is a translucent tint over the biome wash now: a province at 0.07 has
         almost nothing for brightness() to brighten. The --wm-t property is the
         fill's own weight, written on the element by territoryLayer. */
      #worldmap .wm-cell:hover { filter: brightness(1.4);
        fill-opacity: calc(var(--wm-t, 0.2) + 0.2); }
      #worldmap .wm-band, #worldmap .wm-terrain, #worldmap .wm-borders,
      #worldmap .wm-roads, #worldmap .wm-towns, #worldmap .wm-furniture,
      #worldmap .wm-lettering, #worldmap .wm-overlay,
      #worldmap .wm-relhatch { pointer-events: none; }

      /* ── The campaign's counters ─────────────────────────────────────────
         Everything above the territory fill is pointer-events:none so a click
         on a mountain still lands on the ground. A counter takes that back for
         *itself*, and only over its hit disc: pointer-events:all rather than the
         default, so an unpainted circle is still a target and a camp triangle
         does not have to be hunted for with the cursor.

         These are the one thing on the chart drawn in map units that does not
         scale with the map. They carry numbers, so they follow the type rule —
         applyView writes scale(--wm-k) into their transform, which is why every
         size below is the size it is on the glass at every zoom. Do not put a
         calc(N * var(--wm-k)) on anything inside one: it would be applied
         twice. */
      #worldmap .wm-counter { pointer-events: auto; cursor: pointer; }
      #worldmap .wm-counter .wm-hit { fill: none; pointer-events: all; }
      #worldmap .wm-countertext { font-size: 2.6px; font-weight: 700; fill: #12100e;
        text-anchor: middle; font-family: 'Inter', sans-serif; pointer-events: none; }
      #worldmap .wm-countertext.warn { font-size: 3px; fill: var(--bone); }
      /* The strength plate under a host. Dark and near-opaque rather than tinted
         with the owner's accent: this number is read *against* the neighbouring
         stack's, so it has to be the same colour on both or the comparison is
         about the crowns instead of the men. */
      /* **No plate.** The strength used to sit on a dark rounded box, which read
         as a piece of interface dropped on the map rather than as part of the
         mark — and a box is a shape competing with the figure above it. The
         number carries its own casing instead, the way every place name on this
         chart does: paint-order stroke, so it is legible over pale sand and
         dark water alike without anything drawn behind it. */
      #worldmap .wm-hostmen { font-size: 4.2px; font-weight: 700; fill: var(--bone);
        text-anchor: middle; font-family: 'Inter', sans-serif; pointer-events: none;
        font-variant-numeric: tabular-nums;
        paint-order: stroke; stroke: rgba(9,9,8,0.92); stroke-width: 1.5px;
        stroke-linejoin: round; }
      /* The arm's mark on the board takes the same casing, for the same reason. */
      #worldmap .wm-armmark { fill: var(--bone); }
      #worldmap .wm-armmark.host { fill: #f0e8d3; stroke: rgba(9,9,8,0.92);
        stroke-width: 1.6px; paint-order: stroke; stroke-linejoin: round; }
      /* The arm above a host's name in the column. */
      #worldmap .wm-armline { display: flex; align-items: center; gap: 5px;
        font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase;
        color: var(--bone-deep); margin-bottom: 1px; }
      #worldmap .wm-armline svg { width: 12px; height: 12px; flex: none; display: block; }
      #worldmap .wm-army.mine .wm-armline { color: var(--gold-deep); }
      #worldmap .wm-hoststack { font-size: 2.7px; font-weight: 500; fill: var(--bone-dim); }
      #worldmap .wm-counter > path, #worldmap .wm-counter > circle:not(.wm-hit) {
        transition: filter 100ms ease; }
      #worldmap .wm-counter:hover > path, #worldmap .wm-counter:hover > circle:not(.wm-hit) {
        filter: brightness(1.45) drop-shadow(0 0 1.2px rgba(240,232,211,0.9)); }
      /* The selected counter wears a ring rather than a colour, so a host does
         not change crown when you click it. */
      #worldmap .wm-counter.sel > path, #worldmap .wm-counter.sel > circle:not(.wm-hit) {
        stroke: var(--gold-pale); stroke-width: 0.7; paint-order: stroke; }
      #worldmap .wm-marcher.mine > path { stroke: rgba(240,232,211,0.85); }
      #worldmap .wm-camp path { fill: #7a2f26; stroke: #12100e; stroke-width: 0.4; }
      #worldmap .wm-camp .wm-campin { fill: #e0742a; stroke: none; }
      #worldmap .wm-camp.paid path { fill: #6d675e; }
      #worldmap .wm-camp.paid .wm-campin { fill: #a89e8c; }
      #worldmap .wm-warn path, #worldmap .wm-warn circle:not(.wm-hit) {
        fill: #d8a53a; stroke: #12100e; stroke-width: 0.4; }
      #worldmap .wm-warn.urgent path, #worldmap .wm-warn.urgent circle:not(.wm-hit) { fill: #a8302f; }

      /* The hover card. One element, moved rather than rebuilt, and never a
         hit target of its own — a tooltip that eats the click that dismissed it
         is worse than no tooltip. */
      #worldmap .wm-tip { position: absolute; z-index: 90; pointer-events: none;
        max-width: 340px; padding: 8px 11px; background: rgba(10,10,9,0.96);
        border: 1px solid rgba(200,164,76,0.34); color: var(--bone);
        font-size: 11.5px; line-height: 1.55; white-space: pre-line;
        box-shadow: 0 6px 22px rgba(0,0,0,0.6); }
      #worldmap .wm-tip.gone { display: none; }

      /* ── The terrain vocabulary ───────────────────────────────────────────
         Every glyph here is a *fill* with a hairline casing, never a hairline
         on its own. A stroke is held to a constant weight on the glass while
         the geometry under it grows with the map, so a shape drawn only in
         strokes is a wire when you are close and nothing when you are out —
         which is exactly what the anchor did before it was filled, and exactly
         what the first cut of these glyphs did. Only the casings and the two
         genuinely linear marks (the marsh's water courses, the coast's swell)
         are multiplied by --wm-k; the shapes themselves belong to the ground
         and grow with it.

         **Read the widths below at twice their face value.** --wm-k is 1 at the
         fit view, where the chart draws about 518 map units across a thousand
         pixels of glass — so "0.2px" is a fifth of a *map unit*, which is a
         tenth of a screen pixel shy of a half. Every casing here was picked
         against that arithmetic and not against the number.

         One ink family for the lot, so the land reads as one hand: bone for
         anything standing up out of the ground, near-black underneath it. */
      #worldmap .wm-terr path { fill: rgba(228,218,196,0.34); stroke: none;
        stroke-linecap: round; stroke-linejoin: round; }

      /* Mountains. The dark body is the range; the pale triangle down each
         summit's west face is the whole difference between a black tooth and a
         mountain at eight pixels. */
      #worldmap .wm-terr .ridge { fill: rgba(20,18,14,0.76);
        stroke: rgba(238,230,208,0.6); stroke-width: calc(0.3px * var(--wm-k, 1)); }
      #worldmap .wm-terr .ridgelit { stroke: none; }
      /* The shadow a peak throws on its own county. Warm-dark rather than black:
         a neutral shadow over ground this coloured reads as a hole. */
      #worldmap .wm-terr .ridgecast { fill: rgba(26,20,14,0.22); stroke: none; }
      #worldmap .wm-terr .ridgesnow { fill: rgba(252,250,244,0.72); stroke: none; }
      #worldmap .wm-terr .ridgehach { fill: none; stroke: rgba(228,220,198,0.34);
        stroke-width: calc(0.24px * var(--wm-k, 1)); stroke-linecap: round; }
      #worldmap .wm-terr .hill { fill: rgba(28,25,20,0.4);
        stroke: rgba(230,221,199,0.5); stroke-width: calc(0.26px * var(--wm-k, 1)); }
      #worldmap .wm-terr .hilllit { stroke: none; }

      /* The Deadwood. A canopy says "wood" from across the room; the bare fork
         says which wood this is.

         Dark fill under a bone casing, the same pairing the ridge uses, and for
         the same reason: the ground under a glyph runs from near-black where
         nobody holds it to a pale gold where you do, and a mark drawn in one
         value alone disappears on half the board. Two values cannot. */
      #worldmap .wm-terr .tree { fill: rgba(24,25,19,0.55);
        stroke: rgba(230,222,200,0.55); stroke-width: calc(0.22px * var(--wm-k, 1)); }
      #worldmap .wm-terr .tree.bare { fill: rgba(226,218,196,0.44); stroke: none; }

      #worldmap .wm-terr .tussock { fill: rgba(168,192,178,0.5); stroke: none; }
      #worldmap .wm-terr .marshwater { fill: none; stroke: rgba(146,180,192,0.62);
        stroke-width: calc(0.4px * var(--wm-k, 1)); stroke-linecap: butt; }
      #worldmap .wm-terr .tuft { fill: rgba(216,205,182,0.3); stroke: none; }
      #worldmap .wm-terr .ripple { fill: none; stroke: rgba(154,190,204,0.44);
        stroke-width: calc(0.27px * var(--wm-k, 1)); }
      #worldmap .wm-terr .shingle { fill: rgba(214,204,182,0.4); stroke: none; }
      #worldmap .wm-terr .ruin { fill: rgba(28,25,20,0.42);
        stroke: rgba(222,212,190,0.55); stroke-width: calc(0.18px * var(--wm-k, 1)); }

      /* The continents' three. Fronds take the tree's dark-under-bone pairing —
         same family, sharper silhouette; the understorey dome goes casingless so
         it stays background. Dunes are the one warm mark on the chart and carry
         no casing at all: sand has no edges, the crest line is its one hard
         fact. Seracs are pale-under-black — the tree's pairing inverted, because
         ice is the one ground brighter than what stands on it. */
      #worldmap .wm-terr .frond { fill: rgba(18,28,16,0.6);
        stroke: rgba(214,226,196,0.5); stroke-width: calc(0.2px * var(--wm-k, 1)); }
      #worldmap .wm-terr .canopy { fill: rgba(30,42,26,0.4); stroke: none; }
      #worldmap .wm-terr .dune { fill: rgba(205,180,132,0.28); stroke: none; }
      #worldmap .wm-terr .dunecrest { fill: none; stroke: rgba(16,14,10,0.5);
        stroke-width: calc(0.22px * var(--wm-k, 1)); stroke-linecap: round; }
      #worldmap .wm-terr .serac { fill: rgba(198,222,232,0.5);
        stroke: rgba(10,12,14,0.55); stroke-width: calc(0.2px * var(--wm-k, 1));
        stroke-linejoin: round; }
      #worldmap .wm-terr .icedrift { fill: none; stroke: rgba(212,232,240,0.5);
        stroke-width: calc(0.3px * var(--wm-k, 1)); stroke-linecap: round; }

      /* The level of detail for the terrain is not a CSS rule — it is the one
         layer big enough that keeping both versions in the document costs more
         than swapping them. See terrFar and applyView. */

      /* ── The three pens ───────────────────────────────────────────────────
         A coast, a frontier and a shire line have to be three visibly different
         marks or the map is one weight of line drawn in three places. In order
         of how much they are allowed to say:

           coast     a heavy near-black shoreline with a fine bone line inside it
           frontier  a narrow near-black line on a wide pale casing
           shire     a fine dotted hint, and nothing more

         The frontier's casing is what makes it read as *drawn*. The geometry
         under it is traced off a raster and smoothed — it is soft, and that is
         not fixable from this file — but a soft line lifted off the ground by a
         pale halo and topped with a thin dark stroke reads as a pen. A single
         wide dark stroke, which is what this was, reads as puff. */
      #worldmap .wm-shire { fill: none; stroke: rgba(226,216,193,0.2);
        stroke-width: calc(0.2px * var(--wm-k, 1)); stroke-linecap: round;
        stroke-dasharray: calc(0.28px * var(--wm-k, 1)) calc(1.15px * var(--wm-k, 1)); }
      #worldmap .wm-frontier-case { fill: none; stroke: rgba(240,232,210,0.2);
        stroke-width: calc(2.1px * var(--wm-k, 1)); stroke-linejoin: round; }
      #worldmap .wm-frontier { fill: none; stroke: rgba(13,12,10,0.92);
        stroke-width: calc(0.62px * var(--wm-k, 1)); stroke-linejoin: round; }
      #worldmap .wm-borders { paint-order: stroke; }

      /* The shoreline, and it is the heaviest pen on the sheet, because the one
         thing this world is about is which ground you can walk to.

         Both of these used to be plain stroke-width attributes in map units —
         constant *on the paper* rather than on the glass, so four notches in the
         coast was an eight-pixel wall round every island. They are --wm-k'd
         like every other line on the chart now. */
      #worldmap .wm-coast { stroke: rgba(5,7,9,0.9);
        stroke-width: calc(1.15px * var(--wm-k, 1)); stroke-linejoin: round; }
      #worldmap .wm-coast-edge { fill: none; stroke: rgba(238,228,202,0.5);
        stroke-width: calc(0.3px * var(--wm-k, 1)); }
      /* The shoreline over the ground. Narrower than the bed's coast and no
         paler: the bed's is doing the drop-shadow job under the fills, and this
         one only has to be the line. */
      #worldmap .wm-coast-case { fill: none; stroke: rgba(240,233,212,0.32);
        stroke-width: calc(2.4px * var(--wm-k, 1)); stroke-linejoin: round; }
      #worldmap .wm-coast-top { fill: none; stroke: rgba(10,11,12,0.85);
        stroke-width: calc(0.9px * var(--wm-k, 1)); stroke-linejoin: round; }
      /* An ironbound coast, and the hachures that say so.

         The line is the coast's own pen laid on twice as heavy; the ticks are a
         *ground* mark and so are in map units rather than glass units, exactly
         as the terrain hatching is — they grow with the ground they describe and
         are correctly sub-pixel at the fit view, where the baked strand and the
         relief are carrying the shore instead. */
      #worldmap .wm-cliff { fill: none; stroke: rgba(8,9,10,0.85);
        stroke-width: calc(0.85px * var(--wm-k, 1)); stroke-linejoin: round;
        stroke-linecap: round; }
      #worldmap .wm-cliff-tick { fill: none; stroke: rgba(12,12,11,0.62);
        stroke-width: calc(0.24px * var(--wm-k, 1)); stroke-linecap: round; }
      /* Right in, a hachure a map unit long is twenty-five pixels of black
         sticking out into the sea, and the coastline is carrying the shore on
         its own by then anyway. */
      #worldmap .wm-lod3 .wm-cliff-tick { display: none; }
      #worldmap .wm-selring { fill: none; stroke: var(--gold-pale);
        stroke-width: calc(0.55px * var(--wm-k, 1));
        stroke-dasharray: calc(2.2px * var(--wm-k, 1)) calc(1.1px * var(--wm-k, 1)); }
      /* Nothing selected. The ring is still in the tree - see borderLayer. */
      #worldmap .wm-selring.off { display: none; }
      /* The holder's colour, as a band inside the frontier — this is where a
         chart has always put identity, and it stays whichever view is on.

         Narrower than it was, and it matters. At 1.7 units of a saturated
         national accent at half opacity it was the loudest mark on the sheet
         and it read as a *glow* rather than as a line: the puffiness people saw
         in the borders was mostly this, not the frontier under it. Narrow and
         more opaque is the same amount of ink saying a cleaner thing, and it
         leaves the near-black frontier line visible on top of it, which is what
         separates two crowns of similar colour. */
      #worldmap .wm-band { stroke-width: calc(1.15px * var(--wm-k, 1)); }
      #worldmap .wm-cell.sel + .wm-band, #worldmap .wm-band.sel {
        stroke-width: calc(2.0px * var(--wm-k, 1)); }
      /* Your own frontier, drawn half again as heavy as anybody else's.
         The tint and the hatch already say "yours" inside the province; this is
         the same sentence said at its edge, which is what makes the *shape* of
         your realm findable when it is six provinces scattered among sixty.
         Multiplied by --wm-k like every other line on the chart, so it stays
         one weight on the glass however far in you are. */
      #worldmap .wm-band.own { stroke-width: calc(2.1px * var(--wm-k, 1)); }
      #worldmap .wm-band.own.sel { stroke-width: calc(2.9px * var(--wm-k, 1)); }
      /* The selected band, on ground somebody holds, used to carry its 0.78 as
         an opacity attribute written at build time — which made selection a
         markup change. A rule beats a presentation attribute without needing
         an important flag, so the attribute stays at the unselected weight and this
         lifts it. Unowned ground has no lifted state and matches nothing here. */
      #worldmap .wm-band[data-owned="1"].sel { opacity: 0.78; }

      /* Roads and crossings. */
      #worldmap .wm-road-case { fill: none; stroke: rgba(10,9,8,0.62);
        stroke-width: calc(0.95px * var(--wm-k, 1)); stroke-linecap: round; }
      #worldmap .wm-road { fill: none; stroke: rgba(210,184,130,0.7);
        stroke-width: calc(0.46px * var(--wm-k, 1)); stroke-linecap: round; }
      #worldmap .wm-track { fill: none; stroke: rgba(214,204,182,0.42);
        stroke-width: calc(0.34px * var(--wm-k, 1));
        stroke-dasharray: calc(0.8px * var(--wm-k, 1)) calc(1.5px * var(--wm-k, 1));
        stroke-linecap: round; }
      #worldmap .wm-ford { fill: none; stroke: rgba(126,170,190,0.6);
        stroke-width: calc(0.62px * var(--wm-k, 1)); stroke-linecap: round; }
      #worldmap .wm-pass { fill: none; stroke: rgba(220,210,188,0.42);
        stroke-width: calc(0.42px * var(--wm-k, 1));
        stroke-dasharray: calc(1.9px * var(--wm-k, 1)) calc(1.2px * var(--wm-k, 1)); }
      #worldmap .wm-cross { fill: none; stroke: rgba(228,218,196,0.7);
        stroke-width: calc(0.32px * var(--wm-k, 1)); stroke-linecap: round; }

      /* ── Towns ────────────────────────────────────────────────────────────
         **Nothing in this block is multiplied by --wm-k, and that is not an
         oversight — it is where the group already is.** A town is drawn inside
         .wm-town, which applyView scales by the zoom's reciprocal, so every
         width and every size here is already a size on the glass and a second
         calc(... * var(--wm-k)) would apply the reciprocal twice: a keep that
         shrank to nothing four notches in. .wm-mark had exactly that calc, and
         with the silhouette growing with the ground underneath it the pair came
         out as a black blob with an invisible outline at the old maximum. */
      #worldmap .wm-groundpic { float: right; width: 88px; margin: 2px 0 8px 14px;
        filter: saturate(0.62) sepia(0.14) contrast(0.97);
        -webkit-mask-image: radial-gradient(ellipse 92% 88% at 50% 46%, #000 62%, transparent 98%);
        mask-image: radial-gradient(ellipse 92% 88% at 50% 46%, #000 62%, transparent 98%); }
      #worldmap .wm-mark { fill: #17150f; stroke: rgba(232,222,198,0.9);
        stroke-width: 0.3; stroke-linejoin: round; }
      /* The shadow a keep throws, and the light on its north-west face. Both
         are the same path as the body, moved — see litMark. The cast copy loses
         its outline or the shadow would have a bone edge round it; the lit copy
         keeps a wide one, because the rim is *made* of that stroke. */
      #worldmap .wm-markcast .wm-mark { fill: rgba(8,8,7,0.42); stroke: none; }
      #worldmap .wm-markcast .wm-gate { fill: rgba(8,8,7,0.3); }
      #worldmap .wm-marklit .wm-mark { fill: #cfc4a6; stroke: rgba(244,238,220,0.95);
        stroke-width: 0.5; }
      #worldmap .wm-marklit .wm-gate { fill: none; }
      #worldmap .wm-gate { fill: rgba(226,216,193,0.75); stroke: none; }
      #worldmap .wm-pole { stroke: rgba(232,222,198,0.75); stroke-width: 0.22; fill: none; }
      #worldmap .wm-flag { stroke: rgba(12,11,9,0.8); stroke-width: 0.18; }
      #worldmap .wm-capstar { stroke: rgba(12,11,9,0.75); stroke-width: 0.2; }
      #worldmap .wm-selmark { display: none;
        fill: none; stroke: var(--gold-pale); stroke-width: 0.45; }
      #worldmap .wm-town.sel .wm-selmark { display: inline; }
      /* Home: the double gold laurel under the player's own capital. */
      /* ── Where you are ───────────────────────────────────────────────────
         > *"your position needs to be way more visible"*

         This was two hairline rings, and its own comment called it "the same
         quiet badge at every zoom" — which was the right call on a board of
         twenty-four provinces and is the wrong one on six hundred and fifty.
         At the fit view the player's own seat was two 0.5-unit strokes among a
         hundred and fifty other towns, all of which also have rings, banners
         and stars.

         So it is now the loudest thing on the sheet, in three layers that fail
         independently: a soft disc that survives at any zoom because it is
         *area* rather than line; a heavy ring; and the original two hairlines
         inside it, kept because they are what makes it read as a *badge* close
         up rather than as a blob.

         The breath is slow — four seconds — and moves opacity only. A mark that
         moves or resizes draws the eye away from the map on every cycle; one
         that brightens is findable when you are looking for it and ignorable
         when you are not. A reduced-motion preference turns it off and leaves the
         mark at full strength, because the visibility is the point and the
         animation is only how it is found. */
      #worldmap .wm-homeglow { fill: rgba(200,164,76,0.13); stroke: none;
        animation: wm-breathe 4s ease-in-out infinite; }
      #worldmap .wm-homering { fill: none; stroke: var(--gold); stroke-width: 0.85;
        opacity: 0.75; animation: wm-breathe 4s ease-in-out infinite; }
      @keyframes wm-breathe { 0%, 100% { opacity: 0.42; } 50% { opacity: 0.95; } }
      @media (prefers-reduced-motion: reduce) {
        #worldmap .wm-homeglow, #worldmap .wm-homering { animation: none; opacity: 0.85; }
      }
      #worldmap .wm-home { fill: none; stroke: var(--gold); stroke-width: 0.5; opacity: 0.9; }
      #worldmap .wm-home.in { stroke-width: 0.28; opacity: 0.6; }
      #worldmap .wm-host { stroke: rgba(0,0,0,0.75); stroke-width: 0.22; }
      #worldmap .wm-host.mine { fill: #e8dfc8; }
      #worldmap .wm-host.theirs { fill: #a8362c; }
      #worldmap .wm-siege { fill: none; stroke: #a8362c; stroke-width: 0.4;
        stroke-dasharray: 1 1.4; }
      /* A lane is a line, so it obeys the line rule: constant on the glass. It
         did not, and four notches in it was a rope laid across the county. */
      #worldmap .wm-march { fill: none; stroke-width: calc(0.7px * var(--wm-k, 1));
        stroke-dasharray: calc(1.6px * var(--wm-k, 1)) calc(1.2px * var(--wm-k, 1));
        opacity: 0.95; }
      #worldmap .wm-march.sel { stroke-width: calc(1.15px * var(--wm-k, 1)); opacity: 1; }

      /* The route under the cursor, while one of your hosts is selected.
         Drawn heavier than a march in progress and with a longer dash, so the
         two are never confused: one is a plan and the other is happening. */
      #worldmap .wm-plot { pointer-events: none; }
      #worldmap .wm-plotline { fill: none; stroke-width: calc(1.1px * var(--wm-k, 1));
        stroke-dasharray: calc(2.6px * var(--wm-k, 1)) calc(1.6px * var(--wm-k, 1));
        opacity: 0.95; }
      #worldmap .wm-plotend { stroke-width: calc(0.9px * var(--wm-k, 1)); opacity: 0.95; }
      #worldmap .wm-plottext { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(3.4px * var(--wm-k, 1)); text-anchor: middle;
        letter-spacing: 0.08em; paint-order: stroke; stroke: #12100e;
        stroke-width: calc(0.9px * var(--wm-k, 1)); stroke-linejoin: round; }

      /* What the board says back when you give it an order, or refuse to take
         one. On the board and not in a column: a right-click that does nothing
         and puts its reason on the far side of the screen has explained
         nothing. */
      #worldmap .wm-say { position: absolute; left: 50%; transform: translateX(-50%);
        bottom: 16px; z-index: 6; pointer-events: none;
        padding: 7px 16px; max-width: 70%; text-align: center;
        font-size: 12px; color: var(--bone);
        background: rgba(14,13,11,0.92); border: 1px solid rgba(200,164,76,0.3);
        clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%);
        opacity: 0; transition: opacity 220ms ease; }
      #worldmap .wm-say.on { opacity: 1; }

      /* The gesture, said once where the orders are. A control nobody can find
         is worth about what a control that does not exist is worth, and this
         one has no button to advertise it. */
      #worldmap .wm-order-hint { margin: 10px 0 2px; padding: 7px 9px; font-size: 11.5px;
        line-height: 1.45; color: var(--bone-dim);
        background: rgba(200,164,76,0.08); border-left: 2px solid var(--gold-deep); }
      #worldmap .wm-order-hint b { color: var(--gold-pale); font-weight: 600; }

      /* Names. The halo is the whole trick: without it a name over hatching is
         mud, and with it the same name reads over anything.

         Type does not scale with the map. Every name is drawn at a size
         multiplied by --wm-k so it is the same height on the glass at every
         zoom — which is what makes a hundred and twenty-seven provinces
         readable at all — and the LOD rules below decide which of them are on
         screen, because constant-size type means zooming out removes room
         rather than making it. */
      /* Sizes were 2.85–4.1 here, which on a 1600-pixel screen is six to eight
         pixels of Cinzel — the owner's first note after actually playing was
         'impossible to read', and they were right: these sizes were inherited
         from a fit view nobody reads names at. Half again across the scale is
         eleven to fourteen pixels — small print, but print. The LOD rules
         below are what keep that from becoming porridge: fewer names survive
         each step out, and the fit view only ever shows the crowns' seats. */
      /* These are in map units inside .wm-town, which applyView scales by the
         zoom's reciprocal — so they are a *constant size on the glass*, and
         these numbers are that size. They were set by eye against a chart
         nobody had tried to read a place name off, and every one of them was
         about two thirds of what it needed to be. Raised by half, with the
         halo raised to match: a bigger name over hatching without a bigger
         stroke behind it is a bigger smudge. */
      /* **The names, raised again — the second time of asking.**

         > *"the map city names still need to be way bigger"*

         Raised about 55% across the rank ladder rather than by setting a new
         flat size, because the ladder is doing a job: *how big a name is says
         how important the place is*, and flattening it to make everything
         legible would throw that away to fix the smaller complaint. The casing
         goes up with it — a heavier stroke under heavier type, or the names
         start to smear into the ground they are written on. */
      #worldmap .wm-label { font-family: 'Cinzel', Georgia, serif;
        font-size: 18.5px;
        fill: #f0e8d3; text-anchor: middle; letter-spacing: 0.4px;
        paint-order: stroke; stroke: rgba(9,9,8,0.94);
        stroke-width: 4.4px; stroke-linejoin: round; }
      #worldmap .wm-label.cap { fill: var(--gold-pale); font-size: 21.5px; }
      /* Rank 0 is a seat of a crown, 3 is a village. The further out the view,
         the fewer of them survive — and how *big* a name is says how important
         the place is, which is the reference's rule and was not this map's: a
         village and a capital were within half a point of each other. */
      #worldmap .wm-label.r0 { font-size: 22.5px; letter-spacing: 2.1px; }
      #worldmap .wm-label.r1 { font-size: 19.0px; letter-spacing: 1.3px; }
      #worldmap .wm-label.r2 { font-size: 16.5px; }
      #worldmap .wm-label.r3 { font-size: 15.0px;
        fill: rgba(226,216,193,0.86); }
      #worldmap .wm-lod0 .r1, #worldmap .wm-lod0 .r2, #worldmap .wm-lod0 .r3,
      #worldmap .wm-lod1 .r2, #worldmap .wm-lod1 .r3,
      #worldmap .wm-lod2 .r3 { display: none; }

      /* ── The regions ──────────────────────────────────────────────────────
         The largest type on the sheet, and the only type at the fit view. A
         name in spaced small caps with what the place *is* in brackets under
         it, which is the reference's own device.

         The casing matters more here than anywhere else on the chart. A whisper
         of a name — this was opacity 0.19 before, for the crowns — is a whisper
         over a pale province and gone entirely over a dark one, so it was never
         one thing: it was a name that appeared and disappeared depending on
         whose ground it happened to lie on. Paint-order stroke fixes that for
         the price of one declaration, and it is what lets the fill be strong
         enough to survive being read across a mountain range. */
      #worldmap .wm-regionname { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(7.4px * var(--wm-k, 1)); text-anchor: middle;
        letter-spacing: calc(2.6px * var(--wm-k, 1));
        fill: rgba(238,229,206,0.5); paint-order: stroke;
        stroke: rgba(8,8,7,0.62); stroke-width: calc(1.5px * var(--wm-k, 1));
        stroke-linejoin: round; }
      #worldmap .wm-regionsub { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(3.9px * var(--wm-k, 1)); text-anchor: middle;
        letter-spacing: calc(0.5px * var(--wm-k, 1)); font-style: italic;
        fill: rgba(232,222,198,0.4); paint-order: stroke;
        stroke: rgba(8,8,7,0.55); stroke-width: calc(1.1px * var(--wm-k, 1));
        stroke-linejoin: round; }
      /* One notch in and the crowns take over the middle of the sheet. A region
         name then has nothing to caption that is on the screen. */
      #worldmap .wm-lod1 .wm-regions, #worldmap .wm-lod2 .wm-regions,
      #worldmap .wm-lod3 .wm-regions { display: none; }

      #worldmap .wm-realmname { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(4.6px * var(--wm-k, 1)); text-anchor: middle;
        letter-spacing: calc(1.4px * var(--wm-k, 1)); opacity: 0.42;
        paint-order: stroke; stroke: rgba(8,8,7,0.5);
        stroke-width: calc(1.1px * var(--wm-k, 1)); stroke-linejoin: round;
        text-transform: uppercase; }
      /* The people, under the name. Smaller, not upper-cased and not letter-
         spaced, so it reads as a gloss on the name rather than as a second
         name — which is the whole distinction the reference map draws between
         THE JADE EMPIRE and (Lizardfolk Dominion). */
      #worldmap .wm-realmpeople { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(2.9px * var(--wm-k, 1)); text-anchor: middle;
        letter-spacing: calc(0.3px * var(--wm-k, 1)); opacity: 0.36;
        paint-order: stroke; stroke: rgba(8,8,7,0.45);
        stroke-width: calc(0.9px * var(--wm-k, 1)); stroke-linejoin: round;
        font-style: italic; }
      /* Zoomed right in, a nation's name across its whole territory is a
         caption for something off screen. And zoomed right *out*, the regions
         own the sheet: twenty-four crowns lettered at the fit view is porridge,
         and it was porridge drawn at nineteen percent so nobody could see it
         was porridge. */
      #worldmap .wm-lod3 .wm-realmname, #worldmap .wm-lod0 .wm-realmname,
      #worldmap .wm-lod3 .wm-realmpeople,
      #worldmap .wm-lod0 .wm-realmpeople { display: none; }
      #worldmap .wm-wave { fill: none; stroke: rgba(140,178,196,0.13);
        stroke-width: calc(0.22px * var(--wm-k, 1)); stroke-linecap: round; }

      /* Cartographer's furniture. */
      #worldmap .wm-neat { fill: none; stroke: rgba(200,164,76,0.34); stroke-width: 0.4; }
      #worldmap .wm-neat.thin { stroke: rgba(200,164,76,0.2); stroke-width: 0.22; }
      #worldmap .wm-plate { fill: rgba(13,14,16,0.8); stroke: rgba(200,164,76,0.28);
        stroke-width: 0.28; }
      #worldmap .wm-maptitle { font-family: 'Cinzel', Georgia, serif; font-size: 4.4px;
        fill: var(--gold-pale); text-anchor: middle; letter-spacing: 0.9px;
        text-transform: uppercase; }
      #worldmap .wm-flourish { fill: rgba(200,164,76,0.5); stroke: rgba(200,164,76,0.5);
        stroke-width: 0.22; }
      #worldmap .wm-crose-ring { fill: none; stroke: rgba(200,164,76,0.4); stroke-width: 0.25; }
      #worldmap .wm-crose-ring.thin { stroke: rgba(200,164,76,0.22); stroke-width: 0.18; }
      #worldmap .wm-crose-a { fill: var(--gold-pale); }
      #worldmap .wm-crose-b { fill: rgba(200,164,76,0.55); }
      #worldmap .wm-crose-c { fill: none; stroke: rgba(200,164,76,0.4); stroke-width: 0.25; }
      #worldmap .wm-crose-n { font-family: 'Cinzel', Georgia, serif; font-size: 3px;
        fill: var(--gold-pale); text-anchor: middle; }
      /* The waters are named, and the names obey the type rule like everything
         else lettered. They did not: these were a flat font-size in map units,
         so four notches in "The Drowned Sea" was a foot high. */
      #worldmap .wm-seaname { font-family: 'Cinzel', Georgia, serif;
        font-size: calc(3.8px * var(--wm-k, 1)); fill: rgba(154,186,200,0.46);
        letter-spacing: calc(1.9px * var(--wm-k, 1)); text-transform: uppercase;
        text-anchor: middle; }
      #worldmap .wm-seaname.small { font-size: calc(2.9px * var(--wm-k, 1));
        fill: rgba(154,186,200,0.32); text-anchor: end; }
      /* The central sea gets the largest type on the water, which is what it is
         owed: every landmass on the board faces onto it. */
      #worldmap .wm-seaname.great { font-size: calc(6.6px * var(--wm-k, 1));
        letter-spacing: calc(3.4px * var(--wm-k, 1)); fill: rgba(160,194,208,0.44);
        paint-order: stroke; stroke: rgba(6,10,14,0.5);
        stroke-width: calc(1.4px * var(--wm-k, 1)); stroke-linejoin: round; }
      /* Right in, a sea's name is a caption for water that is off the screen. */
      #worldmap .wm-lod3 .wm-seanames { display: none; }
      #worldmap .wm-legend-h { font-family: 'Cinzel', Georgia, serif; font-size: 2.7px;
        fill: var(--gold); letter-spacing: 0.4px; text-transform: uppercase; }
      #worldmap .wm-legend-t { font-size: 2.05px; fill: rgba(216,206,186,0.75); }
      #worldmap .wm-scale path { fill: none; stroke: rgba(216,206,186,0.6); stroke-width: 0.25; }
      #worldmap .wm-scale .wm-scalefill { fill: rgba(216,206,186,0.55); stroke: none; }
      #worldmap .wm-scale-t { font-size: 2.3px; fill: rgba(216,206,186,0.6); text-anchor: middle;
        letter-spacing: 0.2px; }
      #worldmap .wm-colophon { font-size: 2.3px; fill: rgba(216,206,186,0.4); text-anchor: start;
        font-style: italic; }
      /* The realm entire, an inch across, with the window drawn on it. */
      #worldmap .wm-minicoast { fill: rgba(120,112,92,0.5); stroke: rgba(226,216,193,0.4);
        stroke-width: 1.6; }
      #worldmap .wm-minibox { fill: rgba(200,164,76,0.14); stroke: var(--gold-pale);
        stroke-width: 2.2; }
      /* wm-modal, wm-sheet and wm-odds were the war menu's, and went
         with it to ui/warcall.ts. Nothing else on this screen is a modal:
         the map is a board, and everything on it answers in the column. */

      /* ── The detail column ───────────────────────────────────────────────
         One column with a subject rather than a province, so it needs a way to
         say what it is currently about and a way back. */
      #worldmap .wm-tabs { display: flex; gap: 0; margin: -4px 0 14px;
        border-bottom: 1px solid rgba(200,164,76,0.18); }
      #worldmap .wm-tab { flex: 1; text-align: center; padding: 7px 4px 9px; cursor: pointer;
        font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--bone-deep); border-bottom: 2px solid transparent;
        position: relative; }
      #worldmap .wm-tab:hover { color: var(--bone); }
      #worldmap .wm-tab.on { color: var(--gold); border-bottom-color: var(--gold-deep); }
      #worldmap .wm-badge { font-style: normal; margin-left: 6px; padding: 1px 5px;
        font-size: 9.5px; letter-spacing: 0; background: rgba(200,164,76,0.22);
        color: var(--gold-pale); border-radius: 2px; }
      #worldmap .wm-badge.urgent { background: #8f2b24; color: #f2e2d4; }
      #worldmap .wm-swatch { display: inline-block; width: 9px; height: 9px; border-radius: 2px;
        margin-right: 7px; vertical-align: baseline; flex: none; }
      #worldmap .wm-roster { margin: 4px 0 8px; line-height: 1.5; font-size: 11px; }
      /* A bar. Used for a march's progress and for supply, which are the two
         numbers on this screen that are fractions of something. */
      #worldmap .wm-meter { height: 4px; background: rgba(255,255,255,0.07); margin: 3px 0 9px; }
      #worldmap .wm-meter i { display: block; height: 100%; background: var(--gold-deep); }
      #worldmap .wm-meter.bad i { background: #a8362c; }

      #worldmap .wm-warnrow { padding: 7px 9px; margin-bottom: 5px; cursor: pointer;
        font-size: 11px; line-height: 1.5; color: var(--bone-dim);
        background: rgba(216,165,58,0.07); border-left: 2px solid rgba(216,165,58,0.55); }
      #worldmap .wm-warnrow:hover { background: rgba(216,165,58,0.16); color: var(--bone); }
      #worldmap .wm-warnrow.urgent { border-left-color: #a8302f; background: rgba(168,48,47,0.12); }
      #worldmap .wm-warnrow.camp { border-left-color: #7a2f26; background: rgba(122,47,38,0.12); }
      #worldmap .wm-warncard { padding: 9px 11px; margin-bottom: 9px;
        background: rgba(255,255,255,0.03); border-left: 2px solid rgba(216,165,58,0.6); }
      #worldmap .wm-warncard.massing { border-left-color: rgba(200,164,76,0.4); }
      #worldmap .wm-warncard.camp { border-left-color: #7a2f26; }
      #worldmap .wm-warncard > b { display: block; font-weight: 500; color: var(--gold-pale);
        margin-bottom: 5px; }
      #worldmap .wm-warncard .wm-acts { margin-top: 8px; }

      #worldmap .wm-offer { padding: 9px 11px; margin-bottom: 9px;
        background: rgba(255,255,255,0.04); border-left: 2px solid var(--gold-deep); }
      #worldmap .wm-offer.urgent { border-left-color: #a8302f; background: rgba(168,48,47,0.1); }
      #worldmap .wm-offer > b { display: block; font-weight: 500; margin-bottom: 4px; }
      #worldmap .wm-offer .wm-dim { display: block; font-size: 10.5px; line-height: 1.5;
        color: var(--bone-deep); }
      #worldmap .wm-offer .wm-acts { margin-top: 8px; }

      #worldmap .wm-war { padding: 9px 11px; margin-bottom: 9px;
        background: rgba(255,255,255,0.03); border-left: 2px solid #a8362c; }
      #worldmap .wm-war > b { display: flex; justify-content: space-between; cursor: pointer;
        font-weight: 500; color: var(--bone); }
      #worldmap .wm-war > b:hover { color: var(--gold-pale); }
      #worldmap .wm-war > b i { font-style: normal; font-variant-numeric: tabular-nums; }
      #worldmap .wm-war.up > b i { color: #7d9436; }
      #worldmap .wm-war.down > b i { color: #a8362c; }
      #worldmap .wm-war .wm-dim { display: block; font-size: 10.5px; color: var(--bone-deep);
        margin-top: 4px; }
      #worldmap .wm-war .wm-acts { margin-top: 8px; }
      /* War score runs -100..+100 from the middle, so the bar has a middle. */
      #worldmap .wm-scorebar { position: relative; height: 4px; margin: 6px 0 2px;
        background: rgba(255,255,255,0.07); }
      #worldmap .wm-scorebar::after { content: ''; position: absolute; left: 50%; top: -2px;
        width: 1px; height: 8px; background: rgba(226,216,193,0.4); }
      #worldmap .wm-scorebar i { position: absolute; top: 0; height: 100%; }
      #worldmap .wm-scorebar i.up { background: #7d9436; }
      #worldmap .wm-scorebar i.down { background: #a8362c; }

      #worldmap .wm-crown { margin-bottom: 4px; }
      #worldmap .wm-crownhead { display: flex; align-items: center; gap: 2px; padding: 5px 6px;
        cursor: pointer; }
      #worldmap .wm-crownhead:hover { background: rgba(255,255,255,0.05); }
      #worldmap .wm-crown.open .wm-crownhead { background: rgba(200,164,76,0.12); }
      #worldmap .wm-crown.war .wm-crownhead { border-left: 2px solid #a8362c; padding-left: 4px; }
      #worldmap .wm-crownhead span { flex: 1; color: var(--bone-dim); }
      #worldmap .wm-crownhead span em { display: block; font-style: normal; font-size: 9.5px;
        letter-spacing: 0.1em; text-transform: uppercase; color: var(--bone-deep); }
      #worldmap .wm-crownhead b { font-variant-numeric: tabular-nums; font-weight: 500; }
      #worldmap .wm-crownhead b.up { color: #7d9436; }
      #worldmap .wm-crownhead b.down { color: #a8362c; }
      #worldmap .wm-crown .wm-acts { padding: 6px 6px 10px; }
      #worldmap .wm-rival[data-act] { cursor: pointer; }
      #worldmap .wm-rival[data-act]:hover { background: rgba(255,255,255,0.05); }
      #worldmap .wm-rival.war i { box-shadow: 0 0 0 1px #a8362c; }

      /* ── The sea, the navy and the markets (tradeui) ──────────────────────
         Everything drawn in map units below is multiplied by --wm-k where it is
         a line or a letter, which is the rule the rest of the chart follows:
         constant weight on the glass at every zoom. Anything inside a counter is
         NOT multiplied, because applyView has already folded the reciprocal of
         the zoom into that counter's own transform, and doing it twice makes a
         mark that grows as you zoom out. */

      /* Lanes. Two hundred and fifty-two of them, so the resting weight is
         almost nothing: the layer is there to say the sea has roads, not to be
         read. The lanes out of the selected province are drawn again on top at
         full strength, which is what turns the layer into an answer. */
      #worldmap .wm-lanes { pointer-events: none; }
      /* Traffic is scenery: it never takes a click, and it is quiet enough that
         a fleet — which is information — is never mistaken for it. Hidden at the
         fit view, where six hundred and fifty provinces are already competing
         and a scatter of two-unit hulls is noise. */
      #worldmap .wm-trafficlayer { pointer-events: none; }
      #worldmap .wm-hull { fill: rgba(232,223,200,0.5); }
      #worldmap .wm-sail { fill: rgba(232,223,200,0.34); }
      #worldmap .wm-lod0 .wm-trafficlayer { display: none; }
      #worldmap .wm-lane { fill: none; stroke: rgba(124,170,192,0.26);
        stroke-width: calc(0.26px * var(--wm-k, 1));
        stroke-dasharray: calc(1.2px * var(--wm-k, 1)) calc(1.6px * var(--wm-k, 1));
        stroke-linecap: round; }
      #worldmap .wm-lane.lit { stroke: rgba(170,214,236,0.62);
        stroke-width: calc(0.42px * var(--wm-k, 1)); }
      /* A voyage under way: the same dashed course a march gets, in the crown's
         own colour, so a convoy and a marching host read as the same kind of
         object moving on different ground. */
      #worldmap .wm-course { fill: none; stroke-width: calc(0.6px * var(--wm-k, 1));
        stroke-dasharray: calc(2.0px * var(--wm-k, 1)) calc(1.4px * var(--wm-k, 1));
        opacity: 0.9; }
      #worldmap .wm-course.sel { stroke-width: calc(1.05px * var(--wm-k, 1)); opacity: 1; }

      /* The harbour mark. Drawn off the town toward the water by portSlot, and
         inside .wm-town — so, like everything else in there, its widths are
         plain: the group has already applied the zoom's reciprocal. Its offset
         from the town shrinks with it, which is the point: an anchor four units
         off a town is beside it at the fit view and a county away at the close
         one if the offset is left on the ground. */
      #worldmap .wm-port { pointer-events: none; }
      #worldmap .wm-anchor { fill: #dbe4e8; stroke: rgba(8,10,12,0.85);
        stroke-width: 0.28; stroke-linejoin: round;
        fill-rule: evenodd; }
      #worldmap .wm-anchor-eye { fill: none; stroke: #dbe4e8; stroke-width: 0.4; }
      #worldmap .wm-portpip { fill: rgba(216,182,96,0.92); stroke: rgba(8,10,12,0.7);
        stroke-width: 0.16; }

      /* Hosts and hulls: the same banner, on land and on water. The flag takes
         the crown's accent as a fill attribute; everything else here is the
         casing that keeps it legible over both ash and deep water. */
      /* Weights up with the mark. A banner drawn half again as big on a
         hairline pole reads as a flag that has come loose from it. */
      /* The relief. Soft-light rather than multiply or overlay: multiply can
         only darken, so a lit slope does nothing and the whole board sinks;
         overlay swings so hard that a pale desert blows out and a dark forest
         blocks up. Soft-light bends the colour underneath toward the shade
         without taking it over, which is what a hillshade over a coloured map
         is supposed to do. Pointer-events off — it is the width of the board and
         would otherwise swallow every click on the ground.
         (No backticks in this file: it is one template literal.) */
      /* ── The phone layout ──────────────────────────────────────────────
       *
       * Measured before it was written. At 390px the three desktop columns
       * resolve to 268 + 455 + 320 = 1,043 inside a 390 box whose parent is
       * overflow:hidden, so the chart showed 122 pixels of sea, the detail
       * column — the whole interactive half of this screen — landed at x=723
       * and did not exist, and the zoom controls were off the right edge.
       *
       * Driven by a .narrow class rather than a bare media block: the gesture
       * layer and the hover card need the same flag in script. NARROW_PX is the
       * one number, exported from ui/touch.ts.
       *
       * (No backticks anywhere in this string. It is one template literal, and
       * a backtick in a comment ends the stylesheet. It has cost four sessions.)
       */
      /* The strip is 44px on a phone rather than 40, and it scrolls sideways
         rather than dropping chips — so the map's own top edge follows it. */
      #worldmap.narrow { top: 44px; }
      /* Panels away: the same shape the narrow layout already uses, reached on
         purpose instead of by shrinking the window. The log goes with them —
         it is the chronicle, and somebody who has asked for the map does not
         want a third of the bottom edge reading it out. */
      #worldmap .wm-keyplate.away { display: none; }
      /* Welded to the corner of the map cell, not to the furniture's phantom
         box. Width in px so it is the same size on the glass at every zoom —
         a minimap that grew as you zoomed in would be telling you about the
         window rather than about the realm. */
      #worldmap .wm-minimap { position: absolute; right: 0; bottom: 17px;
        width: 168px; height: auto; pointer-events: none;
        background: rgba(10,10,9,0.82); border-top: 1px solid rgba(200,164,76,0.28);
        border-left: 1px solid rgba(200,164,76,0.28); }
      #worldmap .wm-map { position: relative; }
      /* Bottom-left corner of the board, opposite the minimap, and only when
         it has been asked for. */
      #worldmap .wm-keybox { position: absolute; left: 0; bottom: 0; width: 190px;
        background: rgba(10,10,9,0.86); border-top: 1px solid rgba(200,164,76,0.28);
        border-right: 1px solid rgba(200,164,76,0.28); padding: 5px 7px 7px;
        pointer-events: none; }
      #worldmap .wm-keybox.away { display: none; }
      #worldmap .wm-keyhead { font-family: 'Cinzel', Georgia, serif; font-size: 10px;
        letter-spacing: 0.8px; text-transform: uppercase; color: var(--gold);
        margin-bottom: 3px; }
      #worldmap .wm-keysvg { width: 100%; height: auto; display: block; overflow: visible; }
      /* The distance readout, kept when the scale bar went, and put where the
         eye already is: on the minimap. It is the one piece of the old
         cartography that was answering a question rather than decorating. */
      #worldmap .wm-scaletag { position: absolute; right: 0; bottom: 0;
        padding: 2px 6px; font-size: 9.5px; letter-spacing: 0.4px;
        color: rgba(216,206,186,0.66); background: rgba(10,10,9,0.86);
        border-left: 1px solid rgba(200,164,76,0.28);
        border-top: 1px solid rgba(200,164,76,0.28);
        pointer-events: none; font-variant-numeric: tabular-nums; }
      #worldmap.wm-bare .wm-grid { grid-template-columns: 1fr; grid-template-rows: 1fr;
        grid-template-areas: "map"; }
      #worldmap.wm-bare .wm-left, #worldmap.wm-bare .wm-right, #worldmap.wm-bare .wm-log {
        display: none; }
      #worldmap.narrow .wm-grid { grid-template-columns: 1fr; grid-template-rows: 1fr;
        grid-template-areas: "map"; }
      /* Stacked *on* the chart rather than beside it, and shut until asked for.
         62vh and not the settlement's 50: these are dense reading panels — the
         realm column alone is 2,594 pixels of content — and the chart behind
         one does not need to stay legible while it is open. */
      #worldmap.narrow .wm-left,
      #worldmap.narrow .wm-right,
      #worldmap.narrow .wm-log {
        grid-area: map; position: absolute; left: 0; right: 0; top: auto;
        bottom: 52px; height: 62vh; display: none; z-index: 6;
        border: none; border-top: 1px solid rgba(200,164,76,0.16);
        /* Opaque. A sheet is *over* the map, not a wash on it: at 0.97 the
           chart's own title read straight through the province name. */
        background: #0b0b09; }
      #worldmap.narrow.sheet-left .wm-left { display: block; }
      #worldmap.narrow.sheet-right .wm-right { display: block; }
      #worldmap.narrow.sheet-chron .wm-log { display: block; }

      /* The bar. Hidden on desktop, where the columns are columns. */
      #worldmap .wm-sheetbar { display: none; }
      #worldmap.narrow .wm-sheetbar {
        position: absolute; left: 0; right: 0; bottom: 0; height: 52px; z-index: 7;
        display: flex; align-items: stretch;
        background: linear-gradient(0deg, rgba(12,12,10,0.99), rgba(12,12,10,0.92));
        border-top: 1px solid rgba(200,164,76,0.3); }
      #worldmap .wm-sheettab { flex: 1 1 0; display: flex; align-items: center;
        justify-content: center; gap: 5px; cursor: pointer; position: relative;
        font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--bone-deep);
        border-right: 1px solid rgba(200,164,76,0.12); }
      #worldmap .wm-sheettab:last-child { border-right: none; }
      #worldmap .wm-sheettab.on { color: var(--gold-pale); background: rgba(200,164,76,0.16); }

      /* THE KEY is a legend rather than a control and is ~300px wide on a
         390px screen. The scale bar and the colophon are the same case. */
      /* The legend is a legend rather than a control, and it is ~300px wide on a
         390px screen — it sat across the top-left corner of the board. The view
         switch stays: that one *is* a control. */
      #worldmap.narrow .wm-keylist,
      #worldmap.narrow .wm-key,
      #worldmap.narrow .wm-colophon,
      #worldmap.narrow .wm-scale { display: none; }

      /* The drawn furniture — the cartouche, the key plate, the scale ticks, the
         neat line and the thumbnail — is inside the SVG and therefore laid out
         in *map* coordinates against a landscape sheet. On a 390x844 viewport
         the chart letterboxes and all of it lands across the middle of the
         board: a title bar and a legend floating over the Weltering.
         The compass survives, because north on a map you can drag is a control
         and not an ornament. */
      #worldmap.narrow .wm-cartouche,
      #worldmap.narrow .wm-maptitle,
      #worldmap.narrow .wm-flourish,
      #worldmap.narrow .wm-plate,
      #worldmap.narrow .wm-legend-h,
      #worldmap.narrow .wm-legend-t,
      #worldmap.narrow .wm-neat,
      #worldmap.narrow .wm-keyplate,
      #worldmap.narrow .wm-scalebar,
      #worldmap.narrow .wm-mini,
      #worldmap.narrow .wm-minibox { display: none; }
      /* The switch wraps to two rows at this width; let it, and keep it clear
         of the zoom column on the other side. */
      #worldmap.narrow .wm-view { right: 62px; }
      #worldmap.narrow .wm-viewtabs { flex-wrap: wrap; }

      /* Scrolling the muster must not scroll away the only way off it. */
      #worldmap.narrow .wm-tabs { position: sticky; top: 0; z-index: 2;
        background: rgba(10,10,8,0.97); }

      /* A finger is not a cursor. 44px is the figure the rest of this layout is
         built to, and the crown and host rows were 26-30. */
      #worldmap.narrow .wm-rival,
      #worldmap.narrow .wm-army,
      #worldmap.narrow .wm-tab { min-height: 44px; }
      #worldmap.narrow .wm-btn { padding: 11px 13px; }
      #worldmap.narrow .wm-zoom span { width: 38px; height: 38px; font-size: 18px; }

      /* Screen, because a shelf is light *added* to dark water — multiply would
         only deepen it, and the whole point is that shallow reads brighter than
         deep. */
      /* Rivers. Round caps and joins because a watercourse has no corners, and
         a width that comes from the flow rather than from CSS — see rivers.ts.
         Scaled by --wm-k with everything else that is drawn rather than
         grown, so a river is the same weight on the glass at every zoom. */
      #worldmap .wm-rivers path { fill: none; stroke: rgba(138,186,206,0.92);
        stroke-linecap: round; stroke-linejoin: round;
        vector-effect: non-scaling-stroke; }
      #worldmap .wm-shelf { mix-blend-mode: screen; opacity: 0.5;
        pointer-events: none; }
      /* Soft-light and low: the grain is the surface the chart is drawn on, not
         a sheet of paper lying over the top of it. Overlay was tried and blows
         the deserts out; multiply only dirties everything. */
      #worldmap .wm-paper { mix-blend-mode: soft-light; opacity: 0.5;
        pointer-events: none; }
      #worldmap .wm-relief { mix-blend-mode: overlay; opacity: 0.62;
        pointer-events: none; }
      /* The strand, the cliffs' rock and the snow. Plain source-over and no
         blend mode at all, and that is the whole reason it exists: it is the one
         layer allowed to change what colour the ground *is*, where the relief
         may only light it and the fabric may only darken it. It sits under both
         of those in the document, so they still get to do their jobs on top. */
      #worldmap .wm-ground { opacity: 0.88; pointer-events: none; }
      /* The biome wash: the ground's own colour, per raster cell, under the
         politics. Fully opaque and no blend mode — it *is* the ground, and the
         bed's flat land fill under it is only there for the moment before it
         bakes. pointer-events:none matters here: it covers every province on
         the sheet and it must not take the mouse from the cells over it. */
      #worldmap .wm-biome { pointer-events: none; }
      /* The fabric. Multiply, because every mark it makes is a thing sitting on
         the ground and casting on it — a canopy, a dune's lee, a tussock. Ice is
         the exception and it writes a pale value, which multiply would swallow,
         so the layer carries a little screen back via opacity rather than a
         second element. */
      #worldmap .wm-fabric { mix-blend-mode: multiply; opacity: 0.72;
        pointer-events: none; }
      #worldmap .wm-hostflag .wm-pole { stroke: rgba(238,229,206,0.92); stroke-width: 0.62; }
      #worldmap .wm-hostflag .wm-flag { stroke: rgba(12,11,9,0.9); stroke-width: 0.34; }
      #worldmap .wm-hostflag .wm-flag { stroke: rgba(10,9,8,0.85); stroke-width: 0.3; }
      #worldmap .wm-hostflag.mine .wm-flag { stroke: rgba(240,232,211,0.9); stroke-width: 0.38; }
      #worldmap .wm-hostflag.hostile .wm-pole { stroke: #d8837a; }
      #worldmap .wm-countertext.host { font-size: 2.4px; fill: var(--bone);
        paint-order: stroke; stroke: rgba(9,9,8,0.9); stroke-width: 0.9; }

      /* The stack under a banner: one pip a host, the held ones filled.

         Drawn inside the banner's own counter, so it is scaled by the same
         reciprocal and stays glued to the mark at every zoom — see hostPips()
         for why this is the gesture and not a fan of banners. The hit disc is
         nearly twice the dot, because these are small and are the one thing on
         the board a player picks a *particular* one of. */
      #worldmap .wm-hostpip { cursor: pointer; }
      #worldmap .wm-hostpip .wm-piphit { fill: transparent; }
      #worldmap .wm-hostpip .wm-pipdot { fill: rgba(18,16,14,0.55);
        stroke: var(--gold-pale); stroke-width: 0.34; }
      #worldmap .wm-hostpip.on .wm-pipdot { fill: var(--gold-pale);
        stroke: rgba(18,16,14,0.85); stroke-width: 0.3; }
      #worldmap .wm-hostpip:hover .wm-pipdot { stroke: #fff; stroke-width: 0.5; }

      /* The marquee. A hairline box with a wash inside it, in the same gold the
         route preview uses: both are a gesture in progress rather than a fact
         about the realm. */
      #worldmap .wm-marquee { pointer-events: none; }
      /* Heavier than the route preview's hairline on purpose: the preview is a
         thing being *considered* and this is a thing being *done*, and at the
         weight the preview uses the box came out as a faint rectangle a
         screenshot could barely find. */
      #worldmap .wm-marqbox { fill: rgba(230,207,146,0.16);
        stroke: var(--gold-pale); stroke-width: calc(2.4px * var(--wm-k, 1));
        stroke-dasharray: calc(4.5px * var(--wm-k, 1)) calc(2.6px * var(--wm-k, 1)); }

      /* The levy, on the ground it is raised from. A sheaf of spears whose
         height is the share this province sends, with the number under it.
         Oxblood when something is holding it down — a siege, a rising,
         brigands — because that is a mark a player should be able to find
         without reading any of them. */
      #worldmap .wm-levy .wm-levyplate { fill: rgba(14,12,10,0.72);
        stroke: rgba(200,164,76,0.5); stroke-width: 0.4; }
      #worldmap .wm-levy path { stroke: var(--gold-pale); stroke-width: 1.05; fill: none;
        stroke-linecap: round; stroke-linejoin: round; }
      #worldmap .wm-levy .wm-levyhead { fill: var(--gold-pale); stroke-width: 0.5; }
      #worldmap .wm-levy.held path { stroke: #d8837a; }
      #worldmap .wm-levy.held .wm-levyhead { fill: #d8837a; }
      #worldmap .wm-levy.held .wm-levyplate { stroke: rgba(200,86,74,0.7); }
      #worldmap .wm-countertext.levy { font-size: 3.6px; fill: var(--gold-pale);
        paint-order: stroke; stroke: rgba(9,9,8,0.95); stroke-width: 1.4; }
      #worldmap .wm-levy.held .wm-countertext.levy { fill: #e0918a; }

      /* The key for those marks, and the way to put them away. Beside the say
         line rather than in the zoom cluster: the cluster is about the view and
         this is about what is on the paper. */
      #worldmap .wm-levykey { position: absolute; left: 12px; bottom: 16px; z-index: 6;
        cursor: pointer; user-select: none; padding: 6px 11px; font-size: 11.5px;
        color: var(--gold-pale); border: 1px solid var(--gold-deep);
        background: rgba(20,18,15,0.82); white-space: nowrap; }
      #worldmap .wm-levykey:hover { background: rgba(200,164,76,0.24); }

      /* A row of doors under a panel's numbers. The Muster's two are the Levies
         screen and its marks. */
      #worldmap .wm-btnrow { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 2px; }
      #worldmap .wm-held { font-style: normal; font-size: 9.5px; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--gold-pale); border: 1px solid var(--gold-deep);
        padding: 0 4px; margin-left: 6px; vertical-align: 1px; }
      #worldmap .wm-btn.on { border-color: var(--gold); background: rgba(200,164,76,0.18); }
      #worldmap .wm-fleet .wm-fleetplate { fill: rgba(9,15,20,0.88);
        stroke: rgba(150,186,204,0.55); stroke-width: 0.3; }
      #worldmap .wm-fleet .wm-hull { fill: #cfd8dc; stroke: rgba(8,10,12,0.9);
        stroke-width: 0.3; stroke-linejoin: round; }
      #worldmap .wm-fleet .wm-mast { stroke: #cfd8dc; stroke-width: 0.38; fill: none; }
      #worldmap .wm-fleet .wm-flag { stroke: rgba(8,10,12,0.85); stroke-width: 0.28; }
      #worldmap .wm-fleet.mine .wm-fleetplate { stroke: var(--gold-pale); stroke-width: 0.42; }
      #worldmap .wm-fleet.hostile .wm-fleetplate { stroke: #c8564a; stroke-width: 0.42; }
      /* A laden convoy is the one that matters: it is an invasion, and an empty
         escort is not. The full flag says so, and so does a heavier rim. */
      #worldmap .wm-fleet.laden .wm-fleetplate { stroke-width: 0.55; }
      #worldmap .wm-countertext.fleet { font-size: 3px; fill: rgba(208,232,244,0.98);
        paint-order: stroke; stroke: rgba(6,10,14,0.95); stroke-width: 1.1; }

      /* A market: scales, on the seat province the fair is held in. */
      #worldmap .wm-market .wm-scalesbeam { fill: none; stroke: rgba(232,222,198,0.9);
        stroke-width: 0.32; stroke-linecap: round; }
      #worldmap .wm-market .wm-scalespan { stroke: rgba(10,9,8,0.8); stroke-width: 0.24; }
      #worldmap .wm-market .wm-termring { fill: none; stroke: rgba(200,164,76,0.75);
        stroke-width: 0.3; }
      #worldmap .wm-market.mine .wm-scalesbeam { stroke: var(--gold-pale); }
      #worldmap .wm-countertext.market { font-size: 2.4px; fill: var(--bone);
        paint-order: stroke; stroke: rgba(9,9,8,0.9); stroke-width: 0.9; }
      /* Flow arrows. The width is the gold that moved, written as an attribute,
         so it is deliberately not set here — only the ink is. */
      #worldmap .wm-flows { pointer-events: none; }
      #worldmap .wm-flow { fill: none; stroke: rgba(200,164,76,0.26); stroke-linecap: round; }
      #worldmap .wm-flowhead { fill: rgba(216,182,96,0.34); stroke: none; }
      #worldmap .wm-flow.lit { stroke: rgba(226,196,116,0.8); }
      #worldmap .wm-flowhead.lit { fill: rgba(240,214,140,0.92); }

      /* ── The panels ──────────────────────────────────────────────────────── */
      #worldmap .wm-stat.wm-click { cursor: pointer; }
      #worldmap .wm-stat.wm-click:hover { background: rgba(255,255,255,0.05); }
      #worldmap .wm-sub.tight { margin: 12px 0 6px; padding-top: 8px; }
      /* A factor. The travelling state is deliberately the loud one: a merchant
         on the road is earning nothing for anybody, and a player who cannot see
         that will keep re-posting and wonder why nothing pays. */
      #worldmap .wm-factor { padding: 7px 9px; margin-bottom: 5px; cursor: pointer;
        background: rgba(255,255,255,0.03); border-left: 2px solid var(--gold-deep); }
      #worldmap .wm-factor:hover { background: rgba(200,164,76,0.12); }
      #worldmap .wm-factor.travelling { border-left-color: #8a6a2c;
        background: rgba(216,165,58,0.06); }
      #worldmap .wm-factor b { display: block; font-weight: 500; color: var(--gold-pale); }
      #worldmap .wm-factor span { display: block; font-size: 11px; color: var(--bone-deep); }
      #worldmap .wm-onroad { color: #d8a53a; font-style: italic; }
      #worldmap .wm-yours { color: var(--gold-pale); }
      #worldmap .wm-noderow { padding: 6px 9px; margin-bottom: 4px; cursor: pointer;
        background: rgba(255,255,255,0.02); border-left: 2px solid rgba(200,164,76,0.14); }
      #worldmap .wm-noderow:hover { background: rgba(255,255,255,0.06); }
      #worldmap .wm-noderow.held { border-left-color: var(--gold-deep); }
      #worldmap .wm-noderow.sel { background: rgba(200,164,76,0.14);
        border-left-color: var(--gold); }
      #worldmap .wm-noderow b { display: block; font-weight: 500; }
      #worldmap .wm-noderow span { display: block; font-size: 10.5px; color: var(--bone-deep); }
      #worldmap .wm-fmark { font-style: normal; margin-left: 6px; color: var(--gold);
        font-size: 10px; }
      /* One market, opened. */
      #worldmap .wm-market { padding: 10px 12px; margin: 4px 0 12px;
        background: rgba(255,255,255,0.035); border-left: 2px solid var(--gold-deep); }
      #worldmap .wm-market.drained { border-left-color: #a8362c; }
      #worldmap .wm-market > b { display: flex; justify-content: space-between;
        align-items: baseline; font-weight: 500; color: var(--gold-pale); margin-bottom: 6px; }
      #worldmap .wm-nodekind { font-style: normal; font-size: 10px; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--bone-deep); }
      /* Somebody dragging value out of a market is an act of war and reads as
         one: this is the only red on the card. */
      #worldmap .wm-drain { margin: 2px 0 8px; font-size: 11px; line-height: 1.55;
        color: var(--oxblood-light, #a8362c); font-style: italic; }
      /* ── The order book ──────────────────────────────────────────────────
         Same vocabulary as the factors above it: a card with a gold rule down
         the left for something of yours, a flat row for something of somebody
         else's. A sell wears the brighter rule because its goods are already
         out of the treasury — the escrow is the thing about an ask that a
         player has to be able to see without reading it. */
      #worldmap .wm-order { padding: 7px 9px; margin-bottom: 5px;
        background: rgba(255,255,255,0.03); border-left: 2px solid rgba(200,164,76,0.3); }
      #worldmap .wm-order.sell { border-left-color: var(--gold); }
      #worldmap .wm-order b { display: block; font-weight: 500; color: var(--gold-pale); }
      #worldmap .wm-order b i { font-style: normal; float: right; color: var(--bone-dim);
        font-variant-numeric: tabular-nums; }
      #worldmap .wm-order span { display: block; font-size: 11px; color: var(--bone-deep);
        line-height: 1.5; }
      #worldmap .wm-order .wm-dim { color: rgba(200,190,170,0.4); font-size: 10.5px; }
      #worldmap .wm-order .wm-acts { margin-top: 6px; }
      /* One good's two sides. */
      #worldmap .wm-bookgood { margin-bottom: 9px; }
      #worldmap .wm-bookgood > b { display: flex; justify-content: space-between;
        align-items: baseline; font-weight: 500; font-size: 11px; letter-spacing: 0.14em;
        text-transform: uppercase; color: var(--gold-deep); margin-bottom: 3px; }
      #worldmap .wm-bookgood > b i { font-style: normal; letter-spacing: 0;
        text-transform: none; font-size: 10.5px; color: var(--bone-deep);
        font-variant-numeric: tabular-nums; }
      #worldmap .wm-bookrow { display: flex; align-items: baseline; gap: 7px; padding: 2px 6px;
        cursor: pointer; font-variant-numeric: tabular-nums;
        border-left: 2px solid rgba(200,164,76,0.14); }
      #worldmap .wm-bookrow:hover { background: rgba(255,255,255,0.06); }
      /* Asks and bids are opposite sides of the same line and have to be told
         apart at a glance; the rule down the left is the whole difference. */
      #worldmap .wm-bookrow.ask { border-left-color: rgba(216,165,58,0.5); }
      #worldmap .wm-bookrow.bid { border-left-color: rgba(109,168,196,0.45); }
      #worldmap .wm-bookrow.mine { background: rgba(200,164,76,0.1); cursor: default;
        border-left-color: var(--gold); }
      #worldmap .wm-bookside { font-size: 10px; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--bone-deep); width: 30px; flex: none; }
      #worldmap .wm-bookrow b { font-weight: 500; width: 46px; flex: none; }
      #worldmap .wm-bookamt { width: 34px; flex: none; color: var(--bone-dim); font-size: 11px; }
      #worldmap .wm-bookrow .wm-dim { flex: 1; text-align: right; font-size: 10.5px;
        color: rgba(200,190,170,0.45); overflow: hidden; text-overflow: ellipsis;
        white-space: nowrap; }
      #worldmap .wm-bookmore { padding: 1px 6px 1px 8px; font-size: 10px; font-style: italic;
        color: rgba(200,190,170,0.4); }
      /* The form. The chosen side and the chosen good wear the same lit state
         the view tabs do, because they are the same kind of control. */
      #worldmap .wm-btn.on { background: rgba(200,164,76,0.2); border-color: var(--gold);
        color: var(--gold-pale); }
      #worldmap .wm-btn.tiny { padding: 4px 7px; font-size: 11px; clip-path: none; }
      #worldmap .wm-goods { display: flex; flex-wrap: wrap; gap: 4px; margin: 7px 0; }
      #worldmap .wm-good { padding: 4px 7px; cursor: pointer; font-size: 11px;
        background: rgba(255,255,255,0.03); border: 1px solid rgba(200,164,76,0.16); }
      #worldmap .wm-good:hover { background: rgba(200,164,76,0.14); border-color: var(--gold-deep); }
      #worldmap .wm-good.on { background: rgba(200,164,76,0.2); border-color: var(--gold);
        color: var(--gold-pale); }
      #worldmap .wm-good i { font-style: normal; margin-left: 5px; font-size: 10px;
        color: var(--bone-deep); font-variant-numeric: tabular-nums; }
      #worldmap .wm-step { display: flex; align-items: center; gap: 4px; margin-bottom: 7px; }
      #worldmap .wm-step b { flex: 1; text-align: center; font-weight: 500;
        color: var(--gold-pale); font-variant-numeric: tabular-nums; }
      #worldmap .wm-step b i { display: block; font-style: normal; font-size: 10px;
        letter-spacing: 0.12em; text-transform: uppercase; color: var(--bone-deep); }
      #worldmap .wm-warncard.landing { border-left-color: #6da8c4; }
      /* Four tabs will not sit on one 220px line, and a key that has to be read
         sideways is not a key. */
      #worldmap .wm-viewtabs { flex-wrap: wrap; }
      #worldmap .wm-viewtabs span:nth-child(3) { border-left: none; }
    `;
    document.head.appendChild(s);
  }
}
